import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { CertificateDoc, QuizEngine } from "./CertPortal";
import { emailMaterialOrder, emailToolRecall, emailInvoice, emailInvoiceAdjustRequest, emailInvoiceVoidRequest } from "./email";
import SHARED_PRODUCTS from "./sharedProducts";
import SHARED_SETTINGS from "./sharedSettings";
import SHARED_VISITS from "./sharedVisits";
import SHARED_REP_PROFILES from "./sharedRepProfiles";
import SHARED_REP_CUSTOMERS from "./sharedRepCustomers";
import ChangeLoginEmail from "./ChangeLoginEmail";
import { uploadAvatar } from "./imageUpload";
import { updateProfile as supabaseUpdateProfile, saveCustomerAssignment, getAllPortalOrders, getAllProfiles, getAllCustomerAssignments, getInventoryMovementsChecked, subscribeToInventoryChanges, savePortalOrder, createInventoryRequest, cancelInventoryRequest, listInventoryRequests, subscribeInventoryRequests, createInvoiceVoidRequest, cancelInvoiceVoidRequest, listInvoiceVoidRequests, subscribeInvoiceVoidRequests, listTrainingVideos, subscribeTrainingVideos, listRepTrainingProgress, upsertRepTrainingProgress, subscribeRepTrainingProgress, getRepCertification, insertRepCertification, subscribeRepCertifications } from "./supabase";
import { computeRepMetrics, groupInvoicesByMonth, parseInvoiceDate as repMetricsParseDate, deriveInvoiceDisplayStatus, displayStatusLabel, displayStatusPillStyle, partitionPeriodByPaidInvoices, batchizeRepInvoices } from "./repMetrics";
import { listRepCommissionPayments, subscribeRepCommissionPayments, getRepCommissionRates } from "./supabase";
import { useCommissionLedger, CorrectionsCard } from "./commissionLedgerUi";
import { repCorrectionSummary, netOwed, ytdPaidWithCorrections, frozenBatchLines, settlementBatches, correctionOnlySettlements, fmtSignedMoney } from "./commissionCorrectionsView";
import { listRepRouteCustomers, upsertRepRouteCustomer, deactivateRepRouteCustomer, subscribeRepRouteCustomers } from "./supabase";
import { listShopVisits, upsertShopVisit, subscribeShopVisits } from "./supabase";
import { listMaterialOrders, upsertMaterialOrder, subscribeMaterialOrders } from "./supabase";
import { serverCancelPatch } from "./orderRemovals";
import { getCustomerDiscountStrict } from "./supabase";
import { resolveCustomerEntry, customerPickerOptions } from "./customerPick";
import { computePricing, readDiscountWithTimeout, DISCOUNT_READ_TIMEOUT_MS, DISCOUNT_MIGRATION_MSG, samePct, formatPct, legalInvoiceTotals, invoiceDiscountFields } from "./invoiceDiscount";
import { displayRepStamp } from "./adminInvoicePure";
import { selectInvoicesToPush, pushLocalInvoices } from "./invoiceBackfill";
import { repInventoryRows, flattenCatalogForPicker, dedupeInvoicesById, REP_LEDGER_TYPES, isUnstampedInvoice, isRepCreatedInvoice, buildRepIdentity, normSku } from "./inventoryMath";
import { inventoryCatalog } from "./inventoryCatalog";
import { createRefreshCoordinator, attachFreshness } from "./refreshCoordinator";
import { adjustmentPatch, applyInProgressByFields, invoiceRequestEligibility, latestByInvoice, requestPill, buildProposedItems, diffItems, recomputeTotal, extrasFromInvoice, hasAdjustmentChange, validateProposal, parseItems as parseInvItems } from "./invoiceAdjustments";
import { createAdjustmentRequest, cancelAdjustmentRequest, listAdjustmentRequests, subscribeAdjustmentRequests } from "./supabase";
import { subscribePortalOrdersSignal } from "./supabase";
import { decideToggle } from "./toggleDecision";
import { cachePatchFromRemittance, cachePatchFromReversal, sortRemittanceRowsForApply, assignInPlace, repReceivedText } from "./repRemittances";
import { MY_REMITTANCES, useRepRemittanceFor } from "./remittanceStore";
import { listMyRemittances, subscribeRemittances } from "./supabase";
import { createFetchGate, createDebounced } from "./remittanceSync";
// legacy-seed-ref — used only as a last-resort display fallback if the rep
// portal mounts before SHARED_PRODUCTS has been hydrated from Supabase.
// Do not mutate, and do not round-trip back to Supabase from here.
import { REP_FALLBACK_PRODUCTS as ALL_PRODUCTS, REP_FALLBACK_KITS as ALL_KITS } from "./seeds/catalogSeed";

// ─── Rep-name matching ────────────────────────────────────────────────────────
// The seed data stamps a short display name (e.g. "Marcus T.") on
// INIT_ACCOUNTS and on customer_settings.rep when admin assigns reps,
// while the rep's own profile carries their full name ("Marcus Thompson").
// Exact normalized equality (lower(trim(x)) === lower(trim(y))) would
// never match those two — and that means customer orders placed by
// dealers never associate to the rep, so the rep sees nothing and gets
// no commission. This helper accepts:
//
//   • exact normalized match                ("marcus t." === "marcus t.")
//   • first-name + last-initial match       ("Marcus T." vs "Marcus Thompson")
//
// We deliberately require BOTH the first token AND the last-name initial
// to match so we don't collide "Marcus T." with an unrelated "Marcus V.".
// For single-token names (e.g. just "Marcus") we fall back to first-token
// equality so those still match.
export function namesMatchFuzzy(a, b) {
  const norm = s => String(s || "").trim().toLowerCase();
  const na = norm(a), nb = norm(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  // Strip trailing period from initials ("marcus t." → "marcus t")
  const clean = s => s.replace(/\./g, "").replace(/\s+/g, " ").trim();
  const ca = clean(na), cb = clean(nb);
  if (ca === cb) return true;
  const ta = ca.split(" "), tb = cb.split(" ");
  const firstA = ta[0], firstB = tb[0];
  if (!firstA || !firstB || firstA !== firstB) return false;
  // Single-token names with matching first token → same person.
  if (ta.length === 1 || tb.length === 1) return true;
  // Multi-token: require the last-name initial to match on both sides.
  const lastA = ta[ta.length - 1], lastB = tb[tb.length - 1];
  const initA = lastA.charAt(0), initB = lastB.charAt(0);
  return !!initA && initA === initB;
}

// ─── SHARED INVOICE STORE ─────────────────────────────────────────────────────
// This is the bridge between rep-created invoices and customer portal invoices.
// In production this would be a database — here it's a module-level store
// that both portals read from and write to within the same session.
// FIX: Orders and Invoices need to share shipping/tracking state so customer
// sees the same status admin entered in the Orders tab. Adding updateShipping
// and markDelivered methods so admin tracking edits flow to the invoice modal.
// (Bug: customer invoice had no shipping status bar or tracking info)
export const SHARED_INVOICES = {
  _data: (() => { try { const r=localStorage.getItem("mpcare_shared_invoices"); return r?JSON.parse(r):[]; } catch(e){ return []; } })(),
  _listeners: [],
  _save() { try { localStorage.setItem("mpcare_shared_invoices",JSON.stringify(this._data)); } catch(e){ console.warn("[SHARED_INVOICES] could not save the local invoice cache (storage full or blocked):", e?.message||e, { invoices: this._data.length }); } },
  // _emit saves + notifies so every mutator is a one-liner at the end.
  _emit() { this._save(); this._listeners.forEach(fn => fn([...this._data])); },
  add(invoice) {
    this._data.unshift({...invoice, sharedAt: new Date().toISOString(), customerSeen: false, shippingStatus: invoice.shippingStatus || "pending"});
    this._emit();
  },
  getAll() { return [...this._data]; },
  getForCustomer(company) { return this._data.filter(i=>i.customer===company); },
  markSeen(id) {
    const inv = this._data.find(i=>i.id===id);
    if(inv) { inv.customerSeen = true; this._save(); }
  },
  // Pass-through for Square payment details — paymentId is the Square
  // payment ID, which refund-payment.js needs to issue the refund later.
  // paidLast4 is the card/account last 4, for receipt display.
  markPaid(id, method, details) {
    const inv = this._data.find(i=>i.id===id);
    if(inv) {
      inv.status = "paid";
      inv.paidMethod = method;
      inv.paidDate = new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"});
      if(details?.paymentId)    inv.paymentId    = details.paymentId;
      if(details?.transactionId) inv.transactionId = details.transactionId;
      if(details?.last4)        inv.paidLast4    = details.last4;
      if(details?.receiptUrl)   inv.receiptUrl   = details.receiptUrl;
      // Clear any prior payment-pending state
      delete inv.pendingMethod;
      delete inv.pendingDate;
    }
    this._emit();
  },
  // Reverse of markPaid — the rep un-marked the invoice as paid. Clears every
  // payment-detail field stamped by markPaid so admin / customer views don't
  // show a ghost receipt on an invoice that is no longer paid.
  markUnpaid(id, note) {
    const inv = this._data.find(i=>i.id===id);
    if(inv) {
      inv.status = "pending";
      delete inv.paidMethod;
      delete inv.paidDate;
      delete inv.paymentId;
      delete inv.transactionId;
      delete inv.paidLast4;
      delete inv.receiptUrl;
      delete inv.pendingMethod;
      delete inv.pendingDate;
      delete inv.pendingNote;
      if(note) inv.unpaidNote = note;
      inv.unpaidDate = new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"});
    }
    this._emit();
  },
  // Check / wire transfers can't be confirmed in-app — the customer is telling
  // us they mailed or initiated payment, we mark the invoice pending until
  // admin verifies receipt. Distinct status so it doesn't count as paid.
  markPendingConfirmation(id, method, note) {
    const inv = this._data.find(i=>i.id===id);
    if(inv) {
      inv.status = "payment_pending";
      inv.pendingMethod = method;
      inv.pendingDate = new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"});
      if(note) inv.pendingNote = note;
    }
    this._emit();
  },
  // Admin reverses a pending confirmation — e.g. the promised wire never
  // arrived. The invoice flips back to outstanding so it stays in the
  // outstanding bucket and the customer can retry payment.
  clearPendingPayment(id, note) {
    const inv = this._data.find(i=>i.id===id);
    if(inv) {
      inv.status = "outstanding";
      delete inv.pendingMethod;
      delete inv.pendingDate;
      if(note) inv.pendingClearedNote = note;
      inv.pendingClearedDate = new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"});
    }
    this._emit();
  },
  updateShipping(id, fields) {
    const inv = this._data.find(i=>i.id===id);
    if(inv) {
      Object.assign(inv, fields);
      // Auto-promote shippingStatus when tracking is added
      if(fields.tracking && inv.shippingStatus !== "delivered") inv.shippingStatus = "shipped";
    }
    this._emit();
  },
  markDelivered(id) {
    const inv = this._data.find(i=>i.id===id);
    if(inv) { inv.shippingStatus = "delivered"; inv.deliveredDate = new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}); }
    this._emit();
  },
  // Rep-delivered: admin recorded that a sales rep dropped off the order in
  // person. Goes straight to delivered (skipping shipped) and stamps which
  // rep delivered it + any notes. Used by the Ship Now modal's rep option.
  markDeliveredByRep(id, { repName, deliveredDate, notes } = {}) {
    const inv = this._data.find(i=>i.id===id);
    if(inv) {
      inv.shippingStatus = "delivered";
      inv.deliveryMethod = "rep";
      inv.deliveredByRep = repName || "";
      inv.deliveredDate = deliveredDate || new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"});
      if(notes) inv.shippingNotes = notes;
    }
    this._emit();
  },
  // Admin-initiated order cancellation. Stamps the invoice as cancelled and
  // records why + how the refund was handled so the customer detail modal
  // and the cancellation email can render the full story.
  cancel(id, { reason, note, refundStatus, refundTransactionId, refundLast4, refundMethod } = {}) {
    const inv = this._data.find(i=>i.id===id);
    if(inv) {
      inv.status = "cancelled";
      inv.shippingStatus = "cancelled";
      inv.cancelledAt = new Date().toISOString();
      inv.cancelledDate = new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"});
      inv.cancelReason = reason || "Not specified";
      if (note) inv.cancelNote = note;
      if (refundStatus) inv.refundStatus = refundStatus;
      if (refundTransactionId) inv.refundTransactionId = refundTransactionId;
      if (refundLast4) inv.refundLast4 = refundLast4;
      if (refundMethod) inv.refundMethod = refundMethod;
    }
    this._emit();
  },
  // FORWARD-ONLY server-state applier (admin void propagation). Given
  // portal_orders row(s) from Supabase, flips a cached invoice to cancelled
  // ONLY when the DB says status==='cancelled' and the cache is not already
  // cancelled. It NEVER overwrites paid / pending / payment_pending status,
  // paidMethod / paymentId, shipping / tracking fields, notes, items or totals
  // (portal_orders.status is stale for payments). Cancelled is terminal.
  // Returns [{id, patch}] for the invoices that changed so callers can patch
  // their own local copies surgically.
  // In-flight guard: while an admin void of an invoice is being processed the
  // admin's own realtime echo must NOT flip the live cache object to
  // cancelled, otherwise refund logic that reads the same object would see a
  // cancelled invoice. begin/end are called around the void by the admin UI.
  _voidInFlight: new Set(),
  _voidDeferred: {},   // id -> DB row swallowed while the void was in flight
  beginAdminVoid(id) { if(id) { this._voidInFlight.add(id); delete this._voidDeferred[id]; } },
  endAdminVoid(id) {
    this._voidInFlight.delete(id);
    // If the DB echo arrived while we were skipping it and the flow aborted
    // before the local mirror ran, apply it now (forward-only, so a no-op when
    // the admin's own SHARED_INVOICES.cancel() already ran).
    const row = this._voidDeferred[id];
    delete this._voidDeferred[id];
    if(row) this.applyServerState(row);
  },
  // FORWARD-ONLY, IDEMPOTENT adjustment applier (rep invoice adjustment,
  // migration 0028). Given portal_orders row(s), applies items / total /
  // subtotal / payment terms to the cached invoice ONLY when the DB row carries
  // an adjustment_request_id the cache does not yet have, and the cached
  // invoice is not paid / payment_pending / cancelled. Never touches shipping,
  // notes, status or any paid field. Called from applyServerState so every
  // existing DB-to-cache sync loop (admin mount + realtime, rep, dealer)
  // picks it up. Returns [{id, patch}] so callers can patch their own copies.
  // opts.allowRevert: pass true ONLY for rows from a getAllPortalOrders batch
  // (known newest per invoice_ref). Single realtime rows never revert the cache.
  applyAdjustment(rowOrRows, opts) {
    const rows = Array.isArray(rowOrRows) ? rowOrRows : (rowOrRows ? [rowOrRows] : []);
    const changed = [];
    rows.forEach(row => {
      // A row WITHOUT a marker matters only when the cached invoice carries one
      // (revert after an undone approve); adjustmentPatch decides.
      if(!row) return;
      const ref = row.invoice_ref || row.invoice_id;
      const inv = this._data.find(i => (ref && i.id===ref) || (!ref && row.order_ref && i.orderId===row.order_ref));
      if(!inv) return;
      // A void in flight on this invoice owns it: skip, the next sync applies.
      if(this._voidInFlight.has(inv.id)) return;
      const patch = adjustmentPatch(inv, row, opts);
      if(!patch) return;
      Object.assign(inv, patch);
      changed.push({ id: inv.id, patch });
    });
    if(changed.length) this._emit();
    return changed;
  },
  // FORWARD-ONLY, IDEMPOTENT rep-remittance applier (migration 0033). Given
  // invoice_rep_remittances rows (any status), mirrors an ACTIVE remittance onto
  // the cached invoice as paid ("Received via sales rep") and reverts ONLY an
  // invoice whose paidVia is rep_remittance with the matching remittanceId when
  // the row is reversed. Uses the pure cachePatchFromRemittance / cachePatchFromReversal:
  // it never overrides a cancelled invoice or an invoice already paid another way,
  // never touches items, total, shipping or notes, and never sets a paymentId (so
  // the refund logic can never treat it as a card payment). Rows are applied oldest
  // first so a reversal of an older row lands before a newer active row. The DB wins:
  // called from the same DB-to-cache sync places as applyServerState. The object is
  // updated IN PLACE (every holder of the reference sees it). Returns [{id}] changed.
  applyRemittance(rowOrRows) {
    const rows = sortRemittanceRowsForApply(Array.isArray(rowOrRows) ? rowOrRows : (rowOrRows ? [rowOrRows] : []));
    const changed = [];
    rows.forEach(rem => {
      const inv = this._data.find(i => i.id === rem.invoice_id);
      if(!inv) return;
      // A void in flight on this invoice owns it: skip, the next sync applies.
      if(this._voidInFlight.has(inv.id)) return;
      const res = rem.status === "reversed" ? cachePatchFromReversal(inv, rem) : cachePatchFromRemittance(inv, rem);
      if(!res || !res.ok || !res.changed) return;
      assignInPlace(inv, res.invoice);
      changed.push({ id: inv.id });
    });
    if(changed.length) this._emit();
    return changed;
  },
  // FORWARD-ONLY delivery-method applier. Copies delivery_method / delivered_by_rep
  // from portal_orders rows onto the cached invoice ONLY when the DB row names a
  // delivery method (or a delivering rep) the cache does not have yet. It never
  // touches status, items, totals, payment, shipping or notes. The rep-stock
  // calculation (inventoryMath) needs it: a dealer order delivered by a rep takes
  // stock out of that rep, and a device that hydrated the order from the DB never
  // received the delivery fields. Returns the changed invoice ids.
  applyDelivery(rowOrRows) {
    const rows = Array.isArray(rowOrRows) ? rowOrRows : (rowOrRows ? [rowOrRows] : []);
    const changed = [];
    rows.forEach(row => {
      if(!row || !row.delivery_method) return;
      const ref = row.invoice_ref || row.invoice_id;
      const inv = this._data.find(i => (ref && i.id===ref) || (!ref && row.order_ref && i.orderId===row.order_ref));
      if(!inv) return;
      const by = row.delivered_by_rep || "";
      if(inv.deliveryMethod === row.delivery_method && (!by || inv.deliveredByRep === by)) return;
      inv.deliveryMethod = row.delivery_method;
      if(by) inv.deliveredByRep = by;
      changed.push(inv.id);
    });
    if(changed.length) this._emit();
    return changed;
  },
  // FORWARD-ONLY, ADDITIVE account-discount applier (per-customer discount, migration 0034).
  // Copies subtotal + discount from portal_orders row(s) onto a CACHED invoice that has no
  // discount yet (an invoice this device cached before it knew the row carried one). A
  // discount / subtotal already present is never overwritten, and items, total, status,
  // payment, shipping and notes are never touched. Rows without a stored rate do nothing, so
  // every invoice created before the feature stays exactly as it was. Returns changed ids.
  applyDiscount(rowOrRows) {
    const rows = Array.isArray(rowOrRows) ? rowOrRows : (rowOrRows ? [rowOrRows] : []);
    const changed = [];
    rows.forEach(row => {
      if(!row) return;
      const fields = invoiceDiscountFields(row);
      if(!fields.discount) return;
      const ref = row.invoice_ref || row.invoice_id;
      const inv = this._data.find(i => (ref && i.id===ref) || (!ref && row.order_ref && i.orderId===row.order_ref));
      if(!inv || inv.discount) return;
      inv.discount = fields.discount;
      if(inv.subtotal == null) inv.subtotal = fields.subtotal;
      changed.push(inv.id);
    });
    if(changed.length) this._emit();
    return changed;
  },
  applyServerState(rowOrRows, opts) {
    const rows = Array.isArray(rowOrRows) ? rowOrRows : (rowOrRows ? [rowOrRows] : []);
    // Adjustment reconciliation runs first; a cancelled row is a no-op there.
    try { this.applyAdjustment(rows, opts); }
    catch(e) { console.warn("[SHARED_INVOICES.applyAdjustment] failed:", e?.message || e); }
    try { this.applyDiscount(rows); }
    catch(e) { console.warn("[SHARED_INVOICES.applyDiscount] failed:", e?.message || e); }
    const changed = [];
    rows.forEach(row => {
      if(!row || row.status !== "cancelled") return;
      const ref = row.invoice_ref || row.invoice_id;
      const inv = this._data.find(i => (ref && i.id===ref) || (!ref && row.order_ref && i.orderId===row.order_ref));
      if(!inv) return;
      if(this._voidInFlight.has(inv.id)) { this._voidDeferred[inv.id] = row; return; }
      const patch = serverCancelPatch(inv, row);
      if(!patch) return;
      Object.assign(inv, patch);
      changed.push({ id: inv.id, patch });
    });
    if(changed.length) this._emit();
    return changed;
  },
  subscribe(fn) { this._listeners.push(fn); return ()=>{ this._listeners=this._listeners.filter(l=>l!==fn); }; }
};


// ─── SHARED MATERIAL ORDERS STORE ────────────────────────────────────────────
// Bridge between rep/customer material orders and admin notifications. Phase
// C (PR following #64): add() now preserves a caller-supplied status / placedAt
// if present so the Supabase mirror can echo server state back into the local
// cache without clobbering it. Backward compatible — callers that omit those
// fields still get the legacy defaults ("pending" / now()).
//
// Never cleared, never deleted from. Soft-delete / status flips go through
// markShipped (or a future markCancelled) which UPDATE existing entries
// in place. Phase C's Supabase mirror sits ON TOP of this store.
export const SHARED_MATERIAL_ORDERS = {
  _data: (() => { try { const r=localStorage.getItem("mpcare_shared_material_orders"); return r?JSON.parse(r):[]; } catch(e){ return []; } })(),
  _listeners: [],
  _save() { try { localStorage.setItem("mpcare_shared_material_orders",JSON.stringify(this._data)); } catch(e){} },
  add(order) {
    // Respect the caller's stable id (from placeOrder: "MAT-XXXX") and
    // status / placedAt when provided, so Supabase hydration can re-insert
    // the authoritative row shape without double-timestamping or forcing
    // status back to "pending". Legacy callers get the pre-Phase-C defaults.
    const entry = {
      ...order,
      placedAt: order.placedAt || new Date().toISOString(),
      status: order.status || "pending",
    };
    // Upsert semantics (Mechanic #2 of Phase C mark-shipped UI):
    //   • New id → unshift, save, fire.
    //   • Existing id → MERGE incoming fields onto the existing entry
    //     in-place (Supabase-latest wins on any field the caller passed),
    //     save, fire listeners with the updated _data.
    // Previously the duplicate branch returned early WITHOUT applying the
    // new field values — so when the realtime subscription fired after an
    // admin marked an order shipped on another device, local cache kept
    // showing status='pending' indefinitely. Grep-verified all 5 callers
    // either pass fresh client-generated ids (create path) or explicitly
    // want Supabase-latest state merged into the cache (hydrate path);
    // none depended on the old "ignore on duplicate id" no-op. See PR
    // body for the caller-by-caller audit.
    if (entry.id) {
      const existingIdx = this._data.findIndex(x => x.id === entry.id);
      if (existingIdx !== -1) {
        this._data[existingIdx] = { ...this._data[existingIdx], ...entry };
        this._save();
        this._listeners.forEach(fn => fn([...this._data]));
        return;
      }
    }
    this._data.unshift(entry);
    this._save();
    this._listeners.forEach(fn => fn([...this._data]));
  },
  getAll() { return [...this._data]; },
  markShipped(id) {
    const o = this._data.find(x=>x.id===id);
    if(o) { o.status="shipped"; o.shippedDate=new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}); }
    this._save();
    this._listeners.forEach(fn => fn([...this._data]));
  },
  subscribe(fn) { this._listeners.push(fn); return ()=>{ this._listeners=this._listeners.filter(l=>l!==fn); }; }
};


// ─── THEME ────────────────────────────────────────────────────────────────────
const C = {
  navy:"#0B1F3A", navyMid:"#12305A", blue:"#1560BD", blueMid:"#1A73E8",
  blueLight:"#E8F0FE", teal:"#0D7A5F", tealLight:"#E0F4EE",
  amber:"#B45309", amberLight:"#FEF3C7", red:"#B91C1C", redLight:"#FEE2E2",
  ink:"#0F172A", inkMid:"#334155", inkLight:"#64748B",
  border:"#E2E8F0", surface:"#F8FAFC", white:"#FFFFFF",
  coral:"#9A3412", coralLight:"#FFF7ED", green:"#166534", greenLight:"#DCFCE7",
  purple:"#5B21B6", purpleLight:"#EDE9FE",
};

const S = {
  app:{ fontFamily:"'DM Sans',system-ui,sans-serif", background:C.surface, minHeight:"100vh", color:C.ink },
  shell:{ display:"grid", gridTemplateColumns:"220px 1fr", minHeight:"100vh" },
  sidebar:{ background:"#0F2744", display:"flex", flexDirection:"column" },
  navItem:{ display:"flex", alignItems:"center", gap:9, padding:"9px 18px", fontSize:13, color:"rgba(255,255,255,0.6)", cursor:"pointer", borderLeft:"3px solid transparent" },
  navActive:{ color:C.white, background:"rgba(255,255,255,0.09)", borderLeft:`3px solid #38BDF8` },
  navDiv:{ height:1, background:"rgba(255,255,255,0.08)", margin:"6px 0" },
  catLabel:{ fontSize:10, color:"rgba(255,255,255,0.3)", padding:"10px 18px 4px", letterSpacing:"0.07em", textTransform:"uppercase" },
  main:{ display:"flex", flexDirection:"column", minHeight:"100vh" },
  topbar:{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:"0 24px", height:54, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 },
  content:{ flex:1, overflowY:"auto", padding:24 },
  card:{ background:C.white, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden", marginBottom:16 },
  cardHdr:{ padding:"13px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" },
  cardTitle:{ fontSize:13, fontWeight:600, color:C.ink },
  metrics:{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 },
  metric:{ background:C.white, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px" },
  mLabel:{ fontSize:11, color:C.inkLight, marginBottom:4 },
  mValue:{ fontSize:22, fontWeight:600, color:C.ink },
  mSub:{ fontSize:11, color:C.inkLight, marginTop:2 },
  twoCol:{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 },
  threeCol:{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 },
  fg:{ marginBottom:14 },
  label:{ display:"block", fontSize:12, fontWeight:500, color:C.inkMid, marginBottom:5 },
  input:{ width:"100%", padding:"9px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, color:C.ink, background:C.white, outline:"none", boxSizing:"border-box" },
  select:{ width:"100%", padding:"9px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, color:C.ink, background:C.white, outline:"none", boxSizing:"border-box" },
  btnPrimary:{ background:C.blue, color:C.white, border:"none", padding:"10px 20px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer" },
  btnSecondary:{ background:"transparent", color:C.ink, border:`1px solid ${C.border}`, padding:"9px 16px", borderRadius:8, fontSize:13, cursor:"pointer" },
  btnSmall:{ background:C.blueLight, color:C.blue, border:"none", padding:"5px 11px", borderRadius:6, fontSize:11, fontWeight:500, cursor:"pointer" },
  btnGhost:{ background:"none", border:"none", color:C.blue, fontSize:12, cursor:"pointer", padding:0 },
  btnDanger:{ background:C.redLight, color:C.red, border:"none", padding:"5px 11px", borderRadius:6, fontSize:11, fontWeight:500, cursor:"pointer" },
  btnSuccess:{ background:C.greenLight, color:C.green, border:"none", padding:"5px 11px", borderRadius:6, fontSize:11, fontWeight:500, cursor:"pointer" },
  pillGreen:{ fontSize:10, fontWeight:500, padding:"2px 8px", borderRadius:8, background:C.greenLight, color:C.green },
  pillAmber:{ fontSize:10, fontWeight:500, padding:"2px 8px", borderRadius:8, background:C.amberLight, color:C.amber },
  pillRed:{ fontSize:10, fontWeight:500, padding:"2px 8px", borderRadius:8, background:C.redLight, color:C.red },
  pillBlue:{ fontSize:10, fontWeight:500, padding:"2px 8px", borderRadius:8, background:C.blueLight, color:C.blue },
  pillPurple:{ fontSize:10, fontWeight:500, padding:"2px 8px", borderRadius:8, background:C.purpleLight, color:C.purple },
  pillGray:{ fontSize:10, fontWeight:500, padding:"2px 8px", borderRadius:8, background:"#F3F4F6", color:"#374151" },
  tableHdr:{ display:"grid", gap:8, padding:"8px 18px", fontSize:11, fontWeight:600, color:C.inkLight, borderBottom:`1px solid ${C.border}`, background:C.surface },
  tableRow:{ display:"grid", gap:8, padding:"11px 18px", borderBottom:`1px solid ${C.border}`, fontSize:12, alignItems:"center" },
  modal:{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 },
  modalBox:{ background:C.white, borderRadius:16, padding:"28px 32px", width:560, maxHeight:"85vh", overflowY:"auto" },
};

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
// ALL_PRODUCTS / ALL_KITS are now imported from ./seeds/catalogSeed.js as
// REP_FALLBACK_PRODUCTS / REP_FALLBACK_KITS. They're last-resort fallbacks
// for a few rep-portal demo surfaces only — live catalog data comes from
// SHARED_PRODUCTS (populated by the admin AppShell from Supabase).
const ALL_PRODUCTS_AND_KITS = [...ALL_PRODUCTS, ...ALL_KITS];

const REP_ACCOUNTS = [
  {id:1,company:"Apex Car Care LLC",      contact:"John Smith",  email:"john@apexcarcare.com",   phone:"619-555-0100"},
  {id:2,company:"FastLane Auto Parts",    contact:"Maria Reyes", email:"maria@fastlane.com",      phone:"623-555-0200"},
  {id:5,company:"Westside Repair Center", contact:"Dave Kim",    email:"dave@westsiderepair.com", phone:"661-555-0300"},
];

// NOTE: the hardcoded INIT_REP_INVENTORY seed was removed — rep on-hand is now
// derived from the Supabase `inventory_movements` ledger + the rep's invoices,
// identically to how the admin portal computes it (see App.js AdminRepInventoryTab).
// CLAUDE.md rule #1: code must never write hardcoded seed data at runtime, and
// this file must not fall back to a fake-looking inventory that would hide a
// real empty state. An empty inventory means no `assign` movements have been
// posted for this rep yet — the RepInventory screen renders an explicit
// empty-state message in that case.

const INIT_INVOICES = [
  {id:"INV-F041",date:"Apr 9, 2025", customer:"Apex Car Care LLC",    items:[{sku:"7001",name:"Catalytic converter cleaner",qty:12,price:4.80},{sku:"808",name:"DPF purge filter",qty:6,price:5.20}], total:88.80, status:"paid",   sent:"email"},
  {id:"INV-F040",date:"Apr 7, 2025", customer:"FastLane Auto Parts",  items:[{sku:"7000",name:"Fuel system cleaner",qty:24,price:3.90}],                                                               total:93.60, status:"pending", sent:"print"},
  {id:"INV-F039",date:"Apr 2, 2025", customer:"Westside Repair Center",items:[{sku:"8101",name:"DPF foam cleaner",qty:12,price:6.10},{sku:"GL100",name:"Nitrile gloves",qty:100,price:2.40}],           total:313.20,status:"paid",  sent:"email"},
];

// ─── TRAINING DATA ────────────────────────────────────────────────────────────
const INIT_TRAINING = [
  {id:1, title:"How to demo catalytic converter cleaner",  category:"product",   duration:"8 min",  required:true,  watched:true,  thumbnail:"🎬", desc:"Step-by-step demo script for repair shops. How to present the P0420 code solution and close the sale.", uploadedBy:"Sam T.", date:"Mar 15, 2025"},
  {id:2, title:"DPF system explainer for diesel shops",    category:"product",   duration:"12 min", required:true,  watched:true,  thumbnail:"🎬", desc:"Full DPF product line walkthrough — purge filter, foam cleaner, flush liquid. Includes demo technique and objection handling.", uploadedBy:"Sam T.", date:"Mar 20, 2025"},
  {id:3, title:"How to handle objections at the counter",  category:"sales",     duration:"15 min", required:true,  watched:false, thumbnail:"🎬", desc:"Top 10 objections from shop owners and how to respond. Covers price, brand familiarity, and DIY alternatives.", uploadedBy:"Sam T.", date:"Apr 1, 2025"},
  {id:4, title:"Fuel system products — the full story",    category:"product",   duration:"10 min", required:false, watched:false, thumbnail:"🎬", desc:"Deep dive into how fuel system cleaners work, what makes ours different from competitors, and how to explain it simply.", uploadedBy:"Sam T.", date:"Apr 3, 2025"},
  {id:5, title:"Using the wholesale portal — rep walkthrough", category:"admin", duration:"6 min",  required:true,  watched:true,  thumbnail:"🎬", desc:"How to log in, receive inventory, create invoices, and track your commission in the portal.", uploadedBy:"Sam T.", date:"Apr 5, 2025"},
  {id:6, title:"Marketing materials — how to use them",    category:"marketing", duration:"5 min",  required:false, watched:false, thumbnail:"🎬", desc:"How to place shelf talkers, use counter displays, and leave behind flyers for maximum shop visibility.", uploadedBy:"Sam T.", date:"Apr 6, 2025"},
];

// ─── MARKETING MATERIALS DATA ─────────────────────────────────────────────────
const INIT_MATERIALS = [
  {id:1, name:"Product flyer — DPF line",         category:"flyer",   price:0,    unit:"per pack of 10", active:true,  stock:500, desc:"Full-color double-sided flyer covering the DPF product line with key selling points and QR code to shop."},
  {id:2, name:"Shelf talker — catalytic cleaner",  category:"shelf",   price:0,    unit:"per pack of 5",  active:true,  stock:200, desc:"3x5 shelf talker with P0420 cure claim and MSRP. Fits standard retail shelf strips."},
  {id:3, name:"Counter display — starter kit",    category:"display", price:25.00, unit:"per unit",       active:true,  stock:40,  desc:"Countertop cardboard display holding 12 units. Pre-printed, fully branded. Ships flat, assembles in 2 minutes."},
  {id:4, name:"Window cling — MPC branding",      category:"other",   price:0,    unit:"per pack of 3",  active:true,  stock:150, desc:"Static cling for shop window. 8x10 inches. MotorPower Care logo + distributor badge."},
  {id:5, name:"Banner — trade show 3x6ft",        category:"banner",  price:45.00, unit:"per unit",       active:false, stock:12,  desc:"Retractable banner stand with full MotorPower Care branding. For SEMA or regional trade shows only."},
  {id:6, name:"Product catalog — 2026 full line",  category:"flyer",   price:0,    unit:"per pack of 10", active:true,  stock:300, desc:"Full 2026 product catalog with all SKUs, pricing, and product descriptions. Leave behind at every shop visit."},
];

// ─── TOOL LENDING DATA ────────────────────────────────────────────────────────
const INIT_TOOLS = [
  {id:1, name:"OBD-II diagnostic scanner",        value:280,  minUnitsPerMonth:12, category:"diagnostic", maxCirculation:20, active:true,  desc:"Professional OBD-II scanner that reads and clears P0420, P0430, and DPF codes. Demonstrates product need in real time.", currentLoaned:8},
  {id:2, name:"DPF pressure differential tester", value:180,  minUnitsPerMonth:8,  category:"diagnostic", maxCirculation:15, active:true,  desc:"Tests DPF back-pressure to show blockage level. Justifies DPF product purchase on the spot.", currentLoaned:5},
  {id:3, name:"Fuel injector flow tester",         value:350,  minUnitsPerMonth:12, category:"diagnostic", maxCirculation:10, active:true,  desc:"Shows injector flow rate vs spec. Demonstrates the need for fuel system cleaning.", currentLoaned:3},
  {id:4, name:"Emission analyzer (4-gas)",        value:620,  minUnitsPerMonth:20, category:"emissions",  maxCirculation:8,  active:false, desc:"4-gas emission analyzer for shops doing pre-inspection testing. High minimum — enterprise accounts only.", currentLoaned:0},
];

const INIT_AGREEMENTS = [
  {id:1, toolId:1, toolName:"OBD-II diagnostic scanner",         shop:"Apex Car Care LLC",       rep:"Marcus T.", startDate:"Feb 1, 2025", endDate:"", minUnits:12, lastMonthUnits:18, status:"compliant",  warnings:0, value:280, duration:"month_to_month", damageClause:true, stolenClause:true, buyOption:true, buyPrice:220, shopContact:"John Smith", shopEmail:"john@apexcarcare.com", shopPhone:"619-555-0100",
    repComments:[{date:"Feb 5, 2025",text:"Tool delivered and shop trained on usage. John confirmed minimum order commitment."},{date:"Mar 10, 2025",text:"Checked in - tool working great. Shop exceeded minimum by 6 units."}],
    maintenanceLog:[{date:"Mar 1, 2025",type:"Inspection",notes:"Tool in excellent condition. Battery replaced."}],
    notifications:[]},
  {id:2, toolId:1, toolName:"OBD-II diagnostic scanner",         shop:"FastLane Auto Parts",     rep:"Marcus T.", startDate:"Mar 1, 2025", endDate:"", minUnits:12, lastMonthUnits:9,  status:"warning",    warnings:1, value:280, duration:"month_to_month", damageClause:true, stolenClause:true, buyOption:false, buyPrice:0, shopContact:"Maria Reyes", shopEmail:"maria@fastlane.com", shopPhone:"623-555-0200",
    repComments:[{date:"Mar 5, 2025",text:"Tool placed. Maria understands minimum requirements."},{date:"Apr 2, 2025",text:"Only 9 units ordered in March. Called Maria - she said supply chain delays. Warning issued."}],
    maintenanceLog:[],
    notifications:[{date:"Apr 2, 2025",type:"warning",text:"Warning sent: minimum not met for March (9/12 units)."}]},
  {id:3, toolId:2, toolName:"DPF pressure differential tester",  shop:"Apex Car Care LLC",       rep:"Marcus T.", startDate:"Jan 15, 2025", endDate:"", minUnits:8,  lastMonthUnits:12, status:"compliant",  warnings:0, value:180, duration:"fixed", fixedMonths:12, damageClause:true, stolenClause:true, buyOption:true, buyPrice:140, shopContact:"John Smith", shopEmail:"john@apexcarcare.com", shopPhone:"619-555-0100",
    repComments:[{date:"Jan 20, 2025",text:"DPF tester delivered. Trained 2 techs on proper usage."}],
    maintenanceLog:[{date:"Feb 15, 2025",type:"Calibration",notes:"Sensor recalibrated per manufacturer schedule."},{date:"Apr 1, 2025",type:"Inspection",notes:"All readings within spec. Minor cosmetic wear."}],
    notifications:[]},
  {id:4, toolId:3, toolName:"Fuel injector flow tester",         shop:"Westside Repair Center",  rep:"Sarah L.",  startDate:"Mar 15, 2025", endDate:"", minUnits:12, lastMonthUnits:4,  status:"recall",     warnings:2, value:350, duration:"until_minimum_not_met", damageClause:true, stolenClause:true, buyOption:true, buyPrice:280, shopContact:"Dave Kim", shopEmail:"dave@westsiderepair.com", shopPhone:"661-555-0300",
    repComments:[{date:"Mar 20, 2025",text:"Tool placed. Dave seemed hesitant about minimum commitment."},{date:"Apr 5, 2025",text:"Only 4 units in March. Second consecutive miss. Starting recall process."}],
    maintenanceLog:[],
    notifications:[{date:"Mar 31, 2025",type:"warning",text:"Warning sent: minimum not met for March (4/12 units)."},{date:"Apr 5, 2025",type:"recall",text:"Recall notice sent: tool will be collected by sales rep or shipping. Buy option available at $280."}]},
];

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
const Badge = ({type,children}) => {
  const map = {compliant:S.pillGreen, warning:S.pillAmber, recall:S.pillRed, collected:S.pillGray, paid:S.pillGreen, pending:S.pillAmber, active:S.pillGreen, inactive:S.pillGray, required:S.pillBlue, optional:S.pillGray, product:S.pillBlue, sales:S.pillGreen, admin:S.pillGray, marketing:S.pillPurple};
  return <span style={map[type]||S.pillGray}>{children}</span>;
};

// A SKU with fewer than this many units on hand counts as "running low".
const LOW_STOCK_UNITS = 10;

// Amber banner shown when the inventory ledger could not be re-read from the
// server: the numbers on screen are the last good (or last saved) ones, never a
// silent fallback. `message` is the reason; Retry re-runs the fetch.
function LedgerStaleBanner({message, onRetry}) {
  if(!message) return null;
  return (
    <div role="alert" style={{background:"#FEF3C7",border:"1px solid #F59E0B",borderRadius:8,padding:"10px 14px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,fontSize:12,color:"#92400E"}}>
      <div style={{flex:1}}>
        <div style={{fontWeight:600}}>Inventory could not be refreshed, showing the last saved numbers</div>
        <div style={{marginTop:2,opacity:0.85}}>{message}</div>
      </div>
      {onRetry && <button onClick={onRetry} style={{background:"transparent",border:"1px solid #F59E0B",color:"#92400E",fontSize:11,padding:"4px 10px",borderRadius:6,cursor:"pointer"}}>Retry</button>}
    </div>
  );
}

// Unified 4-value (+Cancelled) invoice pill — renders the SAME label + color
// as the admin + customer portals via deriveInvoiceDisplayStatus.
const InvoicePill = ({inv}) => {
  const code = deriveInvoiceDisplayStatus(inv);
  return <span style={displayStatusPillStyle(code)}>{displayStatusLabel(code)}</span>;
};

const Modal = ({onClose, title, children}) => (
  <div style={S.modal}>
    <div style={S.modalBox}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
        <div style={{fontSize:18,fontWeight:600,color:C.ink}}>{title}</div>
        <button onClick={onClose} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:C.inkLight}}>×</button>
      </div>
      {children}
    </div>
  </div>
);

// ─── REP SIDEBAR ──────────────────────────────────────────────────────────────
function RepSidebar({page, setPage, repName, onExit}) {
  const navGroups = [
    { label:"Field sales", items:[
      {key:"dashboard",  label:"Dashboard"},
      {key:"inventory",  label:"My inventory"},
      {key:"invoices",   label:"Invoices"},
      {key:"sales",      label:"Sales report"},
      {key:"commission", label:"My commission"},
    ]},
    { label:"Route & visits", items:[
      {key:"route",      label:"📍 Route planner"},
      {key:"prospects",  label:"📋 Potential customers"},
    ]},
    { label:"Resources", items:[
      {key:"training",   label:"Training hub"},
      {key:"marketing",  label:"Marketing materials"},
      {key:"tools",      label:"Tool lending"},
    ]},
  ];
  return (
    <div style={S.sidebar}>
      <div style={{padding:"22px 18px 16px", borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <img src="/mpc-logo.png" alt="MPC" style={{width:36,height:36,objectFit:"contain"}}/>
          <div>
            <div style={{fontSize:14,fontWeight:600,color:C.white}}>MotorPower Care</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:1}}>Rep portal</div>
          </div>
        </div>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
        {navGroups.map(g=>(
          <div key={g.label}>
            <div style={S.catLabel}>{g.label}</div>
            {g.items.map(it=>(
              <div key={it.key} style={{...S.navItem,...(page===it.key?S.navActive:{})}} onClick={()=>setPage(it.key)}>
                {it.label}
              </div>
            ))}
            <div style={S.navDiv}/>
          </div>
        ))}
      </div>
      <div style={{padding:"14px 16px",borderTop:"1px solid rgba(255,255,255,0.08)"}}>
        <div style={{background:"rgba(255,255,255,0.06)",borderRadius:8,padding:"10px 12px",marginBottom:8}}>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginBottom:3}}>Signed in as</div>
          <div style={{fontSize:13,fontWeight:500,color:C.white}}>{repName}</div>
        </div>
        <button onClick={onExit} style={{background:"rgba(239,68,68,0.15)",border:"none",color:"#FCA5A5",fontSize:12,padding:"7px 12px",borderRadius:6,cursor:"pointer",width:"100%"}}>Sign out</button>
      </div>
    </div>
  );
}

// Re-export shared invoice date parser from repMetrics so every rep-side page
// dates invoices identically to the admin-side dashboards.
const parseInvoiceDate = repMetricsParseDate;

// ─── REP DASHBOARD ────────────────────────────────────────────────────────────
function RepDashboard({setPage, inventory, ledgerError="", onRetryLedger, agreements, commissionRate, invoices, repName, videos=[], earnedCerts=[], lastSyncAt, onRefreshNow}) {
  const effectiveRate = commissionRate ?? 8; // default 8% if admin hasn't set one
  // "Synced Xs ago" + a manual Refresh now button: the figures below are always a snapshot
  // of this device's cache (Rule Zero: Supabase is the source of truth, this is only a
  // display of the last successful sync), so a rep who notices a number looks off — e.g.
  // comparing against another device — can see how stale it is and force an immediate
  // resync instead of waiting on the normal 60s/focus/visibility/realtime ticks.
  const [, forceTick] = useState(0);
  useEffect(()=>{ const iv = setInterval(()=>forceTick(n=>n+1), 5000); return ()=>clearInterval(iv); },[]);
  const [refreshing, setRefreshing] = useState(false);
  const syncedLabel = (() => {
    if(!lastSyncAt) return "Not synced yet";
    const secs = Math.max(0, Math.round((Date.now()-lastSyncAt)/1000));
    if(secs < 5) return "Synced just now";
    if(secs < 60) return `Synced ${secs}s ago`;
    const mins = Math.round(secs/60);
    return `Synced ${mins} min${mins===1?"":"s"} ago`;
  })();
  const handleRefreshNow = async () => {
    if(refreshing || !onRefreshNow) return;
    setRefreshing(true);
    try { await onRefreshNow(); } finally { setRefreshing(false); }
  };
  // Derive unit / revenue / commission via the unified rep-metrics helper so
  // this card matches the Commission page, the admin Reps card, and the
  // admin Commission payout table exactly.
  const m = computeRepMetrics({
    repName: repName || "",
    // invoices are already scoped to this rep by the parent via mergedInvoices,
    // so pass "*" as the customer-name sentinel by feeding every invoice's
    // customer name through — we just need the filter to not exclude anything.
    customerNames: (invoices || []).map(i => i.customer).filter(Boolean),
    allInvoices: invoices || [],
    rate: effectiveRate,
  });
  const totalSold = m.monthUnits;
  const revenue = m.monthRevenue;
  const commission = m.monthCommission;
  // "Running low" counts only SKUs this rep actually holds or was assigned
  // (ledger received > 0) with fewer than LOW_STOCK_UNITS on hand. An oversold row
  // (negative on-hand) is below the threshold too. Sold-only rows (never received,
  // e.g. kits) are not "low stock", they are shown as oversold on the inventory page.
  const lowStock = inventory.filter(i=>(i.received||0)>0 && i.onHand<LOW_STOCK_UNITS).length;
  const snapshotRows = [...inventory].sort((a,b)=>a.onHand-b.onHand || (a.key<b.key?-1:1)).slice(0,5);
  const toolAlerts = agreements.filter(a=>a.status!=="compliant").length;
  // Training progress — reads the live video store, not a hardcoded 60%.
  // "Watched" fires from TrainingHub's video player via videos[i].watched=true.
  const requiredVideos = (videos||[]).filter(v=>v.required);
  const completedRequired = requiredVideos.filter(v=>v.watched).length;
  const nextRequired = requiredVideos.find(v=>!v.watched);
  const trainingPct = requiredVideos.length > 0
    ? Math.round(completedRequired / requiredVideos.length * 100)
    : 0;
  const isCertified = requiredVideos.length > 0 && completedRequired === requiredVideos.length;
  return (
    <div style={S.content}>
      <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:C.inkLight}}>{syncedLabel}</span>
        <button
          onClick={handleRefreshNow}
          disabled={refreshing || !onRefreshNow}
          style={{fontSize:11,padding:"4px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:C.white,color:refreshing?C.inkLight:C.blue,cursor:refreshing?"default":"pointer"}}
        >{refreshing?"Refreshing...":"Refresh now"}</button>
      </div>
      <LedgerStaleBanner message={ledgerError} onRetry={onRetryLedger}/>
      <div style={S.metrics}>
        {[
          {l:"Units sold this month",  v:totalSold,                s:`${totalSold===0?"No sales yet this month":"All products"}`,                pg:"sales"},
          {l:"Revenue this month",     v:`$${revenue.toFixed(0)}`,  s:`${revenue===0?"$0 this month":"At your pricing"}`,                        pg:"sales"},
          {l:"Commission earned this month", v:`$${commission.toFixed(0)}`, s:`${effectiveRate}% base rate · this month`, up:true, pg:"commission"},
          {l:"Tool alerts",            v:toolAlerts,                s:toolAlerts>0?"Action required":"All compliant", warn:toolAlerts>0,          pg:"tools"},
        ].map(card=>(
          <div
            key={card.l}
            onClick={()=>card.pg&&setPage(card.pg)}
            style={{...S.metric,...(card.warn?{borderTop:`3px solid ${C.red}`}:{}),cursor:card.pg?"pointer":"default",transition:"box-shadow 0.15s, transform 0.15s"}}
            onMouseEnter={e=>{if(card.pg){e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.08)";e.currentTarget.style.transform="translateY(-1px)";}}}
            onMouseLeave={e=>{if(card.pg){e.currentTarget.style.boxShadow="none";e.currentTarget.style.transform="none";}}}
          >
            <div style={S.mLabel}>{card.l}</div>
            <div style={S.mValue}>{card.v}</div>
            <div style={{...S.mSub,...(card.up?{color:C.teal}:{}),...(card.warn?{color:C.red}:{})}}>{card.s}</div>
          </div>
        ))}
      </div>

      {toolAlerts > 0 && (
        <div style={{background:C.redLight,border:`1px solid ${C.red}`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:C.red}}>Tool lending — action required</div>
            <div style={{fontSize:12,color:C.red,marginTop:2}}>{toolAlerts} shop{toolAlerts>1?"s":""} not meeting monthly minimum. Review now to avoid recalls.</div>
          </div>
          <button style={S.btnDanger} onClick={()=>setPage("tools")}>Review →</button>
        </div>
      )}

      {lowStock > 0 && (
        <div style={{background:C.amberLight,border:`1px solid #FCD34D`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:13,fontWeight:600,color:C.amber}}>{lowStock} SKU{lowStock>1?"s":""} running low — request restock from MPC</div>
          <button style={S.btnSmall} onClick={()=>setPage("inventory")}>View inventory →</button>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
        <div style={S.card}>
          <div style={S.cardHdr}><span style={S.cardTitle}>Inventory snapshot</span><button style={S.btnGhost} onClick={()=>setPage("inventory")}>Manage →</button></div>
          <div style={{overflowX:"auto"}}>
            <div style={{minWidth:460}}>
              {snapshotRows.map(item=>(
                <div key={item.sku} style={{display:"grid",gridTemplateColumns:"minmax(120px,1fr) 130px 60px 80px",gap:8,padding:"10px 18px",borderBottom:`1px solid ${C.border}`,fontSize:12,alignItems:"center"}}>
                  <span style={{color:C.ink,fontWeight:500}}>{item.name}</span>
                  <span style={{color:item.onHand<LOW_STOCK_UNITS?C.red:C.teal,fontWeight:600,textAlign:"right",whiteSpace:"nowrap"}}>{item.oversold?`Oversold by ${item.oversoldBy}`:item.onHand}</span>
                  <span style={{color:C.inkLight,textAlign:"right",whiteSpace:"nowrap"}}>{item.sold} sold</span>
                  <span style={item.onHand<LOW_STOCK_UNITS?S.pillRed:S.pillGreen}>{item.oversold?"Oversold":(item.onHand<LOW_STOCK_UNITS?"Low stock":"In stock")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={S.card}>
          <div style={S.cardHdr}><span style={S.cardTitle}>Recent invoices</span><button style={S.btnGhost} onClick={()=>setPage("invoices")}>All invoices →</button></div>
          {/* Real invoices only — previously fell back to INIT_INVOICES demo
              data when invoices was empty, which leaked fake invoices into
              new reps' dashboards. Empty state now explicitly tells them to
              create an invoice. */}
          {(invoices||[]).length===0 ? (
            <div style={{padding:"24px 18px",textAlign:"center",color:C.inkLight,fontSize:13}}>
              No invoices yet — <span style={{color:C.blue,cursor:"pointer"}} onClick={()=>setPage("invoices")}>create your first invoice</span>.
            </div>
          ) : (
            [...(invoices||[])]
              .sort((a,b)=>{ const da=a.sharedAt?new Date(a.sharedAt):(a.date?new Date(a.date):new Date(0)); const db=b.sharedAt?new Date(b.sharedAt):(b.date?new Date(b.date):new Date(0)); return db - da; })
              .slice(0,5)
              .map(inv=>(
                <div key={inv.id} onClick={()=>setPage("invoices")} style={{display:"grid",gridTemplateColumns:"80px 1fr 80px 70px",gap:8,padding:"10px 18px",borderBottom:`1px solid ${C.border}`,fontSize:12,alignItems:"center",cursor:"pointer"}} onMouseEnter={e=>{e.currentTarget.style.background=C.blueLight;}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}>
                  <span style={{fontWeight:600,color:C.ink,fontFamily:"monospace",fontSize:11}}>{inv.id}</span>
                  <span style={{color:C.inkLight,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{inv.customer}</span>
                  <span style={{fontWeight:600,color:C.ink,textAlign:"right"}}>${(Number(inv.total)||0).toFixed(2)}</span>
                  <InvoicePill inv={inv}/>
                </div>
              ))
          )}
        </div>
      </div>

      <div style={S.card}>
        <div style={S.cardHdr}><span style={S.cardTitle}>Training progress</span><button style={S.btnGhost} onClick={()=>setPage("training")}>Go to training →</button></div>
        <div style={{padding:"14px 18px"}}>
          {/* Real progress from the live videos store. Previously hardcoded
              60% / "3 of 5" / "How to handle objections" — completely
              detached from what the rep actually watched. */}
          {requiredVideos.length===0 ? (
            <div style={{fontSize:12,color:C.inkLight}}>No required training videos set. Check <span style={{color:C.blue,cursor:"pointer"}} onClick={()=>setPage("training")}>Training hub</span> for optional content.</div>
          ) : (
            <>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                <div style={{flex:1,height:8,background:C.border,borderRadius:4,overflow:"hidden"}}>
                  <div style={{width:trainingPct+"%",height:"100%",background:isCertified?C.green:C.teal,borderRadius:4,transition:"width 0.3s"}}/>
                </div>
                <span style={{fontSize:12,color:C.inkLight}}>{completedRequired} of {requiredVideos.length} required videos completed</span>
              </div>
              {isCertified ? (
                <div style={{fontSize:12,color:C.green,fontWeight:500}}>
                  ✓ All required videos complete — you are a Certified MPC Rep.
                  {earnedCerts.length>0 && <> <span style={{color:C.blue,cursor:"pointer",fontWeight:400}} onClick={()=>setPage("training")}>View your certificate →</span></>}
                </div>
              ) : nextRequired ? (
                <div style={{fontSize:12,color:C.inkLight}}>
                  Complete <strong style={{color:C.amber,cursor:"pointer"}} onClick={()=>setPage("training")}>"{nextRequired.title}"</strong> to become a certified rep.
                </div>
              ) : (
                <div style={{fontSize:12,color:C.inkLight}}>Keep going to earn your Certified Rep badge.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── INVENTORY ────────────────────────────────────────────────────────────────
function SearchableProductSelect({value, onChange, products, placeholder}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const selected = products.find(p=>p.sku===value);
  const filtered = search
    ? products.filter(p=>p.sku.toLowerCase().includes(search.toLowerCase())||p.name.toLowerCase().includes(search.toLowerCase()))
    : products;

  return (
    <div style={{position:"relative"}}>
      <input
        style={{...S.input,fontSize:12,paddingRight:24}}
        value={open ? search : (selected ? `[${selected.sku}] ${selected.name}` : "")}
        placeholder={placeholder||"Search by SKU or name..."}
        onFocus={()=>{setOpen(true);setSearch("");}}
        onChange={e=>{setSearch(e.target.value);setOpen(true);}}
        onBlur={()=>setTimeout(()=>setOpen(false),200)}
      />
      {open && (
        <div style={{position:"absolute",top:"100%",left:0,right:0,maxHeight:220,overflowY:"auto",background:C.white,border:`1px solid ${C.border}`,borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",zIndex:100,marginTop:2}}>
          {filtered.length===0 && <div style={{padding:"10px 12px",fontSize:12,color:C.inkLight}}>No products found</div>}
          {filtered.map(p=>(
            <div key={p.sku}
              onMouseDown={(e)=>{e.preventDefault();onChange(p.sku);setOpen(false);setSearch("");}}
              style={{padding:"8px 12px",fontSize:12,cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",background:p.sku===value?C.blueLight:C.white,borderBottom:`1px solid ${C.border}`}}
            >
              <div>
                <span style={{fontWeight:600,color:C.blue,fontFamily:"monospace",fontSize:11}}>{p.sku}</span>
                <span style={{marginLeft:8,color:C.ink}}>{p.name}</span>
                {p.isKit&&<span style={{marginLeft:6,fontSize:9,fontWeight:600,padding:"1px 5px",borderRadius:4,background:"#EDE9FE",color:"#7C3AED"}}>KIT</span>}
              </div>
              <span style={{fontSize:11,color:C.inkLight}}>${p.price.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// RepInventory — reads the Supabase `inventory_movements` ledger (via the
// `inventory` prop, which is derived identically to admin's formula) and lets
// the rep file stock-receipt REQUESTS via `createInventoryRequest` (one INSERT
// per SKU into inventory_requests with status='pending'). Admin reviews each
// request and either Approves (server-side INSERT of one rep_receive row into
// inventory_movements with qty=approved_qty) or Rejects (no ledger row). RLS
// guarantees the rep can only file requests keyed to their own auth.uid() —
// the rep cannot self-approve, and the legacy direct-INSERT path on
// inventory_movements was removed in migration 0020.
//
// Props:
//   inventory       — rows from inventoryMath.repInventoryRows
//                     [{sku, name, received, sold, onHand (SIGNED), oversold, oversoldBy, price, isKit}]
//   movements       — full rep-scoped movement history (for the audit panel)
//   productList     — live catalog for the request dialog
//   loading         — true while initial ledger fetch is in flight
//   errorMsg        — set when the ledger could not be re-read (amber banner + Retry)
//   onReceive       — async ({sku, qty, reason}) → files an inventory_requests row
//   onRefresh       — manual refresh, in case realtime isn't enabled
//   requests        — RLS-filtered inventory_requests rows for this rep
//   requestsError   — set when the requests fetch fails
//   onCancelRequest — async (id) → cancels a pending request
function RepInventory({inventory, movements, productList, loading, errorMsg, onReceive, onRefresh, requests = [], requestsError = "", onCancelRequest}) {
  const [showReceive, setShowReceive] = useState(false);
  const [receiveItems, setReceiveItems] = useState([{sku:"", qty:0}]);
  const [receiveReason, setReceiveReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // The SAME flattened list the invoice picker uses (variation lines instead of
  // their parent), so a SKU requested here is exactly a SKU the rep can invoice.
  const allItems = useMemo(
    () => flattenCatalogForPicker(productList && productList.length > 0 ? productList : ALL_PRODUCTS_AND_KITS),
    [productList]
  );
  const oversoldRows = inventory.filter(i => i.oversold);

  const handleReceive = async () => {
    setSubmitError("");
    const valid = receiveItems.filter(r => r.sku && r.qty > 0);
    if(valid.length === 0) { setSubmitError("Add at least one SKU with qty > 0."); return; }
    setSubmitting(true);
    try {
      // One rep_receive INSERT per SKU — RLS accepts each individually.
      for (const r of valid) {
        // eslint-disable-next-line no-await-in-loop
        await onReceive({ sku: r.sku, qty: r.qty, reason: receiveReason });
      }
      setShowReceive(false);
      setReceiveItems([{sku:"",qty:0}]);
      setReceiveReason("");
      if(onRefresh) await onRefresh();
    } catch(e) {
      console.warn("[RepInventory] receive failed:", e?.message||e);
      setSubmitError(e?.message || "Receive failed. Check console for details.");
    } finally {
      setSubmitting(false);
    }
  };

  // Audit-panel labeling — admin movements (assign/return/adjust_rep) are
  // tagged "From MPC", rep self-reports as "Self-reported". Warehouse rows
  // never appear here (RLS filters movements to this rep's rep_id only).
  const movementLabel = {
    assign:      { text: "From MPC",        color: C.blue,   bg: C.blueLight },
    return:      { text: "Returned to MPC", color: "#B45309", bg: "#FEF3C7" },
    adjust_rep:  { text: "MPC adjustment",  color: "#7C3AED", bg: "#EDE9FE" },
    rep_receive: { text: "Self-reported",    color: C.teal,   bg: "#CCFBF1" },
  };

  // Display only entries that affect this rep's on-hand. Most recent first.
  // `movements` is already server-sorted by created_at DESC.
  const auditRows = (movements || []).filter(m =>
    m && REP_LEDGER_TYPES.includes(m.movement_type)
  );

  return (
    <div style={S.content}>
      {showReceive && (
        <Modal title="Request stock from MPC" onClose={()=>{ if(!submitting) setShowReceive(false); }}>
          <div style={{fontSize:13,color:C.inkLight,marginBottom:16}}>
            Enter the SKUs and quantities you received. Each line is sent to admin for
            approval — you&rsquo;ll see it as <b>Pending</b> in <i>My stock requests</i> below.
            Approved requests add to your inventory; rejected ones do not.
          </div>
          {receiveItems.map((r,i)=>(
            <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 80px 30px",gap:8,marginBottom:8,alignItems:"center"}}>
              <SearchableProductSelect
                value={r.sku}
                onChange={sku=>setReceiveItems(ri=>ri.map((x,xi)=>xi===i?{...x,sku}:x))}
                products={allItems}
                placeholder="Search by SKU or name..."
              />
              <input type="number" value={r.qty} min="0" onChange={e=>setReceiveItems(ri=>ri.map((x,xi)=>xi===i?{...x,qty:parseInt(e.target.value)||0}:x))} style={{...S.input,textAlign:"center"}} placeholder="Qty"/>
              {receiveItems.length>1 && <button onClick={()=>setReceiveItems(ri=>ri.filter((_,xi)=>xi!==i))} style={{background:"none",border:"none",color:C.inkLight,cursor:"pointer",fontSize:18}}>×</button>}
            </div>
          ))}
          <button style={S.btnGhost} onClick={()=>setReceiveItems(ri=>[...ri,{sku:"",qty:0}])}>+ Add SKU</button>
          <div style={{marginTop:14}}>
            <label style={S.label}>Reason / reference (optional)</label>
            <input
              style={S.input}
              value={receiveReason}
              onChange={e=>setReceiveReason(e.target.value)}
              placeholder="e.g. PO-12345, truck delivery Apr 14"
            />
          </div>
          {submitError && <div style={{marginTop:10, padding:"8px 12px", background:C.redLight, color:C.red, fontSize:12, borderRadius:6}}>{submitError}</div>}
          <div style={{display:"flex",gap:10,marginTop:18,justifyContent:"flex-end"}}>
            <button style={S.btnSecondary} onClick={()=>setShowReceive(false)} disabled={submitting}>Cancel</button>
            <button style={S.btnPrimary} onClick={handleReceive} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit for approval"}
            </button>
          </div>
        </Modal>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:18,fontWeight:600,color:C.ink}}>My inventory</div>
          <div style={{fontSize:13,color:C.inkLight,marginTop:2}}>
            Live from the inventory ledger. Updates automatically when admin assigns stock to you, when admin approves your request, or when you create invoices.
          </div>
        </div>
        <button style={S.btnPrimary} onClick={()=>setShowReceive(true)}>+ Request stock from MPC</button>
      </div>

      <LedgerStaleBanner message={errorMsg} onRetry={onRefresh}/>

      {oversoldRows.length > 0 && (
        <div style={{padding:"10px 14px", marginBottom:12, background:C.redLight, color:C.red, borderRadius:8, fontSize:13}}>
          <b>{oversoldRows.length} product{oversoldRows.length>1?"s are":" is"} oversold</b>: more units were invoiced than were ever received.
          Ask MPC to post the missing stock (request it below) so the count is correct.
        </div>
      )}

      <div style={S.card}>
        <div style={{overflowX:"auto"}}>
        <div style={{minWidth:720}}>
        <div style={{...S.tableHdr, gridTemplateColumns:"minmax(160px,1fr) 130px 80px 80px 100px 90px"}}>
          <span>Product</span><span style={{textAlign:"right"}}>On hand</span><span style={{textAlign:"right"}}>Sold</span><span style={{textAlign:"right"}}>Total rcvd</span><span style={{textAlign:"right"}}>Value on hand</span><span>Status</span>
        </div>
        {loading && inventory.length === 0 && (
          <div style={{padding:"20px 18px", fontSize:13, color:C.inkLight}}>Loading inventory ledger…</div>
        )}
        {!loading && inventory.length === 0 && (
          <div style={{padding:"20px 18px", fontSize:13, color:C.inkLight}}>
            No inventory on hand yet. Admin hasn&rsquo;t assigned stock to you, and you haven&rsquo;t self-reported any receipts.
            Use <b>+ Receive stock from MPC</b> to log a shipment.
          </div>
        )}
        {inventory.map(item=>(
          <div key={item.sku} style={{...S.tableRow, gridTemplateColumns:"minmax(160px,1fr) 130px 80px 80px 100px 90px"}}>
            <div>
              <div style={{fontWeight:500,color:C.ink}}>
                <span style={{fontFamily:"monospace",fontSize:11,color:C.blue,fontWeight:600,marginRight:6}}>{item.sku}</span>
                {item.name}
                {item.isKit&&<span style={{marginLeft:6,fontSize:9,fontWeight:600,padding:"1px 5px",borderRadius:4,background:"#EDE9FE",color:"#7C3AED"}}>KIT</span>}
              </div>
              <div style={{fontSize:11,color:C.inkLight}}>${Number(item.price||0).toFixed(2)}/unit</div>
            </div>
            <span style={{fontWeight:item.oversold?700:600,color:item.onHand<LOW_STOCK_UNITS?C.red:C.teal,textAlign:"right",whiteSpace:"nowrap"}}>{item.oversold?`Oversold by ${item.oversoldBy}`:item.onHand}</span>
            <span style={{color:C.inkLight,textAlign:"right"}}>{item.sold}</span>
            <span style={{color:C.inkLight,textAlign:"right"}}>{item.received}</span>
            <span style={{fontWeight:500,color:C.ink,textAlign:"right"}}>${(Math.max(0,item.onHand)*Number(item.price||0)).toFixed(2)}</span>
            {item.oversold ? <span style={S.pillRed}>Oversold</span> : <Badge type={item.onHand<LOW_STOCK_UNITS?"warning":"active"}>{item.onHand<LOW_STOCK_UNITS?"Low stock":"In stock"}</Badge>}
          </div>
        ))}
        </div>
        </div>
        <div style={{padding:"12px 18px",background:C.surface,display:"flex",justifyContent:"space-between",fontSize:12}}>
          <span style={{color:C.inkLight}}>Total inventory value on hand</span>
          <span style={{fontWeight:600,color:C.ink}}>${inventory.reduce((s,i)=>s+Math.max(0,i.onHand)*Number(i.price||0),0).toFixed(2)}</span>
        </div>
      </div>

      {/* My stock requests — inventory_requests rows scoped to this rep by RLS.
          Pending rows do NOT count toward on-hand; only after admin approves
          does a rep_receive row land in inventory_movements and feed the
          inventory table above. Rejected/cancelled rows stay visible for
          audit. */}
      <div style={{...S.card, marginTop:16}}>
        <div style={S.cardHdr}>
          <span style={S.cardTitle}>My stock requests</span>
          <span style={{fontSize:11, color:C.inkLight}}>
            Awaiting admin approval, plus your recent decisions.
          </span>
        </div>
        {requestsError && (
          <div style={{padding:"10px 14px", margin:"10px 14px", background:C.redLight, color:C.red, borderRadius:8, fontSize:13}}>
            {requestsError}
          </div>
        )}
        {(requests || []).length === 0 ? (
          <div style={{padding:"20px 18px", fontSize:13, color:C.inkLight}}>
            You haven&rsquo;t filed any stock-receipt requests yet. Use <b>+ Request stock from MPC</b>
            above to submit one — admin will review and approve before it affects your inventory.
          </div>
        ) : (
          <div style={{maxHeight:340, overflowY:"auto"}}>
            <div style={{...S.tableHdr, gridTemplateColumns:"130px 1fr 80px 110px 1fr 90px"}}>
              <span>Filed</span>
              <span>SKU / product</span>
              <span style={{textAlign:"right"}}>Qty</span>
              <span>Status</span>
              <span>Decision note</span>
              <span></span>
            </div>
            {(requests || []).map(req => {
              const status = req.status || 'pending';
              const isPending   = status === 'pending';
              const isApproved  = status === 'approved';
              const isRejected  = status === 'rejected';
              const isCancelled = status === 'cancelled';
              const pillStyle = isPending   ? { color:"#92400E", bg:"#FEF3C7" }
                              : isApproved  ? { color:C.teal,    bg:"#CCFBF1" }
                              : isRejected  ? { color:C.red,     bg:C.redLight }
                              :               { color:C.inkLight, bg:C.surface };
              const showApprovedDelta = isApproved
                && req.approved_qty != null
                && Number(req.approved_qty) !== Number(req.requested_qty);
              return (
                <div key={req.id} style={{...S.tableRow, gridTemplateColumns:"130px 1fr 80px 110px 1fr 90px"}}>
                  <span style={{color:C.inkLight, fontSize:11}}>
                    {req.created_at ? new Date(req.created_at).toLocaleString() : "—"}
                  </span>
                  <span>
                    <span style={{fontFamily:"monospace", fontSize:11, color:C.blue, fontWeight:600, marginRight:6}}>{req.sku}</span>
                    <span style={{color:C.ink}}>{req.product_name || ""}</span>
                    {req.reason && <div style={{fontSize:11, color:C.inkLight, marginTop:2}}>{req.reason}</div>}
                  </span>
                  <span style={{textAlign:"right", fontWeight:600, color:C.ink}}>
                    {isApproved ? (req.approved_qty != null ? req.approved_qty : req.requested_qty) : req.requested_qty}
                    {showApprovedDelta && (
                      <div style={{fontSize:10, color:C.inkLight, fontWeight:400, marginTop:2}}>
                        (asked {req.requested_qty})
                      </div>
                    )}
                  </span>
                  <span>
                    <span style={{display:"inline-block", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600, color:pillStyle.color, background:pillStyle.bg}}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </span>
                  <span style={{fontSize:12, color:C.inkMid}}>
                    {req.decision_note || (isApproved ? "Approved" : isRejected ? "Rejected" : isCancelled ? "Cancelled by you" : "")}
                    {(isApproved || isRejected) && req.decided_by && (
                      <div style={{fontSize:10, color:C.inkLight, marginTop:2}}>by {req.decided_by}</div>
                    )}
                  </span>
                  <span>
                    {isPending && onCancelRequest && (
                      <button
                        style={{...S.btnGhost, padding:"4px 10px", fontSize:12}}
                        onClick={()=>onCancelRequest(req.id)}
                      >
                        Cancel
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Audit panel — every admin-initiated and rep-initiated ledger row for this rep */}
      <div style={{...S.card, marginTop:16}}>
        <div style={S.cardHdr}>
          <span style={S.cardTitle}>Inventory history</span>
          <span style={{fontSize:11, color:C.inkLight}}>
            Append-only. Records cannot be edited or deleted.
          </span>
        </div>
        {auditRows.length === 0 ? (
          <div style={{padding:"20px 18px", fontSize:13, color:C.inkLight}}>
            No inventory movements yet. Movements appear here the moment admin assigns stock to you
            or you self-report a receipt.
          </div>
        ) : (
          <div style={{maxHeight:360, overflowY:"auto"}}>
            <div style={{...S.tableHdr, gridTemplateColumns:"140px 130px 1fr 70px 1fr"}}>
              <span>When</span><span>Type</span><span>SKU / product</span><span style={{textAlign:"right"}}>Qty</span><span>Reason / reference</span>
            </div>
            {auditRows.map(m=>{
              const tag = movementLabel[m.movement_type] || { text: m.movement_type, color: C.inkMid, bg: C.surface };
              const signedQty = Number(m.qty)||0;
              return (
                <div key={m.id} style={{...S.tableRow, gridTemplateColumns:"140px 130px 1fr 70px 1fr"}}>
                  <span style={{color:C.inkLight, fontSize:11}}>
                    {m.created_at ? new Date(m.created_at).toLocaleString() : "—"}
                  </span>
                  <span>
                    <span style={{display:"inline-block", padding:"2px 8px", borderRadius:6, fontSize:10, fontWeight:600, color:tag.color, background:tag.bg}}>
                      {tag.text}
                    </span>
                  </span>
                  <div>
                    <div style={{fontFamily:"monospace", fontSize:11, color:C.blue, fontWeight:600}}>{m.sku}</div>
                    <div style={{fontSize:11, color:C.inkLight}}>{m.product_name || ""}</div>
                  </div>
                  <span style={{textAlign:"right", fontWeight:600, color: signedQty>=0 ? C.teal : C.red}}>
                    {signedQty > 0 ? "+" : ""}{signedQty}
                  </span>
                  <span style={{color:C.inkMid, fontSize:12}}>
                    {m.reason || m.reference || <span style={{color:C.inkLight}}>—</span>}
                    {m.created_by && <span style={{display:"block", fontSize:10, color:C.inkLight}}>by {m.created_by}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── COMPANY & CUSTOMER INFO ─────────────────────────────────────────────────
const MPC_INFO_DEFAULT = {
  name:"MotorPower Care Inc.", address:"1234 Commerce Blvd, Suite 200",
  city:"San Diego, CA 92101", email:"info@mpcare.com",
  phone:"(619) 555-0100", website:"www.mpcare.com", taxId:"82-4567890",
};
// Read company info from admin settings (shared via localStorage), fall back to defaults.
// Field list kept in sync with App.js's own getMpcInfo() — these are two
// independent copies of the same function (one per file) reading the same
// mpcare_company_info cache, and they'd drifted: this copy was missing
// bankAccountName/bankSwift/bankAddress/wireNote/checkPayee/checkAddress,
// so the rep-side invoice's Payment information block would silently show
// blanks for those fields even when admin had them configured. Found during
// a deep-diagnose pass on a company-info-not-reflecting report — not the
// field that report was about (name/address, which were already correct
// here), but the same class of bug and worth closing while in here.
function getMpcInfo() {
  try {
    const s = localStorage.getItem("mpcare_company_info");
    if(s) {
      const d = JSON.parse(s);
      return {
        name: d.name || MPC_INFO_DEFAULT.name,
        address: d.address || MPC_INFO_DEFAULT.address,
        city: [d.city, d.state, d.zip].filter(Boolean).join(", ") || MPC_INFO_DEFAULT.city,
        email: d.email || MPC_INFO_DEFAULT.email,
        phone: d.phone || MPC_INFO_DEFAULT.phone,
        website: d.website || MPC_INFO_DEFAULT.website,
        taxId: d.taxId || MPC_INFO_DEFAULT.taxId,
        logo: d.logo || "",
        invoiceNotes: d.invoiceNotes || "",
        invoicePrefix: d.invoicePrefix || "INV",
        bankName: d.bankName || "",
        bankRouting: d.bankRouting || "",
        bankAccount: d.bankAccount || "",
        paypalEmail: d.paypalEmail || "",
        zelleEmail: d.zelleEmail || "",
        // Wire transfer instructions (editable in admin Settings)
        bankAccountName: d.bankAccountName || d.name || MPC_INFO_DEFAULT.name,
        bankSwift: d.bankSwift || "",
        bankAddress: d.bankAddress || "",
        wireNote: d.wireNote || "",
        checkPayee: d.checkPayee || d.name || MPC_INFO_DEFAULT.name,
        checkAddress: d.checkAddress || "",
      };
    }
  } catch(e) {}
  return MPC_INFO_DEFAULT;
}
const CUSTOMER_INFO = {
  "Apex Car Care LLC":      {address:"1234 Industrial Blvd, Suite 100",city:"San Diego, CA 92101",  email:"john@apexcarcare.com",    phone:"619-555-0100",resaleLicense:"CA-RS-4821093"},
  "FastLane Auto Parts":    {address:"500 Commerce Ave",               city:"Phoenix, AZ 85001",    email:"maria@fastlane.com",      phone:"623-555-0200",resaleLicense:"AZ-RS-7734201"},
  "Westside Repair Center": {address:"77 Main St",                     city:"Bakersfield, CA 93301",email:"dave@westsiderepair.com", phone:"661-555-0300",resaleLicense:"CA-RS-5509874"},
};

// ─── NOTIFICATION LOG ─────────────────────────────────────────────────────────
const NOTIFICATION_LOG = [];
const addNotification = (type, data) => {
  NOTIFICATION_LOG.unshift({id:Date.now(), time:new Date().toLocaleTimeString(), type, ...data});
};

// ─── LEGAL INVOICE VIEWER ─────────────────────────────────────────────────────
// "Received by the company: Check #123, Mar 12, 2026" under a rep invoice row (own RLS-scoped rows only).
function RepReceivedLine({invoiceId}) {
  const rem = useRepRemittanceFor(invoiceId);
  if(!rem) return null;
  return <div style={{fontSize:10,color:"#5B21B6",marginTop:3,lineHeight:1.3,fontWeight:600}}>Received by the company: {repReceivedText(rem)}</div>;
}

export function LegalInvoiceModal({inv, onClose, onToggleStatus, requestSlot}) {
  // The company already received this invoice's payment from the rep (own RLS-scoped rows).
  const companyRem = useRepRemittanceFor(inv && inv.id);
  if(!inv) return null;
  const mpc = getMpcInfo();
  const cust = inv.customerInfo || CUSTOMER_INFO[inv.customer] || {};
  // Totals block. Without a stored account discount this is exactly what it always was (Subtotal and
  // Total due both the items sum). A rep field invoice WITH one shows Subtotal, the discount line and
  // Total due = the stored total (the amount charged and commissioned). See legalInvoiceTotals.
  const totals = legalInvoiceTotals(inv);
  const subtotal = totals.subtotal;
  const today = inv.date||new Date().toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"});
  const isPaid = inv.status==="paid";
  const isCancelled = inv.status==="cancelled";
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:2000,padding:"20px 0",overflowY:"auto"}}>
      <div style={{background:C.white,width:700,borderRadius:12,overflow:"hidden",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{background:"#1E293B",padding:"12px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{color:C.white,fontSize:13,fontWeight:500}}>Invoice {inv.id}</span>
            {companyRem&&!isCancelled&&<span style={{padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:600,background:"#EDE9FE",color:"#5B21B6"}}>Received by the company</span>}
            {onToggleStatus&&!isCancelled&&!companyRem&&(
              <button onClick={()=>onToggleStatus(inv.id)} style={{padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:600,cursor:"pointer",border:"none",background:isPaid?"#DCFCE7":"#FEF3C7",color:isPaid?"#166534":"#B45309"}}>{isPaid?"✓ Paid":"⏳ Pending"}</button>
            )}
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>window.print()} style={{background:"rgba(255,255,255,0.1)",border:"none",color:C.white,padding:"6px 14px",borderRadius:6,fontSize:12,cursor:"pointer"}}>Print / Save PDF</button>
            <button onClick={onClose} style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
          </div>
        </div>
        {requestSlot&&<div className="no-print" style={{padding:"10px 24px",background:C.surface,borderBottom:`1px solid ${C.border}`,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>{requestSlot}</div>}
        {companyRem&&<div className="no-print" style={{padding:"10px 24px",background:"#EDE9FE",borderBottom:"1px solid #C4B5FD",color:"#5B21B6",fontSize:12}}><strong>Received by the company:</strong> {repReceivedText(companyRem)}. This payment was handed in and recorded by the admin.</div>}
        {isCancelled&&<div className="no-print" style={{padding:"10px 24px",background:"#FEE2E2",borderBottom:"1px solid #FCA5A5",color:"#991B1B",fontSize:12}}><strong>Cancelled by admin</strong>{inv.cancelledDate?` on ${inv.cancelledDate}`:""}{inv.cancelReason?`. Reason: ${inv.cancelReason}`:""}</div>}
        <div style={{padding:"40px 48px",fontFamily:"'DM Sans',system-ui,sans-serif"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:32}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
                {mpc.logo?<img src={mpc.logo} alt={mpc.name} style={{maxHeight:56,objectFit:"contain"}} onError={e=>{e.target.style.display="none"}}/>:<img src="/mpc-logo.png" alt="MotorPower Care" style={{width:56,height:56,objectFit:"contain"}}/>}
                <div style={{fontSize:22,fontWeight:700,color:C.navy}}>{mpc.name}</div>
              </div>
              <div style={{fontSize:12,color:C.inkLight,lineHeight:1.9}}>{mpc.address}<br/>{mpc.city}<br/>{mpc.phone}<br/>{mpc.email}<br/>{mpc.website}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:34,fontWeight:700,color:C.navy,marginBottom:6}}>INVOICE</div>
              <div style={{fontSize:13,color:C.inkLight,lineHeight:1.9}}>
                <strong style={{color:C.ink}}>Invoice #:</strong> {inv.id}<br/>
                <strong style={{color:C.ink}}>Date:</strong> {today}<br/>
                <strong style={{color:C.ink}}>Terms:</strong> {inv.paymentTerms||"Net 30"}
              </div>
              <div style={{marginTop:8,display:"inline-block",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:600,color:"#1D4ED8"}}>Tax Exempt — For Resale</div>
            </div>
          </div>
          <div style={{height:2,background:`linear-gradient(to right,${C.navy},${C.blue})`,borderRadius:2,marginBottom:28}}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24,marginBottom:28}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Bill to</div>
              <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:2}}>{inv.customer}</div>
              <div style={{fontSize:12,color:C.inkLight,lineHeight:1.9}}>{cust.address||""}<br/>{cust.city||""}<br/>{cust.phone||""}<br/>{cust.email||""}</div>
              {cust.resaleLicense&&<div style={{marginTop:6,fontSize:11,color:C.inkMid}}><strong>Resale License:</strong> {cust.resaleLicense}</div>}
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>From</div>
              <div style={{fontSize:12,color:C.inkMid,lineHeight:1.9}}>
                <strong style={{color:C.ink}}>Seller:</strong> {mpc.name}<br/>
                <strong style={{color:C.ink}}>Tax ID:</strong> {mpc.taxId}<br/>
                <strong style={{color:C.ink}}>Rep:</strong> {displayRepStamp(inv.rep)||"Sales Rep"}<br/>
                {mpc.phone&&<><strong style={{color:C.ink}}>Phone:</strong> {mpc.phone}</>}
              </div>
            </div>
          </div>
          <div style={{border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",marginBottom:24}}>
            <div style={{display:"grid",gridTemplateColumns:"32px 80px 1fr 60px 80px 90px",background:C.navy,padding:"10px 16px",gap:8}}>
              {["#","SKU","Description","Qty","Price","Total"].map(h=>(
                <div key={h} style={{fontSize:11,fontWeight:600,color:C.white,textAlign:["Qty","Price","Total"].includes(h)?"right":"left"}}>{h}</div>
              ))}
            </div>
            {inv.items.map((item,i)=>{
              const caseSize = Math.max(1, Number(item.case)||1);
              const linePrice = Number(item.price)||0;
              const unitPrice = caseSize > 1 ? linePrice / caseSize : null;
              return (
              <div key={i} style={{display:"grid",gridTemplateColumns:"32px 80px 1fr 60px 80px 90px",gap:8,padding:"11px 16px",borderTop:`1px solid ${C.border}`,background:i%2===0?C.white:C.surface}}>
                <div style={{fontSize:12,color:C.inkLight}}>{i+1}</div>
                <div style={{fontSize:11,fontWeight:600,color:C.blue,fontFamily:"monospace"}}>{item.sku}</div>
                <div style={{fontSize:12,fontWeight:500,color:C.ink}}>{item.name}</div>
                <div style={{fontSize:12,color:C.ink,textAlign:"right"}}>{item.qty}</div>
                <div style={{fontSize:12,color:C.ink,textAlign:"right"}}>
                  ${linePrice.toFixed(2)}
                  {unitPrice != null && (
                    <div style={{fontSize:9,color:C.inkLight,fontWeight:400,marginTop:2}}>
                      ${unitPrice.toFixed(2)}/unit · case of {caseSize}
                    </div>
                  )}
                </div>
                <div style={{fontSize:12,fontWeight:500,color:C.ink,textAlign:"right"}}>${(linePrice*(Number(item.qty)||0)).toFixed(2)}</div>
              </div>
              );
            })}
          </div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:24}}>
            <div style={{width:260}}>
              <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:13,borderBottom:`1px solid ${C.border}`}}><span style={{color:C.inkMid}}>Subtotal</span><span style={{fontWeight:500}}>${subtotal.toFixed(2)}</span></div>
              {totals.hasDiscount&&<div data-testid="invoice-discount-row" style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:13,borderBottom:`1px solid ${C.border}`}}><span style={{color:C.inkMid}}>{totals.label} ({formatPct(totals.discountPct)}%)</span><span style={{color:C.teal,fontWeight:500}}>-${totals.discountAmount.toFixed(2)}</span></div>}
              <div style={{display:"flex",justifyContent:"space-between",padding:"6px 0",fontSize:13,borderBottom:`1px solid ${C.border}`}}><span style={{color:C.inkMid}}>Tax</span><span style={{color:C.teal,fontWeight:500}}>$0.00 — Tax exempt</span></div>
              <div style={{display:"flex",justifyContent:"space-between",padding:"10px 0",fontSize:16,fontWeight:700,color:C.navy}}><span>Total due</span><span>${totals.totalDue.toFixed(2)}</span></div>
              {onToggleStatus&&!isCancelled&&!companyRem&&(
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderTop:`1px solid ${C.border}`}}>
                  <span style={{fontSize:12,color:C.inkMid}}>Payment status</span>
                  <button onClick={()=>onToggleStatus(inv.id)} style={{padding:"5px 14px",borderRadius:20,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid ${isPaid?C.green:C.amber}`,background:isPaid?C.greenLight:C.amberLight,color:isPaid?C.green:C.amber}}>{isPaid?"✓ Paid":"⏳ Pending — Mark as paid"}</button>
                </div>
              )}
            </div>
          </div>
          {inv.notes&&<div style={{background:C.surface,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:C.inkMid}}><strong>Notes:</strong> {inv.notes}</div>}
          <div style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,padding:"12px 16px",marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:"#1D4ED8",marginBottom:4}}>TAX EXEMPTION NOTICE</div>
            <div style={{fontSize:11,color:"#1E40AF",lineHeight:1.6}}>This sale is exempt from sales tax. Purchaser holds a valid resale certificate: {cust.resaleLicense||"on file"}. Seller tax ID: {mpc.taxId}. For wholesale/resale purposes only.</div>
          </div>
          {(mpc.bankName||mpc.paypalEmail||mpc.zelleEmail)&&(
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:11,color:C.inkMid}}>
              <strong style={{color:C.ink}}>Payment information</strong><br/>
              {mpc.bankName&&<span>Bank: {mpc.bankName}{mpc.bankRouting?` | Routing: ${mpc.bankRouting}`:""}{mpc.bankAccount?` | Acct: ${mpc.bankAccount}`:""}<br/></span>}
              {mpc.paypalEmail&&<span>PayPal: {mpc.paypalEmail}<br/></span>}
              {mpc.zelleEmail&&<span>Zelle: {mpc.zelleEmail}<br/></span>}
            </div>
          )}
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14,display:"flex",justifyContent:"space-between"}}>
            <div style={{fontSize:11,color:C.inkLight}}>{mpc.invoiceNotes||"Thank you for your business"} — {mpc.name}</div>
            <div style={{fontSize:11,color:C.inkLight}}>{mpc.email}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── REQUEST-ADJUSTMENT MODAL ────────────────────────────────────────────────
// Lets a rep PROPOSE new lines / payment terms for one of their unpaid field
// invoices. It never changes the invoice: onSubmit files a request that admin
// must approve. SKUs already on the invoice keep their ORIGINAL price / name /
// case size; only newly added SKUs use the catalog price the rep sees when
// creating an invoice (see buildProposedItems in invoiceAdjustments.js).
const ADJ_TERMS = ["Net 15","Net 30","Net 60","Due on receipt","Cash","Check","Credit card","ACH"];
function AdjustRequestModal({inv, pickerProducts, lookup, onClose, onSubmit, busy, error}) {
  const origItems = useMemo(()=>parseInvItems(inv.items),[inv]);
  const [lines, setLines] = useState(()=>origItems.map(i=>({sku:i.sku,qty:Number(i.qty)||1})));
  const [terms, setTerms] = useState(inv.paymentTerms||"Net 30");
  const [reason, setReason] = useState("");
  const [localError, setLocalError] = useState("");
  const extras = useMemo(()=>extrasFromInvoice(inv),[inv]);
  // Original lines stay selectable even if the SKU has since left the catalog.
  const options = useMemo(()=>{
    const have = new Set((pickerProducts||[]).map(p=>p.sku));
    const extra = origItems.filter(i=>i&&i.sku&&!have.has(i.sku)).map(i=>({sku:i.sku,name:i.name||i.sku,price:Number(i.price)||0,case:i.case}));
    return [...(pickerProducts||[]), ...extra];
  },[pickerProducts, origItems]);
  const built = useMemo(()=>buildProposedItems(inv.items, lines, lookup),[inv, lines, lookup]);
  const newTotal = recomputeTotal(built.items, extras);
  const oldTotal = Number(inv.total)||0;
  const diff = useMemo(()=>diffItems(inv.items, built.items).filter(r=>r.kind!=="unchanged"),[inv, built]);
  const unitFor = (sku) => {
    const o = origItems.find(i=>i.sku===sku);
    if(o) return Number(o.price)||0;
    const p = lookup(sku);
    return p ? Number(p.price)||0 : null;
  };
  const setLine = (i,k,v) => setLines(ls=>ls.map((l,li)=>li===i?{...l,[k]:v}:l));
  const submit = () => {
    if(busy) return;
    setLocalError("");
    const why = reason.trim();
    if(!why) { setLocalError("Please enter a reason so admin knows why."); return; }
    if(built.missing.length) { setLocalError(`Product not found in the catalog: ${built.missing.join(", ")}`); return; }
    const bad = validateProposal({items:built.items, total:newTotal}, extras);
    if(bad) { setLocalError(bad); return; }
    if(!hasAdjustmentChange(inv.items, built.items, inv.paymentTerms, terms)) { setLocalError("Nothing was changed. Edit a line or the payment terms first."); return; }
    onSubmit({items:built.items, terms, reason:why});
  };
  const shownError = localError || error;
  const kindColor = {added:C.green, removed:C.red, changed:C.amber};
  return (
    <div style={S.modal}>
      {/* Narrow-screen layout (375px / 320px): width = 94vw (352 / 301px), side
          padding shrinks to 4vw (15 / 13px) so the content box is 322 / 275px.
          Every line is TWO rows: product full width on top, then qty / unit /
          total in a 3-column grid (72px + 2 x 1fr) - never a 5-column row. */}
      <div style={{...S.modalBox,width:"min(720px,94vw)",padding:"20px clamp(12px,4vw,32px)",boxSizing:"border-box"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,marginBottom:12}}>
          <div style={{fontSize:17,fontWeight:600,color:C.ink,minWidth:0,overflowWrap:"anywhere"}}>Request adjustment — {inv.id}</div>
          <button onClick={()=>{ if(!busy) onClose(); }} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:C.inkLight,flexShrink:0,lineHeight:1}}>×</button>
        </div>
        <div style={{fontSize:13,color:C.inkLight,marginBottom:12,lineHeight:1.5}}>
          Change the lines below and explain why. <strong>Nothing changes on the invoice until admin approves.</strong> Products already on the invoice keep their original price; new products use your current catalog price.
        </div>
        <div style={{background:C.surface,borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:C.ink}}>
          <strong>Customer:</strong> {inv.customer} &nbsp;·&nbsp; <strong>Date:</strong> {inv.date}
        </div>
        {lines.map((row,i)=>{
          const unit = row.sku ? unitFor(row.sku) : null;
          const cap = {fontSize:10,color:C.inkLight,fontWeight:600,textTransform:"uppercase"};
          return (
            <div key={i} style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px",marginBottom:8}}>
              <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) 28px",gap:6,alignItems:"center",marginBottom:6}}>
                <div style={{minWidth:0}}><SearchableProductSelect value={row.sku} onChange={sku=>setLine(i,"sku",sku)} products={options} placeholder="Search by SKU or name..."/></div>
                {lines.length>1?<button disabled={busy} aria-label="Remove line" onClick={()=>setLines(ls=>ls.filter((_,li)=>li!==i))} style={{background:"none",border:"none",color:C.inkLight,cursor:"pointer",fontSize:18,padding:0}}>×</button>:<span/>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"72px minmax(0,1fr) minmax(0,1fr)",gap:8,alignItems:"end"}}>
                <label style={{display:"flex",flexDirection:"column",gap:2}}><span style={cap}>Qty</span>
                  <input type="number" min="1" step="1" value={row.qty} disabled={busy} onChange={e=>setLine(i,"qty",parseInt(e.target.value)||1)} style={{...S.input,fontSize:12,padding:"7px 8px",textAlign:"center",width:"100%",boxSizing:"border-box"}}/>
                </label>
                <div style={{display:"flex",flexDirection:"column",gap:2,textAlign:"right"}}><span style={cap}>Unit price</span>
                  <span style={{fontSize:12,color:C.inkLight,padding:"7px 0"}}>{unit!=null?`$${unit.toFixed(2)}`:"—"}</span>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:2,textAlign:"right"}}><span style={cap}>Total</span>
                  <span style={{fontSize:12,fontWeight:600,color:C.ink,padding:"7px 0"}}>{unit!=null?`$${(unit*(Number(row.qty)||0)).toFixed(2)}`:"—"}</span>
                </div>
              </div>
            </div>
          );
        })}
        <button style={S.btnGhost} disabled={busy} onClick={()=>setLines(ls=>[...ls,{sku:"",qty:1}])}>+ Add product</button>
        <div style={{height:1,background:C.border,margin:"12px 0"}}/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12}}>
          <div style={S.fg}><label style={S.label}>Payment terms</label>
            <select style={S.select} value={terms} disabled={busy} onChange={e=>setTerms(e.target.value)}>
              {(ADJ_TERMS.includes(terms)?ADJ_TERMS:[terms,...ADJ_TERMS]).map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div style={{background:newTotal===oldTotal?C.surface:C.amberLight,borderRadius:8,padding:"8px 12px",fontSize:12,color:C.ink,alignSelf:"end"}}>
            <div style={{display:"flex",justifyContent:"space-between"}}><span>Current total</span><span>${oldTotal.toFixed(2)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontWeight:700,fontSize:14}}><span>New total</span><span>${newTotal.toFixed(2)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",color:C.inkLight}}><span>Difference</span><span>{newTotal-oldTotal>=0?"+":"-"}${Math.abs(newTotal-oldTotal).toFixed(2)}</span></div>
            {extras.discountPct>0&&<div data-testid="adjust-discount-note" style={{color:C.inkLight,marginTop:4}}>Includes the customer's {formatPct(extras.discountPct)}% discount, applied to the new subtotal.</div>}
          </div>
        </div>
        {diff.length>0&&(
          <div style={{margin:"10px 0",fontSize:12,color:C.inkMid}}>
            <div style={{fontWeight:600,marginBottom:4}}>What you are asking to change</div>
            {diff.map(r=>(
              <div key={r.sku} style={{display:"flex",justifyContent:"space-between",padding:"2px 0"}}>
                <span><span style={{color:kindColor[r.kind],fontWeight:600,textTransform:"capitalize"}}>{r.kind}</span> · {r.sku} {r.name}</span>
                <span>{r.oldQty} → {r.newQty}</span>
              </div>
            ))}
          </div>
        )}
        <div style={S.fg}><label style={S.label}>Reason for adjustment *</label>
          <textarea style={{...S.input,minHeight:70,resize:"vertical"}} value={reason} disabled={busy} onChange={e=>setReason(e.target.value)} placeholder="e.g. Customer changed the order from 5 to 3 cases, wrong SKU entered…"/>
        </div>
        {shownError&&(<div style={{padding:"8px 12px",background:C.amberLight,color:"#92400E",fontSize:12,borderRadius:6,marginBottom:10,border:"1px solid #FCD34D"}}>{shownError}</div>)}
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button style={S.btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={{...S.btnPrimary,opacity:busy?0.6:1}} onClick={submit} disabled={busy}>{busy?"Submitting…":"Submit for admin approval"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── INVOICE BUILDER ─────────────────────────────────────────────────────────
export function RepInvoices({inventory, setInventory, productList, invoices, setInvoices, repName, repEmail, repId, prefillCustomerId, clearPrefill, customers, adminAssignedCompanies, adminAssignedUserIds, externalViewInvoice, clearExternalView, onSyncPending, discountReadTimeoutMs}) {
  const [creating, setCreating] = useState(!!prefillCustomerId);
  const [form, setForm] = useState({customerId:prefillCustomerId?String(prefillCustomerId):"", items:[{sku:"",qty:1}], notes:"", paymentTerms:"Net 30", status:"pending"});
  const [sentModal, setSentModal] = useState(null);
  const [viewInvoiceId, setViewInvoiceId] = useState(null);
  const [invoiceSearch, setInvoiceSearch] = useState("");

  // Subscribe to SHARED_INVOICES so this page mirrors the route planner's activity timeline.
  // Without this, customer-portal and admin-created invoices were invisible here even though
  // they show up for the same customer on the route planner.
  const [sharedList, setSharedList] = useState(()=>SHARED_INVOICES.getAll());
  useEffect(()=>SHARED_INVOICES.subscribe(list=>setSharedList(list)),[]);

  // ── Invoice void requests (Supabase invoice_void_requests, migration 0022) ──
  // Rep-side: load + realtime-subscribe to THIS rep's void requests. Used to
  // (1) render a "Void pending review" pill on invoices the rep already
  // requested to void, (2) gate the "Request void" button so the rep can't
  // file the same request twice, (3) let the rep cancel their own pending
  // request before admin acts. RLS scopes the fetch to the rep's own rows.
  const [voidRequests, setVoidRequests] = useState([]);
  const [voidModal, setVoidModal] = useState(null);     // { invoice, reason } when open
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState("");

  const refreshVoidRequests = useCallback(async () => {
    if (!repId) return;
    try {
      const rows = await listInvoiceVoidRequests({ repId, limit: 200 });
      setVoidRequests(rows || []);
    } catch (e) {
      console.warn("[RepInvoices] listInvoiceVoidRequests failed:", e?.message || e);
    }
  }, [repId]);

  useEffect(() => { refreshVoidRequests(); }, [refreshVoidRequests]);
  useEffect(() => {
    const unsub = subscribeInvoiceVoidRequests(({ row }) => {
      if (row && row.rep_id && row.rep_id !== repId) return;
      refreshVoidRequests();
    });
    return unsub;
  }, [repId, refreshVoidRequests]);

  // Map invoice_id → the most recent void request for that invoice (any
  // status). Used to gate the action buttons + render the pill.
  const voidRequestByInvoiceId = useMemo(() => {
    const map = {};
    (voidRequests || []).forEach(r => {
      if (!r || !r.invoice_id) return;
      const existing = map[r.invoice_id];
      if (!existing) { map[r.invoice_id] = r; return; }
      const newTs = new Date(r.created_at || 0).getTime();
      const oldTs = new Date(existing.created_at || 0).getTime();
      if (newTs > oldTs) map[r.invoice_id] = r;
    });
    return map;
  }, [voidRequests]);

  // ── Invoice ADJUSTMENT requests (Supabase invoice_adjustment_requests, 0028) ─
  // Same load / realtime pattern as the void requests above. RLS scopes the
  // fetch to this rep's own rows. The rep never edits an invoice locally: a
  // request only reaches the invoice after admin approval, via the normal
  // DB-to-cache sync.
  const [adjRequests, setAdjRequests] = useState([]);
  const [adjModal, setAdjModal] = useState(null);      // invoice being adjusted
  const [adjBusy, setAdjBusy] = useState(false);
  const [adjError, setAdjError] = useState("");
  const [requestNotice, setRequestNotice] = useState("");
  // ONE ref for adjust + void submits: only one request may be in flight per rep.
  const requestBusyRef = useRef(false);

  const refreshAdjRequests = useCallback(async () => {
    if (!repId) return;
    try {
      const rows = await listAdjustmentRequests({ repId, limit: 200 });
      setAdjRequests(rows || []);
    } catch (e) {
      console.warn("[RepInvoices] listAdjustmentRequests failed:", e?.message || e);
    }
  }, [repId]);
  useEffect(() => { refreshAdjRequests(); }, [refreshAdjRequests]);
  useEffect(() => {
    const unsub = subscribeAdjustmentRequests(({ row }) => {
      if (row && row.rep_id && row.rep_id !== repId) return;
      refreshAdjRequests();
    });
    return unsub;
  }, [repId, refreshAdjRequests]);

  const adjRequestByInvoiceId = useMemo(() => latestByInvoice(adjRequests), [adjRequests]);

  // ONE eligibility function for buttons + tooltips (invoiceAdjustments.js).
  const eligibilityFor = (inv) => {
    const pendAdj = (adjRequests || []).find(r => r && r.invoice_id === inv.id && r.status === 'pending');
    const pendVoid = (voidRequests || []).find(r => r && r.invoice_id === inv.id && r.status === 'pending');
    return invoiceRequestEligibility(inv, {
      repId, repEmail, repName, namesMatch: namesMatchFuzzy,
      inLocalList: (invoices || []).some(i => i.id === inv.id),
      pending: pendAdj ? { kind: 'adjust' } : pendVoid ? { kind: 'void' } : null,
    });
  };
  const canRequestVoid = (inv) => !!inv && eligibilityFor(inv).canVoid;

  // Rep-visible pill for the invoice (list row + invoice detail).
  const pillFor = (inv) => requestPill(adjRequestByInvoiceId[inv.id] || null, voidRequestByInvoiceId[inv.id] || null);
  const pillTone = { amber:{color:"#92400E",bg:"#FEF3C7"}, green:{color:C.green,bg:C.greenLight}, red:{color:C.red,bg:C.redLight}, gray:{color:C.inkLight,bg:C.surface} };

  const openAdjustModal = (inv) => { setAdjError(""); setRequestNotice(""); setAdjModal(inv); };
  const closeAdjustModal = () => { if (adjBusy) return; setAdjModal(null); setAdjError(""); };
  const submitAdjustRequest = async ({ items, terms, reason }) => {
    // Synchronous guard: state alone would let a same-tick double click file two.
    if (!adjModal || adjBusy || requestBusyRef.current) return;
    requestBusyRef.current = true;
    const inv = adjModal;
    setAdjBusy(true);
    setAdjError("");
    let row = null;
    try {
      row = await createAdjustmentRequest({
        repId, repName: repName || "", repEmail: repEmail || "",
        invoice: inv, proposedItems: items, proposedTerms: terms, reason,
      });
    } catch (e) {
      // Supabase-first: nothing was written, nothing changes locally.
      console.warn("[RepInvoices] createAdjustmentRequest failed:", e?.message || e);
      setAdjError(e?.message || "Could not submit the request. Nothing was changed. Please try again.");
      setAdjBusy(false);
      requestBusyRef.current = false;
      return;
    }
    requestBusyRef.current = false;
    setAdjBusy(false);
    setAdjModal(null);
    setRequestNotice(`Adjustment request for ${inv.id} sent to admin. The invoice stays as is until admin approves.`);
    refreshAdjRequests();
    // Fire-and-forget admin email AFTER the insert succeeded. A failed email
    // never fails or undoes the request.
    try {
      const p = row?.proposed_changes || {};
      emailInvoiceAdjustRequest({
        repName: repName || "", repEmail: repEmail || "", customer: inv.customer || "", invoiceId: inv.id, reason,
        oldTotal: Number(inv.total) || 0, newTotal: Number(p.total) || 0,
        lines: diffItems(inv.items, items).filter(r => r.kind !== "unchanged").map(r => ({ sku: r.sku, name: r.name, oldQty: r.oldQty, newQty: r.newQty })),
      }).then(res => { if (res && res.success === false) console.warn("[RepInvoices] adjustment request email failed (request is saved):", res.error); })
        .catch(e => console.warn("[RepInvoices] adjustment request email threw (request is saved):", e?.message || e));
    } catch (e) { console.warn("[RepInvoices] adjustment request email setup failed (request is saved):", e?.message || e); }
  };

  const cancelMyAdjustRequest = async (requestId) => {
    try {
      await cancelAdjustmentRequest(requestId);
      await refreshAdjRequests();
    } catch (e) {
      console.warn("[RepInvoices] cancelAdjustmentRequest failed for id", requestId, ":", e?.message || e);
      setRequestNotice(/admin is processing/i.test(String(e?.message || "")) ? "The admin is processing this request: it can no longer be cancelled." : "Could not cancel the request. Please try again.");
      try { await refreshAdjRequests(); } catch (e2) { console.warn("[RepInvoices] refresh after failed cancel failed:", e2?.message || e2); }
    }
  };

  const openVoidModal = (inv) => {
    setVoidError("");
    setVoidModal({ invoice: inv, reason: "" });
  };
  const closeVoidModal = () => {
    if (voidBusy) return;
    setVoidModal(null);
    setVoidError("");
  };

  const submitVoidRequest = async () => {
    if (!voidModal || voidBusy || requestBusyRef.current) return;
    const inv = voidModal.invoice;
    const reason = (voidModal.reason || "").trim();
    if (!reason) { setVoidError("Please enter a reason so admin knows why."); return; }
    requestBusyRef.current = true; // synchronous one-pending guard (state alone allows a same-tick double submit)
    setVoidBusy(true);
    try {
      const createdVoid = await createInvoiceVoidRequest({
        repId,
        repName: repName || "",
        invoiceId: inv.id,
        reason,
        invoiceSnapshot: {
          id:           inv.id,
          customer:     inv.customer || "",
          total:        Number(inv.total) || 0,
          items:        Array.isArray(inv.items) ? inv.items : [],
          paymentTerms: inv.paymentTerms || "",
          date:         inv.date || "",
          status:       inv.status || "",
        },
      });
      await refreshVoidRequests();
      setVoidModal(null);
      setVoidError("");
      setRequestNotice(`Void request for ${inv.id} sent to admin. The invoice stays as is until admin approves.`);
      // Fire-and-forget admin email AFTER the insert succeeded; never fails the request.
      try {
        emailInvoiceVoidRequest({
          repName: repName || "", repEmail: repEmail || "", customer: inv.customer || "", invoiceId: inv.id,
          reason, total: Number(inv.total) || 0, invoiceStatus: inv.status || "",
        }).then(res => { if (res && res.success === false) console.warn("[RepInvoices] void request email failed (request is saved, id " + (createdVoid && createdVoid.id) + "):", res.error); })
          .catch(e => console.warn("[RepInvoices] void request email threw (request is saved):", e?.message || e));
      } catch (e) { console.warn("[RepInvoices] void request email setup failed (request is saved):", e?.message || e); }
    } catch (e) {
      console.warn("[RepInvoices] createInvoiceVoidRequest failed:", e?.message || e);
      setVoidError(e?.message || "Failed to submit void request. Please try again.");
    } finally {
      requestBusyRef.current = false;
      setVoidBusy(false);
    }
  };

  const cancelMyVoidRequest = async (requestId) => {
    try {
      await cancelInvoiceVoidRequest(requestId);
      await refreshVoidRequests();
    } catch (e) {
      console.warn("[RepInvoices] cancelInvoiceVoidRequest failed for id", requestId, ":", e?.message || e);
    }
  };

  // Merge: local invoices this rep created + shared invoices for the rep's customers
  // (or explicitly tagged with this rep). Dedupe by id with local winning so rep-side
  // edits aren't clobbered. An invoice belongs to this rep if ANY of these match:
  //   • inv.userId is in the admin-assigned dealer id set (strongest — survives name drift)
  //   • inv.customer is on the admin-assigned dealer list (by company name)
  //   • inv.customer is on the rep's own route-planner list
  //   • inv.rep matches the rep's name (case-insensitive, trimmed)
  // Matches the canonical matcher in the parent RepPortal so this page stays in lock-step
  // with Dashboard / Commission / Sales.
  const allInvoices = useMemo(()=>{
    const norm = s => String(s || "").trim().toLowerCase();
    const assignedCompanies = adminAssignedCompanies || new Set();
    const assignedUserIds = adminAssignedUserIds || new Set();
    const routeCompanies = new Set((customers||[]).map(c=>norm(c.company)).filter(Boolean));
    const localById = new Set(invoices.map(i=>i.id));
    const relevantShared = sharedList.filter(inv => {
      if(localById.has(inv.id)) return false;
      const invUserId = inv.userId || inv.user_id || "";
      if(invUserId && assignedUserIds.has(invUserId)) return true;
      const invCust = norm(inv.customer);
      if(invCust && assignedCompanies.has(invCust)) return true;
      if(invCust && routeCompanies.has(invCust)) return true;
      // Fuzzy rep-name match (e.g. "Marcus T." on the invoice vs "Marcus
      // Thompson" on the rep profile). Was strict equality — seeded demo
      // data + admin-typed short names never matched, so dealer-placed
      // orders dropped out of the rep's view entirely.
      if(repName && namesMatchFuzzy(inv.rep, repName)) return true;
      return false;
    });
    return [...invoices, ...relevantShared];
  },[invoices, sharedList, customers, repName, adminAssignedCompanies, adminAssignedUserIds]);

  // Resolve viewInvoice from id so it stays fresh after status toggle.
  // Look in the merged list so clicking "View invoice" from the route planner finds
  // customer-portal orders too.
  const viewInvoice = viewInvoiceId ? allInvoices.find(i=>i.id===viewInvoiceId) : null;
  const setViewInvoice = (inv) => setViewInvoiceId(inv ? inv.id : null);

  const toggleInvoiceStatus = (id) => {
    // Rep-created invoices live in BOTH the local `invoices` state and
    // SHARED_INVOICES. Previously this early-returned after toggling local,
    // leaving SHARED stale — the customer kept seeing "Outstanding" / "Pay
    // now" for an invoice the rep had just marked paid. Now we update both
    // stores so every portal reads the same status.
    const local = invoices.find(x=>x.id===id);
    const shared = sharedList.find(x=>x.id===id);
    // An invoice the office created for a dealer (admin Create invoice) is managed by admin and the dealer, like a dealer order:
    // a rep never flips its paid status.
    if((local && local.source === "admin_invoice") || (shared && shared.source === "admin_invoice")) {
      console.warn("Rep portal: toggleInvoiceStatus ignored (office-created invoice) for invoice", id);
      window.alert("This invoice was created by the office. Its payment is recorded by admin.");
      return;
    }
    // A cancelled invoice is terminal: never flip it back to paid / pending.
    const decision = decideToggle(local, shared, MY_REMITTANCES.forInvoice(id));
    if(decision.action !== "toggle") {
      console.warn("Rep portal: toggleInvoiceStatus ignored (" + decision.reason + ") for invoice", id);
      if(decision.message) window.alert(decision.message);
      return;
    }
    const nextStatus = decision.nextStatus;
    if(local) {
      setInvoices(prev=>prev.map(x=>x.id===id?{...x,status:nextStatus}:x));
    }
    // Also propagate to SHARED_INVOICES so the customer portal (and admin)
    // see the change. SHARED_INVOICES is the single source of truth for
    // any non-local consumer of this invoice's status.
    if(shared || local) {
      if(nextStatus === "paid") SHARED_INVOICES.markPaid(id, "manual", {});
      else SHARED_INVOICES.markUnpaid(id, "Un-marked by rep");
    }
  };

  // If prefillCustomerId changes (e.g. navigated from route planner), open the builder
  useEffect(()=>{
    if(prefillCustomerId) {
      setForm(f=>({...f, customerId:String(prefillCustomerId)}));
      setCreating(true);
      if(clearPrefill) clearPrefill();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[prefillCustomerId]);

  // If navigated from route planner with an invoice to view, open it
  useEffect(()=>{
    if(externalViewInvoice) {
      // Try to find this invoice in local list by id, otherwise use the external object directly
      const found = invoices.find(i=>i.id===externalViewInvoice.id);
      if(found) setViewInvoiceId(found.id);
      else setViewInvoiceId(externalViewInvoice.id);
      if(clearExternalView) clearExternalView();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[externalViewInvoice]);

  const addLine = () => setForm(f=>({...f,items:[...f.items,{sku:"",qty:1}]}));
  const setLine = (i,k,v) => setForm(f=>({...f,items:f.items.map((r,ri)=>ri===i?{...r,[k]:v}:r)}));
  const removeLine = (i) => setForm(f=>({...f,items:f.items.filter((_,ri)=>ri!==i)}));

  const prods = productList||ALL_PRODUCTS_AND_KITS;

  // PR-V2B: rep-portal variation support. Build a flat list with variations
  // expanded as first-class siblings, so:
  //   1. SearchableProductSelect can offer each variation as an independent
  //      option (e.g. "Wiper blade — 18\" (WIP18)").
  //   2. SKU lookups in createInvoice / calcTotal / line render resolve
  //      variation SKUs without needing a special-case branch.
  // Parent products that HAVE variations are excluded from prodsForPicker
  // so the rep can't accidentally invoice the parent SKU (which would have
  // no per-size info on the invoice). Products without variations stay as
  // a single picker entry — same UX as today.
  // The flattening itself lives in inventoryMath.flattenCatalogForPicker so the
  // rep "request stock" picker uses the very same list (ledger SKUs == invoice SKUs).
  const prodsForPicker = useMemo(() => flattenCatalogForPicker(prods), [prods]);

  // Products that have variations — used to populate the "Add multiple
  // sizes" modal's parent dropdown. The button itself is hidden when
  // there are zero such products so the UI stays clean for catalogs
  // without variation-typed entries.
  const productsWithVariations = useMemo(
    () => (prods || []).filter(p => Array.isArray(p.variations) && p.variations.length > 0),
    [prods]
  );

  const findProdBySku = (sku) => prodsForPicker.find(p => p.sku === sku) || null;
  // Stable catalog lookup for the adjustment modal (same prices the create-
  // invoice line editor uses).
  const lookupProduct = useCallback((sku) => prodsForPicker.find(p => p.sku === sku) || null, [prodsForPicker]);

  // PR-V2B: variation matrix modal — lets the rep enter case counts for
  // multiple sizes of one variation-having product in a single dialog,
  // then commits N line items to form.items. Mirrors the customer-side
  // matrix UX from PR-V2A so the experience is the same on both portals.
  const [variationModal, setVariationModal] = useState(null);
  const openVariationModal = () => setVariationModal({ parentSku: "", qtys: {} });
  const closeVariationModal = () => setVariationModal(null);
  const confirmVariationModal = () => {
    if (!variationModal || !variationModal.parentSku) return;
    const parent = (prods || []).find(p => p.sku === variationModal.parentSku);
    if (!parent || !Array.isArray(parent.variations)) return;
    const newLines = [];
    parent.variations.forEach(v => {
      const q = Number(variationModal.qtys[v.sku]) || 0;
      if (q > 0) newLines.push({ sku: v.sku, qty: q });
    });
    if (newLines.length === 0) { closeVariationModal(); return; }
    setForm(f => ({
      ...f,
      // Strip empty placeholder rows (sku="") before appending so the
      // newly-added variation lines don't sit beneath stale blank rows.
      items: [...f.items.filter(it => (it.sku || "").trim()), ...newLines],
    }));
    closeVariationModal();
  };

  const calcTotal = () => form.items.reduce((s,r)=>{
    const prod = findProdBySku(r.sku);
    return s+(prod?prod.price*r.qty:0);
  },0);

  // ── Customer ACCOUNT DISCOUNT (per-customer rate the admin sets in Edit Account) ──────────────
  // The rate is ALWAYS customer_settings.discount of the customer's dealer ACCOUNT, read strictly
  // (no localStorage fallback, a failed read is never 0%). The rep cannot change it. It is looked up
  // by the dealer's uid only (resolveDiscountKey), never by company name: a customer without a linked
  // dealer account (rep-typed, prospect) gets 0% and a visible note. The rate is read when the
  // customer is selected (the live preview) and AGAIN right before the invoice is built; a failed
  // read blocks the invoice, and a rate that moved since the preview is shown to the rep first.
  //
  // The picked id is resolved through resolveCustomerEntry in BOTH the form (this block) and
  // createInvoice, so they always agree. When the route list holds two entries with the same id (the
  // admin-assigned dealer copy that carries the uid and a rep-side duplicate without it) the one that
  // carries the uid wins; two entries with the same company name but different ids only produce a
  // warning, never a name-based discount.
  const pickerCustomers = customers || REP_ACCOUNTS;
  const pickerOptions = useMemo(()=>customerPickerOptions(pickerCustomers),[pickerCustomers]);
  const custResolution = useMemo(()=>resolveCustomerEntry(pickerCustomers, form.customerId),[pickerCustomers, form.customerId]);
  const selectedCust = custResolution.entry;
  const hasSelectedCust = !!selectedCust;
  const discountKey = custResolution.key;
  const linkedDuplicate = custResolution.linkedDuplicate;
  const readTimeoutMs = discountReadTimeoutMs || DISCOUNT_READ_TIMEOUT_MS;
  // idle | no_account | loading | ok {pct,found,key} | error {message,key}
  const [discState, setDiscState] = useState({status:"idle"});
  const [discReload, setDiscReload] = useState(0);
  const [discNotice, setDiscNotice] = useState("");
  // The rep's acknowledgement "Create this invoice without a discount". It is tied to ONE dealer uid and
  // is cleared whenever the customer changes or the rate is read again (a Retry, a new selection).
  const [ackKey, setAckKey] = useState(null);
  const ackKeyRef = useRef(null);
  useEffect(()=>{ ackKeyRef.current = ackKey; });
  const discReqRef = useRef(0);
  useEffect(()=>{
    setDiscNotice("");
    setAckKey(null);
    if(!creating) return undefined;
    if(!hasSelectedCust){ setDiscState({status:"idle"}); return undefined; }
    if(!discountKey){ setDiscState({status:"no_account"}); return undefined; }
    let live = true;
    const token = ++discReqRef.current;
    setDiscState({status:"loading", key:discountKey});
    // readDiscountWithTimeout never rejects and never hangs (10 s), so a stuck request ends in the red box with Retry.
    readDiscountWithTimeout(getCustomerDiscountStrict, discountKey, readTimeoutMs).then(res=>{
      if(!live || token!==discReqRef.current) return;
      setDiscState(res && res.ok ? {status:"ok", pct:res.pct, found:!!res.found, key:discountKey} : {status:"error", message:(res && res.error) || "The customer's discount could not be read.", key:discountKey});
    });
    return ()=>{ live = false; };
  },[creating, hasSelectedCust, discountKey, discReload, readTimeoutMs]);
  // What the UI may rely on: a state only counts for the dealer uid it was read for, so for the one
  // render after the rep switches customers the old customer's rate is never shown or used.
  const discView = !hasSelectedCust ? {status:"idle"}
    : !discountKey ? {status:"no_account"}
    : (discState.key === discountKey ? discState : {status:"loading"});
  // What the form holds right now, so createInvoice can tell that the rep changed it while the
  // fresh discount read was in flight. A changed form also makes any "rate changed" note stale.
  const formSig = JSON.stringify([form.customerId, form.items.map(r=>[r.sku, r.qty]), form.notes, form.paymentTerms, form.status]);
  const formSigRef = useRef(formSig);
  useEffect(()=>{ formSigRef.current = formSig; });
  useEffect(()=>{ setDiscNotice(""); },[formSig]);
  const createLockRef = useRef(false);
  const createAttemptRef = useRef(0);   // bumped by every attempt and by Cancel-during-the-read
  const readPhaseRef = useRef(false);   // true only while the strict rate read of an attempt is in flight
  const [discReading, setDiscReading] = useState(false);
  const rawPreview = discView.status==="ok" && discView.pct>0
    ? computePricing(form.items.filter(r=>r.sku&&r.qty>0).map(r=>({price:findProdBySku(r.sku)?.price||0, qty:r.qty})), discView.pct)
    : null;
  const previewPricing = rawPreview && rawPreview.discount ? rawPreview : null;   // an empty basket has no discount block
  // A dealer account whose rate is not known yet (loading / failed read): the undiscounted figure must not be shown as "Total".
  const ratePending = hasSelectedCust && !!discountKey && (discView.status==="loading" || discView.status==="error");
  // No customer_settings row visible for a linked dealer account: UNKNOWN, not a confirmed 0%.
  const needsAck = discView.status==="ok" && !discView.found;
  const acknowledged = !!discountKey && ackKey === discountKey;
  const discountBlocksCreate = creating && hasSelectedCust && (ratePending || (needsAck && !acknowledged));

  const [creatingBusy, setCreatingBusy] = useState(false);
  // Cancel / close. While the discount is still being read nothing has been saved yet, so the attempt is
  // abandoned (createInvoice sees the new attempt number and stops) and the lock is freed at once.
  const cancelCreate = () => {
    if (readPhaseRef.current) {
      createAttemptRef.current += 1;
      readPhaseRef.current = false;
      createLockRef.current = false;
      setDiscReading(false);
      setCreatingBusy(false);
    }
    setCreating(false);
  };
  const createInvoice = async (sendMethod) => {
    if (creatingBusy || createLockRef.current) return;
    const resolved = resolveCustomerEntry(customers || REP_ACCOUNTS, form.customerId);
    const cust = resolved.entry;
    if(!cust) return;
    const sigAtClick = formSig;
    // PR-V2B: variation-aware lookup. findProdBySku resolves variation
    // SKUs by searching expanded prodsForPicker, so name + price come
    // back correctly for variation lines. parentSku + variationLabel
    // are carried onto the invoice item (mirrors the customer-side
    // placeOrder serialization from PR-V2A).
    //
    // `case` (units-per-case) is also baked onto each item so the
    // dealer's invoice modal can render the per-unit price under the
    // case price (matches the customer-portal placeOrder serialization
    // at App.js:1832). Variations inherit case from the parent product
    // already in prodsForPicker, so the same lookup suffices. Default
    // 1 keeps single-unit catalog items rendering cleanly.
    const items = form.items.filter(r=>r.sku&&r.qty>0).map(r=>{
      const prod = findProdBySku(r.sku);
      const item = {
        ...r,
        name: prod?.name || "",
        price: prod?.price || 0,
        case: Number(prod?.case) || 1,
      };
      if (prod?.parentSku) item.parentSku = prod.parentSku;
      if (prod?.variationLabel) item.variationLabel = prod.variationLabel;
      return item;
    });
    // Account discount. Money is committed here, so the rate is read FRESH from Supabase (strict:
    // never the localStorage cache, never 0 on a failed read) immediately before the invoice is
    // built. Nothing below this block runs, and nothing is saved or emailed, unless the rate is known
    // and is the one the rep is looking at. A customer without a linked dealer account (no uid) has
    // no account discount (0%) and no read is made; the form already told the rep so.
    const discountKeyNow = resolved.key;
    const attempt = ++createAttemptRef.current;
    createLockRef.current = true;
    setCreatingBusy(true);
    setDiscNotice("");
    try {
      let pricing = computePricing(items, 0);
      if (discountKeyNow) {
        readPhaseRef.current = true;
        setDiscReading(true);
        let fresh;
        try { fresh = await readDiscountWithTimeout(getCustomerDiscountStrict, discountKeyNow, readTimeoutMs); }
        finally { if (createAttemptRef.current === attempt) { readPhaseRef.current = false; setDiscReading(false); } }
        if (createAttemptRef.current !== attempt) {
          // The rep pressed Cancel (or closed the window) while the rate was being read: nothing was saved, nothing is emailed.
          console.warn("[RepPortal createInvoice] cancelled by the rep while the customer's discount was being read: nothing was saved");
          return;
        }
        if (!fresh || !fresh.ok) {
          // Fail closed: no invoice, no email. The red message with Retry is shown in the form.
          console.warn("[RepPortal createInvoice] invoice NOT created: the customer's discount rate could not be read:", fresh && fresh.error);
          setDiscState({status:"error", message:(fresh && fresh.error) || "The customer's discount could not be read.", key:discountKeyNow});
          setAckKey(null);
          return;
        }
        if (formSigRef.current !== sigAtClick) {
          // The rep edited the form while the rate was being read: what they would have saved is not what is on screen now.
          setDiscNotice("The form changed while the customer's discount was being checked. Review the total, then press the button again.");
          return;
        }
        const shown = discView.status==="ok" ? discView.pct : null;
        // No customer_settings row visible for a linked dealer account is UNKNOWN, not a confirmed 0%: the rep must
        // have ticked "Create this invoice without a discount" for THIS dealer before an invoice is created.
        const unknownNow = !fresh.found;
        const ackedNow = ackKeyRef.current === discountKeyNow;
        if (unknownNow) {
          console.warn("[RepPortal createInvoice] no customer_settings row was found for dealer account", discountKeyNow, "(customer", cust.company, "). That is UNKNOWN, not a confirmed 0% discount.", ackedNow ? "The rep acknowledged it: creating the invoice WITHOUT a discount." : "The rep has not acknowledged it: nothing is created.");
        }
        if (shown === null || !samePct(shown, fresh.pct) || (unknownNow && !ackedNow)) {
          // The rate the rep was shown (or never got to see) is not the current one, or the setting is unknown and not acknowledged:
          // show what the database says now and let them review. Never create silently.
          setDiscState({status:"ok", pct:fresh.pct, found:!!fresh.found, key:discountKeyNow});
          setAckKey(null);
          const was = shown>0 ? formatPct(shown)+"%" : "none";
          setDiscNotice(unknownNow
            ? `No discount setting was found for this dealer account${shown>0 ? " (it showed "+was+" a moment ago)" : ""}. Tick "Create this invoice without a discount" to continue, then press the button again.`
            : shown === null
              ? `The customer's discount is ${fresh.pct>0 ? formatPct(fresh.pct)+"%" : "none"}. Review the total, then press the button again.`
              : `The customer's discount changed from ${was} to ${fresh.pct>0 ? formatPct(fresh.pct)+"%" : "none"}. Review the total, then press the button again.`);
          return;
        }
        pricing = computePricing(items, fresh.pct);
        if (pricing.discount && !(pricing.total > 0)) {
          // A rate just under 100% on a small basket still rounds to a $0.00 invoice; several readers treat a stored total of 0 as
          // "no total" and would show or charge the gross amount. Fail closed: nothing is saved or emailed.
          console.warn("[RepPortal createInvoice] invoice NOT created: the discount", fresh.pct, "% would make the invoice total", pricing.total, "(subtotal", pricing.subtotal, ") for dealer account", discountKeyNow);
          setDiscState({status:"error", message:"This customer's discount would make the invoice total $0.00. Ask your admin to check the discount set on this account.", key:discountKeyNow});
          setAckKey(null);
          return;
        }
      }
      // Collision-resistant id — THIS is the root cause of "rep's confirmed
      // sale doesn't show up for admin". The id used to be `INV-F` + the last
      // 3 digits of Date.now() — only 1,000 possible values, cycling every
      // 1 second. savePortalOrder's idempotency guard (supabase.js, added to
      // stop network-retry duplicates) looks up an existing portal_orders row
      // by invoice_ref BEFORE inserting: if ANY row already has that
      // invoice_ref — even a genuinely different invoice that happened to
      // collide on this weak id — it returns the EXISTING row and skips the
      // insert. The new invoice's real data is then never written to
      // portal_orders at all. No error surfaces: savePortalOrder still
      // resolves to a (wrong, stale) row, so createInvoice's
      // `if (!supabaseRow) onSyncPending(...)` failure check never fires —
      // completely silent. Meanwhile SHARED_INVOICES.add() below always
      // mirrors locally regardless of the Supabase outcome, so the rep who
      // created it still sees it in their OWN browser session — but admin,
      // reading from a different device via getAllPortalOrders(), only ever
      // sees the row that "won". That split — confirmed on the rep's side,
      // invisible to admin — is exactly the reported symptom.
      //
      // Fix: 6 timestamp digits (kept for rough human-eyeball
      // chronological sorting, not for entropy) + 6 random base-36
      // characters. The randomness is what actually prevents collisions —
      // timestamp digits alone are NOT safe here even at higher precision,
      // because two different reps (or the same rep double-clicking) CAN
      // submit within the same millisecond in a live multi-user system, at
      // which point every caller shares the same timestamp bucket and only
      // the random suffix distinguishes them. Verified empirically before
      // shipping: 200,000 rapid-fire calls in a tight loop (deliberately
      // worse than any real concurrent-submit scenario, since real
      // invoices are created seconds to minutes apart by humans) produced
      // ZERO collisions with 6 random chars — a 2-char suffix, tested the
      // same way, collided in >25% of calls.
      const newInv = {
        id:`INV-F${String(Date.now()).slice(-6)}${Math.random().toString(36).slice(2,8).toUpperCase()}`,
        date:new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}),
        // A customer with an account discount: the invoice stores the NET total plus the breakdown that
        // explains it (subtotal at catalog prices, discount {pct, amount, label}), computed once by
        // computePricing, the same function that drew the preview. Without a discount the invoice is
        // exactly what it always was (total = calcTotal(), no subtotal / discount fields).
        customer:cust.company, items, total:pricing.discount ? pricing.total : calcTotal(),
        ...(pricing.discount ? {subtotal:pricing.subtotal, discount:pricing.discount} : {}),
        status:form.status||"pending", sent:sendMethod,
        notes:form.notes, paymentTerms:form.paymentTerms, rep:repName||"Sales Rep", repEmail:repEmail||"", repId:repId||"",
        customerId:String(cust.id||""),
        customerInfo: {address:cust.address||"", city:"", email:cust.email||"", phone:cust.phone||"", resaleLicense:cust.resaleLicense||"", ein:cust.ein||""},
      };

      // Rule Zero (CLAUDE.md "READ THIS SECOND"): Supabase-first write. We await
      // savePortalOrder BEFORE any localStorage mirror so the cross-device source
      // of truth is the authoritative one. If Supabase accepts, the mirror
      // proceeds normally. If it rejects (RLS, network, table missing — any
      // reason), we still write the localStorage mirror so the rep doesn't lose
      // the invoice they just created, AND we tell the parent to surface a
      // sync-pending banner. The rep's `syncPortalOrders` retry loop at the
      // parent level will push it to Supabase on the next mount / tick /
      // window focus, at which point the banner auto-clears.
      //
      // Preserves the dealer's Supabase auth id as user_id whenever the
      // route-planner / assigned-customer record has one — that's the strongest
      // match key for the dealer's cross-device Orders page and the rep's own
      // sync on other laptops. When there's no dealer UID (typical for
      // route-planner customers without an account), user_id stays null and
      // the match falls back to customer-company-name.
      // (The account-discount lookup above uses the dealer uid for the READ only; user_id on the row
      // is unchanged: it is still just what this expression gives.)
      const dealerUserId = cust.userId || cust.supabaseId || cust.user_id || null;
      let supabaseRow = null;
      let syncReason = "";
      let columnsMissing = false;   // migration 0034 not run: a discounted invoice cannot be stored
      try {
        supabaseRow = await savePortalOrder({
          orderId: newInv.id,
          invoiceId: newInv.id,
          userId: dealerUserId,
          customer: cust.company,
          customerEmail: cust.email || "",
          total: newInv.total,
          status: newInv.status,
          paymentTerms: newInv.paymentTerms,
          rep: repName || "Sales Rep",
          items,
          source: "rep_invoice",
          subtotal: newInv.subtotal,
          discount: newInv.discount,
          onError: (info) => { if (info && info.kind === "discount_columns_missing") { syncReason = info.message; columnsMissing = true; } },
        });
      } catch(e) {
        console.warn("[RepPortal createInvoice] portal_orders sync threw, will retry:", e?.message || e);
        supabaseRow = null;
      }
      // (the busy flag and the create lock are released by the finally below, however this function ends)

      // localStorage mirror — always runs, regardless of Supabase outcome.
      // Rule Zero permits localStorage as a cache, and this mirror prevents
      // the rep from losing in-flight work if Supabase is momentarily down.
      // Inventory is DERIVED from movements − invoices in the parent shell, so
      // creating an invoice automatically reduces `available` and increases
      // `sold` without any parallel inventory array mutation here.
      // An email that was deliberately NOT sent (database update 0034 missing) must not be recorded as emailed.
      const mirrorInv = (columnsMissing && sendMethod==="email") ? {...newInv, sent:"not-emailed"} : newInv;
      setInvoices(prev=>[mirrorInv,...prev]);
      addNotification("invoice_created",{invoiceId:newInv.id,customer:cust.company,total:newInv.total,rep:repName||"Sales Rep",method:sendMethod});
      SHARED_INVOICES.add({...mirrorInv, repCreated:true, notificationSent:true});

      // Surface sync status to the parent so the rep sees a non-blocking banner
      // when Supabase didn't accept the write. Auto-clears once the retry loop
      // confirms the invoice exists in portal_orders.
      if (!supabaseRow && typeof onSyncPending === "function") {
        onSyncPending(newInv.id, syncReason);
      }

      // Send email notification for emailed invoices. Full line-item detail
      // AND the company/legal fields the on-screen LegalInvoiceModal shows
      // (mpc company info, resale license) are threaded through here so the
      // emailed PDF is the same legit invoice as the one shown in the app —
      // not a stripped-down summary. A shop with no portal account can then
      // open and save the real thing immediately, no login required, no
      // delay while the rep is on-site waiting.
      //
      // NOT when the database update 0034 is missing: that invoice exists on this device only (admin, the dealer
      // and commission cannot see it), so the customer must not be sent an invoice the company has no record of.
      if(sendMethod==="email" && !columnsMissing){
        emailInvoice({
          customerName:cust.company, customerEmail:cust.email, invoiceId:newInv.id,
          total:newInv.total.toFixed(2), repName:repName||"Sales Rep",
          date:newInv.date, paymentTerms:newInv.paymentTerms, items:newInv.items, notes:newInv.notes,
          // Only a discounted invoice carries these, so every other emailed invoice sends exactly what it always did.
          ...(newInv.discount ? {subtotal:newInv.subtotal, discount:newInv.discount} : {}),
          customerAddress:newInv.customerInfo?.address||cust.address||"",
          customerPhone:newInv.customerInfo?.phone||cust.phone||"",
          resaleLicense:newInv.customerInfo?.resaleLicense||cust.resaleLicense||"",
          mpc:getMpcInfo(),
        });
      }
      setSentModal({inv:mirrorInv, method:sendMethod, email:cust.email, localOnly:columnsMissing, reason:syncReason});
      setCreating(false);
      setForm({customerId:"",items:[{sku:"",qty:1}],notes:"",paymentTerms:"Net 30",status:"pending"});
    } finally {
      // Always release the create lock and the busy flag (unless Cancel already handed this attempt over), whatever path ended the attempt.
      if (createAttemptRef.current === attempt) { createLockRef.current = false; setCreatingBusy(false); }
    }
  };

  // Adjust / void / cancel-request buttons, shared by the list row and the
  // invoice detail. Buttons only appear for invoices this rep owns; a disabled
  // "Request adjustment" carries the reason as its tooltip.
  const renderRequestButtons = (inv, inModal) => {
    const el = eligibilityFor(inv);
    if (!el.owned) return null;
    const base = inModal
      ? {padding:"5px 12px", fontSize:12, borderRadius:6, cursor:"pointer", fontWeight:500}
      : {padding:"3px 6px", fontSize:10, borderRadius:6, cursor:"pointer"};
    const pendAdj = (adjRequests || []).find(r => r && r.invoice_id === inv.id && r.status === 'pending');
    const pendVoid = (voidRequests || []).find(r => r && r.invoice_id === inv.id && r.status === 'pending');
    return (
      <>
        {!pendAdj && !pendVoid && (
          <button
            style={{...base, background:"#fff", color:el.canAdjust?C.blue:C.inkLight, border:`1px solid ${el.canAdjust?C.blue:C.border}`, opacity:el.canAdjust?1:0.6, cursor:el.canAdjust?"pointer":"not-allowed"}}
            title={el.canAdjust ? "Ask admin to correct the lines or payment terms of this invoice" : el.adjustReason}
            disabled={!el.canAdjust}
            onClick={()=>{ if(inModal) setViewInvoiceId(null); openAdjustModal(inv); }}
          >Request adjustment</button>
        )}
        {!pendAdj && !pendVoid && (
          <button
            style={{...base, background:"#fff", color:el.canVoid?C.red:C.inkLight, border:`1px solid ${el.canVoid?C.red:C.border}`, opacity:el.canVoid?1:0.6, cursor:el.canVoid?"pointer":"not-allowed"}}
            title={el.canVoid ? "Request that admin void this invoice" : el.voidReason}
            disabled={!el.canVoid}
            onClick={()=>{ setRequestNotice(""); if(inModal) setViewInvoiceId(null); openVoidModal(inv); }}
          >Request void</button>
        )}
        {pendAdj && pendAdj.initiated_by === 'admin' && (
          <button style={{...base, background:"#fff", color:C.inkLight, border:`1px solid ${C.border}`, opacity:0.6, cursor:"not-allowed"}} disabled title="An admin is adjusting this invoice right now">Admin adjustment in progress</button>
        )}
        {pendAdj && pendAdj.initiated_by !== 'admin' && applyInProgressByFields(pendAdj) && (
          <button style={{...base, background:"#fff", color:C.inkLight, border:`1px solid ${C.border}`, opacity:0.6, cursor:"not-allowed"}} disabled title="The admin has started processing this request, so it can no longer be cancelled">Admin is processing this request</button>
        )}
        {pendAdj && pendAdj.initiated_by !== 'admin' && !applyInProgressByFields(pendAdj) && (
          <button style={{...base, background:"#fff", color:C.inkMid, border:`1px solid ${C.border}`}} title="Cancel your adjustment request before admin acts" onClick={()=>cancelMyAdjustRequest(pendAdj.id)}>Cancel adjustment</button>
        )}
        {pendVoid && (
          <button style={{...base, background:"#fff", color:C.inkMid, border:`1px solid ${C.border}`}} title="Cancel your void request before admin acts" onClick={()=>cancelMyVoidRequest(pendVoid.id)}>Cancel void</button>
        )}
      </>
    );
  };
  const renderRequestPill = (inv) => {
    const pill = pillFor(inv);
    if (!pill) return null;
    const t = pillTone[pill.tone] || pillTone.gray;
    return (
      <div style={{display:"inline-block",marginTop:4,padding:"2px 6px",borderRadius:8,fontSize:9,fontWeight:600,color:t.color,background:t.bg}} title={pill.note ? (pill.key === "adj-admin" ? `Reason: ${pill.note}` : `Admin note: ${pill.note}`) : undefined}>
        {pill.label}{pill.note && (pill.key.endsWith("rejected") || pill.key === "adj-admin") ? ` — ${pill.note.length>40?pill.note.slice(0,40)+"…":pill.note}` : ""}
      </div>
    );
  };

  return (
    <div style={S.content}>
      {viewInvoice&&<LegalInvoiceModal inv={viewInvoice} onClose={()=>setViewInvoice(null)} onToggleStatus={toggleInvoiceStatus}
        requestSlot={eligibilityFor(viewInvoice).owned ? (<>
          {renderRequestPill(viewInvoice) || <span style={{fontSize:11,color:C.inkLight}}>Need to change this invoice?</span>}
          {renderRequestButtons(viewInvoice, true)}
        </>) : null}/>}
      {adjModal&&(
        <AdjustRequestModal
          key={adjModal.id}
          inv={adjModal}
          pickerProducts={prodsForPicker}
          lookup={lookupProduct}
          onClose={closeAdjustModal}
          onSubmit={submitAdjustRequest}
          busy={adjBusy}
          error={adjError}
        />
      )}
      {voidModal && (
        <Modal title={`Request void — ${voidModal.invoice.id}`} onClose={closeVoidModal}>
          <div style={{fontSize:13,color:C.inkLight,marginBottom:12,lineHeight:1.5}}>
            Admin will review and either approve (invoice will be cancelled, removed
            from your commission, and inventory restored) or reject (invoice stays
            as is). The dealer keeps seeing the invoice as &ldquo;{voidModal.invoice.status}&rdquo;
            until admin decides.
          </div>
          <div style={{background:C.surface,borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12,color:C.ink}}>
            <div><strong>Customer:</strong> {voidModal.invoice.customer}</div>
            <div><strong>Total:</strong> ${(Number(voidModal.invoice.total)||0).toFixed(2)}</div>
            <div><strong>Date:</strong> {voidModal.invoice.date}</div>
            <div><strong>Items:</strong> {Array.isArray(voidModal.invoice.items) ? voidModal.invoice.items.length : 0} line item(s)</div>
          </div>
          <div style={S.fg}>
            <label style={S.label}>Reason for void *</label>
            <textarea
              style={{...S.input, minHeight:80, resize:"vertical"}}
              value={voidModal.reason}
              disabled={voidBusy}
              onChange={e=>setVoidModal(v=>({...v, reason:e.target.value}))}
              placeholder="e.g. Wrong dealer selected, duplicate of INV-F123, dealer cancelled order, typo in qty…"
            />
          </div>
          {voidError && (
            <div style={{padding:"8px 12px", background:C.redLight, color:C.red, fontSize:12, borderRadius:6, marginBottom:10}}>
              {voidError}
            </div>
          )}
          <div style={{display:"flex",gap:10,marginTop:10,justifyContent:"flex-end"}}>
            <button style={S.btnSecondary} onClick={closeVoidModal} disabled={voidBusy}>Cancel</button>
            <button style={S.btnPrimary} onClick={submitVoidRequest} disabled={voidBusy}>
              {voidBusy ? "Submitting…" : "Submit void request"}
            </button>
          </div>
        </Modal>
      )}
      {sentModal&&(
        <Modal title={sentModal.localOnly?"Saved on this device only":"Invoice created!"} onClose={()=>setSentModal(null)}>
          {sentModal.localOnly?(
            <div role="alert" data-testid="sent-local-only" style={{background:C.redLight,border:"1px solid #FCA5A5",borderRadius:8,padding:"12px 14px",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:C.red}}>Invoice {sentModal.inv.id} — ${sentModal.inv.total.toFixed(2)}</div>
              {sentModal.inv.discount&&<div data-testid="sent-discount-line" style={{fontSize:12,color:C.red,marginTop:4}}>Includes the customer's {formatPct(sentModal.inv.discount.pct)}% discount (-${Number(sentModal.inv.discount.amount).toFixed(2)} off ${Number(sentModal.inv.subtotal).toFixed(2)})</div>}
              <div style={{fontSize:12,color:C.red,marginTop:6,fontWeight:600}}>Saved on this device only: {sentModal.reason||DISCOUNT_MIGRATION_MSG}.</div>
              <div style={{fontSize:12,color:C.red,marginTop:4}}>Admin, the dealer and commission cannot see this invoice yet, and it has NOT been emailed to the customer. Ask your admin to run the database update. The invoice then syncs by itself; you can still print it from View invoice. Once it has synced, send it to the customer yourself: it was not emailed.</div>
            </div>
          ):(
            <div style={{background:C.greenLight,border:`1px solid #86EFAC`,borderRadius:8,padding:"12px 14px",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:600,color:C.green}}>Invoice {sentModal.inv.id} — ${sentModal.inv.total.toFixed(2)}</div>
              {sentModal.inv.discount&&<div data-testid="sent-discount-line" style={{fontSize:12,color:C.green,marginTop:4}}>Includes the customer's {formatPct(sentModal.inv.discount.pct)}% discount (-${Number(sentModal.inv.discount.amount).toFixed(2)} off ${Number(sentModal.inv.subtotal).toFixed(2)})</div>}
              <div style={{fontSize:12,color:C.green,marginTop:4}}>{sentModal.method==="email"?`Emailed to ${sentModal.email}`:"Ready to print"}</div>
            </div>
          )}
          {!sentModal.localOnly&&<div style={{background:C.blueLight,borderRadius:8,padding:"10px 14px",fontSize:12,color:"#1D4ED8",marginBottom:16}}>Notification sent to admin and rep. Inventory updated automatically.</div>}
          <div style={{display:"flex",gap:10}}>
            <button style={{...S.btnSecondary,flex:1}} onClick={()=>setSentModal(null)}>Close</button>
            <button style={{...S.btnPrimary,flex:1}} onClick={()=>{setViewInvoice(sentModal.inv);setSentModal(null);}}>View invoice →</button>
          </div>
        </Modal>
      )}
      {/* PR-V2B: variation matrix modal — sibling of the Create-invoice
          modal. Rep picks a parent product, enters case counts per size,
          confirms; modal closes and the new lines appear inside the
          create-invoice form. State-driven: opening doesn't dismiss the
          create-invoice modal underneath, so the rep returns to a fully-
          populated form. */}
      {variationModal && (
        <Modal title="Add multiple sizes / models" onClose={closeVariationModal}>
          <div style={S.fg}>
            <label style={S.label}>Choose product</label>
            <select
              style={S.select}
              value={variationModal.parentSku}
              onChange={e => setVariationModal(v => ({ ...v, parentSku: e.target.value, qtys: {} }))}
            >
              <option value="">Pick a product with variations…</option>
              {productsWithVariations.map(p => (
                <option key={p.sku} value={p.sku}>{p.name}</option>
              ))}
            </select>
          </div>
          {variationModal.parentSku && (() => {
            const parent = (prods || []).find(p => p.sku === variationModal.parentSku);
            if (!parent || !Array.isArray(parent.variations) || parent.variations.length === 0) return null;
            const totalQty = parent.variations.reduce((s, v) => s + (Number(variationModal.qtys[v.sku]) || 0), 0);
            const totalAmount = parent.variations.reduce((s, v) => s + (Number(v.price) || 0) * (Number(variationModal.qtys[v.sku]) || 0), 0);
            return (
              <div>
                <div style={{ fontSize: 12, fontWeight: 500, color: C.inkMid, marginBottom: 8 }}>
                  Variations of {parent.name}
                </div>
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 70px 70px", gap: 6, padding: "7px 10px", background: C.surface, fontSize: 10, fontWeight: 600, color: C.inkLight, textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: `1px solid ${C.border}` }}>
                    <span>Size / Model</span>
                    <span style={{ textAlign: "right" }}>Stock</span>
                    <span style={{ textAlign: "right" }}>$/unit</span>
                    <span style={{ textAlign: "center" }}>Qty</span>
                  </div>
                  {parent.variations.map(v => {
                    const stock = Number(v.stock) || 0;
                    const price = Number(v.price) || 0;
                    const currentQty = Number(variationModal.qtys[v.sku]) || 0;
                    return (
                      <div key={v.sku} style={{ display: "grid", gridTemplateColumns: "1fr 60px 70px 70px", gap: 6, padding: "8px 10px", alignItems: "center", fontSize: 12, borderBottom: `1px solid ${C.border}` }}>
                        <div>
                          <div style={{ fontWeight: 600, color: C.ink }}>{v.label || v.sku}</div>
                          <div style={{ fontSize: 10, color: C.inkLight, fontFamily: "monospace" }}>{v.sku}</div>
                        </div>
                        <div style={{ textAlign: "right", color: stock > 0 ? C.teal : C.red, fontWeight: 500 }}>{stock}</div>
                        <div style={{ textAlign: "right", color: C.ink, fontWeight: 500 }}>${price.toFixed(2)}</div>
                        <input
                          type="number"
                          min="0"
                          value={currentQty || ""}
                          onChange={e => {
                            const n = parseInt(e.target.value) || 0;
                            setVariationModal(vm => ({ ...vm, qtys: { ...vm.qtys, [v.sku]: n < 0 ? 0 : n } }));
                          }}
                          placeholder="0"
                          style={{ ...S.input, width: "100%", fontSize: 12, padding: "6px 8px", textAlign: "center" }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, fontSize: 13 }}>
                  <span style={{ color: C.inkLight }}>{totalQty} unit{totalQty === 1 ? "" : "s"} across {parent.variations.filter(v => (Number(variationModal.qtys[v.sku]) || 0) > 0).length} size{parent.variations.filter(v => (Number(variationModal.qtys[v.sku]) || 0) > 0).length === 1 ? "" : "s"}</span>
                  <span style={{ fontWeight: 700, color: C.ink }}>Total: ${totalAmount.toFixed(2)}</span>
                </div>
              </div>
            );
          })()}
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ ...S.btnSecondary, flex: 1 }} onClick={closeVariationModal}>Cancel</button>
            <button
              style={{ ...S.btnPrimary, flex: 1, opacity: variationModal.parentSku && Object.values(variationModal.qtys).some(q => Number(q) > 0) ? 1 : 0.5, cursor: variationModal.parentSku && Object.values(variationModal.qtys).some(q => Number(q) > 0) ? "pointer" : "not-allowed" }}
              onClick={confirmVariationModal}
              disabled={!variationModal.parentSku || !Object.values(variationModal.qtys).some(q => Number(q) > 0)}
            >Add to invoice</button>
          </div>
        </Modal>
      )}

      {creating&&(
        <Modal title="Create invoice" onClose={cancelCreate}>
          <div style={S.fg}><label style={S.label}>Customer *</label>
            <select style={S.select} value={form.customerId} onChange={e=>setForm(f=>({...f,customerId:e.target.value}))}>
              <option value="">Select account</option>
              {pickerOptions.map((a,idx)=><option key={`${a.id}-${idx}`} value={a.id}>{a.company}</option>)}
            </select>
          </div>
          <div style={{fontSize:12,fontWeight:500,color:C.inkMid,marginBottom:8}}>Line items</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 60px 70px 80px 24px",gap:6,marginBottom:6,alignItems:"center"}}>
            <span style={{fontSize:10,color:C.inkLight,fontWeight:600,textTransform:"uppercase"}}>Product / Kit</span>
            <span style={{fontSize:10,color:C.inkLight,fontWeight:600,textTransform:"uppercase",textAlign:"center"}}>Qty</span>
            <span style={{fontSize:10,color:C.inkLight,fontWeight:600,textTransform:"uppercase",textAlign:"right"}}>Unit price</span>
            <span style={{fontSize:10,color:C.inkLight,fontWeight:600,textTransform:"uppercase",textAlign:"right"}}>Total</span>
            <span/>
          </div>
          {form.items.map((row,i)=>{
            // PR-V2B: variation-aware row lookup so variation SKUs (e.g.
            // WIP18) resolve to the parent's name + variation label and
            // the variation's own price, not "—" / $0.
            const rowProd = findProdBySku(row.sku);
            const unitPrice=rowProd?rowProd.price:0;
            const lineTotal=unitPrice*row.qty;
            return (
            <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 60px 70px 80px 24px",gap:6,marginBottom:7,alignItems:"center"}}>
              <SearchableProductSelect
                value={row.sku}
                onChange={sku=>setLine(i,"sku",sku)}
                products={prodsForPicker}
                placeholder="Search by SKU or name..."
              />
              <input type="number" min="1" value={row.qty} onChange={e=>setLine(i,"qty",parseInt(e.target.value)||1)} style={{...S.input,fontSize:12,padding:"7px 8px",textAlign:"center"}}/>
              <span style={{fontSize:12,color:C.inkLight,textAlign:"right"}}>{rowProd?`$${unitPrice.toFixed(2)}`:"\u2014"}</span>
              <span style={{fontSize:12,fontWeight:600,color:C.ink,textAlign:"right"}}>${lineTotal.toFixed(2)}</span>
              {form.items.length>1?<button onClick={()=>removeLine(i)} style={{background:"none",border:"none",color:C.inkLight,cursor:"pointer",fontSize:16}}>×</button>:<span/>}
            </div>
            );
          })}
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button style={S.btnGhost} onClick={addLine}>+ Add product</button>
            {/* PR-V2B matrix entry point — INTENTIONALLY GATED OFF.
                The per-line picker (`SearchableProductSelect` above) already
                exposes every variation as a first-class searchable option,
                so reps have a clear single path to add any size. The matrix
                modal added a second path that confused reps and — more
                critically — let them double-add the same variation
                (`confirmVariationModal` only strips empty placeholder rows;
                it does NOT dedupe against existing filled lines), producing
                duplicate line items that mis-state the dealer's order and
                downstream inventory.
                Code below the gate is preserved (variationModal state,
                openVariationModal, confirmVariationModal, the modal JSX) so
                a future engineer can re-enable the button by flipping
                `false` to `true` once the dedupe issue is fixed. To
                re-enable cleanly, also add a guard inside
                confirmVariationModal that merges with existing items by
                SKU instead of appending. */}
            {false && productsWithVariations.length > 0 && (
              <button style={S.btnGhost} onClick={openVariationModal}>+ Add multiple sizes / models</button>
            )}
          </div>
          <div style={{height:1,background:C.border,margin:"12px 0"}}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
            <div style={S.fg}><label style={S.label}>Payment terms</label>
              <select style={S.select} value={form.paymentTerms} onChange={e=>setForm(f=>({...f,paymentTerms:e.target.value}))}>
                {["Net 15","Net 30","Net 60","Due on receipt","Cash","Check","Credit card","ACH"].map(t=><option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={S.fg}><label style={S.label}>Payment status</label>
              <div style={{display:"flex",gap:6,marginTop:2}}>
                <button type="button" onClick={()=>setForm(f=>({...f,status:"pending"}))} style={{flex:1,padding:"8px 0",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid ${form.status==="pending"?"#F59E0B":C.border}`,background:form.status==="pending"?"#FFFBEB":C.white,color:form.status==="pending"?"#B45309":C.inkMid}}>Pending</button>
                <button type="button" onClick={()=>setForm(f=>({...f,status:"paid"}))} style={{flex:1,padding:"8px 0",borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",border:`1.5px solid ${form.status==="paid"?C.green:C.border}`,background:form.status==="paid"?C.greenLight:C.white,color:form.status==="paid"?C.green:C.inkMid}}>Paid</button>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:600,color:C.ink,alignItems:"flex-end",paddingBottom:14}}>
              <span>Total</span><span data-testid="create-total">{ratePending ? "—" : `$${(previewPricing ? previewPricing.total : calcTotal()).toFixed(2)}`}</span>
            </div>
          </div>
          {hasSelectedCust&&(
            <div data-testid="customer-discount" style={{marginBottom:12}}>
              {discView.status==="loading"&&<div style={{fontSize:12,color:C.inkLight}}>Customer discount: checking...</div>}
              {discView.status==="no_account"&&<div data-testid="discount-no-account" style={{fontSize:12,color:C.inkLight}}>No linked dealer account: no account discount applied</div>}
              {discView.status==="no_account"&&linkedDuplicate&&(
                <div role="status" data-testid="discount-duplicate-warning" style={{marginTop:6,background:C.amberLight,border:"1px solid #F59E0B",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#92400E"}}>
                  There are two entries called {selectedCust.company}; one is linked to the dealer account. This entry is not linked, so no discount is applied to it. If you meant the dealer account, pick the other entry in the Customer list.
                </div>
              )}
              {discView.status==="ok"&&discView.found&&(
                <div data-testid="discount-rate" style={{fontSize:12,fontWeight:discView.pct>0?600:400,color:discView.pct>0?C.green:C.inkLight}}>
                  Customer discount: {discView.pct>0?`${formatPct(discView.pct)}%`:"none"}
                </div>
              )}
              {needsAck&&(
                <div role="alert" data-testid="discount-unknown" style={{background:C.amberLight,border:"1px solid #F59E0B",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#92400E"}}>
                  <div style={{fontWeight:600}}>No discount setting found for this dealer account</div>
                  <div style={{marginTop:2}}>The discount for this customer could not be confirmed, so the invoice would be created at full price. If this customer should have a discount, ask your admin to check their account first.</div>
                  <label style={{display:"flex",alignItems:"center",gap:8,marginTop:8,fontWeight:600,cursor:"pointer"}}>
                    <input type="checkbox" data-testid="discount-ack" checked={acknowledged} onChange={e=>setAckKey(e.target.checked?discountKey:null)}/>
                    Create this invoice without a discount
                  </label>
                </div>
              )}
              {discView.status==="error"&&(
                <div role="alert" data-testid="discount-error" style={{background:C.redLight,border:"1px solid #FCA5A5",borderRadius:8,padding:"8px 12px",fontSize:12,color:C.red,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                  <span>Could not read this customer's discount rate ({discView.message}). The invoice cannot be created until it loads.</span>
                  <button type="button" style={{...S.btnSecondary,fontSize:11,padding:"5px 10px",whiteSpace:"nowrap"}} onClick={()=>setDiscReload(n=>n+1)}>Retry</button>
                </div>
              )}
              {discNotice&&<div role="status" data-testid="discount-notice" style={{marginTop:6,background:C.amberLight,border:"1px solid #F59E0B",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#92400E"}}>{discNotice}</div>}
              {previewPricing&&(
                <div data-testid="discount-preview" style={{marginTop:6,background:C.surface,borderRadius:8,padding:"8px 12px",fontSize:12,color:C.ink}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}><span>Subtotal</span><span>${previewPricing.subtotal.toFixed(2)}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",color:C.green}}><span>{previewPricing.discount.label} ({formatPct(previewPricing.discount.pct)}%)</span><span>-${previewPricing.discount.amount.toFixed(2)}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",fontWeight:700,fontSize:14}}><span>Total</span><span>${previewPricing.total.toFixed(2)}</span></div>
                </div>
              )}
            </div>
          )}
          <div style={S.fg}><label style={S.label}>Notes (optional)</label>
            <input style={S.input} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Delivery notes, references, etc."/>
          </div>
          <div style={{background:C.blueLight,borderRadius:8,padding:"9px 12px",fontSize:12,color:"#1D4ED8",marginBottom:14}}>Tax exempt / resale notice included automatically. Admin notified on creation.</div>
          <div style={{display:"flex",gap:8}}>
            <button style={{...S.btnSecondary,flex:1}} onClick={cancelCreate} disabled={creatingBusy&&!discReading}>Cancel</button>
            <button style={{...S.btnSecondary,flex:1,opacity:(creatingBusy||discountBlocksCreate)?0.6:1,cursor:(creatingBusy||discountBlocksCreate)?"not-allowed":"pointer"}} onClick={()=>createInvoice("print")} disabled={creatingBusy||discountBlocksCreate}>{creatingBusy?"Saving…":"Print"}</button>
            <button style={{...S.btnPrimary,flex:1,opacity:(creatingBusy||discountBlocksCreate)?0.6:1,cursor:(creatingBusy||discountBlocksCreate)?"not-allowed":"pointer"}} onClick={()=>createInvoice("email")} disabled={creatingBusy||discountBlocksCreate}>{creatingBusy?"Saving…":"Email to customer"}</button>
          </div>
        </Modal>
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <div style={{fontSize:18,fontWeight:600,color:C.ink}}>Field invoices</div>
          <div style={{fontSize:13,color:C.inkLight,marginTop:2}}>Professional legal invoices with resale tax exemption.</div>
        </div>
        <button style={S.btnPrimary} onClick={()=>setCreating(true)}>+ Create invoice</button>
      </div>
      {requestNotice&&(
        <div style={{background:C.blueLight,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:12,color:"#1D4ED8",marginBottom:12,display:"flex",justifyContent:"space-between",gap:10}}>
          <span>{requestNotice}</span>
          <button style={{background:"none",border:"none",cursor:"pointer",color:"#1D4ED8"}} onClick={()=>setRequestNotice("")}>×</button>
        </div>
      )}
      <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"center"}}>
        <div style={{position:"relative",flex:1}}>
          <input style={{...S.input,paddingLeft:32,fontSize:12}} value={invoiceSearch} onChange={e=>setInvoiceSearch(e.target.value)} placeholder="Search by customer, invoice #, date, status, items..."/>
          <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:14,color:C.inkLight}}>🔍</span>
        </div>
        {invoiceSearch&&<button style={{...S.btnSecondary,fontSize:11,padding:"7px 12px",whiteSpace:"nowrap"}} onClick={()=>setInvoiceSearch("")}>Clear</button>}
        <span style={{fontSize:12,color:C.inkLight,whiteSpace:"nowrap"}}>{(()=>{
          const q=invoiceSearch.toLowerCase().trim();
          // Filter across the MERGED list so the count matches what's actually
          // displayed below. Previously filtered over `invoices` (rep-created
          // only), hiding customer-placed orders from the rep entirely.
          const filtered=q?allInvoices.filter(inv=>{
            const itemStr=Array.isArray(inv.items)?inv.items.map(i=>`${i.sku||""} ${i.name||""}`).join(" "):(inv.items||"");
            return (inv.id||"").toLowerCase().includes(q)||(inv.customer||"").toLowerCase().includes(q)||(inv.date||"").toLowerCase().includes(q)||(inv.status||"").toLowerCase().includes(q)||itemStr.toLowerCase().includes(q)||(inv.notes||"").toLowerCase().includes(q)||(inv.paymentTerms||"").toLowerCase().includes(q);
          }):allInvoices;
          return `${filtered.length} of ${allInvoices.length}`;
        })()}</span>
      </div>
      {(()=>{
        const q=invoiceSearch.toLowerCase().trim();
        const filtered=q?allInvoices.filter(inv=>{
          const itemStr=Array.isArray(inv.items)?inv.items.map(i=>`${i.sku||""} ${i.name||""}`).join(" "):(inv.items||"");
          return (inv.id||"").toLowerCase().includes(q)||(inv.customer||"").toLowerCase().includes(q)||(inv.date||"").toLowerCase().includes(q)||(inv.status||"").toLowerCase().includes(q)||itemStr.toLowerCase().includes(q)||(inv.notes||"").toLowerCase().includes(q)||(inv.paymentTerms||"").toLowerCase().includes(q);
        }):allInvoices;
        // Dedicated Source pill so the rep can tell at a glance which
        // invoices they created in the field vs which ones a dealer placed
        // through the customer portal (the latter counts in commission but
        // the rep doesn't bill for them directly).
        const sourcePill = (inv) => {
          if(inv.source === "admin_invoice") return { label:"Office invoice", bg:"#FEF3C7", color:"#92400E" };
          if(inv.source === "portal_order") return { label:"Dealer order", bg:"#EEF2FF", color:"#4338CA" };
          if(inv.repCreated || inv.id?.startsWith("INV-F"))     return { label:"Field invoice", bg:"#E0F4EE", color:C.teal };
          return { label:"Invoice", bg:"#F1F5F9", color:"#475569" };
        };
        return (
      <div style={S.card}>
        <div style={{...S.tableHdr,gridTemplateColumns:"100px 90px 1fr 100px 80px 96px 132px"}}>
          <span>Invoice</span><span>Date</span><span>Customer</span><span>Source</span><span style={{textAlign:"right"}}>Total</span><span>Status</span><span></span>
        </div>
        {filtered.length===0?(
          <div style={{padding:"24px",textAlign:"center",color:C.inkLight,fontSize:13}}>No invoices match your search.</div>
        ):filtered.map(inv=>{
          const src = sourcePill(inv);
          // Dealer-placed orders can't be toggled by the rep — the dealer
          // owns the payment side. Rep-created invoices keep the existing
          // "click to toggle paid" affordance.
          const isDealerOrder = inv.source === "portal_order" || inv.source === "admin_invoice";
          const total = Number(inv.total||0);
          const isCancelled = inv.status === "cancelled";
          // Void-request awareness for this row: shows a pill below the
          // status when a request is pending / approved / rejected, and
          // swaps the action button accordingly.
          // Adjustment + void requests share ONE pill (requestPill) and one
          // set of action buttons (renderRequestButtons).
          return (
          <div key={inv.id} style={{...S.tableRow,gridTemplateColumns:"100px 90px 1fr 100px 80px 96px 132px"}}>
            <span style={{fontWeight:600,color:C.ink}}>{inv.id}</span>
            <span style={{color:C.inkLight}}>{inv.date}</span>
            <span style={{color:C.ink}}>{inv.customer}</span>
            <span><span style={{display:"inline-block",background:src.bg,color:src.color,fontSize:10,fontWeight:600,padding:"3px 8px",borderRadius:6}}>{src.label}</span></span>
            <span style={{fontWeight:600,color:C.ink,textAlign:"right"}}>${total.toFixed(2)}</span>
            <span>
              <span style={{cursor:isDealerOrder?"default":"pointer",display:"inline-block"}} title={isCancelled?"Cancelled by admin":isDealerOrder?"Managed by dealer / admin":"Click to toggle status"} onClick={()=>{ if(!isDealerOrder && !isCancelled) toggleInvoiceStatus(inv.id); }}><Badge type={inv.status}>{inv.status}</Badge></span>
              {renderRequestPill(inv)}
              <RepReceivedLine invoiceId={inv.id}/>
              {isCancelled && <div style={{fontSize:10,color:"#991B1B",marginTop:3,lineHeight:1.3}}>Cancelled by admin{inv.cancelReason?`: ${inv.cancelReason}`:""}{inv.cancelledDate?` (${inv.cancelledDate})`:""}</div>}
            </span>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <button style={S.btnSmall} onClick={()=>setViewInvoice(inv)}>View →</button>
              {renderRequestButtons(inv, false)}
            </div>
          </div>
          );
        })}
      </div>
        );
      })()}
    </div>
  );
}

// ─── SALES REPORT ─────────────────────────────────────────────────────────────
function RepSalesReport({inventory, invoices, repName}) {
  const [period, setPeriod] = useState("month");
  // Derive the top-line metrics from the rep's actual invoices so this report
  // matches the Dashboard card and the Commission page. Per-product breakdown
  // below is also derived from invoice items, with inventory providing the
  // display metadata (name, SKU, price).
  const invs = (invoices || []).filter(i => i && i.status !== "cancelled");
  // Keys are normSku (trim + upper-case), the same grouping inventoryMath uses for the
  // My inventory rows, so a spacing / case variant of a SKU lands on the same line and
  // never makes a second 0-unit full-revenue row.
  const productRevenue = {}; // normSku(sku) → revenue (unchanged: qty x price over every non-cancelled invoice)
  const revenueNames = {};   // normSku(sku) → product name, for products that have revenue but no inventory row
  const revenueSkus = {};    // normSku(sku) → first-seen spelling, for display
  invs.forEach(inv => {
    if (!Array.isArray(inv.items)) return;
    inv.items.forEach(it => {
      const sku = it.sku || it.name || "—";
      const key = normSku(sku);
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      productRevenue[key] = (productRevenue[key] || 0) + qty * price;
      if (!revenueSkus[key]) revenueSkus[key] = String(sku).trim();
      if (!revenueNames[key] && it.name) revenueNames[key] = it.name;
    });
  });
  // UNITS sold per product come from inventoryMath (the "sold" of each My inventory
  // row: the same all-time rule and units), so the Sales report and the Inventory page
  // can never show a different number for the same product. Revenue and commission
  // math are untouched. (The old raw item.qty sum counted CASES for dealer orders.)
  // A product with revenue but no inventory row (e.g. only sold on carrier-shipped dealer
  // orders, which never take stock from the rep) keeps its revenue line with 0 units.
  const inventoryWithLive = [
    ...inventory.map(it => ({ ...it, sold: Number(it.sold) || 0 })),
    ...Object.keys(productRevenue)
      .filter(key => !inventory.some(it => normSku(it.sku) === key || normSku(it.name) === key))
      .map(key => ({ sku: revenueSkus[key] || key, name: revenueNames[key] || revenueSkus[key] || key, sold: 0, price: 0 })),
  ];
  const totalUnits = inventoryWithLive.reduce((s, it) => s + it.sold, 0);
  const totalRev = invs.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const topProduct = [...inventoryWithLive].sort((a,b)=>b.sold-a.sold)[0];

  const periodLabels = {day:["Mon","Tue","Wed","Thu","Fri","Sat","Sun"], week:["Wk 1","Wk 2","Wk 3","Wk 4"], month:["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"], year:["2022","2023","2024","2025"]};
  const barData = {day:[4,7,3,9,12,8,2], week:[42,38,55,61], month:[120,95,140,88,110,130,115,98,145,160,122,180], year:[850,1200,1480,620]};

  const bars = barData[period];
  const maxBar = Math.max(...bars);

  return (
    <div style={S.content}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:18,fontWeight:600,color:C.ink}}>Sales report</div>
        <div style={{display:"flex",gap:4}}>
          {["day","week","month","year"].map(p=>(
            <button key={p} onClick={()=>setPeriod(p)} style={{padding:"6px 14px",borderRadius:7,border:`1px solid ${period===p?C.blue:C.border}`,background:period===p?C.blueLight:C.white,color:period===p?C.blue:C.inkMid,fontSize:12,cursor:"pointer",fontWeight:period===p?500:400}}>
              {p.charAt(0).toUpperCase()+p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
        <div style={S.metric}><div style={S.mLabel}>Units sold</div><div style={S.mValue}>{totalUnits}</div><div style={S.mSub}>All products</div></div>
        <div style={S.metric}><div style={S.mLabel}>Revenue (all time)</div><div style={S.mValue}>${totalRev.toFixed(0)}</div><div style={S.mSub}>At your sell price</div></div>
        <div style={S.metric}><div style={S.mLabel}>Top product</div><div style={{fontSize:14,fontWeight:600,color:C.ink,marginTop:4}}>{topProduct?.name}</div><div style={S.mSub}>{topProduct?.sold} units sold</div></div>
      </div>

      <div style={S.card}>
        <div style={S.cardHdr}><span style={S.cardTitle}>Units sold — {period === "day" ? "this week" : period === "week" ? "this month" : period === "month" ? "this year" : "all years"}</span></div>
        <div style={{padding:"20px 24px 16px"}}>
          <div style={{display:"flex",alignItems:"flex-end",gap:6,height:120}}>
            {bars.map((val,i)=>(
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{fontSize:10,color:C.inkLight}}>{val}</div>
                <div style={{width:"100%",background:C.teal,borderRadius:"3px 3px 0 0",height:`${(val/maxBar)*90}px`,transition:"height 0.3s"}}/>
                <div style={{fontSize:10,color:C.inkLight,whiteSpace:"nowrap"}}>{periodLabels[period][i]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={S.card}>
        <div style={S.cardHdr}><span style={S.cardTitle}>Per-product breakdown</span></div>
        <div style={{...S.tableHdr,gridTemplateColumns:"1fr 70px 80px 90px 70px"}}>
          <span>Product</span><span style={{textAlign:"right"}}>Units sold</span><span style={{textAlign:"right"}}>Revenue</span><span style={{textAlign:"right"}}>% of sales</span><span>Trend</span>
        </div>
        {[...inventoryWithLive].sort((a,b)=>b.sold-a.sold).map(item=>{
          const rev = productRevenue[normSku(item.sku)] ?? productRevenue[normSku(item.name)] ?? (item.sold * item.price);
          return (
          <div key={item.sku} style={{...S.tableRow,gridTemplateColumns:"1fr 70px 80px 90px 70px"}}>
            <div>
              <div style={{fontWeight:500,color:C.ink}}>{item.name}</div>
              <div style={{fontSize:11,color:C.inkLight}}>SKU {item.sku}</div>
            </div>
            <span style={{fontWeight:600,color:C.ink,textAlign:"right"}}>{item.sold}</span>
            <span style={{color:C.ink,textAlign:"right"}}>${rev.toFixed(2)}</span>
            <span style={{color:C.inkLight,textAlign:"right"}}>{totalUnits>0?Math.round(item.sold/totalUnits*100):0}%</span>
            <span style={{color:C.teal,fontSize:13}}>↑</span>
          </div>
        );})}
      </div>
    </div>
  );
}

// ─── TRAINING HUB ─────────────────────────────────────────────────────────────
// Maps a rep_certifications row (migration 0026) to the client shape the
// "My certificates" list and CertificateDoc already expect. track is kept
// hardcoded "tech" for label purposes only (CertificateDoc's title only
// distinguishes tech/advisor; a rep certification is neither, but "tech"
// was already the pre-existing display choice before this migration).
const repCertRowToClient = (row) => ({
  certNum: row.cert_num,
  type: "rep_certification",
  name: row.rep_name || "Sales Rep",
  shopName: "MotorPower Care",
  track: "tech",
  score: row.score ?? 100,
  date: row.issued_at ? new Date(row.issued_at).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}) : "",
  completedVideos: Array.isArray(row.completed_videos) ? row.completed_videos : [],
  signature: row.rep_name || "Sales Rep",
});

function TrainingHub({isAdmin=false, videos, setVideos, earnedCerts=[], setEarnedCerts, repName, repId, progress=[], setProgress}) {
  const [uploading, setUploading] = useState(false);
  const [newVideo, setNewVideo] = useState({title:"",category:"product",duration:"",required:false,desc:""});
  const [filter, setFilter] = useState("all");
  const [viewCert, setViewCert] = useState(null);
  // Preview player state — the Watch button opens a modal that embeds the
  // video (YouTube / Vimeo iframe, or <video> tag for direct URL / file
  // blob). Same shape as AdminTrainingTab's preview at App.js:4594. Prior
  // to this the Watch button had no onClick and did nothing — the bug was
  // masked because rep-side had no persisted videos to click on.
  const [previewId, setPreviewId] = useState(null);
  const getEmbedUrl = (url, type) => {
    if (!url) return "";
    if (type === "youtube") { const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&?/]+)/); return m ? `https://www.youtube.com/embed/${m[1]}` : url; }
    if (type === "vimeo")   { const m = url.match(/vimeo\.com\/(\d+)/); return m ? `https://player.vimeo.com/video/${m[1]}` : url; }
    return url;
  };
  // Card thumbnail: admin's explicit thumbnailUrl wins when set; otherwise
  // auto-derive YouTube's own thumbnail from the video URL (no API key
  // needed — img.youtube.com serves it for any public video) so a video
  // added without a manual thumbnail still shows a real image instead of
  // the generic ▶ glyph. Same regex as getEmbedUrl above, reused here so
  // the extracted video ID always matches what the embed player uses.
  const getThumbnail = (v) => {
    if (v?.thumbnailUrl) return v.thumbnailUrl;
    if (v?.videoUrl && (v.videoType||"youtube") === "youtube") {
      const m = v.videoUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&?/]+)/);
      if (m) return `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`;
    }
    return "";
  };

  const categories = ["all","product","sales","admin","marketing"];
  // Merge Supabase-persisted progress (migration 0026) onto the video list
  // for rendering. `videos` itself stays the untouched Supabase catalog
  // fetched by the parent (see its hydrate effect) — watched/passed state
  // is derived here, never mutated onto videos directly, so a realtime
  // video-list refetch (any admin edit to any video) can no longer wipe it.
  const videosWithProgress = useMemo(() => videos.map(v => {
    const p = progress.find(p => p.video_id === v.id);
    return { ...v, watched: !!p?.passed, quizScore: p?.quiz_score ?? null };
  }), [videos, progress]);
  const filtered = filter==="all" ? videosWithProgress : videosWithProgress.filter(v=>v.category===filter);
  const required = videosWithProgress.filter(v=>v.required);
  const completed = required.filter(v=>v.watched);

  const addVideo = () => {
    setVideos(v=>[...v,{id:Date.now(),...newVideo,watched:false,thumbnail:"🎬",uploadedBy:"Admin",date:"Apr 10, 2025"}]);
    setUploading(false);
    setNewVideo({title:"",category:"product",duration:"",required:false,desc:""});
  };

  // Quiz-in-progress state. A video with quiz questions attached (migration
  // 0025) opens QuizEngine instead of a plain "Mark watched" click. Videos
  // with no questions keep the simple mark-watched path (trivially passed —
  // there's nothing to test). Both paths persist to Supabase so progress
  // survives refresh and any other admin/rep/device activity.
  const [quizVideo, setQuizVideo] = useState(null);
  const [progressBusy, setProgressBusy] = useState(false);

  const markWatched = async (id) => {
    if (!repId || progressBusy) return;
    const v = videosWithProgress.find(x=>x.id===id);
    if (!v) return;
    if (Array.isArray(v.questions) && v.questions.length > 0) { setQuizVideo(v); return; }
    setProgressBusy(true);
    const saved = await upsertRepTrainingProgress({ repId, repName, videoId: id, watched: true, passed: true });
    setProgressBusy(false);
    if (saved && setProgress) setProgress(ps => [...ps.filter(p=>p.video_id!==id), saved]);
  };

  // QuizEngine (from CertPortal.js — same component the shop cert flow
  // uses) only calls onComplete on an actual pass + typed signature; the
  // fail path only offers Retake / Back, so `passed` here is always true
  // when this fires. Guarded anyway for safety.
  const handleQuizComplete = async ({video, score, passed}) => {
    if (!passed || !repId) { setQuizVideo(null); return; }
    setProgressBusy(true);
    const saved = await upsertRepTrainingProgress({ repId, repName, videoId: video.id, watched: true, passed: true, quizScore: score });
    setProgressBusy(false);
    if (saved && setProgress) setProgress(ps => [...ps.filter(p=>p.video_id!==video.id), saved]);
    setQuizVideo(null);
  };

  // Issue the certificate exactly once, the first time every currently-
  // required video is passed. Never re-derives or overwrites an existing
  // cert once earned — a new required video added later (or an unrelated
  // video edit) must not retroactively touch a rep's already-issued
  // certificate. insertRepCertification is itself insert-once (UNIQUE
  // (rep_id) in migration 0026), so this is safe even if two tabs race.
  useEffect(() => {
    if (!repId || isAdmin) return;
    if (earnedCerts.some(c=>c.type==="rep_certification")) return;
    if (required.length === 0 || completed.length < required.length) return;
    let cancelled = false;
    (async () => {
      // Same collision-resistant pattern as the invoice id fix above (see
      // that comment for the full reasoning + empirical test). Lower
      // stakes here since this fires once per rep ever, not repeatedly,
      // but insertRepCertification's UNIQUE(rep_id) guard only protects
      // against re-issuing THIS rep's cert twice — a cert_num collision
      // against a DIFFERENT rep would still fail the insert on the
      // separate UNIQUE(cert_num) constraint, and the 23505 fallback path
      // only checks by rep_id, so it would return null and silently skip
      // issuing this rep's certificate that round. Closing it for real
      // "once and forever" coverage, not because it's been observed yet.
      const certNum = `MPC-REP-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}${Math.random().toString(36).slice(2,8).toUpperCase()}`;
      const completedVideos = required.map(v => ({ title: v.title, score: v.quizScore ?? 100 }));
      const avgScore = completedVideos.length
        ? Math.round(completedVideos.reduce((s,v)=>s+(v.score||0),0) / completedVideos.length)
        : 100;
      const saved = await insertRepCertification({ repId, repName, certNum, score: avgScore, completedVideos });
      if (saved && !cancelled && setEarnedCerts) setEarnedCerts([repCertRowToClient(saved)]);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repId, isAdmin, required.length, completed.length]);

  return (
    <div style={S.content}>
      {viewCert && <CertificateDoc cert={viewCert} onClose={()=>setViewCert(null)}/>}
      {quizVideo && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.85)",zIndex:1100,overflowY:"auto"}}>
          <div style={{background:C.white,minHeight:"100%"}}>
            <div style={{background:C.navy,padding:"12px 24px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{color:C.white,fontSize:13,fontWeight:500}}>Quiz — {quizVideo.title}</span>
              <button onClick={()=>setQuizVideo(null)} disabled={progressBusy} style={{background:"rgba(255,255,255,0.1)",border:"none",color:C.white,fontSize:12,padding:"5px 12px",borderRadius:6,cursor:progressBusy?"default":"pointer"}}>Exit quiz</button>
            </div>
            <QuizEngine video={quizVideo} techName={repName||"Sales Rep"} onComplete={handleQuizComplete} onExit={()=>setQuizVideo(null)}/>
          </div>
        </div>
      )}
      {previewId && (() => {
        const pv = videos.find(v => v.id === previewId);
        if (!pv) return null;
        return (
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}} onClick={()=>setPreviewId(null)}>
            <div style={{background:C.white,borderRadius:16,padding:"24px 28px",width:720,maxWidth:"90vw"}} onClick={e=>e.stopPropagation()}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:16,fontWeight:600,color:C.ink}}>{pv.title}</div>
                <button style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.inkLight}} onClick={()=>setPreviewId(null)}>×</button>
              </div>
              {pv.videoUrl ? (
                <div style={{borderRadius:10,overflow:"hidden",background:"#000",position:"relative",paddingBottom:"56.25%"}}>
                  {(pv.videoType==="direct" || pv.videoType==="file")
                    ? <video src={pv.videoUrl} controls style={{position:"absolute",top:0,left:0,width:"100%",height:"100%"}}/>
                    : <iframe title="play" src={getEmbedUrl(pv.videoUrl,pv.videoType||"youtube")} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",border:"none"}} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen/>}
                </div>
              ) : <div style={{padding:40,textAlign:"center",background:C.surface,borderRadius:10,color:C.inkLight}}>No video URL provided</div>}
              {pv.desc && <div style={{marginTop:12,fontSize:13,color:C.inkLight}}>{pv.desc}</div>}
            </div>
          </div>
        );
      })()}
      {uploading && (
        <Modal title="Upload training video" onClose={()=>setUploading(false)}>
          <div style={{background:C.blueLight,borderRadius:8,padding:"10px 14px",fontSize:12,color:C.blue,marginBottom:16}}>
            In production, this will upload to a video hosting service. For now, enter the video details and link.
          </div>
          <div style={S.fg}><label style={S.label}>Title *</label><input style={S.input} value={newVideo.title} onChange={e=>setNewVideo(v=>({...v,title:e.target.value}))} placeholder="How to demo catalytic converter cleaner"/></div>
          <div style={S.twoCol}>
            <div style={S.fg}><label style={S.label}>Category</label>
              <select style={S.select} value={newVideo.category} onChange={e=>setNewVideo(v=>({...v,category:e.target.value}))}>
                {["product","sales","admin","marketing"].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={S.fg}><label style={S.label}>Duration</label><input style={S.input} value={newVideo.duration} onChange={e=>setNewVideo(v=>({...v,duration:e.target.value}))} placeholder="8 min"/></div>
          </div>
          <div style={S.fg}><label style={S.label}>Description</label><textarea style={{...S.input,height:72,resize:"vertical"}} value={newVideo.desc} onChange={e=>setNewVideo(v=>({...v,desc:e.target.value}))} placeholder="What reps will learn from this video..."/></div>
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.ink,marginBottom:16,cursor:"pointer"}}>
            <input type="checkbox" checked={newVideo.required} onChange={e=>setNewVideo(v=>({...v,required:e.target.checked}))} style={{width:14,height:14}}/>
            Mark as required for certification
          </label>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button style={S.btnSecondary} onClick={()=>setUploading(false)}>Cancel</button>
            <button style={S.btnPrimary} onClick={addVideo} disabled={!newVideo.title}>Upload video</button>
          </div>
        </Modal>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:18,fontWeight:600,color:C.ink}}>Training hub</div>
          {!isAdmin && <div style={{fontSize:13,color:C.inkLight,marginTop:2}}>Complete all required videos to earn your Certified Rep badge.</div>}
        </div>
        {isAdmin && <button style={S.btnPrimary} onClick={()=>setUploading(true)}>+ Upload video</button>}
      </div>

      {!isAdmin && (
        <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 18px",marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:13,fontWeight:500,color:C.ink}}>Certification progress</span>
            <span style={{fontSize:12,color:C.inkLight}}>{completed.length} of {required.length} required</span>
          </div>
          <div style={{height:8,background:C.border,borderRadius:4,overflow:"hidden"}}>
            <div style={{width:`${required.length>0?completed.length/required.length*100:0}%`,height:"100%",background:C.teal,borderRadius:4,transition:"width 0.3s"}}/>
          </div>
          {completed.length===required.length && required.length>0 && (
            <div style={{marginTop:10,fontSize:12,color:C.teal,fontWeight:500}}>All required videos complete — you are a Certified MPC Rep!</div>
          )}
        </div>
      )}

      <div style={{display:"flex",gap:6,marginBottom:16}}>
        {categories.map(c=>(
          <button key={c} onClick={()=>setFilter(c)} style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${filter===c?C.blue:C.border}`,background:filter===c?C.blueLight:C.white,color:filter===c?C.blue:C.inkMid,fontSize:12,cursor:"pointer"}}>
            {c.charAt(0).toUpperCase()+c.slice(1)}
          </button>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        {filtered.map(v=>{
          const thumb = getThumbnail(v);
          return (
          <div key={v.id} style={{background:C.white,border:`1px solid ${v.watched?"#86EFAC":C.border}`,borderRadius:12,overflow:"hidden"}}>
            {/* Thumbnail / play button. Previously this box had no onClick
                at all — clicking the ▶ glyph did nothing; only the separate
                "Watch" button below opened the preview. Now wired the same
                way, plus renders the real thumbnail image (custom or
                auto-derived YouTube thumb) when one is available. */}
            {/* 16:9 responsive box (position:relative + height:0 +
                paddingBottom:56.25%, same trick this file's own
                video-preview modal below already uses) instead of a fixed
                height:80 box. A fixed short height forced object-fit:cover
                to crop a real YouTube thumbnail down to a sliver — badly
                cutting off video title cards baked into the image. */}
            <div
              style={{position:"relative",paddingBottom:"56.25%",height:0,background:v.watched?C.greenLight:C.surface,borderBottom:`1px solid ${C.border}`,cursor:v.videoUrl?"pointer":"default",overflow:"hidden"}}
              onClick={()=>v.videoUrl && setPreviewId(v.id)}
              title={v.videoUrl ? "Watch this video" : ""}
            >
              {thumb && <img src={thumb} alt="" style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover"}} onError={e=>{e.target.style.display="none"}}/>}
              <span style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:32,color:thumb?"#fff":"inherit",textShadow:thumb?"0 1px 4px rgba(0,0,0,0.6)":"none"}}>{v.watched ? "✓" : "▶"}</span>
            </div>
            <div style={{padding:"12px 14px"}}>
              <div style={{display:"flex",gap:6,marginBottom:6}}>
                <Badge type={v.category}>{v.category}</Badge>
                {v.required && <Badge type="required">Required</Badge>}
                {v.watched && <Badge type="active">Watched</Badge>}
              </div>
              <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:4}}>{v.title}</div>
              <div style={{fontSize:12,color:C.inkLight,marginBottom:8,lineHeight:1.5}}>{v.desc}</div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,color:C.inkLight}}>{v.duration} · {v.date}</span>
                <div style={{display:"flex",gap:6}}>
                  {!v.watched && !isAdmin && <button style={S.btnSmall} disabled={progressBusy} onClick={()=>markWatched(v.id)}>{(Array.isArray(v.questions)&&v.questions.length>0) ? `Take quiz (${v.questions.length})` : "Mark watched"}</button>}
                  {isAdmin && <button style={S.btnDanger} onClick={()=>setVideos(vs=>vs.filter(x=>x.id!==v.id))}>Remove</button>}
                  <button
                    style={{...S.btnPrimary, opacity: v.videoUrl ? 1 : 0.5, cursor: v.videoUrl ? "pointer" : "not-allowed"}}
                    disabled={!v.videoUrl}
                    title={v.videoUrl ? "Watch this video" : "This video has no URL — admin needs to add one"}
                    onClick={()=>v.videoUrl && setPreviewId(v.id)}
                  >
                    Watch
                  </button>
                </div>
              </div>
            </div>
          </div>
          );})}
      </div>

      {/* ── MY CERTIFICATES ─────────────────────────────────────────────── */}
      {!isAdmin && earnedCerts.length>0 && (
        <div style={{marginTop:28}}>
          <div style={{fontSize:16,fontWeight:600,color:C.ink,marginBottom:4}}>My certificates</div>
          <div style={{fontSize:12,color:C.inkLight,marginBottom:14}}>Certificates you have earned. You can view, print, or save them as PDF at any time.</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {earnedCerts.map(cert=>(
              <div key={cert.certNum} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{display:"flex",gap:14,alignItems:"center"}}>
                  <div style={{width:44,height:44,borderRadius:"50%",background:"linear-gradient(135deg,#D4AF37,#B8972A)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:C.white,fontWeight:700,flexShrink:0}}>C</div>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:C.ink}}>{cert.track==="tech"?"Technician Certification":"Service Advisor Certification"}</div>
                    <div style={{fontSize:11,color:C.inkLight,marginTop:2}}>Certificate #{cert.certNum} · Issued {cert.date} · Score: {cert.score}%</div>
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button style={S.btnPrimary} onClick={()=>setViewCert(cert)}>View</button>
                  <button style={S.btnSecondary} onClick={()=>{setViewCert(cert);setTimeout(()=>window.print(),300);}}>Print</button>
                  <button style={{...S.btnSmall,background:C.blueLight,color:C.blue,border:`1px solid ${C.blue}`}} onClick={()=>{setViewCert(cert);setTimeout(()=>window.print(),300);}}>Save PDF</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MARKETING MATERIALS ──────────────────────────────────────────────────────
function MarketingMaterials({isAdmin=false, materials, setMaterials, onPlaceOrder, repName}) {
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({name:"",category:"flyer",price:0,unit:"per pack of 10",active:true,stock:0,desc:""});
  const [orderItems, setOrderItems] = useState([]);
  const [orderPlaced, setOrderPlaced] = useState(null);
  const [toast, setToast] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3000); };

  const addToOrder = (item) => {
    setOrderItems(prev=>{
      const existing = prev.find(i=>i.id===item.id);
      if(existing) return prev.map(i=>i.id===item.id?{...i,qty:i.qty+1}:i);
      return [...prev,{...item,qty:1}];
    });
  };

  const updateQty = (id, qty) => {
    if(qty<=0) { setOrderItems(prev=>prev.filter(i=>i.id!==id)); return; }
    setOrderItems(prev=>prev.map(i=>i.id===id?{...i,qty}:i));
  };

  const removeFromOrder = (id) => setOrderItems(prev=>prev.filter(i=>i.id!==id));

  const placeOrder = () => {
    const orderId = `MAT-${Date.now().toString(36).toUpperCase()}`;
    const order = {id:orderId, items:[...orderItems], total:orderTotal, date:new Date().toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"})};
    setOrderPlaced(order);
    if(onPlaceOrder) onPlaceOrder(order);
    setOrderItems([]);
    showToast("Order placed! Admin has been notified and your materials will be shipped.");
    emailMaterialOrder({accountName:repName||"Sales Rep", source:"rep", items:order.items, total:order.total, orderId});
  };

  const orderTotal = orderItems.reduce((s,i)=>s+i.price*i.qty,0);
  const catColors = {flyer:S.pillBlue,shelf:S.pillGreen,display:S.pillAmber,banner:S.pillPurple,other:S.pillGray};

  return (
    <div style={S.content}>
      {addingItem && (
        <Modal title="Add marketing material" onClose={()=>setAddingItem(false)}>
          <div style={S.fg}><label style={S.label}>Name *</label><input style={S.input} value={newItem.name} onChange={e=>setNewItem(v=>({...v,name:e.target.value}))} placeholder="Product flyer — DPF line"/></div>
          <div style={S.twoCol}>
            <div style={S.fg}><label style={S.label}>Category</label>
              <select style={S.select} value={newItem.category} onChange={e=>setNewItem(v=>({...v,category:e.target.value}))}>
                {["flyer","shelf","display","banner","other"].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={S.fg}><label style={S.label}>Unit label</label><input style={S.input} value={newItem.unit} onChange={e=>setNewItem(v=>({...v,unit:e.target.value}))} placeholder="per pack of 10"/></div>
          </div>
          <div style={S.twoCol}>
            <div style={S.fg}><label style={S.label}>Price (0 = free)</label><input style={S.input} type="number" min="0" step="0.01" value={newItem.price} onChange={e=>setNewItem(v=>({...v,price:parseFloat(e.target.value)||0}))} placeholder="0.00"/></div>
            <div style={S.fg}><label style={S.label}>Stock qty</label><input style={S.input} type="number" min="0" value={newItem.stock} onChange={e=>setNewItem(v=>({...v,stock:parseInt(e.target.value)||0}))}/></div>
          </div>
          <div style={S.fg}><label style={S.label}>Description</label><textarea style={{...S.input,height:72,resize:"vertical"}} value={newItem.desc} onChange={e=>setNewItem(v=>({...v,desc:e.target.value}))} placeholder="Describe the item..."/></div>
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.ink,marginBottom:16,cursor:"pointer"}}>
            <input type="checkbox" checked={newItem.active} onChange={e=>setNewItem(v=>({...v,active:e.target.checked}))} style={{width:14,height:14}}/>
            Active (visible to reps and customers)
          </label>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button style={S.btnSecondary} onClick={()=>setAddingItem(false)}>Cancel</button>
            <button style={S.btnPrimary} onClick={()=>{setMaterials(m=>[...m,{id:Date.now(),...newItem}]);setAddingItem(false);}}>Add item</button>
          </div>
        </Modal>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:18,fontWeight:600,color:C.ink}}>Marketing materials</div>
          <div style={{fontSize:13,color:C.inkLight,marginTop:2}}>{isAdmin?"Manage the catalog of marketing materials available to reps and customers.":"Add free or priced materials to your orders."}</div>
        </div>
        {isAdmin && <button style={S.btnPrimary} onClick={()=>setAddingItem(true)}>+ Add material</button>}
      </div>

      {/* Toast */}
      {toast&&<div style={{position:"fixed",top:24,right:24,background:C.green,color:C.white,padding:"12px 20px",borderRadius:10,fontSize:13,fontWeight:500,zIndex:3000,boxShadow:"0 4px 16px rgba(0,0,0,0.15)"}}>{toast}</div>}

      {/* Order placed confirmation */}
      {!isAdmin && orderPlaced && !orderItems.length && (
        <div style={{background:C.greenLight,border:`1px solid #86EFAC`,borderRadius:10,padding:"14px 18px",marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,color:C.green,marginBottom:6}}>Order #{orderPlaced.id} placed on {orderPlaced.date}</div>
          <div style={{fontSize:12,color:C.green}}>{orderPlaced.items.length} item{orderPlaced.items.length>1?"s":""} — {orderPlaced.total===0?"FREE":`$${orderPlaced.total.toFixed(2)}`}. Admin notified. Materials will be shipped with your next product delivery.</div>
        </div>
      )}

      {!isAdmin && orderItems.length > 0 && (
        <div style={{background:C.white,border:`1px solid ${C.teal}`,borderRadius:10,padding:"14px 18px",marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,color:C.teal,marginBottom:10}}>Your material order ({orderItems.length} item{orderItems.length>1?"s":""})</div>
          {orderItems.map(i=>(
            <div key={i.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,padding:"6px 0",color:C.inkMid,borderBottom:`1px solid ${C.border}`}}>
              <span style={{flex:1}}>{i.name}</span>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{display:"flex",alignItems:"center",border:`1px solid ${C.border}`,borderRadius:6,overflow:"hidden"}}>
                  <button onClick={()=>updateQty(i.id,i.qty-1)} style={{background:C.surface,border:"none",padding:"3px 8px",cursor:"pointer",fontSize:13,color:C.ink}}>-</button>
                  <span style={{padding:"3px 10px",fontSize:12,fontWeight:500,minWidth:28,textAlign:"center"}}>{i.qty}</span>
                  <button onClick={()=>updateQty(i.id,i.qty+1)} style={{background:C.surface,border:"none",padding:"3px 8px",cursor:"pointer",fontSize:13,color:C.ink}}>+</button>
                </div>
                <span style={{minWidth:60,textAlign:"right",fontWeight:500}}>{i.price===0?"FREE":`$${(i.price*i.qty).toFixed(2)}`}</span>
                <button onClick={()=>removeFromOrder(i.id)} style={{background:"none",border:"none",color:C.inkLight,cursor:"pointer",fontSize:16}}>×</button>
              </div>
            </div>
          ))}
          <div style={{borderTop:`1px solid ${C.border}`,marginTop:8,paddingTop:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:13,fontWeight:600,color:C.ink}}>Total: {orderTotal===0?"FREE":`$${orderTotal.toFixed(2)}`}</div>
            <button style={{...S.btnPrimary,padding:"8px 20px"}} onClick={placeOrder}>Place order for shipping</button>
          </div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {materials.filter(m=>isAdmin||m.active).map(item=>(
          <div key={item.id} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",opacity:item.active?1:0.55}}>
            <div style={{background:C.surface,height:56,display:"flex",alignItems:"center",justifyContent:"center",borderBottom:`1px solid ${C.border}`,fontSize:11,fontWeight:500,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.05em"}}>
              {item.category}
            </div>
            <div style={{padding:"12px 14px"}}>
              <div style={{display:"flex",gap:6,marginBottom:6,flexWrap:"wrap"}}>
                <span style={catColors[item.category]||S.pillGray}>{item.category}</span>
                <span style={item.price===0?S.pillGreen:S.pillAmber}>{item.price===0?"Free":`$${item.price.toFixed(2)}`}</span>
                {!item.active && <span style={S.pillGray}>Inactive</span>}
              </div>
              <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:4}}>{item.name}</div>
              <div style={{fontSize:11,color:C.inkLight,marginBottom:6}}>{item.unit} · {item.stock} in stock</div>
              <div style={{fontSize:12,color:C.inkMid,marginBottom:10,lineHeight:1.5}}>{item.desc}</div>
              {isAdmin ? (
                <div style={{display:"flex",gap:6}}>
                  <button style={item.active?S.btnDanger:S.btnSuccess} onClick={()=>setMaterials(m=>m.map(x=>x.id===item.id?{...x,active:!x.active}:x))}>{item.active?"Deactivate":"Activate"}</button>
                  <button style={S.btnSmall}>Edit</button>
                </div>
              ) : (()=>{
                const inCart = orderItems.find(o=>o.id===item.id);
                return inCart ? (
                  <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"center"}}>
                    <div style={{display:"flex",alignItems:"center",border:`1px solid ${C.teal}`,borderRadius:6,overflow:"hidden"}}>
                      <button onClick={()=>updateQty(item.id,inCart.qty-1)} style={{background:C.tealLight,border:"none",padding:"6px 10px",cursor:"pointer",fontSize:14,color:C.teal,fontWeight:600}}>-</button>
                      <span style={{padding:"6px 14px",fontSize:13,fontWeight:600,color:C.teal}}>{inCart.qty}</span>
                      <button onClick={()=>updateQty(item.id,inCart.qty+1)} style={{background:C.tealLight,border:"none",padding:"6px 10px",cursor:"pointer",fontSize:14,color:C.teal,fontWeight:600}}>+</button>
                    </div>
                    <button onClick={()=>removeFromOrder(item.id)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",cursor:"pointer",fontSize:12,color:C.inkLight}}>Remove</button>
                  </div>
                ) : (
                  <button style={{...S.btnPrimary,width:"100%",padding:"8px"}} onClick={()=>addToOrder(item)} disabled={!item.active}>
                    Add to order
                  </button>
                );
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── TOOL LENDING ─────────────────────────────────────────────────────────────
function ToolLending({isAdmin=false, tools, setTools, agreements, setAgreements, repName, customers}) {
  const [tab, setTab] = useState("agreements");
  const [newAgreement, setNewAgreement] = useState(false);
  const [form, setForm] = useState({toolId:"",shop:"",startDate:"",duration:"month_to_month",fixedMonths:"12",minOrdersPerMonth:"",damageClause:true,stolenClause:true,buyOption:false,buyPrice:"",contractNotes:"",shopContact:"",shopEmail:"",shopPhone:""});
  const [addingTool, setAddingTool] = useState(false);
  const [viewContract, setViewContract] = useState(null);
  const [newTool, setNewTool] = useState({name:"",value:0,minUnitsPerMonth:12,category:"diagnostic",maxCirculation:10,active:true,desc:""});

  const selectedTool = form.toolId ? tools.find(t=>t.id===parseInt(form.toolId)) : null;
  const minUnits = form.minOrdersPerMonth ? parseInt(form.minOrdersPerMonth) : (selectedTool?.minUnitsPerMonth || 0);

  const createAgreement = () => {
    if(!selectedTool || !form.shop) return;
    const allAccts = customers || REP_ACCOUNTS;
    const acct = allAccts.find(a=>a.company===form.shop);
    const now = new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
    setAgreements(a=>[...a,{
      id:Date.now(), toolId:selectedTool.id, toolName:selectedTool.name, shop:form.shop, rep:repName||"Sales Rep",
      startDate:form.startDate || new Date().toISOString().slice(0,10), endDate:"",
      minUnits, lastMonthUnits:0, status:"compliant", warnings:0, value:selectedTool.value,
      duration:form.duration, fixedMonths:form.duration==="fixed"?parseInt(form.fixedMonths):null,
      damageClause:form.damageClause, stolenClause:form.stolenClause,
      buyOption:form.buyOption, buyPrice:form.buyOption?parseFloat(form.buyPrice)||0:null,
      contractNotes:form.contractNotes,
      shopContact:form.shopContact || acct?.contact || "",
      shopEmail:form.shopEmail || acct?.email || "",
      shopPhone:form.shopPhone || acct?.phone || "",
      repComments:[{date:now,text:"Agreement created. Tool scheduled for delivery."}],
      maintenanceLog:[], notifications:[]
    }]);
    setNewAgreement(false);
    setForm({toolId:"",shop:"",startDate:"",duration:"month_to_month",fixedMonths:"12",minOrdersPerMonth:"",damageClause:true,stolenClause:true,buyOption:false,buyPrice:"",contractNotes:"",shopContact:"",shopEmail:"",shopPhone:""});
  };

  const [toast, setToast] = useState("");
  const [recallModal, setRecallModal] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [newMaint, setNewMaint] = useState({type:"Inspection",notes:""});

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3000); };
  const now = () => new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});

  const sendReminder = (agr) => {
    const allAccts = customers || REP_ACCOUNTS;
    const acct = allAccts.find(a=>a.company===agr.shop);
    setAgreements(a=>a.map(x=>x.id===agr.id?{...x,
      notifications:[...(x.notifications||[]),{date:now(),type:"reminder",text:`Reminder sent to ${acct?.email||agr.shopEmail||agr.shop}: minimum order of ${agr.minUnits} units not met last month (${agr.lastMonthUnits} units). Please increase orders to maintain your tool lending agreement.`}]
    }:x));
    showToast("Reminder sent to "+agr.shop);
  };

  const startRecall = (agr) => {
    setRecallModal(agr);
  };

  const confirmRecall = (agr) => {
    const allAccts = customers || REP_ACCOUNTS;
    const acct = allAccts.find(a=>a.company===agr.shop);
    const buyMsg = agr.buyOption ? ` You also have the option to purchase this tool${agr.buyPrice?" at $"+agr.buyPrice:""} - please let us know if you are interested.` : "";
    setAgreements(a=>a.map(x=>x.id===agr.id?{...x, status:"recall", warnings:2,
      notifications:[...(x.notifications||[]),{date:now(),type:"recall",text:`Recall notice sent to ${acct?.email||agr.shopEmail||agr.shop}: Your tool (${agr.toolName}) has been recalled due to not meeting the minimum monthly order requirement. A MotorPower Care sales representative or authorized shipping company will arrange pickup. Please have the tool ready to avoid being charged the full replacement value of $${agr.value}.${buyMsg}`}]
    }:x));
    setRecallModal(null);
    showToast("Recall initiated - notification sent to "+agr.shop);
    emailToolRecall({shopName:agr.shop, shopEmail:agr.shopEmail||acct?.email||"", toolName:agr.toolName, toolValue:agr.value, repName:agr.rep||repName||"Sales Rep", buyOption:agr.buyOption, buyPrice:agr.buyPrice});
  };

  const confirmCollected = (agr) => {
    setAgreements(a=>a.map(x=>x.id===agr.id?{...x, status:"collected", endDate:now(),
      notifications:[...(x.notifications||[]),{date:now(),type:"collected",text:`Tool collected from ${agr.shop}. Agreement ended.`}],
      repComments:[...(x.repComments||[]),{date:now(),text:"Tool collected and agreement closed."}]
    }:x));
    showToast("Tool marked as collected from "+agr.shop);
  };

  const addRepComment = (agrId) => {
    if(!newComment.trim()) return;
    setAgreements(a=>a.map(x=>x.id===agrId?{...x,repComments:[...(x.repComments||[]),{date:now(),text:newComment.trim()}]}:x));
    setNewComment("");
    if(viewContract?.id===agrId) setViewContract(v=>({...v,repComments:[...(v.repComments||[]),{date:now(),text:newComment.trim()}]}));
  };

  const addMaintenance = (agrId) => {
    if(!newMaint.notes.trim()) return;
    const entry = {date:now(),type:newMaint.type,notes:newMaint.notes.trim()};
    setAgreements(a=>a.map(x=>x.id===agrId?{...x,maintenanceLog:[...(x.maintenanceLog||[]),entry]}:x));
    setNewMaint({type:"Inspection",notes:""});
    if(viewContract?.id===agrId) setViewContract(v=>({...v,maintenanceLog:[...(v.maintenanceLog||[]),entry]}));
  };

  const statusAction = (agr) => {
    if(agr.status==="warning") return (
      <div style={{display:"flex",gap:4}}>
        <button style={S.btnSmall} onClick={()=>sendReminder(agr)}>Send reminder</button>
        <button style={S.btnDanger} onClick={()=>startRecall(agr)}>Start recall</button>
      </div>
    );
    if(agr.status==="recall") return <button style={S.btnDanger} onClick={()=>confirmCollected(agr)}>Confirm collected</button>;
    if(agr.status==="collected") return <span style={{fontSize:11,color:C.inkLight}}>Closed</span>;
    return <button style={S.btnGhost} onClick={()=>setViewContract(agr)}>View</button>;
  };

  return (
    <div style={S.content}>
      {toast && <div style={{position:"fixed",top:20,right:20,background:C.teal,color:C.white,padding:"10px 20px",borderRadius:8,fontSize:13,fontWeight:500,zIndex:9999,boxShadow:"0 4px 12px rgba(0,0,0,0.15)"}}>{toast}</div>}

      {newAgreement && (
        <Modal title="New tool lending agreement" onClose={()=>setNewAgreement(false)}>
          <div style={{maxHeight:"70vh",overflowY:"auto",paddingRight:4}}>
          <div style={{background:C.amberLight,border:"1px solid #FCD34D",borderRadius:8,padding:"10px 14px",fontSize:12,color:C.amber,marginBottom:16}}>
            This creates a binding tool lending contract. The shop is responsible for the full replacement value if the tool is damaged or stolen.
          </div>

          {/* ─── Tool & Shop selection ─── */}
          <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:8}}>Tool & shop details</div>
          <div style={S.twoCol}>
            <div style={S.fg}><label style={S.label}>Tool *</label>
              <select style={S.select} value={form.toolId} onChange={e=>{const t=tools.find(x=>x.id===parseInt(e.target.value));setForm(f=>({...f,toolId:e.target.value,minOrdersPerMonth:f.minOrdersPerMonth||String(t?.minUnitsPerMonth||"")}));}}>
                <option value="">Select tool</option>
                {tools.filter(t=>t.active).map(t=><option key={t.id} value={t.id}>{t.name} (${t.value})</option>)}
              </select>
            </div>
            <div style={S.fg}><label style={S.label}>Shop *</label>
              <select style={S.select} value={form.shop} onChange={e=>{const allAccts=customers||REP_ACCOUNTS;const acct=allAccts.find(a=>a.company===e.target.value);setForm(f=>({...f,shop:e.target.value,shopContact:acct?.contact||"",shopEmail:acct?.email||"",shopPhone:acct?.phone||""}));}}>
                <option value="">Select account</option>
                {(customers||REP_ACCOUNTS).map(a=><option key={a.id} value={a.company}>{a.company}</option>)}
              </select>
            </div>
          </div>
          {form.shop && <div style={S.twoCol}>
            <div style={S.fg}><label style={S.label}>Shop contact</label><input style={S.input} value={form.shopContact} onChange={e=>setForm(f=>({...f,shopContact:e.target.value}))}/></div>
            <div style={S.fg}><label style={S.label}>Shop email</label><input style={S.input} value={form.shopEmail} onChange={e=>setForm(f=>({...f,shopEmail:e.target.value}))}/></div>
          </div>}
          <div style={S.fg}><label style={S.label}>Start date *</label><input style={S.input} type="date" value={form.startDate} onChange={e=>setForm(f=>({...f,startDate:e.target.value}))}/></div>

          {/* ─── Duration & minimum ─── */}
          <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:8,marginTop:16,paddingTop:12,borderTop:`1px solid ${C.border}`}}>Contract terms</div>
          <div style={S.fg}><label style={S.label}>Agreement duration *</label>
            <select style={S.select} value={form.duration} onChange={e=>setForm(f=>({...f,duration:e.target.value}))}>
              <option value="month_to_month">Month-to-month (auto-renews)</option>
              <option value="fixed">Fixed term</option>
              <option value="until_minimum_not_met">Active until minimum not met</option>
            </select>
          </div>
          {form.duration==="fixed" && (
            <div style={S.fg}><label style={S.label}>Fixed term length (months)</label><input style={S.input} type="number" min="1" value={form.fixedMonths} onChange={e=>setForm(f=>({...f,fixedMonths:e.target.value}))}/></div>
          )}
          <div style={S.twoCol}>
            <div style={S.fg}><label style={S.label}>Minimum orders per month (units) *</label><input style={S.input} type="number" min="1" value={form.minOrdersPerMonth} onChange={e=>setForm(f=>({...f,minOrdersPerMonth:e.target.value}))} placeholder={selectedTool?String(selectedTool.minUnitsPerMonth):"0"}/></div>
            {selectedTool && <div style={S.fg}><label style={S.label}>Tool replacement value</label><div style={{...S.input,background:C.surface,color:C.ink,fontWeight:600}}>${selectedTool.value}</div></div>}
          </div>

          {/* ─── Liability clauses ─── */}
          <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:8,marginTop:16,paddingTop:12,borderTop:`1px solid ${C.border}`}}>Liability & conditions</div>
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:C.ink,marginBottom:8,cursor:"pointer"}}>
            <input type="checkbox" checked={form.damageClause} onChange={e=>setForm(f=>({...f,damageClause:e.target.checked}))}/>
            <span>Damage clause - shop pays full replacement value (${selectedTool?.value||"--"}) if tool is damaged</span>
          </label>
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:C.ink,marginBottom:8,cursor:"pointer"}}>
            <input type="checkbox" checked={form.stolenClause} onChange={e=>setForm(f=>({...f,stolenClause:e.target.checked}))}/>
            <span>Stolen/lost clause - shop pays full replacement value (${selectedTool?.value||"--"}) if tool is stolen or lost</span>
          </label>
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:C.ink,marginBottom:8,cursor:"pointer"}}>
            <input type="checkbox" checked={form.buyOption} onChange={e=>setForm(f=>({...f,buyOption:e.target.checked}))}/>
            <span>Buy option available - shop may purchase the tool at a price determined by MotorPower Care</span>
          </label>
          {form.buyOption && (
            <div style={S.fg}><label style={S.label}>Buy option price ($)</label><input style={S.input} type="number" min="0" step="0.01" value={form.buyPrice} onChange={e=>setForm(f=>({...f,buyPrice:e.target.value}))} placeholder="To be determined by MPC"/></div>
          )}

          {/* ─── Notes ─── */}
          <div style={S.fg}><label style={S.label}>Additional notes / special terms</label><textarea style={{...S.input,height:60,resize:"vertical"}} value={form.contractNotes} onChange={e=>setForm(f=>({...f,contractNotes:e.target.value}))} placeholder="Any additional terms or conditions..."/></div>

          {/* ─── Contract preview ─── */}
          {selectedTool && form.shop && (
            <div style={{marginTop:16,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
              <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:8}}>Contract preview</div>
              <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px 24px",fontSize:11,lineHeight:1.8,color:C.inkMid}}>
                <div style={{textAlign:"center",marginBottom:12}}>
                  <img src="/mpc-logo.png" alt="MPC" style={{width:48,height:48,objectFit:"contain",marginBottom:6}}/>
                  <div style={{fontSize:14,fontWeight:700,color:C.ink}}>TOOL LENDING AGREEMENT</div>
                  <div style={{fontSize:11,color:C.inkLight}}>MotorPower Care Inc.</div>
                </div>
                <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Parties:</strong> This agreement is between <strong>MotorPower Care Inc.</strong> ("MPC") and <strong>{form.shop}</strong> ("Shop"), contact: {form.shopContact||"--"}, email: {form.shopEmail||"--"}.</div>
                <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Tool:</strong> {selectedTool.name} - Replacement value: <strong>${selectedTool.value}</strong></div>
                <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Start date:</strong> {form.startDate || "TBD"}</div>
                <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Duration:</strong> {form.duration==="month_to_month"?"Month-to-month, auto-renewing. Either party may terminate with 30 days written notice.":form.duration==="fixed"?`Fixed term of ${form.fixedMonths} month(s) from start date.`:"Active as long as Shop meets the minimum monthly order requirement. MPC Rep has the right to terminate and remove the tool if minimum is not met."}</div>
                <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Minimum monthly orders:</strong> Shop agrees to purchase a minimum of <strong>{minUnits} units</strong> of MPC product per calendar month for the duration of this agreement. Failure to meet this minimum for one month results in a written warning. Failure for two consecutive months gives MPC the right to immediately recall the tool.</div>
                {form.damageClause && <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Damage liability:</strong> If the tool is damaged while in Shop's possession, Shop is responsible to pay the full replacement value of <strong>${selectedTool.value}</strong> to MPC.</div>}
                {form.stolenClause && <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Theft/loss liability:</strong> If the tool is stolen or lost while in Shop's possession, Shop is responsible to pay the full replacement value of <strong>${selectedTool.value}</strong> to MPC.</div>}
                <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Right to remove:</strong> MPC and its authorized representatives reserve the right to remove the tool at any time if (a) the Shop fails to meet minimum order requirements, (b) the tool is being misused, or (c) the agreement is terminated. If the Shop fails to return the tool upon request, Shop will be billed for the full replacement value of <strong>${selectedTool.value}</strong>.</div>
                {form.buyOption && <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Purchase option:</strong> Shop may purchase the tool at a price of <strong>{form.buyPrice?"$"+form.buyPrice:"to be determined by MPC"}</strong>. This option is available at MPC's sole discretion.</div>}
                <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Tool condition:</strong> Shop must maintain the tool in proper working condition. MPC reserves the right to inspect the tool during any scheduled or unscheduled visit. Shop shall not modify, disassemble, or sublet the tool to any third party.</div>
                {form.contractNotes && <div style={{marginBottom:8}}><strong style={{color:C.ink}}>Additional terms:</strong> {form.contractNotes}</div>}
                <div style={{marginTop:16,paddingTop:12,borderTop:`1px dashed ${C.border}`,display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
                  <div><div style={{fontSize:10,color:C.inkLight,marginBottom:24}}>MPC Representative signature</div><div style={{borderBottom:`1px solid ${C.inkLight}`,marginBottom:4}}></div><div style={{fontSize:10,color:C.inkLight}}>Date: _______________</div></div>
                  <div><div style={{fontSize:10,color:C.inkLight,marginBottom:24}}>Shop authorized signature</div><div style={{borderBottom:`1px solid ${C.inkLight}`,marginBottom:4}}></div><div style={{fontSize:10,color:C.inkLight}}>Date: _______________</div></div>
                </div>
              </div>
            </div>
          )}

          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:16}}>
            <button style={S.btnSecondary} onClick={()=>setNewAgreement(false)}>Cancel</button>
            <button style={{...S.btnPrimary,opacity:(!selectedTool||!form.shop)?0.5:1}} onClick={createAgreement} disabled={!selectedTool||!form.shop}>Create agreement</button>
          </div>
          </div>
        </Modal>
      )}

      {addingTool && (
        <Modal title="Add tool to lending catalog" onClose={()=>setAddingTool(false)}>
          <div style={S.fg}><label style={S.label}>Tool name *</label><input style={S.input} value={newTool.name} onChange={e=>setNewTool(v=>({...v,name:e.target.value}))} placeholder="OBD-II diagnostic scanner"/></div>
          <div style={S.twoCol}>
            <div style={S.fg}><label style={S.label}>Tool value ($)</label><input style={S.input} type="number" value={newTool.value} onChange={e=>setNewTool(v=>({...v,value:parseInt(e.target.value)||0}))}/></div>
            <div style={S.fg}><label style={S.label}>Monthly minimum (units)</label><input style={S.input} type="number" value={newTool.minUnitsPerMonth} onChange={e=>setNewTool(v=>({...v,minUnitsPerMonth:parseInt(e.target.value)||0}))}/></div>
          </div>
          <div style={S.twoCol}>
            <div style={S.fg}><label style={S.label}>Category</label>
              <select style={S.select} value={newTool.category} onChange={e=>setNewTool(v=>({...v,category:e.target.value}))}>
                {["diagnostic","emissions","measurement","other"].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={S.fg}><label style={S.label}>Max in circulation</label><input style={S.input} type="number" value={newTool.maxCirculation} onChange={e=>setNewTool(v=>({...v,maxCirculation:parseInt(e.target.value)||0}))}/></div>
          </div>
          <div style={S.fg}><label style={S.label}>Description</label><textarea style={{...S.input,height:72,resize:"vertical"}} value={newTool.desc} onChange={e=>setNewTool(v=>({...v,desc:e.target.value}))}/></div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
            <button style={S.btnSecondary} onClick={()=>setAddingTool(false)}>Cancel</button>
            <button style={S.btnPrimary} onClick={()=>{setTools(t=>[...t,{id:Date.now(),...newTool,currentLoaned:0}]);setAddingTool(false);}}>Add tool</button>
          </div>
        </Modal>
      )}

      {viewContract && (
        <Modal title={"Agreement - "+viewContract.toolName} onClose={()=>{setViewContract(null);setNewComment("");setNewMaint({type:"Inspection",notes:""});}}>
          <div style={{maxHeight:"70vh",overflowY:"auto",paddingRight:4}}>

          {/* ─── Overview cards ─── */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>Tool</div><div style={{fontSize:13,fontWeight:600,color:C.ink}}>{viewContract.toolName}</div></div>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>Shop</div><div style={{fontSize:13,fontWeight:600,color:C.ink}}>{viewContract.shop}</div><div style={{fontSize:10,color:C.inkLight}}>{viewContract.shopContact||""} {viewContract.shopEmail?"- "+viewContract.shopEmail:""}</div></div>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>Status</div><div style={{marginTop:2}}><Badge type={viewContract.status}>{viewContract.status}</Badge></div></div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:16}}>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>Start date</div><div style={{fontSize:12,fontWeight:600,color:C.ink}}>{viewContract.startDate}</div></div>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>End date</div><div style={{fontSize:12,fontWeight:600,color:viewContract.endDate?C.red:C.inkLight}}>{viewContract.endDate||"Active"}</div></div>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>Tool value</div><div style={{fontSize:12,fontWeight:600,color:C.ink}}>${viewContract.value}</div></div>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>Duration</div><div style={{fontSize:12,fontWeight:600,color:C.ink}}>{viewContract.duration==="fixed"?`Fixed ${viewContract.fixedMonths}mo`:viewContract.duration==="until_minimum_not_met"?"Until min not met":"Month-to-month"}</div></div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>Min orders/month</div><div style={{fontSize:12,fontWeight:600,color:C.ink}}>{viewContract.minUnits} units</div></div>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>Last month orders</div><div style={{fontSize:12,fontWeight:600,color:viewContract.lastMonthUnits>=viewContract.minUnits?C.teal:C.red}}>{viewContract.lastMonthUnits} units</div></div>
            <div style={{background:C.surface,borderRadius:8,padding:"10px 14px"}}><div style={{fontSize:10,color:C.inkLight}}>Rep</div><div style={{fontSize:12,fontWeight:600,color:C.ink}}>{viewContract.rep}</div></div>
          </div>

          {/* ─── Contract clauses ─── */}
          <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:6,marginTop:8}}>Contract clauses</div>
          <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",fontSize:11,lineHeight:1.7,color:C.inkMid,marginBottom:16}}>
            <div style={{marginBottom:4}}>{viewContract.damageClause!==false?"-- Damage: Shop pays $"+viewContract.value+" if tool is damaged.":"-- Damage clause: Not included."}</div>
            <div style={{marginBottom:4}}>{viewContract.stolenClause!==false?"-- Stolen/lost: Shop pays $"+viewContract.value+" if tool is stolen or lost.":"-- Stolen/lost clause: Not included."}</div>
            <div style={{marginBottom:4}}>-- Right to remove: MPC may remove tool if minimum not met. Shop billed ${viewContract.value} if not returned.</div>
            {viewContract.buyOption && <div style={{marginBottom:4}}>-- Buy option: {viewContract.buyPrice?"$"+viewContract.buyPrice:"Price determined by MPC"}.</div>}
            {viewContract.contractNotes && <div style={{marginBottom:4}}>-- Notes: {viewContract.contractNotes}</div>}
          </div>

          {/* ─── Rep comments ─── */}
          <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:6}}>Sales rep comments</div>
          <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
            {(viewContract.repComments||[]).length===0 && <div style={{fontSize:12,color:C.inkLight,padding:"8px 0"}}>No comments yet.</div>}
            {(viewContract.repComments||[]).map((c,i)=>(
              <div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:i<(viewContract.repComments||[]).length-1?`1px solid ${C.border}`:"none"}}>
                <div style={{fontSize:10,color:C.inkLight,minWidth:80,flexShrink:0}}>{c.date}</div>
                <div style={{fontSize:12,color:C.ink,lineHeight:1.5}}>{c.text}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8,marginBottom:16}}>
            <input style={{...S.input,flex:1,fontSize:12}} placeholder="Add a comment..." value={newComment} onChange={e=>setNewComment(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addRepComment(viewContract.id);}}/>
            <button style={S.btnSmall} onClick={()=>addRepComment(viewContract.id)}>Add</button>
          </div>

          {/* ─── Maintenance log ─── */}
          <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:6}}>Maintenance log</div>
          <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
            {(viewContract.maintenanceLog||[]).length===0 && <div style={{fontSize:12,color:C.inkLight,padding:"8px 0"}}>No maintenance recorded.</div>}
            {(viewContract.maintenanceLog||[]).map((m,i)=>(
              <div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:i<(viewContract.maintenanceLog||[]).length-1?`1px solid ${C.border}`:"none"}}>
                <div style={{fontSize:10,color:C.inkLight,minWidth:80,flexShrink:0}}>{m.date}</div>
                <div><Badge type="active">{m.type}</Badge></div>
                <div style={{fontSize:12,color:C.ink,lineHeight:1.5}}>{m.notes}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8,marginBottom:16}}>
            <select style={{...S.select,width:130,fontSize:11}} value={newMaint.type} onChange={e=>setNewMaint(m=>({...m,type:e.target.value}))}>
              {["Inspection","Calibration","Repair","Cleaning","Battery replacement","Other"].map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            <input style={{...S.input,flex:1,fontSize:12}} placeholder="Maintenance notes..." value={newMaint.notes} onChange={e=>setNewMaint(m=>({...m,notes:e.target.value}))}/>
            <button style={S.btnSmall} onClick={()=>addMaintenance(viewContract.id)}>Add</button>
          </div>

          {/* ─── Notification history ─── */}
          {(viewContract.notifications||[]).length>0 && <>
            <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:6}}>Notification history</div>
            <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
              {(viewContract.notifications||[]).map((n,i)=>(
                <div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:i<(viewContract.notifications||[]).length-1?`1px solid ${C.border}`:"none"}}>
                  <div style={{fontSize:10,color:C.inkLight,minWidth:80,flexShrink:0}}>{n.date}</div>
                  <Badge type={n.type==="recall"?"recall":n.type==="warning"?"warning":"active"}>{n.type}</Badge>
                  <div style={{fontSize:11,color:C.inkMid,lineHeight:1.5}}>{n.text}</div>
                </div>
              ))}
            </div>
          </>}

          {/* ─── Actions ─── */}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
            {viewContract.status==="warning" && <button style={S.btnSmall} onClick={()=>{sendReminder(viewContract);setViewContract(v=>({...v,notifications:[...(v.notifications||[]),{date:now(),type:"reminder",text:"Reminder sent."}]}));}}>Send reminder</button>}
            {viewContract.status==="warning" && <button style={S.btnDanger} onClick={()=>{setViewContract(null);startRecall(viewContract);}}>Start recall</button>}
            {viewContract.status==="recall" && <button style={S.btnDanger} onClick={()=>{confirmCollected(viewContract);setViewContract(null);}}>Confirm collected</button>}
            <button style={S.btnSecondary} onClick={()=>{setViewContract(null);setNewComment("");setNewMaint({type:"Inspection",notes:""});}}>Close</button>
          </div>

          </div>
        </Modal>
      )}

      {/* ─── Recall confirmation modal ─── */}
      {recallModal && (
        <Modal title="Start tool recall" onClose={()=>setRecallModal(null)}>
          <div style={{background:C.redLight,border:`1px solid ${C.red}`,borderRadius:8,padding:"12px 16px",marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:C.red,marginBottom:4}}>This will send a recall notification to the shop</div>
            <div style={{fontSize:12,color:C.red}}>The customer will be notified that the tool is being recalled and a sales rep or shipping company will arrange pickup.</div>
          </div>
          <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:8}}>Email preview to: {recallModal.shopEmail||recallModal.shop}</div>
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 20px",fontSize:12,lineHeight:1.8,color:C.inkMid,marginBottom:16}}>
            <div style={{marginBottom:8}}>Dear <strong>{recallModal.shopContact||recallModal.shop}</strong>,</div>
            <div style={{marginBottom:8}}>This is to inform you that your tool lending agreement for the <strong>{recallModal.toolName}</strong> (value: ${recallModal.value}) has been terminated due to not meeting the minimum monthly order requirement of {recallModal.minUnits} units.</div>
            <div style={{marginBottom:8}}>A MotorPower Care sales representative or our authorized shipping partner will contact you to arrange tool pickup. <strong>Please have the tool ready and available to avoid being charged the full replacement value of ${recallModal.value}.</strong></div>
            {recallModal.buyOption && <div style={{marginBottom:8,background:C.blueLight,borderRadius:6,padding:"8px 12px",color:C.blue}}>You also have the option to <strong>purchase this tool{recallModal.buyPrice?" at $"+recallModal.buyPrice:""}</strong> instead of returning it. If you are interested, please contact your MotorPower Care representative.</div>}
            <div style={{marginBottom:8}}>If you have any questions, please contact your representative or reach us at info@mpcare.com.</div>
            <div>Best regards,<br/><strong>MotorPower Care Inc.</strong></div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button style={S.btnSecondary} onClick={()=>setRecallModal(null)}>Cancel</button>
            <button style={S.btnDanger} onClick={()=>confirmRecall(recallModal)}>Send recall notice</button>
          </div>
        </Modal>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:18,fontWeight:600,color:C.ink}}>Tool lending program</div>
          <div style={{fontSize:13,color:C.inkLight,marginTop:2}}>We loan tools to repair shops. They must purchase a monthly minimum to keep the tool.</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          {isAdmin && <button style={S.btnSecondary} onClick={()=>setAddingTool(true)}>+ Add tool</button>}
          <button style={S.btnPrimary} onClick={()=>setNewAgreement(true)}>+ New agreement</button>
        </div>
      </div>

      {agreements.some(a=>a.status==="recall") && (
        <div style={{background:C.redLight,border:`1px solid ${C.red}`,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,color:C.red}}>Recall required</div>
          <div style={{fontSize:12,color:C.red,marginTop:2}}>
            {agreements.filter(a=>a.status==="recall").map(a=>`${a.toolName} at ${a.shop}`).join(" · ")} — 2 missed months. Tool must be collected.
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:6,marginBottom:16}}>
        {["agreements","catalog","policy"].map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:"6px 16px",borderRadius:7,border:`1px solid ${tab===t?C.blue:C.border}`,background:tab===t?C.blueLight:C.white,color:tab===t?C.blue:C.inkMid,fontSize:12,cursor:"pointer"}}>
            {t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      {tab==="agreements" && (
        <div style={S.card}>
          <div style={{...S.tableHdr,gridTemplateColumns:"1fr 180px 100px 100px 100px 80px 160px"}}>
            <span>Tool</span><span>Shop</span><span>Rep</span><span>Min/month</span><span>Last month</span><span>Status</span><span>Action</span>
          </div>
          {agreements.map(agr=>(
            <div key={agr.id} style={{...S.tableRow,gridTemplateColumns:"1fr 180px 100px 100px 100px 80px 160px",background:agr.status==="recall"?C.redLight:agr.status==="warning"?C.amberLight:agr.status==="collected"?"#F3F4F6":"transparent",cursor:"pointer"}} onClick={()=>setViewContract(agr)}>
              <div>
                <div style={{fontWeight:500,color:agr.status==="collected"?C.inkLight:C.ink}}>{agr.toolName}</div>
                <div style={{fontSize:11,color:C.inkLight}}>Since {agr.startDate}{agr.endDate?" - Ended "+agr.endDate:""}</div>
              </div>
              <span style={{color:agr.status==="collected"?C.inkLight:C.ink}}>{agr.shop}</span>
              <span style={{color:C.inkLight}}>{agr.rep}</span>
              <span style={{color:C.inkLight}}>{agr.minUnits} units</span>
              <span style={{fontWeight:500,color:agr.lastMonthUnits>=agr.minUnits?C.teal:C.red}}>{agr.lastMonthUnits} units</span>
              <Badge type={agr.status}>{agr.status}</Badge>
              <div onClick={e=>e.stopPropagation()}>{statusAction(agr)}</div>
            </div>
          ))}
        </div>
      )}

      {tab==="catalog" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {tools.map(tool=>(
            <div key={tool.id} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px",opacity:tool.active?1:0.55}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"start",marginBottom:8}}>
                <div style={{fontSize:14,fontWeight:600,color:C.ink}}>{tool.name}</div>
                <Badge type={tool.active?"active":"inactive"}>{tool.active?"Active":"Inactive"}</Badge>
              </div>
              <div style={{fontSize:12,color:C.inkMid,marginBottom:10,lineHeight:1.5}}>{tool.desc}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,fontSize:12}}>
                <div style={{background:C.surface,borderRadius:6,padding:"6px 8px"}}><div style={{color:C.inkLight}}>Tool value</div><div style={{fontWeight:600,color:C.ink}}>${tool.value}</div></div>
                <div style={{background:C.surface,borderRadius:6,padding:"6px 8px"}}><div style={{color:C.inkLight}}>Monthly minimum</div><div style={{fontWeight:600,color:C.ink}}>{tool.minUnitsPerMonth} units</div></div>
                <div style={{background:C.surface,borderRadius:6,padding:"6px 8px"}}><div style={{color:C.inkLight}}>Currently loaned</div><div style={{fontWeight:600,color:C.ink}}>{tool.currentLoaned} of {tool.maxCirculation}</div></div>
                <div style={{background:C.surface,borderRadius:6,padding:"6px 8px"}}><div style={{color:C.inkLight}}>Category</div><div style={{fontWeight:600,color:C.ink}}>{tool.category}</div></div>
              </div>
              {isAdmin && (
                <div style={{display:"flex",gap:6,marginTop:10}}>
                  <button style={tool.active?S.btnDanger:S.btnSuccess} onClick={()=>setTools(t=>t.map(x=>x.id===tool.id?{...x,active:!x.active}:x))}>{tool.active?"Deactivate":"Activate"}</button>
                  <button style={S.btnSmall}>Edit</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab==="policy" && (
        <div style={S.card}>
          <div style={S.cardHdr}><span style={S.cardTitle}>Tool lending policy</span></div>
          <div style={{padding:"20px 24px"}}>
            {[
              {title:"Eligibility",icon:"1",text:"Only approved wholesale accounts in good standing may participate in the tool lending program. The account must have placed at least one successful order prior to tool placement."},
              {title:"Monthly minimum purchase",icon:"2",text:"Each tool has a defined monthly minimum purchase requirement (units of any MPC product). The shop must meet this minimum every calendar month to retain the tool. Requirements are set per-tool and visible in the catalog above."},
              {title:"First missed month — warning",icon:"3",text:"If a shop fails to meet the monthly minimum, a warning notice is automatically sent to the shop contact and their assigned rep. The rep is expected to make contact within 5 business days."},
              {title:"Second consecutive miss — recall",icon:"4",text:"If the minimum is missed for a second consecutive month, the agreement enters recall status. The assigned rep must physically collect the tool within 15 business days. The shop may not enter a new tool agreement for 12 months."},
              {title:"Tool condition",icon:"5",text:"Tools must be returned in working condition. Damage or loss is charged at the tool's full replacement value listed in the catalog. MPC reserves the right to inspect tools during any shop visit."},
              {title:"Reinstatement",icon:"6",text:"A shop whose tool was recalled may reapply after 12 months. A new agreement requires admin approval and a 2-month probation period with double the minimum purchase requirement."},
            ].map(p=>(
              <div key={p.icon} style={{display:"flex",gap:12,marginBottom:18,paddingBottom:18,borderBottom:`1px solid ${C.border}`}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:600,color:C.blue,flexShrink:0}}>{p.icon}</div>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:4}}>{p.title}</div>
                  <div style={{fontSize:13,color:C.inkMid,lineHeight:1.6}}>{p.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DEMO SEED SCRUBBERS ─────────────────────────────────────────────────────
// Brand-new reps (e.g. Adam Kanoon) should land on a 100% empty dashboard —
// no route customers, no prospects, no tool agreements, no certs that aren't
// theirs. Earlier app versions hydrated the first mount with INIT_* seed data
// and the auto-save effect persisted those seed rows to this browser's
// localStorage, so on later logins the seed kept re-appearing even after
// initial state was switched to empty.
//
// These predicates detect the untouched seed by its stamped markers (first /
// last rows, length). A rep who has actually added / edited any customer,
// prospect, or agreement will diff at one of these fields and be preserved.
// Any match is treated as "demo leakage — wipe" for the authed rep so the
// dashboard goes empty on next render.
function _isLegacyRouteSeed(arr) {
  if (!Array.isArray(arr) || arr.length !== 8) return false;
  return arr[0]?.id === 1 && arr[0]?.company === "Apex Car Care LLC"
      && arr[7]?.id === 8 && arr[7]?.company === "Inland Empire Auto";
}
function _isLegacyProspectSeed(arr) {
  if (!Array.isArray(arr) || arr.length !== 6) return false;
  return arr[0]?.id === 101 && arr[0]?.company === "Golden State Tire & Brake"
      && arr[5]?.id === 106 && arr[5]?.company === "Rancho Performance";
}

// ─── REP PORTAL (main shell) ──────────────────────────────────────────────────
export default function RepPortal({onExit, onSignOut, user}) {
  const [page, setPage] = useState("dashboard");
  // Inventory ledger — Supabase `inventory_movements` table (one row per movement,
  // rep_id = auth.uid()::text, RLS filters to this rep's rows only). The derived
  // `inventory` below uses admin's exact formula so admin and rep always agree.
  // No localStorage fallback is used for the displayed numbers — the cache inside
  // supabase.js getInventoryMovements is only a best-effort offline mirror.
  const [movements, setMovements] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [inventoryError, setInventoryError] = useState("");
  // Stock-receipt approval queue (Supabase `inventory_requests`, migration 0020).
  // Rep-side state for the "My Requests" panel — pending/approved/rejected/cancelled
  // rows scoped to this rep via RLS. Pending rows do NOT count toward on-hand;
  // only after admin approval does a rep_receive row land in inventory_movements
  // and feed the existing `inventory` useMemo above.
  const [repRequests, setRepRequests] = useState([]);
  const [requestsError, setRequestsError] = useState("");
  const [repInvoices, setRepInvoices] = useState(()=>{
    // Demo invoice IDs that must never count as real sales for authenticated users.
    // These were previously seeded into localStorage, inflating sold counts
    // (e.g. 24 phantom units of 7000, 100 phantom GL100).
    const demoIds = new Set(INIT_INVOICES.map(inv=>inv.id));
    try{
      const s=localStorage.getItem("mpcare_rep_invoices");
      if(s){
        const d=JSON.parse(s);
        if(Array.isArray(d)&&d.length>0) {
          // Real users: strip out any lingering demo invoices from cache
          if(user) { const filtered = d.filter(inv=>!demoIds.has(inv.id)); return filtered; }
          return d;
        }
      }
    }catch(e){}
    return user ? [] : INIT_INVOICES;
  });
  // Training videos — migration 0023 (Rule Zero). Supabase-first load +
  // realtime subscribe so any admin upload appears in the rep's TrainingHub
  // within a second on any device. INIT_TRAINING is intentionally not used
  // as a fallback here anymore — per the design Q2(a) decision, empty
  // Supabase renders as empty state (no stub content). The offline cache
  // inside listTrainingVideos handles Supabase-down / no-network cases
  // without falling back to the stale hardcoded seed.
  const [videos, setVideos] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await listTrainingVideos({ repVisibleOnly: true });
        if (!cancelled) setVideos(rows || []);
      } catch (e) {
        console.warn("[RepPortal] listTrainingVideos failed:", e?.message || e);
      }
    };
    load();
    const unsub = subscribeTrainingVideos(() => { if (!cancelled) load(); });
    return () => { cancelled = true; unsub && unsub(); };
  }, []);
  // Rep training progress — migration 0026 (Rule Zero). Previously `watched`
  // lived only as a mutation on the `videos` array above, which the effect
  // above REPLACES wholesale on every realtime training_videos change (any
  // admin edit to any video, anywhere) — silently wiping every rep's
  // progress and their visible certificate, with no persistence to restore
  // from. Now hydrated from Supabase and kept live via realtime subscribe,
  // same pattern as videos above. repId must be the Supabase auth uid
  // (not repKey, which can fall back to email/"demo_rep") — RLS on
  // rep_training_progress checks rep_id = auth.uid()::text.
  const repId = user?.id || null;
  const [progress, setProgress] = useState([]);
  useEffect(() => {
    if (!repId) return;
    let cancelled = false;
    const load = async () => {
      const rows = await listRepTrainingProgress(repId);
      if (!cancelled) setProgress(rows || []);
    };
    load();
    const unsub = subscribeRepTrainingProgress(repId, () => { if (!cancelled) load(); });
    return () => { cancelled = true; unsub && unsub(); };
  }, [repId]);
  const [materials, setMaterials] = useState(INIT_MATERIALS);
  const [tools, setTools] = useState(INIT_TOOLS);
  // Agreements = rep-specific tool-lending records. Authed reps start empty
  // and only see agreements they've created. INIT_AGREEMENTS is demo data
  // only — reserved for the unauthenticated showcase mode.
  const [agreements, setAgreements] = useState(user ? [] : INIT_AGREEMENTS);
  const [routeCustomers, setRouteCustomers] = useState(()=>{
    try {
      const s = localStorage.getItem("mpcare_rep_route_customers");
      if (s) {
        const d = JSON.parse(s);
        if (Array.isArray(d) && d.length > 0) {
          if (user && _isLegacyRouteSeed(d)) {
            try { localStorage.removeItem("mpcare_rep_route_customers"); } catch(e) {}
            return [];
          }
          return d;
        }
      }
    } catch(e) {}
    return user ? [] : INIT_ROUTE_CUSTOMERS;
  });
  const [prospects, setProspects] = useState(()=>{
    try {
      const s = localStorage.getItem("mpcare_rep_prospects");
      if (s) {
        const d = JSON.parse(s);
        if (Array.isArray(d) && d.length > 0) {
          if (user && _isLegacyProspectSeed(d)) {
            try { localStorage.removeItem("mpcare_rep_prospects"); } catch(e) {}
            return [];
          }
          return d;
        }
      }
    } catch(e) {}
    return user ? [] : INIT_POTENTIAL_CUSTOMERS;
  });
  // earnedCerts = certs this rep personally earned. Migration 0026 (Rule
  // Zero): previously plain useState([]) for every authed rep — a
  // certificate a rep had already earned vanished on refresh (or the
  // moment any admin edited any training video, which retriggered the
  // videos-hydrate effect above), and reappeared only if the rep re-clicked
  // "mark watched" on every required video again in that session. Now
  // hydrated from rep_certifications + realtime subscribe, so it's a
  // permanent, cross-device record once earned. Unauthenticated showcase
  // view keeps the demo sample (never touches Supabase).
  const [earnedCerts, setEarnedCerts] = useState(user ? [] : [
    {certNum:"MPC-REP-2025-00001", type:"rep_certification", name:"Marcus T.", shopName:"MotorPower Care", track:"tech", score:95, date:"Mar 28, 2025", completedVideos:[{title:"How to demo catalytic converter cleaner",score:100},{title:"DPF system explainer for diesel shops",score:90},{title:"How to handle objections at the counter",score:95},{title:"Using the wholesale portal — rep walkthrough",score:95}], signature:"Marcus T."}
  ]);
  useEffect(() => {
    if (!repId) return;
    let cancelled = false;
    const load = async () => {
      const row = await getRepCertification(repId);
      if (!cancelled) setEarnedCerts(row ? [repCertRowToClient(row)] : []);
    };
    load();
    const unsub = subscribeRepCertifications(repId, () => { if (!cancelled) load(); });
    return () => { cancelled = true; unsub && unsub(); };
  }, [repId]);
  const [showProfile, setShowProfile] = useState(false);
  // Build rep key for shared profile lookup (userId or email)
  const repKey = user?.id || user?.email || "demo_rep";
  const [profileData, setProfileData] = useState(()=>{
    // Priority: 1) shared rep profile store (admin edits — match by id or email), 2) user.profile from Supabase login, 3) demo defaults
    const shared = SHARED_REP_PROFILES.get(repKey) || (user?.email && SHARED_REP_PROFILES.get(user.email));
    if(shared && shared.name) return { name:shared.name, email:shared.email||"", phone:shared.phone||"", territory:shared.territory||"", avatar:shared.avatar||"", refCode:shared.refCode||"" };
    if(user?.profile) {
      const p = user.profile;
      const meta = user.user_metadata || {};
      return {
        name: p.full_name || meta.full_name || user.name || "Sales Rep",
        email: p.email || user.email || "",
        phone: p.phone || meta.phone || "",
        territory: p.address || meta.address || "",
        avatar: "",
        refCode: p.ref_code || meta.ref_code || ""
      };
    }
    return {name:"Marcus T.", email:"marcus@mpcare.com", phone:"619-555-0148", territory:"Southern California", avatar:"", refCode:"MPC-REP-4815"};
  });
  const [editingProfile, setEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({...profileData});

  // Sync profile from shared store (admin edits propagate here)
  const userEmail = user?.email;
  useEffect(()=>{
    const unsub = SHARED_REP_PROFILES.subscribe((profiles)=>{
      const updated = profiles[repKey] || (userEmail && profiles[userEmail]);
      if(updated && updated.name) {
        setProfileData(prev => {
          // Only update if something actually changed
          if(prev.name===updated.name && prev.email===(updated.email||"") && prev.phone===(updated.phone||"") && prev.territory===(updated.territory||"")) return prev;
          return { ...prev, name:updated.name, email:updated.email||prev.email, phone:updated.phone||prev.phone, territory:updated.territory||prev.territory, refCode:updated.refCode||prev.refCode };
        });
      }
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[repKey]);

  // Backfill rep ref code into customer_settings so admin search can find it, even
  // for reps whose ref code only lives in auth user_metadata (pre-dating the store).
  useEffect(()=>{
    if(!user?.id) return;
    const code = profileData?.refCode || user?.user_metadata?.ref_code;
    if(!code) return;
    saveCustomerAssignment(user.id, { refCode: code }).catch(()=>{});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[user?.id, profileData?.refCode]);

  // Prefill customer when creating invoice from route planner
  const [prefillInvoiceCustomer, setPrefillInvoiceCustomer] = useState(null);
  const createInvoiceForCustomer = (company) => {
    const allCustomers = [...routeCustomers, ...prospects];
    const acct = allCustomers.find(a=>a.company===company);
    if(acct) { setPrefillInvoiceCustomer(acct.id); setPage("invoices"); }
    else { setPage("invoices"); }
  };

  // View invoice from route planner — navigate to invoices page and open the invoice
  const [viewInvoiceFromRoute, setViewInvoiceFromRoute] = useState(null);
  const handleViewInvoice = (inv) => {
    setViewInvoiceFromRoute(inv);
    setPage("invoices");
  };

  // Subscribe to shared product state (admin deactivations/deletions)
  // Pass through ALL product fields — stripping fields (e.g. desc, stock, category)
  // breaks inventory display and invoice creation.
  // inventoryCatalog() is the ONE catalog source (SHARED_PRODUCTS first, the seed
  // display fallback only while it is empty) used by the admin Rep Inventory tab too.
  const [liveProducts, setLiveProducts] = useState(()=>inventoryCatalog());
  useEffect(()=>{
    return SHARED_PRODUCTS.subscribe(()=>{
      setLiveProducts(inventoryCatalog());
    });
  },[]);

  // ── Rep-scoped inventory ledger (Supabase inventory_movements) ──────────────
  // The rep identity used as rep_id in the ledger:
  //   • Live (Supabase) account → user.id === auth.uid(). Admin's assign rows
  //     target this uid and the rep RLS policy lets the rep read them.
  //   • Demo account (no Supabase session) → fall back to display name. RLS
  //     returns nothing (no auth.uid() match) and the rep sees only
  //     same-browser-cached movements via getInventoryMovements' localStorage
  //     fallback. This matches admin's behavior for demo reps.
  const inventoryRepKey = user?.id || profileData?.name || "demo_rep";

  // Reads EVERY page of this rep's ledger (no 1000-row cap) through the checked
  // helper, verified against the exact server count. A failed read is never silent: the
  // amber banner (inventoryError) says the numbers are the last saved ones and offers
  // Retry. The last GOOD in-memory ledger is kept on failure; the localStorage cache is
  // only adopted when this session has never had a good read at all.
  // The refresh runs through ONE coordinator (src/refreshCoordinator.js): never two
  // fetches at once, a request during a fetch triggers exactly one more run, and the
  // focus / tab-visible / 60 s triggers are throttled to one read per 15 s.
  // The CURRENT rep key lives in keyRef, so a fetch that was started for a previous
  // identity can neither be adopted nor re-run with the old key.
  const keyRef = useRef(inventoryRepKey);
  useEffect(() => { keyRef.current = inventoryRepKey; }, [inventoryRepKey]);
  const ledgerGoodKeyRef = useRef(null);     // rep key of the last successful read
  const ledgerRunRef = useRef(null);
  const ledgerRefresherRef = useRef(null);
  ledgerRunRef.current = async (ctx) => {
    const key = keyRef.current;
    // ctx.isCurrent() turns false when the coordinator abandoned this run at its deadline
    // (a fetch that never settles): a LATE result must not be adopted or touch a newer run.
    const current = () => !(ctx && typeof ctx.isCurrent === "function") || ctx.isCurrent();
    try {
      const res = await getInventoryMovementsChecked({ repId: key }, { cache: true });
      if(!current()) {
        console.warn("[RepPortal] a late inventory ledger result was ignored (the read had timed out) for rep", key);
        return false;
      }
      if(key !== keyRef.current) return "stale";      // the rep identity changed mid-fetch: drop the result, re-run with the new key
      if(res.ok) {
        ledgerGoodKeyRef.current = key;
        setMovements(res.rows || []);
        setInventoryError("");
        return true;
      }
      console.warn("[RepPortal] inventory ledger refresh failed for rep", key, ":", res.error);
      if(ledgerGoodKeyRef.current !== key) setMovements(res.rows || []);   // never had a good read: show the saved cache
      setInventoryError(res.error || "The inventory ledger could not be loaded.");
      return false;
    } catch(e) {
      console.warn("[RepPortal] Failed to load inventory_movements for rep", key, ":", e?.message||e);
      if(current()) setInventoryError(e?.message || "The inventory ledger could not be loaded.");
      return false;
    } finally {
      if(current()) setInventoryLoading(false);
    }
  };
  if(!ledgerRefresherRef.current) {
    ledgerRefresherRef.current = createRefreshCoordinator((ctx) => ledgerRunRef.current(ctx), {
      label: "RepPortal inventory ledger",
      // a read that never settles is FAILED: the same amber banner (with Retry) as any failed read
      onTimeout: (ms) => { setInventoryLoading(false); setInventoryError(`The inventory ledger did not answer within ${Math.round(ms/1000)} s, showing the last saved numbers. Use Retry.`); },
    });
  }
  // opts.throttle:true is for the focus / visible / timer triggers; everything else
  // (mount, Retry, realtime, a rep change) runs at once.
  const refreshInventoryLedger = useCallback((opts) => ledgerRefresherRef.current.request(opts), []);

  // Mount and every change of the rep identity (declared AFTER the keyRef effect, so the
  // run sees the new key).
  useEffect(() => { refreshInventoryLedger(); }, [inventoryRepKey, refreshInventoryLedger]);

  // Freshness: re-read when the tab/window regains focus or becomes visible (one call,
  // not two) and every 60 s while it is visible; all throttled to one read per 15 s.
  useEffect(() => attachFreshness(() => { refreshInventoryLedger({ throttle: true }); }), [refreshInventoryLedger]);

  // Realtime sync — admin assigns to me, or my own rep_receive lands, or any
  // warehouse row changes → re-fetch the rep-scoped ledger. RLS on the server
  // side already filters the stream to this rep's rep_id, so the filter here
  // is just belt-and-suspenders to skip a re-fetch on unrelated rows.
  useEffect(() => {
    const unsub = subscribeToInventoryChanges(({ source, row }) => {
      if(source === 'inventory_movements') {
        if(row && row.rep_id && row.rep_id !== inventoryRepKey) return;
        refreshInventoryLedger();
      }
      // warehouse_stock changes don't affect rep on-hand directly but may
      // indicate admin is reconciling after a rep_receive — refresh anyway.
      if(source === 'warehouse_stock') refreshInventoryLedger();
    });
    return unsub;
  }, [inventoryRepKey, refreshInventoryLedger]);

  // Rep-initiated stock receipt — REQUEST flow as of migration 0020.
  // Writes a row to `inventory_requests` with status='pending'. Admin reviews
  // (Approve writes the rep_receive ledger row, Reject leaves it audit-only).
  // RLS enforces: rep_id=auth.uid(), status='pending', no decision fields set.
  // Does NOT mutate inventory_movements directly (the old direct-insert path
  // and its inv_mov_rep_insert RLS policy were removed in 0020).
  const refreshRepRequests = useCallback(async () => {
    if (!inventoryRepKey) return;
    try {
      const rows = await listInventoryRequests({ repId: inventoryRepKey, limit: 200 });
      setRepRequests(rows || []);
      setRequestsError("");
    } catch (e) {
      console.warn("[RepPortal] Failed to load inventory_requests for rep", inventoryRepKey, ":", e?.message || e);
      setRequestsError(e?.message || "Failed to load stock requests.");
    }
  }, [inventoryRepKey]);

  const handleReceiveStock = useCallback(async ({ sku, qty, reason }) => {
    const prod = flattenCatalogForPicker(liveProducts).find(p => p.sku === sku);
    await createInventoryRequest({
      repId: inventoryRepKey,
      repName: profileData?.name || "",
      sku,
      productName: prod?.name || "",
      qty,
      reason: reason || "",
    });
    // Immediate refresh in case realtime is disabled on the project.
    await refreshRepRequests();
  }, [inventoryRepKey, profileData?.name, liveProducts, refreshRepRequests]);

  const handleCancelRequest = useCallback(async (id) => {
    try {
      await cancelInventoryRequest(id);
      await refreshRepRequests();
    } catch (e) {
      console.warn("[RepPortal] cancelInventoryRequest failed for id", id, ":", e?.message || e);
      setRequestsError(e?.message || "Failed to cancel request. Please try again.");
    }
  }, [refreshRepRequests]);

  // Initial fetch + realtime sync for inventory_requests. RLS scopes the stream
  // to this rep's own rows server-side. Any change (admin approve/reject, our
  // own insert/cancel landing) triggers a re-fetch so the panel stays in sync.
  useEffect(() => { refreshRepRequests(); }, [refreshRepRequests]);
  useEffect(() => {
    const unsub = subscribeInventoryRequests(({ row }) => {
      if (row && row.rep_id && row.rep_id !== inventoryRepKey) return;
      refreshRepRequests();
    });
    return unsub;
  }, [inventoryRepKey, refreshRepRequests]);

  // Subscribe to shared settings (admin commission rate changes)
  // Hydrate commission rate from localStorage-backed store on mount + subscribe to live changes
  const initRate = SHARED_SETTINGS.getRepRate(profileData.name);
  const [liveCommissionRate, setLiveCommissionRate] = useState(initRate ? initRate.rate : null);
  const [livePaySchedule, setLivePaySchedule] = useState(initRate ? initRate.paySchedule : null);
  useEffect(()=>{
    // Check stored value on mount (in case it was set before this component rendered)
    const stored = SHARED_SETTINGS.getRepRate(profileData.name);
    if(stored) { setLiveCommissionRate(stored.rate); setLivePaySchedule(stored.paySchedule); }
    return SHARED_SETTINGS.subscribe(({repRates})=>{
      const myRate = repRates[profileData.name];
      if(myRate) {
        setLiveCommissionRate(myRate.rate);
        setLivePaySchedule(myRate.paySchedule);
      }
    });
  },[profileData.name]);

  // FIX (2026-04-20): also pull rate from Supabase rep_commission_rates so a
  // rep signing in on a fresh device gets the correct rate even when this
  // browser's SHARED_SETTINGS localStorage hasn't been populated yet. This
  // closes the cross-device sync gap — admin's edit writes to both stores,
  // reps now read from both. Supabase wins when present because it's
  // cross-device authoritative; falls back to the SHARED_SETTINGS value
  // above if the Supabase row is missing (e.g. first-deploy rep whose rate
  // was only ever set in localStorage).
  useEffect(()=>{
    if (!user?.id) return;
    let cancelled = false;
    getRepCommissionRates().then(rows => {
      if (cancelled) return;
      const mine = (rows || []).find(r => String(r?.rep_id||"") === String(user.id));
      if (mine && mine.rate != null) setLiveCommissionRate(Number(mine.rate) || 0);
    }).catch(e => console.warn("[Rep] getRepCommissionRates failed:", e?.message || e));
    return () => { cancelled = true; };
  },[user?.id]);

  const saveProfile = () => {
    setProfileData({...editForm});
    setEditingProfile(false);
    // Persist to shared store so admin sees changes
    SHARED_REP_PROFILES.set(repKey, { name:editForm.name, email:editForm.email, phone:editForm.phone, territory:editForm.territory, avatar:editForm.avatar, refCode:editForm.refCode });
    // Also persist to Supabase (fire-and-forget)
    if(user?.id) {
      supabaseUpdateProfile(user.id, { full_name:editForm.name, phone:editForm.phone, address:editForm.territory }).catch(e=>console.warn("Profile DB sync:",e.message));
    }
  };
  const cancelEdit = () => { setEditForm({...profileData}); setEditingProfile(false); setAvatarState({kind:"idle",msg:""}); };
  const initials = profileData.name.split(" ").map(w=>w[0]).join("").toUpperCase();
  // Upload status for the avatar picker — shown next to the Upload photo button.
  const [avatarState, setAvatarState] = useState({kind:"idle",msg:""});
  // Previously this used URL.createObjectURL(file), which produces a blob URL
  // that dies the moment the tab reloads — that's why avatars looked saved
  // but came back empty after sign-out. Now we upload to Supabase Storage and
  // store the permanent public URL.
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if(!file) return;
    if(!user?.id) {
      setAvatarState({kind:"error",msg:"Sign in first, then try again."});
      return;
    }
    setAvatarState({kind:"uploading",msg:"Uploading…"});
    try {
      const url = await uploadAvatar(file, user.id);
      setEditForm(f=>({...f,avatar:url}));
      setAvatarState({kind:"success",msg:"Uploaded!"});
      setTimeout(()=>setAvatarState({kind:"idle",msg:""}), 1800);
    } catch(err) {
      console.warn("Avatar upload failed:", err?.message);
      setAvatarState({kind:"error",msg: err?.message || "Upload failed. Try again."});
    }
  };

  const pageTitles = {dashboard:"Dashboard",inventory:"My inventory",invoices:"Field invoices",sales:"Sales report",commission:"Commission",route:"Route planner",prospects:"Potential customers",training:"Training hub",marketing:"Marketing materials",tools:"Tool lending"};

  // URL routing — sync page state to browser URL (every page gets its own path including /rep/dashboard)
  useEffect(()=>{
    const path = "/rep/" + page;
    const qs = window.location.search;
    if(window.location.pathname !== path) window.history.pushState({page}, "", path + qs);
  },[page]);
  useEffect(()=>{
    const onPop = (e) => { if(e.state?.page) setPage(e.state.page); else setPage("dashboard"); };
    window.addEventListener("popstate", onPop);
    const initPath = window.location.pathname.replace(/^\/rep\/?/,"") || "dashboard";
    if(pageTitles[initPath]) setPage(initPath);
    return ()=>window.removeEventListener("popstate", onPop);
  },[]);

  // Persist rep state to localStorage
  useEffect(()=>{try{localStorage.setItem("mpcare_rep_route_customers",JSON.stringify(routeCustomers));}catch(e){}},[routeCustomers]);
  useEffect(()=>{try{localStorage.setItem("mpcare_rep_prospects",JSON.stringify(prospects));}catch(e){}},[prospects]);
  // Sync customers & prospects to shared store so admin control panel sees them
  useEffect(()=>{
    const rn = profileData?.name || "Sales Rep";
    SHARED_REP_CUSTOMERS.set(rn, { customers: routeCustomers, prospects });
  },[routeCustomers, prospects, profileData?.name]);
  // NOTE: no `mpcare_rep_inventory` localStorage persistence — inventory is
  // now derived from the Supabase `inventory_movements` ledger + invoices, so
  // persisting a snapshot would create a stale cache that disagrees with
  // admin's view. The ledger + invoices each have their own caches one layer
  // down (in supabase.js and SHARED_INVOICES respectively) for offline mode.
  useEffect(()=>{try{localStorage.setItem("mpcare_rep_invoices",JSON.stringify(repInvoices));}catch(e){}},[repInvoices]);

  // Subscribe to SHARED_INVOICES so Dashboard + Commission metrics reflect customer-portal orders
  // and invoices created from the route planner, not just local rep-created invoices.
  const [sharedInvoicesList, setSharedInvoicesList] = useState(()=>SHARED_INVOICES.getAll());
  useEffect(()=>SHARED_INVOICES.subscribe(setSharedInvoicesList),[]);

  // Cross-device sync warning banner state. Populated by RepInvoices.createInvoice
  // (via the onSyncPending prop) when a newly-created rep invoice writes to
  // localStorage successfully but the Supabase write to portal_orders fails.
  // The rep can keep working — the invoice is saved locally — but they need
  // to know cross-device visibility (admin, dealer, other laptops) is pending
  // until the retry loop pushes it. Banner auto-clears as soon as the
  // syncPortalOrders tick (mount + every 60s + window focus) observes that
  // the invoice id is now in portal_orders. User can also dismiss manually.
  const [syncPendingInvoiceIds, setSyncPendingInvoiceIds] = useState([]);
  // Plain-language reason per pending invoice (only set when it is known, e.g. the database
  // update for customer discounts, migration 0034, has not been applied yet).
  const [syncPendingReasons, setSyncPendingReasons] = useState({});
  const noteInvoiceSyncReason = (id, reason) => {
    if (!id || !reason) return;
    setSyncPendingReasons(prev => prev[id] === reason ? prev : { ...prev, [id]: reason });
  };
  const markInvoiceSyncPending = (id, reason) => {
    setSyncPendingInvoiceIds(prev => prev.includes(id) ? prev : [...prev, id]);
    noteInvoiceSyncReason(id, reason);
  };

  // When the portal_orders sync (below) last SUCCEEDED, and a stable way to trigger it
  // on demand — surfaced on the Dashboard so a rep can see how fresh the sales figures
  // are and force an immediate refresh instead of waiting on the 60s/focus/realtime ticks.
  const [lastOrdersSyncAt, setLastOrdersSyncAt] = useState(null);
  const syncNowRef = useRef(null);

  // Phase A — rep-customer sync warning state. Populated when a write to
  // rep_route_customers fails (rep manually added/removed a customer and
  // Supabase rejected). The local state still saves so the rep doesn't lose
  // the edit; banner nags until retry succeeds. Follows the same amber-bar
  // pattern as the invoice sync warning above.
  const [customerSyncPendingKeys, setCustomerSyncPendingKeys] = useState([]);
  const markCustomerSyncPending = (key) => {
    setCustomerSyncPendingKeys(prev => prev.includes(key) ? prev : [...prev, key]);
  };
  const clearCustomerSyncPending = (key) => {
    setCustomerSyncPendingKeys(prev => prev.filter(k => k !== key));
  };

  // Phase B — rep visit sync warning state. Populated when a Supabase write
  // to shop_visits fails after a rep logged a visit (from the Route Planner
  // or the Potential Customers screen). Local state still saves so the rep
  // doesn't lose the visit note; banner persists until retry succeeds or the
  // rep dismisses it. Uses the client_id as the stable tracking key so the
  // realtime subscribe / re-poll can auto-clear once the insert lands.
  const [visitSyncPendingIds, setVisitSyncPendingIds] = useState([]);
  const markVisitSyncPending = (id) => {
    setVisitSyncPendingIds(prev => prev.includes(id) ? prev : [...prev, id]);
  };

  // Phase C — material-order sync warning state. Populated when a Supabase
  // write to material_orders fails after a rep places a marketing-materials
  // order. Local state still saves (rep keeps the confirmation); banner nags
  // until the mount/realtime retry tick observes the row in Supabase and
  // auto-clears it via the hydration merge path.
  const [materialOrderSyncPendingIds, setMaterialOrderSyncPendingIds] = useState([]);
  const markMaterialOrderSyncPending = (id) => {
    setMaterialOrderSyncPendingIds(prev => prev.includes(id) ? prev : [...prev, id]);
  };

  // ── Rep identity for rep_route_customers writes ──────────────────────────
  // The rep_route_customers table is keyed by rep_id + customer_key. Rep_id
  // matches the rep's Supabase profiles.id (UUID as text). user.id IS that
  // UUID when the rep is signed in. When the rep isn't signed in (the
  // unauthenticated showcase view), repIdForSync stays null and the Supabase
  // helpers below no-op — everything keeps working from localStorage only.
  const repIdForSync = user?.id ? String(user.id) : null;

  // Admin-assigned dealers for this rep. Pulled from customer_settings (admin assigns
  // rep → dealer there) plus the profiles table (to resolve user_id → company name).
  // Without this, a dealer who was admin-assigned to the rep but NOT on the rep's own
  // route-planner list would have their orders drop on the floor. That's the core bug.
  const [adminAssignedCompanies, setAdminAssignedCompanies] = useState(new Set());
  const [adminAssignedUserIds, setAdminAssignedUserIds] = useState(new Set());

  // Cross-device sync: pull portal_orders + customer_settings + profiles from Supabase
  // on mount. Orders are merged into SHARED_INVOICES (preserving userId — the strongest
  // match key — so dealer orders placed in a different browser still route to this rep).
  // Admin-assigned dealer list is stored in state for the mergedInvoices matcher below.
  //
  // UPDATE: the order portion of the sync is ALSO re-run every 60s and on
  // window focus, so a rep who's already logged in sees a dealer's new order
  // without having to manually refresh the page. The customer-assignments
  // fetch still runs only on mount (those don't change often and require a
  // separate admin action).
  //
  // lastOrdersSyncAt / syncNowRef: surfaced on the Dashboard as a "synced Xs
  // ago" indicator + a manual "Refresh now" button, so a rep who notices a
  // number looks stale (e.g. comparing a phone against a laptop) can force an
  // immediate resync instead of waiting up to 60s for the next tick or hoping
  // a focus/visibility event fires. syncNowRef always points at the CURRENT
  // closure of syncPortalOrders (reassigned every effect run) so the button
  // never calls a stale one.
  useEffect(()=>{
    let mounted = true;
    let profileMapRef = {};
    const norm = s => String(s || "").trim().toLowerCase();
    const myNameNorm = norm(profileData.name);

    // Idempotent: reads SHARED_INVOICES first, only adds rows that aren't
    // already there (by id or orderId). Safe to call repeatedly.
    // Rep remittances (0033): the company's record that this rep handed the payment in.
    // Own rows only (RLS + explicit column list). Replaces the display store ONLY on a
    // successful fetch, then applies the forward-only cache mirror. Degrades silently
    // (a console warning) when the table is missing or the read fails.
    // A response is dropped when a newer fetch of this effect started (latest wins) or when a local
    // remittance mutation happened after it started (remittanceSync), so an old list never re-marks
    // a just-reversed invoice as paid.
    const remitFetchGate = createFetchGate();
    const discBannerShown = new Set();   // invoice ids already put back on the sync-pending banner during this page load (migration 0034 missing)
    const syncRemittances = async () => {
      if(!repIdForSync) return;
      const ticket = remitFetchGate.begin();
      try {
        const res = await listMyRemittances(repIdForSync);
        if(!mounted) return;
        if(!remitFetchGate.isCurrent(ticket)) return;
        if(!res.ok) { console.warn("[RepPortal] listMyRemittances failed; keeping the previous view:", res.error); return; }
        MY_REMITTANCES.setFromRows(res.rows);
        SHARED_INVOICES.applyRemittance(res.rows);
      } catch(e) { console.warn("[RepPortal] syncRemittances failed:", e?.message || e); }
    };
    const syncPortalOrders = async () => {
      syncRemittances();
      try {
        const fetchStartedAt = Date.now();   // a revert is skipped for adjustments newer than this
        const dbOrders = await getAllPortalOrders();
        if(!mounted) return;
        setLastOrdersSyncAt(Date.now());   // a genuinely fresh read landed, whatever happens below
        const rows = dbOrders || [];
        const existing = SHARED_INVOICES.getAll();
        const existingIds = new Set(existing.map(i=>i.id).concat(existing.map(i=>i.orderId)).filter(Boolean));

        // ── Download: Supabase → localStorage ───────────────────────────────
        // Pulls in orders placed on another device (including dealer
        // checkouts + rep-placed orders from a different laptop) so every
        // device shows the same list.
        rows.forEach(o => {
          const oId = o.invoice_ref || o.invoice_id || o.order_ref || o.order_id || o.id;
          const oOrderId = o.order_ref || o.order_id || o.id;
          if(existingIds.has(oId) || existingIds.has(oOrderId) || existingIds.has(String(o.id))) return;
          let items = [];
          try { items = typeof o.items === "string" ? JSON.parse(o.items) : (o.items || []); } catch(e) {}
          const prof = o.user_id ? profileMapRef[o.user_id] : null;
          const custName = o.customer || prof?.company || "Customer";
          SHARED_INVOICES.add({
            id: oId, orderId: oOrderId,
            date: o.created_at ? new Date(o.created_at).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}) : "",
            customer: custName, items,
            // Preserve dealer's supabase id so user_id matching works on the rep side
            // even when the rep name was never stamped on the order (inv.rep empty).
            userId: o.user_id || "",
            customerEmail: o.customer_email || prof?.email || "",
            total: o.total || 0, status: o.status || "outstanding",
            paymentTerms: o.payment_terms || "", rep: o.rep || "",
            source: o.source || "portal_order", fromDb: true,
            // Delivery fields: a dealer order delivered by a rep takes stock out of
            // that rep (inventoryMath), so they must reach every device.
            deliveryMethod: o.delivery_method || "", deliveredByRep: o.delivered_by_rep || "",
            // Account discount (0034): {} for a row without one, so older invoices are exactly as before.
            ...invoiceDiscountFields(o)
          });
          existingIds.add(oId);
        });
        try { SHARED_INVOICES.applyDelivery(rows); }
        catch(e) { console.warn("[RepPortal] applyDelivery failed:", e?.message || e); }

        // ── Forward-only void propagation (admin void → this device) ────────
        // Only flips invoices the DB says are cancelled. Never touches paid /
        // shipping / items. Also patches the rep's OWN repInvoices copy for
        // those ids only, surgically. (In mergedInvoices the SHARED status wins,
        // but the Invoices page renders repInvoices directly, so it needs its
        // own patch.) The repInvoices patch is independent of what
        // applyServerState reports as changed: the shared cache may already be
        // cancelled while this device's repInvoices copy is not.
        try {
          SHARED_INVOICES.applyServerState(rows, { allowRevert: true, allowPaidPatch: true, fetchStartedAt }); // rows = getAllPortalOrders batch (newest per invoice); rep device: a rep-paid INV-F invoice still receives the admin adjustment
          const cancelledRows = rows.filter(r => r && r.status === "cancelled");
          if(cancelledRows.length && mounted) {
            setRepInvoices(prev => {
              let changedAny = false;
              const next = prev.map(inv => {
                const row = cancelledRows.find(r => {
                  const ref = r.invoice_ref || r.invoice_id;
                  return ref ? ref === inv.id : (r.order_ref && inv.orderId === r.order_ref);
                });
                if(!row) return inv;
                const patch = serverCancelPatch(inv, row);
                if(!patch) return inv;
                changedAny = true;
                return { ...inv, ...patch };
              });
              return changedAny ? next : prev;
            });
          }
        } catch(e) { console.warn("[RepPortal] applyServerState (cancel propagation) failed:", e?.message || e); }

        // ── Forward-only adjustment propagation (approved rep adjustment) ────
        // SHARED_INVOICES.applyServerState above already applied it to the
        // shared cache. The rep's OWN repInvoices copy wins over SHARED for
        // items / total in mergedInvoices, so it needs the same idempotent
        // patch (keyed by adjustment_request_id). Never touches status, paid,
        // shipping or notes.
        try {
          // Rows with a marker (forward) plus unmarked rows (revert candidates:
          // adjustmentPatch only acts on them when the local copy carries a marker).
          const adjRows = rows.filter(r => !!r);
          if(adjRows.length && mounted) {
            setRepInvoices(prev => {
              let changedAny = false;
              const next = prev.map(inv => {
                const row = adjRows.find(r => {
                  const ref = r.invoice_ref || r.invoice_id;
                  return ref ? ref === inv.id : (r.order_ref && inv.orderId === r.order_ref);
                });
                if(!row) return inv;
                const patch = adjustmentPatch(inv, row, { allowRevert: true, allowPaidPatch: true, fetchStartedAt }); // batch rows are the deduped newest; rep own copy (rep-paid INV-F still gets the adjustment)
                if(!patch) return inv;
                changedAny = true;
                return { ...inv, ...patch };
              });
              return changedAny ? next : prev;
            });
          }
        } catch(e) { console.warn("[RepPortal] adjustment propagation to repInvoices failed:", e?.message || e); }

        // ── Upload backfill: localStorage → Supabase ────────────────────────
        // Recover any rep-created invoices that were stamped into this
        // browser's localStorage by OLDER builds that didn't call
        // savePortalOrder. Without this one-time push, orders the rep
        // created on an earlier deploy stay stuck on that single device
        // forever. Running this inside the existing sync loop (not a
        // separate useEffect) guarantees it fires AFTER the download so
        // existingRefs reflects the freshest Supabase state.
        //
        // Guards (each row must pass ALL of them before we push):
        //   • repCreated: true         — only rep-invoiced rows qualify
        //   • !fromDb                  — never push a row we just pulled
        //   • not in pushedSet         — per-id flag (localStorage key
        //                                 mpcare_rep_invoices_pushed) so
        //                                 each invoice is attempted at most
        //                                 once per browser even if the push
        //                                 throws; prevents an endless retry
        //                                 storm on every 60-second tick
        //   • rep matches this rep     — namesMatchFuzzy stops a shared
        //                                 browser from uploading another
        //                                 rep's stale data as if it were ours
        //   • invoice_ref not already  — existingRefs built from what we
        //     on Supabase                just pulled; duplicate pushes
        //                                 become no-ops, not dupe rows.
        //                                 There's no DB UNIQUE constraint,
        //                                 so this application-level check
        //                                 is the only guard.
        const existingRefs = new Set();
        rows.forEach(o => {
          if(o.invoice_ref) existingRefs.add(o.invoice_ref);
          if(o.order_ref)   existingRefs.add(o.order_ref);
          if(o.id != null)  existingRefs.add(String(o.id));
        });

        // Auto-clear the sync-pending banner for any invoice that has now
        // landed in portal_orders (either this backfill tick just pushed it,
        // or a different device pushed it, or the realtime subscription on
        // another admin already pulled it). Banner is dismissed silently —
        // the rep's invoice is now visible cross-device, no further action
        // needed from them.
        setSyncPendingInvoiceIds(prev => prev.filter(id => !(existingRefs.has(id) || existingRefs.has(String(id)))));

        const PUSHED_LS_KEY = "mpcare_rep_invoices_pushed";
        let pushedSet = new Set();
        try { const raw = localStorage.getItem(PUSHED_LS_KEY); if(raw) pushedSet = new Set(JSON.parse(raw)); } catch(e) {}
        const locals = SHARED_INVOICES.getAll();
        const toPush = selectInvoicesToPush({ locals, pushedSet, existingRefs, repName: profileData.name, namesMatch: namesMatchFuzzy });
        // The push loop lives in invoiceBackfill.js (so it can be executed in tests). Only a VERIFIED
        // non-null return marks an invoice as pushed: the previous version marked pushed even on a null
        // return, which stuck a transiently failed invoice in this browser forever. Per CLAUDE.md §0
        // (Architectural Rule Zero) the Supabase row is the authoritative source, so a null means we MUST
        // retry on the next tick (60 s cadence). A discounted invoice is retried with the SAME subtotal /
        // discount it was created with (never recomputed). If that retry fails only because the database
        // update 0034 is missing, the invoice goes back on the sync-pending banner, which is in-memory
        // state and was lost on a reload: once per page load, so a rep who dismissed it is not nagged.
        await pushLocalInvoices({
          toPush, pushedSet, save: savePortalOrder, repName: profileData.name, isMounted: () => mounted,
          onDiscountColumnsMissing: (inv, info) => {
            setSyncPendingReasons(prev => prev[inv.id] === info.message ? prev : { ...prev, [inv.id]: info.message });
            if(!discBannerShown.has(inv.id)) {
              discBannerShown.add(inv.id);
              setSyncPendingInvoiceIds(prev => prev.includes(inv.id) ? prev : [...prev, inv.id]);
            }
          },
        });
        try { localStorage.setItem(PUSHED_LS_KEY, JSON.stringify([...pushedSet])); } catch(e) {}
      } catch(e) { console.warn("Rep portal: portal_orders sync failed:", e.message); }
    };
    syncNowRef.current = syncPortalOrders;

    (async ()=>{
      // Build profile map once — used for both the order merge and the dealer list.
      try {
        const profs = await getAllProfiles();
        profs.forEach(p => { profileMapRef[p.id] = { company: p.company || p.full_name || p.email || "Customer", email: p.email || "" }; });
      } catch(e) {}

      // Pull customer_settings: {user_id → {rep, ...}}. Filter to rows where rep
      // matches the logged-in rep's name (case-insensitive + trimmed so admin-entered
      // strings with minor casing drift still bind).
      try {
        const assignments = await getAllCustomerAssignments();
        if(mounted && assignments && myNameNorm) {
          const companies = new Set();
          const userIds = new Set();
          Object.entries(assignments).forEach(([uid, a]) => {
            // Fuzzy match the rep name admin typed in customer_settings
            // vs the rep's profile name. Without this, a dealer admin-
            // assigned to "Marcus T." is invisible to the rep logged in
            // as "Marcus Thompson".
            if(a && namesMatchFuzzy(a.rep, profileData.name)) {
              userIds.add(uid);
              const prof = profileMapRef[uid];
              if(prof?.company) companies.add(norm(prof.company));
            }
          });
          setAdminAssignedCompanies(companies);
          setAdminAssignedUserIds(userIds);
        }
      } catch(e) { console.warn("Rep portal: could not fetch customer assignments:", e.message); }

      // First order sync (same code path as the interval / focus handlers use).
      await syncPortalOrders();
    })();

    // Poll for new orders every 60s while the rep portal is open. Interval
    // is cheap: a single Supabase select, only pushes delta rows.
    const iv = setInterval(syncPortalOrders, 60000);
    // When the rep tab regains focus (e.g. after seeing a notification from
    // another app), resync immediately so the new order shows up without a
    // up-to-60s wait. Mobile Safari/Chrome frequently foreground a backgrounded
    // tab (switching apps at a shop) via visibilitychange WITHOUT firing focus,
    // so both are wired — this is the most likely reason a phone session lagged
    // behind a laptop session that never left the foreground.
    const onFocus = () => { syncPortalOrders(); };
    window.addEventListener("focus", onFocus);
    const onVisible = () => { if(document.visibilityState === "visible") syncPortalOrders(); };
    document.addEventListener("visibilitychange", onVisible);
    // An admin decision on an adjustment / void request changes portal_orders:
    // resync right away instead of waiting up to 60s for the next tick.
    const unsubAdj = subscribeAdjustmentRequests(({ row }) => {
      if(row && row.status === "approved") syncPortalOrders();
    });
    const unsubVoid = subscribeInvoiceVoidRequests(({ row }) => {
      if(row && row.status === "approved") syncPortalOrders();
    });
    // An admin void / adjust changes portal_orders: a realtime SIGNAL (never the
    // event row itself, which is never applied here) triggers the same full,
    // guarded syncPortalOrders() as the poll. Coalesced so a burst of dealer
    // orders elsewhere causes one resync. Own random channel name (PR #85 lesson).
    let ordersSignalTimer = null;
    const unsubOrders = subscribePortalOrdersSignal(() => {
      if(ordersSignalTimer) return;
      ordersSignalTimer = setTimeout(() => { ordersSignalTimer = null; if(mounted) syncPortalOrders(); }, 1500);
    });
    // An admin marking / reversing "received from the rep": re-fetch (the event row is never applied).
    // Realtime bursts collapse into one fetch (500 ms debounce).
    const remitDeb = createDebounced(() => { if(mounted) syncRemittances(); });
    const unsubRemit = subscribeRemittances(() => { if(mounted) remitDeb.trigger(); });

    return ()=>{
      mounted = false;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      if(syncNowRef.current === syncPortalOrders) syncNowRef.current = null;
      unsubAdj && unsubAdj();
      unsubVoid && unsubVoid();
      unsubOrders && unsubOrders();
      unsubRemit && unsubRemit();
      remitDeb.cancel();
      // Rep sign-out / rep id change: empty the IN-MEMORY display mirror only (nothing stored is touched),
      // so the next rep never sees the previous rep's rows.
      MY_REMITTANCES.clear();
      if(ordersSignalTimer) { clearTimeout(ordersSignalTimer); ordersSignalTimer = null; }
    };
  },[profileData.name, repIdForSync]);

  // ── Phase A — rep_route_customers cross-device sync ──────────────────────
  // Fetches the rep's route customers + prospects from Supabase on mount,
  // merges them into state, backfills any localStorage entries that don't
  // yet have a Supabase row, and subscribes to realtime so admin's
  // "assign dealer to this rep" action propagates live.
  //
  // CLAUDE.md §0 (Architectural Rule Zero) compliance:
  //   • Supabase is the source of truth (fetch on mount, subscribe for live
  //     updates). localStorage stays as a read-only offline cache.
  //   • localStorage is never cleared or deleted from — even if Supabase
  //     rejects a write, the local entry stays so the rep keeps working.
  //   • Backfill is idempotent: reads both sides, only inserts missing rows.
  //   • Failures surface via the customerSyncPendingKeys banner (same amber
  //     pattern used by the invoice sync).
  useEffect(() => {
    if (!repIdForSync) return; // unauthenticated showcase — no sync possible
    let mounted = true;
    const repNameNow = profileData?.name || "";

    const mergeSupabaseRows = (rows) => {
      // Convert DB rows into the shape the existing route-planner UI expects.
      // customer_data JSONB already holds {company, contact, email, phone, ...};
      // we augment with a stable numeric id (derived from the row's
      // bigserial primary key) so the rest of the UI keeps working unchanged.
      const toLocal = (r) => ({
        ...(r.customer_data || {}),
        id: r.customer_data?.id || r.id,           // preserve original id if present, else use row id
        _supabaseRowId: r.id,                       // audit only
        _supabaseKey:   r.customer_key,             // for later upsert/deactivate
        _supabaseSource: r.source,                  // 'admin_assign' | 'rep_manual'
      });
      const liveCustomers = rows.filter(r => !r.is_prospect).map(toLocal);
      const liveProspects = rows.filter(r =>  r.is_prospect).map(toLocal);

      // Merge strategy: Supabase-first union with localStorage. A local entry
      // whose customer_key already appears in the Supabase set is dropped from
      // the local-only bucket (Supabase wins per Rule Zero). A local entry
      // that doesn't appear in Supabase yet is kept in state AND queued for
      // backfill below.
      setRouteCustomers(prev => {
        const supabaseKeys = new Set(liveCustomers.map(c => String(c._supabaseKey)));
        const localOnly = (prev || []).filter(c => {
          const k = String(c.userId || c.supabaseId || c.user_id || `local-${c.id}`);
          return !supabaseKeys.has(k);
        });
        return [...liveCustomers, ...localOnly];
      });
      setProspects(prev => {
        const supabaseKeys = new Set(liveProspects.map(c => String(c._supabaseKey)));
        const localOnly = (prev || []).filter(c => {
          const k = String(c.userId || c.supabaseId || c.user_id || `local-${c.id}`);
          return !supabaseKeys.has(k);
        });
        return [...liveProspects, ...localOnly];
      });
    };

    const backfillLocalOnly = async (supabaseRows) => {
      // Idempotent: for each localStorage entry (via routeCustomers / prospects
      // state AND the SHARED_REP_CUSTOMERS store), compute the customer_key
      // and insert only if no Supabase row exists for that key.
      const existingKeys = new Set((supabaseRows || []).map(r => String(r.customer_key)));
      const sharedLocal = SHARED_REP_CUSTOMERS.get(repNameNow) || {};
      const allLocalCustomers = [
        ...(routeCustomers || []),
        ...((sharedLocal.customers || []).filter(c =>
          !(routeCustomers || []).some(rc => rc.id === c.id))),
      ];
      const allLocalProspects = [
        ...(prospects || []),
        ...((sharedLocal.prospects || []).filter(p =>
          !(prospects || []).some(pp => pp.id === p.id))),
      ];
      let synced = 0, inserted = 0, failed = 0;
      const todo = [
        ...allLocalCustomers.map(c => ({ c, isProspect: false })),
        ...allLocalProspects.map(c => ({ c, isProspect: true })),
      ];
      for (const { c, isProspect } of todo) {
        if (!mounted) break;
        if (!c || !c.company) continue;
        const key = String(c.userId || c.supabaseId || c.user_id || `local-${c.id || Date.now()}`);
        if (existingKeys.has(key)) { synced++; continue; }
        const row = await upsertRepRouteCustomer({
          repId: repIdForSync,
          repName: repNameNow,
          customerKey: key,
          customerData: c,
          isProspect,
          source: 'rep_manual',
          active: true,
        });
        if (row) inserted++; else failed++;
      }
      if (inserted > 0 || failed > 0) {
        console.info(`[rep_route_customers backfill] ${synced} already synced, ${inserted} newly inserted, ${failed} failed.`);
      }
      return { synced, inserted, failed };
    };

    (async () => {
      try {
        const rows = await listRepRouteCustomers(repIdForSync);
        if (!mounted) return;
        mergeSupabaseRows(rows);
        await backfillLocalOnly(rows);
        // Re-fetch after backfill so state has the just-inserted rows too.
        if (!mounted) return;
        const refetched = await listRepRouteCustomers(repIdForSync);
        if (!mounted) return;
        mergeSupabaseRows(refetched);
      } catch(e) {
        console.warn("[rep_route_customers mount] sync failed:", e?.message || e);
      }
    })();

    // Realtime: admin assigns a new dealer to this rep → row appears within
    // seconds without a refresh. Gracefully no-ops if realtime is disabled.
    const unsub = subscribeRepRouteCustomers(repIdForSync, async () => {
      if (!mounted) return;
      const rows = await listRepRouteCustomers(repIdForSync);
      if (!mounted) return;
      mergeSupabaseRows(rows);
    });

    return () => { mounted = false; if (typeof unsub === "function") unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repIdForSync, profileData?.name]);

  // Phase A — diff-based write path. Any user action that modifies
  // routeCustomers / prospects (add, edit, remove in the Route Planner UI)
  // triggers this effect. We compare the incoming state to the previous
  // state held in a ref and fire idempotent upsert / deactivate per diff
  // entry. localStorage is still mirrored via the existing effects below —
  // this adds the Supabase write on top, preserving Rule Zero ordering.
  //
  // The prevRef.initialized guard SKIPS the first run. The hydration
  // useEffect above sets state from Supabase on mount; firing writes for
  // that change would be a loop (Supabase → state → Supabase with same
  // data). After the first run the ref is initialized and subsequent
  // state changes (which are genuinely user-driven) sync correctly.
  const repCustomersPrevRef = useRef({ customers: [], prospects: [], initialized: false });
  useEffect(() => {
    if (!repIdForSync) return;
    const keyOf = (c) =>
      String(c?.userId || c?.supabaseId || c?.user_id || c?._supabaseKey || `local-${c?.id}`);
    const prev = repCustomersPrevRef.current;
    if (!prev.initialized) {
      repCustomersPrevRef.current = { customers: routeCustomers || [], prospects: prospects || [], initialized: true };
      return;
    }
    const currentKeys = new Set([
      ...(routeCustomers || []).map(keyOf),
      ...(prospects     || []).map(keyOf),
    ]);
    const removedKeys = [];
    [...(prev.customers || []), ...(prev.prospects || [])].forEach(c => {
      const k = keyOf(c);
      if (!currentKeys.has(k)) removedKeys.push(k);
    });
    // Fire writes in order: upsert additions/edits first (so the rep's
    // latest state is persisted), then deactivate removals.
    (async () => {
      const repName = profileData?.name || "";
      for (const c of (routeCustomers || [])) {
        const k = keyOf(c);
        const prevEq = [...(prev.customers || []), ...(prev.prospects || [])].find(p => keyOf(p) === k);
        if (prevEq && JSON.stringify(prevEq) === JSON.stringify(c)) continue; // unchanged
        const row = await upsertRepRouteCustomer({
          repId: repIdForSync, repName,
          customerKey: k, customerData: c,
          isProspect: false,
          source: c._supabaseSource || 'rep_manual',
          active: true,
        });
        if (!row) markCustomerSyncPending(k); else clearCustomerSyncPending(k);
      }
      for (const c of (prospects || [])) {
        const k = keyOf(c);
        const prevEq = [...(prev.customers || []), ...(prev.prospects || [])].find(p => keyOf(p) === k);
        if (prevEq && JSON.stringify(prevEq) === JSON.stringify(c)) continue;
        const row = await upsertRepRouteCustomer({
          repId: repIdForSync, repName,
          customerKey: k, customerData: c,
          isProspect: true,
          source: c._supabaseSource || 'rep_manual',
          active: true,
        });
        if (!row) markCustomerSyncPending(k); else clearCustomerSyncPending(k);
      }
      for (const k of removedKeys) {
        // Soft delete (active=false); never DELETE. Unknown-row deactivate
        // is a no-op on the DB side so it's safe.
        await deactivateRepRouteCustomer(repIdForSync, k);
      }
    })();
    repCustomersPrevRef.current = { customers: routeCustomers || [], prospects: prospects || [], initialized: true };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCustomers, prospects, repIdForSync]);

  // ── Phase B — shop_visits cross-device sync + backfill ───────────────────
  // Fetches the rep's visit log from Supabase on mount, merges into the
  // SHARED_VISITS localStorage store (mirrors onto every SHARED_VISITS
  // subscriber — admin panel, rep-facing visit lists). Backfills any
  // localStorage-only entries not yet in Supabase, keyed by client_id for
  // exact-key idempotency. Subscribes to realtime so admin + other-device
  // views sync live.
  //
  // CLAUDE.md §0 (Architectural Rule Zero) compliance:
  //   • Supabase is the source of truth (fetch on mount + subscribe).
  //   • localStorage is a mirror. Never cleared, never deleted from.
  //   • Backfill is idempotent via client_id unique index — re-runs are
  //     safe no-ops on rows already synced.
  //   • If Supabase write fails in logVisit, localStorage still saves so
  //     the rep doesn't lose the visit note. Amber banner surfaces the
  //     pending sync state until retry succeeds.
  useEffect(() => {
    if (!repIdForSync) return; // unauthenticated showcase — no sync possible
    let mounted = true;

    // Map a shop_visits DB row into the SHARED_VISITS entry shape so the
    // rest of the app (which subscribes to SHARED_VISITS) renders without
    // any shape changes. DB column names → JS store field names.
    const dbRowToVisit = (r) => ({
      id: r.client_id || r.id,              // prefer client_id (stable across devices); fall back to uuid
      _supabaseUuid: r.id,                  // audit only
      rep: r.rep_name || "Sales Rep",
      company: r.shop_name || "",
      date: r.visited_at ? new Date(r.visited_at).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}) : "",
      note: r.notes || "",
      type: r.visit_type || "shop_visit",
      duration: Number(r.duration_minutes) || 30,
      createdAt: r.created_at || r.visited_at || new Date().toISOString(),
      fromDb: true,
    });

    const syncShopVisits = async () => {
      try {
        const rows = await listShopVisits(repIdForSync);
        if (!mounted) return;
        // Merge rows into SHARED_VISITS: add rows whose id (client_id
        // preferred) isn't already in the store. Never remove existing
        // entries — localStorage is a superset cache.
        const existing = SHARED_VISITS.getAll();
        const existingIds = new Set(existing.map(v => String(v.id)));
        let added = 0;
        for (const r of rows) {
          const v = dbRowToVisit(r);
          if (existingIds.has(String(v.id))) {
            // Auto-clear banner for any id that has now landed.
            setVisitSyncPendingIds(prev => prev.filter(k => String(k) !== String(v.id)));
            continue;
          }
          SHARED_VISITS.add(v);
          existingIds.add(String(v.id));
          added++;
        }
        if (added > 0) console.info(`[shop_visits hydrate] merged ${added} row(s) from Supabase into SHARED_VISITS.`);

        // Backfill localStorage → Supabase. Exact-key dedupe via client_id
        // guarantees re-runs are safe. Only push this rep's own rows —
        // fuzzy name match mirrors the invoice/customer backfill guards.
        const local = SHARED_VISITS.getAll();
        const supabaseClientIds = new Set((rows || []).map(r => String(r.client_id || "")));
        let synced = 0, inserted = 0, failed = 0;
        const repNameNow = profileData?.name || "";
        for (const v of local) {
          if (!mounted) break;
          if (!v || !v.id) continue;
          if (v.fromDb) continue; // came from Supabase this run — don't echo back
          if (!namesMatchFuzzy(v.rep, repNameNow)) continue;
          if (supabaseClientIds.has(String(v.id))) { synced++; continue; }
          const row = await upsertShopVisit({
            clientId: String(v.id),
            repId: repIdForSync,
            repName: v.rep || repNameNow,
            company: v.company,
            note: v.note,
            date: v.date,
            type: v.type,
            duration: v.duration,
          });
          // row is null if insert was ignored due to duplicate OR failed.
          // Both cases are safe to retry (client_id unique index makes retry
          // idempotent): if it was actually a duplicate, the banner auto-
          // clears on the next realtime tick / remount when hydration
          // observes the id is now in Supabase. If it was a genuine failure,
          // the banner persists until the retry succeeds.
          if (row) { inserted++; }
          else { failed++; markVisitSyncPending(String(v.id)); }
        }
        if (inserted > 0 || failed > 0) {
          console.info(`[shop_visits backfill] ${synced} already synced, ${inserted} newly inserted, ${failed} failed.`);
        }
      } catch(e) {
        console.warn("[shop_visits mount] sync failed:", e?.message || e);
      }
    };

    syncShopVisits();

    // Realtime subscription — live INSERT/UPDATE from admin edits or
    // this rep's other devices. Gracefully no-ops if realtime is disabled.
    const unsub = subscribeShopVisits(repIdForSync, async () => {
      if (!mounted) return;
      await syncShopVisits();
    });

    return () => { mounted = false; if (typeof unsub === "function") unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repIdForSync, profileData?.name]);

  // ── Phase C — material_orders cross-device sync + backfill ───────────────
  // Rep's marketing-material orders (source='rep') sync to Supabase so admin
  // + this rep's other devices see them. Same Supabase-first pattern as
  // Phase B, exact-key dedupe on client_id (the "MAT-XXXX" id generated in
  // placeOrder). Backfill lifts any localStorage-only orders to Supabase on
  // mount. Never clears / deletes localStorage.
  useEffect(() => {
    if (!repIdForSync) return; // showcase mode — no sync
    let mounted = true;
    const repNameNow = profileData?.name || "";

    const dbRowToOrder = (r) => ({
      id: r.client_id || String(r.id),
      _supabaseRowId: r.id,
      source: r.source || "rep",
      accountName: r.account_name || "",
      rep: r.rep_name || repNameNow,
      userId: r.user_id || "",
      items: Array.isArray(r.items) ? r.items : [],
      total: Number(r.total) || 0,
      status: r.status || "pending",
      shippedDate: r.shipped_date || null,
      date: r.placed_at ? new Date(r.placed_at).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}) : "",
      placedAt: r.placed_at || new Date().toISOString(),
      fromDb: true,
    });

    const syncMaterialOrders = async () => {
      try {
        const rows = await listMaterialOrders({ repId: repIdForSync });
        if (!mounted) return;
        const existing = SHARED_MATERIAL_ORDERS.getAll();
        const existingIds = new Set(existing.map(o => String(o.id)));

        let added = 0;
        for (const r of rows) {
          const o = dbRowToOrder(r);
          if (existingIds.has(String(o.id))) {
            // Auto-clear banner for any id that has now landed in Supabase.
            setMaterialOrderSyncPendingIds(prev => prev.filter(k => String(k) !== String(o.id)));
            continue;
          }
          SHARED_MATERIAL_ORDERS.add(o);
          existingIds.add(String(o.id));
          added++;
        }
        if (added > 0) console.info(`[material_orders hydrate] merged ${added} row(s) from Supabase.`);

        // Backfill: push any localStorage-only source='rep' orders that
        // aren't yet in Supabase. Idempotent via client_id unique index.
        const local = SHARED_MATERIAL_ORDERS.getAll();
        const supabaseClientIds = new Set((rows || []).map(r => String(r.client_id || "")));
        let synced = 0, inserted = 0, failed = 0;
        for (const o of local) {
          if (!mounted) break;
          if (!o || !o.id) continue;
          if (o.fromDb) continue;
          // Only push this rep's own rows — admin/customer sessions handle
          // their own scope. Matches SHARED_MATERIAL_ORDERS .add-time tag.
          if (o.source && o.source !== 'rep') continue;
          if (supabaseClientIds.has(String(o.id))) { synced++; continue; }
          const row = await upsertMaterialOrder({
            clientId: String(o.id),
            orderRef: String(o.id),
            source: 'rep',
            accountName: o.accountName || repNameNow,
            repName: o.rep || repNameNow,
            repId: repIdForSync,
            userId: null,
            items: Array.isArray(o.items) ? o.items : [],
            total: Number(o.total) || 0,
            status: o.status || 'pending',
            placedAt: o.placedAt || new Date().toISOString(),
          });
          if (row) { inserted++; }
          else { failed++; markMaterialOrderSyncPending(String(o.id)); }
        }
        if (inserted > 0 || failed > 0) {
          console.info(`[material_orders backfill] ${synced} already synced, ${inserted} newly inserted, ${failed} failed.`);
        }
      } catch(e) {
        console.warn("[material_orders mount] sync failed:", e?.message || e);
      }
    };

    syncMaterialOrders();

    const unsubMO = subscribeMaterialOrders({ repId: repIdForSync }, async () => {
      if (!mounted) return;
      await syncMaterialOrders();
    });

    return () => { mounted = false; if (typeof unsubMO === "function") unsubMO(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repIdForSync, profileData?.name]);

  // Merge local rep invoices + shared invoices scoped to this rep.
  // An invoice belongs to this rep if ANY of:
  //   • inv.rep matches the rep's name (case-insensitive, trimmed) — the direct stamp
  //   • inv.userId / inv.user_id is in the admin-assigned dealer id set — the strongest
  //     identity match; survives rep-name drift entirely
  //   • inv.customer is on the admin-assigned dealer list (by company name)
  //   • inv.customer is on the rep's own route-planner list
  // Previous strict equality missed dealer orders where inv.rep was empty or the
  // customer wasn't on the rep's personal route list even though admin had assigned them.
  const mergedInvoices = useMemo(()=>{
    const norm = s => String(s || "").trim().toLowerCase();
    const repNameNorm = norm(profileData.name);
    const routeCompanies = new Set((routeCustomers||[]).map(c=>norm(c.company)).filter(Boolean));
    // Build a lookup of every shared invoice so we can overlay payment /
    // shipping status onto the local copy. Previously the merge excluded
    // any shared invoice that already existed in local — so when the
    // customer paid via Square, SHARED updated but the rep's view kept
    // showing "pending" from the stale local copy. Now the rep always
    // sees the current payment status regardless of which side changed it.
    const sharedById = {};
    sharedInvoicesList.forEach(inv => { if(inv?.id) sharedById[inv.id] = inv; });
    const isRelevantShared = (inv) => {
      const invUserId = inv.userId || inv.user_id || "";
      if(invUserId && adminAssignedUserIds.has(invUserId)) return true;
      const invCust = norm(inv.customer);
      if(invCust && adminAssignedCompanies.has(invCust)) return true;
      if(invCust && routeCompanies.has(invCust)) return true;
      // Same fuzzy match as RepInvoices uses — see namesMatchFuzzy doc.
      if(profileData.name && namesMatchFuzzy(inv.rep, profileData.name)) return true;
      return false;
    };
    // Overlay: whichever fields shared has (status, paid*, pending*, shipping,
    // cancellation) win because SHARED is what the customer + admin mutate.
    // Non-status fields (items, date, rep notes) come from whichever has data.
    const overlaid = repInvoices.map(local => {
      const s = sharedById[local.id];
      if(!s) return local;
      return {
        ...local,
        status: s.status ?? local.status,
        paidMethod: s.paidMethod ?? local.paidMethod,
        paidDate: s.paidDate ?? local.paidDate,
        paymentId: s.paymentId ?? local.paymentId,
        transactionId: s.transactionId ?? local.transactionId,
        paidLast4: s.paidLast4 ?? local.paidLast4,
        receiptUrl: s.receiptUrl ?? local.receiptUrl,
        pendingMethod: s.pendingMethod,
        pendingDate: s.pendingDate,
        pendingNote: s.pendingNote,
        shippingStatus: s.shippingStatus ?? local.shippingStatus,
        tracking: s.tracking ?? local.tracking,
        carrier: s.carrier ?? local.carrier,
        deliveredDate: s.deliveredDate ?? local.deliveredDate,
        cancelledAt: s.cancelledAt,
        cancelledDate: s.cancelledDate,
        cancelReason: s.cancelReason,
        refundStatus: s.refundStatus,
        refundTransactionId: s.refundTransactionId,
        refundMethod: s.refundMethod,
      };
    });
    const localIds = new Set(repInvoices.map(i=>i.id));
    const onlyShared = sharedInvoicesList.filter(inv => !localIds.has(inv.id) && isRelevantShared(inv));
    return [...overlaid, ...onlyShared];
  },[repInvoices, sharedInvoicesList, routeCustomers, profileData.name, adminAssignedCompanies, adminAssignedUserIds]);

  // ── Derived inventory ──────────────────────────────────────────────────────
  // ONE calculation for every screen: src/inventoryMath.js (the admin Rep
  // Inventory tab and the delete-impact modal call the same functions).
  //   received(sku) = SIGNED sum of this rep's ledger rows
  //                   (assign / return / adjust_rep / rep_receive)
  //   sold(sku)     = UNITS on invoices the rep created + dealer orders the rep
  //                   delivered himself; cancelled / voided never deduct, so a
  //                   void restores the units automatically
  //   onHand        = received − sold, SIGNED (a negative number is "oversold",
  //                   never hidden behind a 0)
  // Invoices: this device's rep invoices (status overlaid from the shared copy)
  // plus every shared invoice not held locally. inventoryMath decides which of
  // them belong to THIS rep (rep stamp is authoritative), so another rep or the
  // admin sharing this browser cannot reduce this rep's stock. A duplicate
  // invoice id collapses to the newest copy before anything is summed.
  const inventoryInvoices = useMemo(()=>{
    const isVoided = x => ["cancelled","canceled","removed"].includes(String(x?.status||"").trim().toLowerCase());
    const sharedById = {};
    sharedInvoicesList.forEach(inv => { if(inv?.id && !sharedById[inv.id]) sharedById[inv.id] = inv; });
    // Device-local rule: a rep-created invoice that carries NO usable rep stamp (no repId,
    // no email, an empty or generic name such as "Sales Rep") and lives in THIS rep's
    // local list is this rep's; it is attributed here instead of being dropped.
    const stampLocal = l => (l && isRepCreatedInvoice(l) && isUnstampedInvoice(l)) ? { ...l, repId: inventoryRepKey } : l;
    const local = (repInvoices || []).map(l0 => {
      const l = stampLocal(l0);
      const s = l && sharedById[l.id];
      if(!s) return l;
      return {
        ...l,
        // cancelled is terminal: either copy being cancelled restores the units
        status: (isVoided(s) || isVoided(l)) ? "cancelled" : (s.status ?? l.status),
        deliveryMethod: s.deliveryMethod || l.deliveryMethod,
        deliveredByRep: s.deliveredByRep || l.deliveredByRep,
      };
    });
    const localIds = new Set((repInvoices || []).map(i => i && i.id));
    return dedupeInvoicesById([...local, ...sharedInvoicesList.filter(i => i && !localIds.has(i.id))]);
  },[repInvoices, sharedInvoicesList, inventoryRepKey]);

  // The rep's identity is built by the SAME helper the admin tab and the delete-impact
  // figure use (inventoryMath.buildRepIdentity): the shared-profile display name and every
  // rep_name admin snapshotted on this rep's ledger rows are aliases (an old invoice
  // stamped with a previous display name still belongs to this rep); the customer list is
  // this rep's route customers + the dealers admin assigned to him.
  const inventoryRep = useMemo(()=>buildRepIdentity({
    rep: { key: inventoryRepKey, name: profileData.name, email: profileData.email || user?.email || "", supabaseId: user?.id },
    ledgerRows: movements,
    sharedRepProfiles: SHARED_REP_PROFILES.getAll(),
    sharedRepCustomers: { [profileData.name]: { customers: routeCustomers || [] } },
    assignedAccounts: Array.from(adminAssignedCompanies || []).map(company => ({ company, rep: profileData.name })),
  }),[inventoryRepKey, profileData.name, profileData.email, user?.id, user?.email, movements, routeCustomers, adminAssignedCompanies]);

  const inventory = useMemo(()=>
    repInventoryRows({ movements, invoices: inventoryInvoices, rep: inventoryRep, catalog: liveProducts })
      .map(r => ({ ...r, name: r.name || r.sku })),
  [movements, inventoryInvoices, inventoryRep, liveProducts]);

  // `setInventory` is retained as a no-op for prop-shape compatibility with a
  // couple of legacy invoice flows (RepInvoices). The real mutation paths are:
  //   • admin assigns → inventory_movements INSERT (admin) → realtime → refresh → useMemo
  //   • rep receives  → inventory_movements INSERT (rep)   → realtime → refresh → useMemo
  //   • rep sells     → SHARED_INVOICES.add → mergedInvoices change       → useMemo
  // Writing back through setInventory would create a parallel state that drifts
  // from the ledger — exactly the bug Phase 2 is fixing.
  const setInventory = () => {};

  return (
    <div style={S.app}>
      {/* Profile Modal */}
      {showProfile && (
        <div style={S.modal} onClick={()=>{setShowProfile(false);cancelEdit();}}>
          <div style={{...S.modalBox,width:440}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div style={{fontSize:18,fontWeight:600,color:C.ink}}>My Profile</div>
              <button onClick={()=>{setShowProfile(false);cancelEdit();}} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:C.inkLight}}>×</button>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:24,padding:"16px 0",borderBottom:`1px solid ${C.border}`}}>
              {profileData.avatar
                ?<img src={profileData.avatar} alt="" style={{width:48,height:48,borderRadius:"50%",objectFit:"cover",border:`2px solid ${C.border}`}} onError={e=>{e.target.style.display="none"}}/>
                :<div style={{width:48,height:48,borderRadius:"50%",background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:600,color:C.blue}}>{initials}</div>}
              <div>
                <div style={{fontSize:16,fontWeight:600,color:C.ink}}>{profileData.name}</div>
                <span style={{fontSize:10,fontWeight:500,padding:"2px 8px",borderRadius:8,background:C.greenLight,color:C.green}}>Certified Rep</span>
              </div>
            </div>
            {!editingProfile ? (
              <div>
                {profileData.refCode&&<div style={{background:C.blueLight,borderRadius:8,padding:"10px 14px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div><div style={{fontSize:10,fontWeight:600,color:C.inkLight,textTransform:"uppercase",marginBottom:2}}>Your reference code</div><div style={{fontSize:15,fontWeight:700,fontFamily:"monospace",color:C.blue}}>{profileData.refCode}</div></div>
                  <button style={{background:C.blueLight,color:C.blue,border:`1px solid ${C.blue}`,padding:"4px 10px",borderRadius:6,fontSize:10,fontWeight:500,cursor:"pointer"}} onClick={()=>{navigator.clipboard.writeText(profileData.refCode);}}>Copy</button>
                </div>}
                {[["Name",profileData.name],["Email",profileData.email],["Phone",profileData.phone],["Territory",profileData.territory]].map(([label,value])=>(
                  <div key={label} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
                    <span style={{fontSize:12,color:C.inkLight,fontWeight:500}}>{label}</span>
                    <span style={{fontSize:12,color:C.ink,fontWeight:500}}>{value}</span>
                  </div>
                ))}
                <div style={{display:"flex",gap:10,marginTop:20}}>
                  <button onClick={()=>setEditingProfile(true)} style={S.btnPrimary}>Edit Profile</button>
                  <button onClick={onSignOut} style={{background:C.redLight,color:C.red,border:"none",padding:"10px 20px",borderRadius:8,fontSize:13,fontWeight:500,cursor:"pointer"}}>Sign Out</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:8}}>Profile picture</div>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                  {editForm.avatar
                    ?<img src={editForm.avatar} alt="" style={{width:48,height:48,borderRadius:"50%",objectFit:"cover",border:`2px solid ${C.border}`}} onError={e=>{e.target.style.display="none"}}/>
                    :<div style={{width:48,height:48,borderRadius:"50%",background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:600,color:C.blue}}>{initials}</div>}
                  <div>
                    <label style={{...S.btnSmall,cursor:avatarState.kind==="uploading"?"wait":"pointer",display:"inline-block",opacity:avatarState.kind==="uploading"?0.6:1}}>
                      {avatarState.kind==="uploading" ? "Uploading…" : (editForm.avatar ? "Replace photo" : "Upload photo")}
                      <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{display:"none"}} disabled={avatarState.kind==="uploading"}/>
                    </label>
                    {editForm.avatar&&avatarState.kind!=="uploading"&&<button style={{...S.btnDanger,marginLeft:6}} onClick={()=>setEditForm(f=>({...f,avatar:""}))}>Remove</button>}
                    {avatarState.msg && <div style={{fontSize:11,marginTop:5,color:avatarState.kind==="error"?C.red:avatarState.kind==="success"?C.green:C.inkLight}}>{avatarState.msg}</div>}
                  </div>
                </div>
                {[["Name","name"],["Email","email"],["Phone","phone"],["Territory","territory"]].map(([label,key])=>(
                  <div key={key} style={S.fg}>
                    <label style={S.label}>{label}</label>
                    <input style={S.input} value={editForm[key]} onChange={e=>setEditForm(f=>({...f,[key]:e.target.value}))}/>
                  </div>
                ))}
                <div style={{display:"flex",gap:10,marginTop:16}}>
                  <button onClick={saveProfile} style={S.btnPrimary}>Save Changes</button>
                  <button onClick={cancelEdit} style={S.btnSecondary}>Cancel</button>
                </div>
              </div>
            )}
            {/* Login account — self-service auth email change. Always visible so
                the rep can reach it whether or not they're editing their profile. */}
            <div style={{marginTop:16}}>
              <ChangeLoginEmail user={user}/>
            </div>
          </div>
        </div>
      )}
      {/* Sign Out Bar */}
      <div style={{display:'flex',justifyContent:'flex-end',padding:'8px 16px',background:'#1a1a2e'}}>
        <button
          onClick={onSignOut}
          style={{background:'transparent',border:'1px solid #aaa',color:'#ccc',
                  padding:'5px 14px',borderRadius:'6px',cursor:'pointer',fontSize:'13px'}}
        >Sign Out</button>
      </div>
      <div style={S.shell}>
        <RepSidebar page={page} setPage={setPage} repName={profileData.name} onExit={onExit}/>
        <div style={S.main}>
          <div style={S.topbar}>
            <span style={{fontSize:15,fontWeight:600,color:C.ink}}>{pageTitles[page]}</span>
            <div style={{display:"flex",gap:10,alignItems:"center",cursor:"pointer",padding:"4px 10px",borderRadius:8,border:"1px solid transparent",transition:"all 0.15s"}} onClick={()=>setShowProfile(true)} onMouseEnter={e=>{e.currentTarget.style.background=C.blueLight;e.currentTarget.style.borderColor=C.border;}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor="transparent";}}>
              <span style={{fontSize:10,fontWeight:500,padding:"3px 9px",borderRadius:8,background:C.greenLight,color:C.green}}>Certified Rep</span>
              {profileData.avatar
                ?<img src={profileData.avatar} alt="" style={{width:28,height:28,borderRadius:"50%",objectFit:"cover"}} onError={e=>{e.target.style.display="none"}}/>
                :<div style={{width:28,height:28,borderRadius:"50%",background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:600,color:C.blue}}>{initials}</div>}
              <span style={{fontSize:12,color:C.inkMid}}>{profileData.name}</span>
              <span style={{fontSize:14,color:C.inkLight,marginLeft:2}}>▾</span>
            </div>
          </div>
          {syncPendingInvoiceIds.length > 0 && (
            <div style={{margin:"12px 24px 0", padding:"10px 14px", background:"#FEF3C7", border:"1px solid #F59E0B", borderRadius:8, display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12, color:"#92400E"}}>
              <div style={{flex:1}}>
                <strong>{syncPendingInvoiceIds.length === 1 ? `Invoice ${syncPendingInvoiceIds[0]}` : `${syncPendingInvoiceIds.length} invoices (${syncPendingInvoiceIds.join(", ")})`} saved on this device but cloud sync is pending.</strong>{" "}
                Will retry automatically. Admin and the dealer won't see {syncPendingInvoiceIds.length === 1 ? "it" : "them"} on their devices until sync succeeds.
                {(() => { const why = syncPendingInvoiceIds.map(i => syncPendingReasons[i]).find(Boolean); return why ? <div data-testid="sync-pending-reason" style={{marginTop:4}}><strong>{why}.</strong> Ask your admin to run it; the invoice then syncs by itself.</div> : null; })()}
              </div>
              <button
                onClick={() => setSyncPendingInvoiceIds([])}
                style={{marginLeft:12, background:"transparent", border:"1px solid #F59E0B", color:"#92400E", fontSize:11, padding:"4px 10px", borderRadius:6, cursor:"pointer"}}
              >Dismiss</button>
            </div>
          )}
          {customerSyncPendingKeys.length > 0 && (
            <div style={{margin:"12px 24px 0", padding:"10px 14px", background:"#FEF3C7", border:"1px solid #F59E0B", borderRadius:8, display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12, color:"#92400E"}}>
              <div style={{flex:1}}>
                <strong>{customerSyncPendingKeys.length === 1 ? "1 customer edit" : `${customerSyncPendingKeys.length} customer edits`} saved on this device but cloud sync is pending.</strong>{" "}
                Admin and your other devices won't see the change until sync succeeds. Will retry automatically.
              </div>
              <button
                onClick={() => setCustomerSyncPendingKeys([])}
                style={{marginLeft:12, background:"transparent", border:"1px solid #F59E0B", color:"#92400E", fontSize:11, padding:"4px 10px", borderRadius:6, cursor:"pointer"}}
              >Dismiss</button>
            </div>
          )}
          {visitSyncPendingIds.length > 0 && (
            <div style={{margin:"12px 24px 0", padding:"10px 14px", background:"#FEF3C7", border:"1px solid #F59E0B", borderRadius:8, display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12, color:"#92400E"}}>
              <div style={{flex:1}}>
                <strong>{visitSyncPendingIds.length === 1 ? "1 visit" : `${visitSyncPendingIds.length} visits`} logged on this device but cloud sync is pending.</strong>{" "}
                Admin won't see {visitSyncPendingIds.length === 1 ? "it" : "them"} until sync succeeds. Will retry automatically.
              </div>
              <button
                onClick={() => setVisitSyncPendingIds([])}
                style={{marginLeft:12, background:"transparent", border:"1px solid #F59E0B", color:"#92400E", fontSize:11, padding:"4px 10px", borderRadius:6, cursor:"pointer"}}
              >Dismiss</button>
            </div>
          )}
          {materialOrderSyncPendingIds.length > 0 && (
            <div style={{margin:"12px 24px 0", padding:"10px 14px", background:"#FEF3C7", border:"1px solid #F59E0B", borderRadius:8, display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12, color:"#92400E"}}>
              <div style={{flex:1}}>
                <strong>{materialOrderSyncPendingIds.length === 1 ? "1 marketing-material order" : `${materialOrderSyncPendingIds.length} marketing-material orders`} placed on this device but cloud sync is pending.</strong>{" "}
                Admin won't see {materialOrderSyncPendingIds.length === 1 ? "it" : "them"} in fulfillment until sync succeeds. Will retry automatically.
              </div>
              <button
                onClick={() => setMaterialOrderSyncPendingIds([])}
                style={{marginLeft:12, background:"transparent", border:"1px solid #F59E0B", color:"#92400E", fontSize:11, padding:"4px 10px", borderRadius:6, cursor:"pointer"}}
              >Dismiss</button>
            </div>
          )}
          {page==="dashboard"  && <RepDashboard setPage={setPage} inventory={inventory} ledgerError={inventoryError} onRetryLedger={refreshInventoryLedger} agreements={agreements} commissionRate={liveCommissionRate} invoices={mergedInvoices} repName={profileData.name} videos={videos} earnedCerts={earnedCerts} lastSyncAt={lastOrdersSyncAt} onRefreshNow={()=>syncNowRef.current&&syncNowRef.current()}/>}
          {page==="inventory"  && <RepInventory inventory={inventory} movements={movements} productList={liveProducts} loading={inventoryLoading} errorMsg={inventoryError} onReceive={handleReceiveStock} onRefresh={refreshInventoryLedger} requests={repRequests} requestsError={requestsError} onCancelRequest={handleCancelRequest}/>}
          {page==="invoices"   && <RepInvoices inventory={inventory} setInventory={setInventory} productList={liveProducts} invoices={repInvoices} setInvoices={setRepInvoices} repName={profileData.name} repEmail={profileData.email||user?.email||""} repId={user?.id||""} prefillCustomerId={prefillInvoiceCustomer} clearPrefill={()=>setPrefillInvoiceCustomer(null)} customers={routeCustomers} adminAssignedCompanies={adminAssignedCompanies} adminAssignedUserIds={adminAssignedUserIds} externalViewInvoice={viewInvoiceFromRoute} clearExternalView={()=>setViewInvoiceFromRoute(null)} onSyncPending={markInvoiceSyncPending}/>}
          {page==="sales"      && <RepSalesReport inventory={inventory} invoices={mergedInvoices} repName={profileData.name}/>}
          {page==="commission" && <RepCommission repName={profileData.name} liveRate={liveCommissionRate} livePaySchedule={livePaySchedule} invoices={mergedInvoices} onJumpToInvoice={handleViewInvoice}/>}
          {page==="route"      && <RepRoutePlanner customers={routeCustomers} setCustomers={setRouteCustomers} prospects={prospects} setProspects={setProspects} onCreateInvoice={createInvoiceForCustomer} onViewInvoice={handleViewInvoice} repName={profileData.name} repIdForSync={repIdForSync} onVisitSyncPending={markVisitSyncPending}/>}
          {page==="prospects"  && <RepPotentialCustomers prospects={prospects} setProspects={setProspects} customers={routeCustomers} setCustomers={setRouteCustomers} repName={profileData.name} repIdForSync={repIdForSync} onVisitSyncPending={markVisitSyncPending}/>}
          {page==="training"   && <TrainingHub isAdmin={false} videos={videos} setVideos={setVideos} earnedCerts={earnedCerts} setEarnedCerts={setEarnedCerts} repName={profileData.name} repId={repId} progress={progress} setProgress={setProgress}/>}
          {page==="marketing"  && <MarketingMaterials isAdmin={false} materials={materials} setMaterials={setMaterials} repName={profileData.name} onPlaceOrder={async (order)=>{
                    // Phase C — Rule Zero: Supabase-first. Await the
                    // material_orders insert, then mirror to localStorage
                    // regardless of outcome. If the write fails, local still
                    // saves so the rep doesn't lose the order; amber banner
                    // surfaces the pending state and auto-clears once the
                    // mount/realtime retry observes the row in Supabase.
                    //
                    // order.id is "MAT-XXXX" (generated in MarketingMaterials
                    // placeOrder). We reuse it as client_id — exact-key
                    // dedupe via the non-partial unique index on client_id.
                    let supabaseOk = true;
                    if (repIdForSync) {
                      try {
                        const row = await upsertMaterialOrder({
                          clientId: String(order.id),
                          orderRef: String(order.id),
                          source: 'rep',
                          accountName: profileData.name || "",
                          repName: profileData.name || "",
                          repId: repIdForSync,
                          userId: null,
                          items: Array.isArray(order.items) ? order.items : [],
                          total: Number(order.total) || 0,
                          status: 'pending',
                          placedAt: new Date().toISOString(),
                        });
                        if (!row) supabaseOk = false;
                      } catch(e) {
                        console.warn("[RepPortal placeMaterialOrder] material_orders write threw:", e?.message || e);
                        supabaseOk = false;
                      }
                    }
                    // localStorage mirror — always runs. Preserves the
                    // pre-generated id so admin's existing notification
                    // subscriber (SHARED_MATERIAL_ORDERS.subscribe in App.js)
                    // sees the same id as the Supabase row.
                    SHARED_MATERIAL_ORDERS.add({...order, source:"rep", accountName:profileData.name, rep: profileData.name || ""});
                    if (!supabaseOk) markMaterialOrderSyncPending(String(order.id));
                  }}/>}
          {page==="tools"      && <ToolLending isAdmin={false} tools={tools} setTools={setTools} agreements={agreements} setAgreements={setAgreements} repName={profileData.name} customers={routeCustomers}/>}
        </div>
      </div>
    </div>
  );
}

// ─── COMMISSION DATA ─────────────────────────────────────────────────────────
const INIT_REP_PROFILES = [
  {id:1, name:"Marcus T.", email:"marcus@mpcare.com", phone:"619-555-0148", rate:8, paySchedule:"15th of month", startDate:"Jan 1, 2025", notes:"Senior rep — DPF specialist"},
  {id:2, name:"Sarah L.",  email:"sarah@mpcare.com",  phone:"661-555-0200", rate:6, paySchedule:"15th of month", startDate:"Feb 1, 2025", notes:"New rep — dealer focus"},
  {id:3, name:"James R.",  email:"james@mpcare.com",  phone:"760-555-0300", rate:7, paySchedule:"End of month",  startDate:"Mar 1, 2025", notes:"Fleet and commercial accounts"},
];

const INIT_COMMISSION_PAYMENTS = [
  {id:1, repName:"Marcus T.", period:"March 2025", amount:248.40, invoiceCount:8,  salesTotal:3105, rate:8, status:"paid",    paidDate:"Apr 15, 2025", method:"ACH", notes:""},
  {id:2, repName:"Marcus T.", period:"February 2025", amount:196.00, invoiceCount:6, salesTotal:2450, rate:8, status:"paid",  paidDate:"Mar 15, 2025", method:"ACH", notes:""},
  {id:3, repName:"Sarah L.",  period:"March 2025", amount:89.40,  invoiceCount:3,  salesTotal:1490, rate:6, status:"pending", paidDate:"",            method:"",    notes:""},
  {id:4, repName:"Marcus T.", period:"April 2025", amount:312.40, invoiceCount:10, salesTotal:3905, rate:8, status:"pending", paidDate:"",            method:"",    notes:""},
];

// ─── REP COMMISSION PAGE ──────────────────────────────────────────────────────
// map of period -> invoices for drill-down
const PERIOD_INVOICES = {
  "March 2025":[
    {id:"INV-F039",date:"Mar 2, 2025", customer:"Westside Repair Center",items:"DPF foam cleaner ×12, Nitrile gloves ×100",total:313.20},
    {id:"INV-F038",date:"Mar 7, 2025", customer:"Apex Car Care LLC",      items:"Catalytic converter cleaner ×24",           total:115.20},
    {id:"INV-F037",date:"Mar 12, 2025",customer:"FastLane Auto Parts",    items:"Fuel system cleaner ×24",                   total:93.60},
    {id:"INV-F036",date:"Mar 18, 2025",customer:"Apex Car Care LLC",      items:"DPF purge filter ×12",                      total:62.40},
    {id:"INV-F035",date:"Mar 22, 2025",customer:"Westside Repair Center", items:"Engine flush ×12, Octane booster ×8",        total:87.60},
    {id:"INV-F034",date:"Mar 25, 2025",customer:"Apex Car Care LLC",      items:"Anti-wear protection ×6",                   total:30.60},
    {id:"INV-F033",date:"Mar 28, 2025",customer:"FastLane Auto Parts",    items:"DPF flush liquid ×6",                       total:34.80},
    {id:"INV-F032",date:"Mar 30, 2025",customer:"Apex Car Care LLC",      items:"Diesel power cleaner ×12",                  total:55.20},
  ],
  "April 2025":[
    {id:"INV-F041",date:"Apr 9, 2025", customer:"Apex Car Care LLC",    items:"Catalytic converter cleaner ×12, DPF purge filter ×6",total:88.80},
    {id:"INV-F040",date:"Apr 7, 2025", customer:"FastLane Auto Parts",  items:"Fuel system cleaner ×24",                            total:93.60},
    {id:"INV-F039",date:"Apr 2, 2025", customer:"Westside Repair Center",items:"DPF foam cleaner ×12, Nitrile gloves ×100",          total:313.20},
    {id:"INV-F038",date:"Apr 3, 2025", customer:"Apex Car Care LLC",    items:"Engine flush ×12",                                   total:54.00},
    {id:"INV-F037",date:"Apr 4, 2025", customer:"FastLane Auto Parts",  items:"Octane booster ×8",                                  total:33.60},
    {id:"INV-F036",date:"Apr 5, 2025", customer:"Apex Car Care LLC",    items:"DPF purge filter ×6",                                total:31.20},
    {id:"INV-F035",date:"Apr 6, 2025", customer:"Westside Repair Center",items:"Catalytic converter cleaner ×24",                   total:115.20},
    {id:"INV-F034",date:"Apr 7, 2025", customer:"Apex Car Care LLC",    items:"Fuel system cleaner ×12",                            total:46.80},
    {id:"INV-F033",date:"Apr 8, 2025", customer:"FastLane Auto Parts",  items:"Anti-wear protection ×6",                            total:30.60},
    {id:"INV-F032",date:"Apr 9, 2025", customer:"Apex Car Care LLC",    items:"DPF flush liquid ×6",                                total:34.80},
  ],
};

// Same key AdminCommissionTab writes to. Shared between the two modules via
// localStorage so the rep sees exactly what admin has confirmed as paid out —
// NOT what the customer has paid on their invoices (those are two different
// things: customer paying the invoice makes commission EARNABLE; admin
// marking the period paid is when the rep actually RECEIVES that commission).
const REP_COMMISSION_PAID_LS = "mpcare_commission_paid_periods";
function loadRepCommissionPaidMap() {
  try { const raw = localStorage.getItem(REP_COMMISSION_PAID_LS); return raw ? JSON.parse(raw) : {}; } catch(e) { return {}; }
}

function RepCommission({repName, liveRate, livePaySchedule, invoices, onJumpToInvoice}) {
  // Look up this rep's demo profile by name. IMPORTANT: no fallback to
  // INIT_REP_PROFILES[0] — that's Marcus T., and a brand-new rep like Adam
  // Kanoon would otherwise see Marcus's rate / paySchedule / customer set on
  // their commission page header. For reps not in the demo seed, start with
  // an empty profile and let liveRate / livePaySchedule (set by admin) fill
  // the real values in.
  const baseProfile = INIT_REP_PROFILES.find(r=>r.name===repName) || { rate: 0, paySchedule: "" };
  const profile = { ...baseProfile, rate: liveRate ?? baseProfile.rate ?? 0, paySchedule: livePaySchedule ?? baseProfile.paySchedule ?? "" };
  const rate = profile.rate;
  const [expandedPeriod, setExpandedPeriod] = useState(null);

  // Use the shared rep-metrics helper so the numbers this page shows are the
  // same numbers the Rep Dashboard shows and the Admin Commission table pays.
  const m = computeRepMetrics({
    repName,
    customerNames: (invoices || []).map(i => i.customer).filter(Boolean),
    allInvoices: invoices || [],
    rate,
  });
  const activeInvoices = m.invoices.filter(inv => parseInvoiceDate(inv));
  const periods = groupInvoicesByMonth(activeInvoices, rate);
  const periodTotals = (list) => list.reduce((s,inv)=>s+(Number(inv.total)||0), 0);

  // ── Admin-confirmed commission payouts — Supabase first, localStorage fallback ──
  // New source of truth: rep_commission_payments table, one row per invoice
  // that admin has actually paid out on. Legacy fallback (the per-period
  // `mpcare_commission_paid_periods` map) is consulted inside the partition
  // helper so paid-before-this-deploy periods still render as paid until
  // admin's next action migrates them into Supabase.
  //
  // This replaces the prior period-level flag that caused BOTH reported bugs:
  //   (a) New invoices in an already-paid month inherited the paid status
  //       and the rep's pending payout dropped to $0.00.
  //   (b) "April 2026 paid" covered every future April 2026 order.
  // With per-invoice rows a new invoice arriving after admin's payout has
  // NO matching row in either store → stays pending.
  const [paidPeriodsMap, setPaidPeriodsMap] = useState(loadRepCommissionPaidMap);
  const [paidRows, setPaidRows] = useState([]);
  const [paidLoaded, setPaidLoaded] = useState(false);
  const paidInvoiceIds = useMemo(() => {
    const s = new Set();
    paidRows.forEach(r => { if (r?.invoice_id) s.add(String(r.invoice_id)); });
    return s;
  },[paidRows]);
  const paidRowByInvoiceId = useMemo(() => {
    const map = {};
    paidRows.forEach(r => {
      if (!r?.invoice_id) return;
      const k = String(r.invoice_id);
      const prev = map[k];
      if (!prev || new Date(r.paid_at||0) > new Date(prev.paid_at||0)) map[k] = r;
    });
    return map;
  },[paidRows]);
  // Legacy map slice for THIS rep — keyed by period label only.
  const legacyForRep = useMemo(() => {
    const out = {};
    Object.entries(paidPeriodsMap || {}).forEach(([k,v]) => {
      const sep = k.indexOf("__");
      if (sep < 0) return;
      if (k.slice(0, sep) === repName) out[k.slice(sep + 2)] = v;
    });
    return out;
  },[paidPeriodsMap, repName]);

  // Initial fetch + realtime subscription: admin's Mark Paid / Undo on
  // another device updates this page live.
  useEffect(()=>{
    let cancelled = false;
    (async () => {
      const rows = await listRepCommissionPayments();
      if (!cancelled) { setPaidRows(rows); setPaidLoaded(true); }
    })();
    const unsub = subscribeRepCommissionPayments(async () => {
      const rows = await listRepCommissionPayments();
      if (!cancelled) { setPaidRows(rows); setPaidLoaded(true); }
    });
    return () => { cancelled = true; unsub && unsub(); };
  },[]);

  // Commission corrections (migration 0031): this rep's rows only, through RLS
  // (rep_id = auth.uid()), so there is deliberately NO rep_name / rep_id filter
  // in the query. Fetched on mount + realtime with an explicit rep-safe column
  // list (no admin identity / internal notes). It does NOT read the payments a
  // third time: the paid rows above (this rep's own rows through RLS) are reused.
  // Degrades to nothing (no card, no error) when the table does not exist yet.
  const ledger = useCommissionLedger({ withPayments: false, repSafe: true });

  // Refresh legacy map on focus / storage so the same-tab admin ↔ rep
  // localStorage path (used only when Supabase is unreachable) still works.
  useEffect(()=>{
    const reload = () => setPaidPeriodsMap(loadRepCommissionPaidMap());
    window.addEventListener("focus", reload);
    window.addEventListener("storage", reload);
    return ()=>{
      window.removeEventListener("focus", reload);
      window.removeEventListener("storage", reload);
    };
  },[]);
  const paidInfoFor = (periodLabel) => legacyForRep[periodLabel] || null;

  // ── Batch-based rollup ───────────────────────────────────────────────────
  // Admin clicks Mark Paid → snapshot becomes a frozen batch. New invoices
  // arriving after show up in a fresh pending bucket (per period). This
  // matches the admin-side batch UI in the Commission tab.
  const { batches: paidBatchesList, pendingPeriods: pendingPeriodsBatched, totalPaid, totalPending } = useMemo(() => {
    const filteredRows = paidRows.filter(r => {
      if (!r) return false;
      if (r.rep_name && r.rep_name === repName) return true;
      // Fall back to rep_id match if rep_name is missing — legacy rows.
      return false;
    });
    return batchizeRepInvoices(activeInvoices, rate, filteredRows, legacyForRep);
  },[activeInvoices, rate, paidRows, legacyForRep, repName]);

  const currentYear = new Date().getFullYear();
  const ytdInvoices = activeInvoices.filter(inv => parseInvoiceDate(inv).getFullYear() === currentYear);
  const ytdEarned = m.ytdEarned;
  const ytdPaidOut = paidBatchesList
    .filter(b => b.paidAt && new Date(b.paidAt).getFullYear() === currentYear)
    .reduce((s,b) => s + b.commission, 0);
  const pendingTotal = totalPending;
  // Corrections are shown beside (never mixed into) the invoice numbers above.
  // Anchoring uses the paid rows already loaded above, and only once they have
  // loaded AND are non-empty: listRepCommissionPayments returns [] on a failed
  // read, and an empty list must not turn every open correction inert (a rep with
  // no payout rows at all cannot have a payout-anchored correction to show).
  const anchorRows = (paidLoaded && paidRows.length > 0) ? paidRows : undefined;
  // An orphaned claim (settled, no payment rows, older than 10 minutes) is shown as
  // Pending re-open and a younger one as Settling: neither is Settled / paid out.
  const corrSummary = repCorrectionSummary({ corrections: ledger.corrections, scoped: true, paidRows: anchorRows, detectOrphans: !!anchorRows });
  const netOwedTotal = netOwed({ pendingPayout: pendingTotal, openTotal: corrSummary.openTotal });
  const ytdPaidOutWithCorr = ytdPaidWithCorrections({ ytdPaid: ytdPaidOut, corrections: corrSummary.settled, year: currentYear });
  const settleMap = settlementBatches(corrSummary.settled);
  const corrOnlyHistory = correctionOnlySettlements(corrSummary.settled);
  const frozenRows = paidRows;

  const itemsLabel = (inv) => Array.isArray(inv.items)
    ? inv.items.map(i=>`[${i.sku||"—"}] ${i.name||""} x${i.qty||0}`).join(", ")
    : (inv.items || "");

  const renderInvoiceRows = (list, { alreadyPaid = false } = {}) => (
    <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`}}>
      <div style={{display:"grid",gridTemplateColumns:"90px 80px 1fr 1fr 90px 90px 60px",gap:8,padding:"8px 18px",fontSize:10,fontWeight:600,color:C.inkLight}}>
        <span>Invoice</span><span>Date</span><span>Customer</span><span>Items</span><span style={{textAlign:"right"}}>Total</span><span style={{textAlign:"right"}}>Commission</span><span></span>
      </div>
      {list.map(inv=>(
        <div key={inv.id} style={{display:"grid",gridTemplateColumns:"90px 80px 1fr 1fr 90px 90px 60px",gap:8,padding:"8px 18px",fontSize:11,alignItems:"center",borderTop:`1px solid ${C.border}`,background: alreadyPaid ? "#F0FDF4" : "transparent"}}>
          <span style={{fontWeight:600,color:C.blue}}>{inv.id}</span>
          <span style={{color:C.inkLight}}>{inv.date||""}</span>
          <span style={{color:C.ink}}>{inv.customer||""}</span>
          <span style={{color:C.inkLight,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={itemsLabel(inv)}>{itemsLabel(inv)}</span>
          <span style={{fontWeight:500,color:C.ink,textAlign:"right"}}>${(Number(inv.total)||0).toFixed(2)}</span>
          <span style={{fontWeight:500,color:C.teal,textAlign:"right"}}>${((Number(inv.total)||0)*rate/100).toFixed(2)}</span>
          {onJumpToInvoice
            ? <button style={{...S.btnSmall,fontSize:10,padding:"3px 8px"}} onClick={()=>onJumpToInvoice(inv)}>View</button>
            : <span></span>}
        </div>
      ))}
      <div style={{display:"grid",gridTemplateColumns:"90px 80px 1fr 1fr 90px 90px 60px",gap:8,padding:"10px 18px",fontSize:11,fontWeight:600,background:C.tealLight}}>
        <span></span><span></span><span></span>
        <span style={{color:C.teal}}>Subtotal</span>
        <span style={{color:C.ink,textAlign:"right"}}>${periodTotals(list).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
        <span style={{color:C.teal,textAlign:"right"}}>${(periodTotals(list)*rate/100).toFixed(2)}</span>
        <span></span>
      </div>
    </div>
  );

  // Paid batch expansion: the amounts and rate that were actually PAID (frozen
  // in the paid rows, never total x today's rate). An invoice that is in the
  // paid rows but no longer among the active invoices is shown as Voided (resolved
  // from the unfiltered invoice list) so the batch count and the rows agree.
  const renderBatchRows = (b) => {
    const lines = frozenBatchLines({ batch: b, paidRows: frozenRows, corrections: corrSummary.all, allInvoices: invoices || [] });
    if (!lines) return renderInvoiceRows(b.invoices, { alreadyPaid: true });
    const cols = "90px 80px 1fr 1fr 90px 90px 60px";
    return (
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:"grid",gridTemplateColumns:cols,gap:8,padding:"8px 18px",fontSize:10,fontWeight:600,color:C.inkLight}}>
          <span>Invoice</span><span>Date</span><span>Customer</span><span>Items</span><span style={{textAlign:"right"}}>Total</span><span style={{textAlign:"right"}}>Commission paid</span><span></span>
        </div>
        {lines.map(ln => {
          const inv = ln.inv;
          return (
            <div key={ln.rowKey}>
              <div style={{display:"grid",gridTemplateColumns:cols,gap:8,padding:"8px 18px",fontSize:11,alignItems:"center",borderTop:`1px solid ${C.border}`,background: ln.voided ? "#FEF2F2" : "#F0FDF4"}}>
                <span style={{fontWeight:600,color:C.blue}}>{ln.id}</span>
                <span style={{color:C.inkLight}}>{(inv&&inv.date)||""}</span>
                <span style={{color:C.ink}}>{(inv&&inv.customer)||""}{ln.voided && <span style={{...S.pillRed,marginLeft:6}}>{ln.cancelled?"Voided":"Not in your list"}</span>}{ln.duplicate && <span style={{...S.pillAmber,marginLeft:6}}>Duplicate payout row</span>}</span>
                <span style={{color:C.inkLight,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={inv?itemsLabel(inv):""}>{inv?itemsLabel(inv):""}</span>
                <span style={{fontWeight:500,color:C.ink,textAlign:"right"}}>${(Number.isFinite(ln.invoiceAmount)?ln.invoiceAmount:(Number(inv&&inv.total)||0)).toFixed(2)}</span>
                <span style={{fontWeight:500,color:C.teal,textAlign:"right"}} title={ln.rate!=null?`Paid at ${Number(ln.rate).toFixed(2)}%`:""}>${(Number.isFinite(ln.commission)?ln.commission:0).toFixed(2)}</span>
                {inv && onJumpToInvoice
                  ? <button style={{...S.btnSmall,fontSize:10,padding:"3px 8px"}} onClick={()=>onJumpToInvoice(inv)}>View</button>
                  : <span></span>}
              </div>
              {ln.corrections.map(c => (
                <div key={c.id} style={{display:"grid",gridTemplateColumns:cols,gap:8,padding:"4px 18px 6px",fontSize:10,color:C.inkLight,background:"#FFFBEB"}}>
                  <span></span><span>{c.created_at?new Date(c.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric"}):""}</span>
                  <span style={{gridColumn:"3 / span 2"}}>Correction ({c.status}): {c.reason}</span>
                  <span></span>
                  <span style={{fontWeight:600,color:c.delta<0?C.red:C.green,textAlign:"right"}}>{fmtSignedMoney(c.delta)}</span>
                  <span></span>
                </div>
              ))}
            </div>
          );
        })}
        {settleMap.has(b.batchId) && (
          <div style={{padding:"8px 18px",fontSize:11,color:C.inkMid,background:"#FFFBEB",borderTop:`1px solid ${C.border}`}}>
            Corrections settled in this payout: {settleMap.get(b.batchId).count} ({fmtSignedMoney(settleMap.get(b.batchId).total)}). You received ${b.commission.toFixed(2)} for the invoices {fmtSignedMoney(settleMap.get(b.batchId).total)} for corrections = ${(b.commission+settleMap.get(b.batchId).total).toFixed(2)}.
          </div>
        )}
        <div style={{display:"grid",gridTemplateColumns:cols,gap:8,padding:"10px 18px",fontSize:11,fontWeight:600,background:C.tealLight}}>
          <span></span><span></span><span></span>
          <span style={{color:C.teal}}>Batch total</span>
          <span></span>
          <span style={{color:C.teal,textAlign:"right"}}>${b.commission.toFixed(2)}</span>
          <span></span>
        </div>
      </div>
    );
  };

  return (
    <div style={S.content}>
      {/* Rate card — Pending payout flashes red when > 0 so the rep can see
          at a glance that something is owed to them. */}
      <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 20px",marginBottom:16,display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:16}}>
        <div>
          <div style={{fontSize:11,color:C.inkLight,marginBottom:3}}>Your commission rate</div>
          <div style={{fontSize:28,fontWeight:700,color:C.teal}}>{rate}%</div>
          <div style={{fontSize:12,color:C.inkLight}}>On all invoiced sales</div>
        </div>
        <div>
          <div style={{fontSize:11,color:C.inkLight,marginBottom:3}}>Pending payout (all invoices not yet paid out)</div>
          <div style={{fontSize:24,fontWeight:600,color:pendingTotal>0?"#B91C1C":C.inkLight}}>${pendingTotal.toFixed(2)}</div>
          <div style={{fontSize:12,color:C.inkLight}}>Pay schedule: {profile.paySchedule||"—"}</div>
          {corrSummary.open.length>0 && (
            <div style={{fontSize:12,marginTop:6,color:C.inkMid}}>
              Open corrections <strong style={{color:corrSummary.openTotal<0?C.red:C.green}}>{fmtSignedMoney(corrSummary.openTotal)}</strong>
              {" · "}<strong>Net owed ${netOwedTotal.toFixed(2)}</strong>
              <div style={{fontSize:11,color:C.inkLight,marginTop:2}}>Net owed = pending payout + open corrections (an invoice changed after it was paid).</div>
            </div>
          )}
        </div>
        <div>
          <div style={{fontSize:11,color:C.inkLight,marginBottom:3}}>YTD earned</div>
          <div style={{fontSize:24,fontWeight:600,color:C.ink}}>${ytdEarned.toFixed(2)}</div>
          <div style={{fontSize:12,color:C.inkLight}}>{ytdInvoices.length} invoices this year</div>
        </div>
        <div>
          <div style={{fontSize:11,color:C.inkLight,marginBottom:3}}>YTD paid out</div>
          <div style={{fontSize:24,fontWeight:600,color:C.green}}>${ytdPaidOutWithCorr.toFixed(2)}</div>
          <div style={{fontSize:12,color:C.inkLight}}>{paidBatchesList.filter(b => b.paidAt && new Date(b.paidAt).getFullYear() === currentYear).length} batches this year{ytdPaidOutWithCorr!==ytdPaidOut ? ` · incl. settled corrections ${fmtSignedMoney(ytdPaidOutWithCorr-ytdPaidOut)}` : ""}</div>
        </div>
      </div>

      {/* Corrections: only rendered when this rep has any (and nothing at all
          when migration 0031 has not been run). */}
      <CorrectionsCard summary={corrSummary} mode="rep" title="Corrections to earlier payouts"/>

      {/* ── Pending ── Everything not yet in a batch. When admin marks a
          batch paid, these rows clear out and appear in Payment history
          below. New invoices arriving after spawn a fresh row here. */}
      {pendingPeriodsBatched.length > 0 && (
        <div style={S.card}>
          <div style={S.cardHdr}>
            <span style={S.cardTitle}>Pending payment</span>
            <span style={S.pillAmber}>Awaiting next payout</span>
          </div>
          {pendingPeriodsBatched.map(p => {
            const expandKey = p.periodLabel + "-pending";
            const isExpanded = expandedPeriod === expandKey;
            return (
              <div key={expandKey}>
                <div
                  onClick={()=>setExpandedPeriod(isExpanded?null:expandKey)}
                  style={{display:"grid",gridTemplateColumns:"120px 1fr 100px 100px 120px",gap:8,padding:"12px 18px",borderBottom:`1px solid ${C.border}`,fontSize:12,alignItems:"center",cursor:"pointer",background:isExpanded?C.blueLight:C.amberLight}}
                >
                  <span style={{fontWeight:600,color:C.ink}}>{p.periodLabel}</span>
                  <span style={{color:C.inkLight}}>
                    {p.invoices.length} invoice{p.invoices.length===1?"":"s"} · ${p.sales.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} sales · {rate}% rate
                  </span>
                  <span style={{fontWeight:700,color:"#B91C1C",textAlign:"right"}}>${p.commission.toFixed(2)}</span>
                  <span style={S.pillAmber}>Pending</span>
                  <span style={{fontSize:11,color:C.blue}}>View invoices {isExpanded?"▲":"▼"}</span>
                </div>
                {isExpanded && renderInvoiceRows(p.invoices)}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Payment history (batches) ── One row per admin payout. Frozen:
          new invoices never retroactively join a batch. Each batch shows
          paidAt + method + invoice count. Expand to see the exact invoices
          that were in the batch. */}
      <div style={S.card}>
        <div style={S.cardHdr}>
          <span style={S.cardTitle}>Payment history</span>
          <span style={{fontSize:11,color:C.inkLight}}>{paidBatchesList.length} batch{paidBatchesList.length===1?"":"es"}</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"120px 120px 100px 80px 100px 100px",gap:8,padding:"8px 18px",fontSize:11,fontWeight:600,color:C.inkLight,borderBottom:`1px solid ${C.border}`,background:C.surface}}>
          <span>Paid date</span><span>Method</span><span style={{textAlign:"right"}}>Invoices</span><span style={{textAlign:"right"}}>Period</span><span style={{textAlign:"right"}}>Commission</span><span></span>
        </div>
        {paidBatchesList.length===0 && (
          <div style={{padding:"20px 18px",fontSize:12,color:C.inkLight,textAlign:"center"}}>No payouts yet. Once admin marks a batch paid, it'll appear here.</div>
        )}
        {paidBatchesList.map(b => {
          const expandKey = b.batchId + "-history";
          const isExpanded = expandedPeriod === expandKey;
          return (
            <div key={expandKey}>
              <div
                onClick={()=>setExpandedPeriod(isExpanded?null:expandKey)}
                style={{display:"grid",gridTemplateColumns:"120px 120px 100px 80px 100px 100px",gap:8,padding:"11px 18px",borderBottom:`1px solid ${C.border}`,fontSize:12,alignItems:"center",cursor:"pointer",background:isExpanded?C.blueLight:"transparent"}}
              >
                <span style={{fontWeight:600,color:C.ink}}>{b.paidAt ? new Date(b.paidAt).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}) : "—"}</span>
                <span style={{color:C.inkLight}}>{b.method||"—"}</span>
                <span style={{color:C.inkLight,textAlign:"right"}}>{b.invoiceCount}</span>
                <span style={{color:C.inkLight,fontSize:11,textAlign:"right"}}>{b.periodLabel || (b.invoices[0] ? repMetricsParseDate(b.invoices[0])?.toLocaleDateString("en-US",{month:"short",year:"numeric"}) : "—")}</span>
                <span style={{fontWeight:600,color:C.green,textAlign:"right"}}>${b.commission.toFixed(2)}</span>
                <span style={{display:"flex",gap:4,justifyContent:"flex-end",alignItems:"center"}}>
                  {b.legacy && <span style={S.pillGray}>Legacy</span>}
                  <span style={S.pillGreen}>Paid</span>
                </span>
              </div>
              {isExpanded && renderBatchRows(b)}
            </div>
          );
        })}
        {corrOnlyHistory.map(cs => (
          <div key={"corr-"+cs.batchId} style={{display:"grid",gridTemplateColumns:"120px 120px 100px 80px 100px 100px",gap:8,padding:"11px 18px",borderBottom:`1px solid ${C.border}`,fontSize:12,alignItems:"center",background:"#FFFBEB"}} title={`Corrections settlement ${cs.batchId}`}>
            <span style={{fontWeight:600,color:C.ink}}>{cs.paidAt ? new Date(cs.paidAt).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"}) : "—"}</span>
            <span style={{color:C.inkLight}}>{cs.method||"—"}</span>
            <span style={{color:C.inkLight,textAlign:"right"}}>{cs.count}</span>
            <span style={{color:C.inkLight,fontSize:11,textAlign:"right"}}>Corrections</span>
            <span style={{fontWeight:600,color:cs.total<0?C.red:C.green,textAlign:"right"}}>{fmtSignedMoney(cs.total)}</span>
            <span style={{display:"flex",gap:4,justifyContent:"flex-end"}}><span style={S.pillAmber}>Corrections settlement</span></span>
          </div>
        ))}
      </div>
    </div>
  );
}
// ─── ROUTE PLANNER ────────────────────────────────────────────────────────────

const INIT_ROUTE_CUSTOMERS = [
  {id:1, company:"Apex Car Care LLC",       contact:"John Smith",  email:"john@apexcarcare.com",  phone:"619-555-0100", address:"1234 Industrial Blvd, San Diego, CA 92101",    lat:32.7157, lng:-117.1611, lastVisit:"2025-03-14", visitCycle:30, notes:"Prefers morning visits. Heavy DPF customer.", priority:"high",   tier:"distributor", ytd:42100, ein:"82-1234567", website:"www.apexcarcare.com"},
  {id:2, company:"FastLane Auto Parts",      contact:"Maria Reyes", email:"maria@fastlane.com",    phone:"623-555-0200", address:"500 Commerce Ave, Phoenix, AZ 85001",          lat:33.4484, lng:-112.0740, lastVisit:"2025-03-28", visitCycle:30, notes:"Interested in adding wiper blade line.",    priority:"medium", tier:"dealer",      ytd:18450, ein:"74-9876543", website:""},
  {id:3, company:"Westside Repair Center",   contact:"Dave Kim",    email:"dave@westsiderepair.com",phone:"661-555-0300", address:"77 Main St, Bakersfield, CA 93301",            lat:35.3733, lng:-119.0187, lastVisit:"2025-02-20", visitCycle:30, notes:"New owner — needs product training.",       priority:"high",   tier:"dealer",      ytd:9200,  ein:"33-7890123", website:""},
  {id:4, company:"Pacific Fleet Mgmt",       contact:"Amy Chen",    email:"amy@pacificfleet.com",  phone:"858-555-0400", address:"2200 Harbor Dr, Long Beach, CA 90802",         lat:33.7701, lng:-118.1937, lastVisit:"2025-04-01", visitCycle:30, notes:"Fleet of 80 vehicles. Big opportunity.",    priority:"high",   tier:"enterprise",  ytd:0,     ein:"55-6543210", website:"www.pacificfleet.com"},
  {id:5, company:"Desert Auto Service",      contact:"Rick Torres", email:"rick@desertauto.com",   phone:"760-555-0500", address:"340 Palm Canyon Dr, Palm Springs, CA 92262",   lat:33.8303, lng:-116.5453, lastVisit:"2025-03-05", visitCycle:30, notes:"Good catalyst cleaner volume.",             priority:"medium", tier:"dealer",      ytd:7800,  ein:"",           website:""},
  {id:6, company:"Valley Truck Center",      contact:"Bob Walsh",   email:"bob@valleytruck.com",   phone:"559-555-0600", address:"4500 E Kings Canyon Rd, Fresno, CA 93702",     lat:36.7378, lng:-119.7871, lastVisit:"2025-01-15", visitCycle:30, notes:"Diesel specialist — DPF focus.",            priority:"high",   tier:"distributor", ytd:31200, ein:"91-3456789", website:"www.valleytruck.com"},
  {id:7, company:"Coastal Motor Works",      contact:"Lisa Park",   email:"lisa@coastalmotor.com", phone:"619-555-0700", address:"850 Rosecrans St, San Diego, CA 92106",        lat:32.7341, lng:-117.2141, lastVisit:"2025-04-05", visitCycle:30, notes:"Recently added engine flush to lineup.",   priority:"low",    tier:"dealer",      ytd:4100,  ein:"",           website:""},
  {id:8, company:"Inland Empire Auto",       contact:"Carlos V.",   email:"carlos@inlandemp.com",  phone:"909-555-0800", address:"1100 Iowa Ave, Riverside, CA 92507",           lat:33.9806, lng:-117.3755, lastVisit:"2025-03-20", visitCycle:30, notes:"Growing account — 3 locations.",           priority:"medium", tier:"distributor", ytd:15600, ein:"",           website:""},
];

const INIT_POTENTIAL_CUSTOMERS = [
  {id:101, company:"Golden State Tire & Brake",  contact:"Tony Morales",  email:"tony@gstirebrake.com",   phone:"619-555-1100", address:"2700 National Ave, San Diego, CA 92113",       lat:32.6869, lng:-117.1237, notes:"Tire and brake shop, 4 bays. Could use catalytic cleaner and DPF line.", priority:"high",   tier:"dealer", website:"", ein:"", visitHistory:[], comments:[{date:"Mar 10, 2025",text:"Cold called - Tony interested in DPF products. Asked for samples."}], reminders:[{date:"2025-04-15",text:"Follow up on DPF samples",done:false}], lastVisit:"2025-03-10", visitCycle:14},
  {id:102, company:"Summit Auto Electric",       contact:"Karen Liu",     email:"karen@summitauto.com",   phone:"858-555-1200", address:"4480 Clairemont Dr, San Diego, CA 92117",      lat:32.8328, lng:-117.2063, notes:"Specializes in electrical and engine diagnostics.",                      priority:"medium", tier:"dealer", website:"www.summitautoelectric.com", ein:"", visitHistory:[], comments:[{date:"Mar 22, 2025",text:"Met at SEMA regional. She wants pricing sheet for fuel system line."}], reminders:[{date:"2025-04-20",text:"Send pricing sheet and follow up",done:false}], lastVisit:"2025-03-22", visitCycle:14},
  {id:103, company:"Border City Diesel",         contact:"Miguel Espinoza",email:"miguel@borderdiesel.com",phone:"619-555-1300", address:"1180 E Beyer Blvd, San Ysidro, CA 92173",     lat:32.5545, lng:-117.0489, notes:"Heavy diesel shop near border. Fleet customers. Big DPF opportunity.",   priority:"high",   tier:"distributor", website:"", ein:"", visitHistory:[], comments:[{date:"Feb 28, 2025",text:"Drove by, shop was busy. Left business card with front desk."},{date:"Mar 15, 2025",text:"Miguel called back. Wants demo of DPF foam cleaner."}], reminders:[{date:"2025-04-12",text:"Schedule DPF demo visit",done:false}], lastVisit:"2025-03-15", visitCycle:14},
  {id:104, company:"Oceanview Motors",           contact:"Sarah Bennett", email:"sarah@oceanviewmotors.com",phone:"760-555-1400", address:"310 S Coast Hwy, Oceanside, CA 92054",        lat:33.1959, lng:-117.3795, notes:"European car specialist. Interested in catalytic converter cleaner.",    priority:"medium", tier:"dealer", website:"www.oceanviewmotors.com", ein:"", visitHistory:[], comments:[], reminders:[], lastVisit:"", visitCycle:21},
  {id:105, company:"High Desert Auto Repair",    contact:"Jim Booker",    email:"jim@highdesertauto.com", phone:"760-555-1500", address:"56100 29 Palms Hwy, Yucca Valley, CA 92284",   lat:34.1142, lng:-116.4325, notes:"Remote location, general repair. Could be good for basic product line.", priority:"low",    tier:"dealer", website:"", ein:"", visitHistory:[], comments:[{date:"Apr 1, 2025",text:"Phone call. Jim says he orders from O'Reilly currently. Open to wholesale pricing."}], reminders:[{date:"2025-04-25",text:"Send wholesale comparison sheet",done:false}], lastVisit:"", visitCycle:30},
  {id:106, company:"Rancho Performance",         contact:"Alex Vargas",   email:"alex@ranchoperf.com",    phone:"951-555-1600", address:"24850 Alessandro Blvd, Moreno Valley, CA 92553",lat:33.9250, lng:-117.2178, notes:"Performance shop. Octane booster and fuel system products would fit.",   priority:"medium", tier:"dealer", website:"www.ranchoperformance.com", ein:"", visitHistory:[], comments:[], reminders:[{date:"2025-04-18",text:"First visit - bring sample kit",done:false}], lastVisit:"", visitCycle:21},
];

// Haversine distance between two lat/lng points (miles)
function haversine(lat1, lng1, lat2, lng2) {
  const R = 3959;
  const dLat = (lat2-lat1)*Math.PI/180;
  const dLng = (lng2-lng1)*Math.PI/180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
  return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
}

// Sort by distance from starting point (closest to furthest)
function sortByRoute(customers, startLat=32.7157, startLng=-117.1611) {
  if(customers.length===0) return [];
  return [...customers]
    .map(c=>({...c, distFromStart:Math.round(haversine(startLat,startLng,c.lat,c.lng))}))
    .sort((a,b)=>a.distFromStart-b.distFromStart);
}

// Visit status based on days since last visit
function visitStatus(lastVisit, cycle=30) {
  if(!lastVisit) return {status:"never",days:999,label:"Never visited",color:"#7C3AED",bg:"#EDE9FE"};
  const days = Math.floor((Date.now()-new Date(lastVisit))/(1000*60*60*24));
  const pct = days/cycle;
  if(pct<0.7)  return {status:"ok",     days, label:`${days}d ago`,      color:C.teal,   bg:C.tealLight};
  if(pct<1.0)  return {status:"soon",   days, label:`Due in ${cycle-days}d`, color:"#B45309", bg:"#FEF3C7"};
  if(pct<1.3)  return {status:"late",   days, label:`${days-cycle}d late`,color:"#B91C1C", bg:"#FEE2E2"};
  return              {status:"overdue",days, label:`${days-cycle}d overdue`,color:"#7C3AED",bg:"#EDE9FE"};
}

// ─── POTENTIAL CUSTOMERS ─────────────────────────────────────────────────────
function RepPotentialCustomers({prospects, setProspects, customers, setCustomers, repName, repIdForSync, onVisitSyncPending}) {
  const [sortMode, setSortMode] = useState("distance");
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [addingProspect, setAddingProspect] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newComment, setNewComment] = useState("");
  const [reminderForm, setReminderForm] = useState({date:"",text:""});
  const [importError, setImportError] = useState("");
  const [toast, setToast] = useState("");
  const [newProsp, setNewProsp] = useState({company:"",contact:"",phone:"",email:"",address:"",city:"",state:"",zip:"",ein:"",tier:"dealer",notes:"",priority:"medium",website:""});
  // Duplicate-add guard — same reasoning as RepRoutePlanner's "Add to
  // route": "Add prospect" had no check against this rep's existing
  // prospects OR route customers, so re-adding a shop already on either
  // list silently created a second, unrelated row instead of surfacing it.
  const [dupWarning, setDupWarning] = useState(null);
  const today = new Date().toISOString().slice(0,10);
  const nowStr = () => new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3000); };

  const sorted = (() => {
    let list = [...prospects];
    if(search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c=>c.company?.toLowerCase().includes(q)||c.contact?.toLowerCase().includes(q)||c.address?.toLowerCase().includes(q)||c.email?.toLowerCase().includes(q));
    }
    if(sortMode==="distance") return sortByRoute(list);
    if(sortMode==="priority") return [...list].sort((a,b)=>["high","medium","low"].indexOf(a.priority)-["high","medium","low"].indexOf(b.priority));
    if(sortMode==="recent") return [...list].sort((a,b)=>(b.lastVisit||"").localeCompare(a.lastVisit||""));
    return [...list].sort((a,b)=>a.company.localeCompare(b.company));
  })();

  const selected = prospects.find(c=>c.id===selectedId);
  const priorityColor = {high:{color:"#B91C1C",bg:"#FEE2E2"}, medium:{color:"#B45309",bg:"#FEF3C7"}, low:{color:C.teal,bg:C.tealLight}};
  const dueReminders = prospects.reduce((n,p)=>(p.reminders||[]).filter(r=>!r.done&&r.date<=today).length+n,0);

  const markAsCustomer = (prospect) => {
    if(!window.confirm(`Convert "${prospect.company}" to a route customer? They will be moved to the Route Planner.`)) return;
    setCustomers(cs=>[...cs,{...prospect, comments:undefined, reminders:undefined, visitHistory:[...(prospect.visitHistory||[]),{date:today,note:"Converted from potential customer to active route."}]}]);
    setProspects(ps=>ps.filter(p=>p.id!==prospect.id));
    setSelectedId(null);
    showToast(prospect.company+" moved to Route Planner");
  };

  const deleteProspect = (prospect) => {
    if(!window.confirm(`Permanently delete "${prospect.company}"? This business will be removed from your list.`)) return;
    setProspects(ps=>ps.filter(p=>p.id!==prospect.id));
    setSelectedId(null);
    showToast(prospect.company+" deleted");
  };

  const addComment = (prospId) => {
    if(!newComment.trim()) return;
    setProspects(ps=>ps.map(p=>p.id===prospId?{...p,comments:[...(p.comments||[]),{date:nowStr(),text:newComment.trim()}]}:p));
    setNewComment("");
  };

  const addReminder = (prospId) => {
    if(!reminderForm.date||!reminderForm.text.trim()) return;
    setProspects(ps=>ps.map(p=>p.id===prospId?{...p,reminders:[...(p.reminders||[]),{date:reminderForm.date,text:reminderForm.text.trim(),done:false}]}:p));
    setReminderForm({date:"",text:""});
  };

  const toggleReminder = (prospId, idx) => {
    setProspects(ps=>ps.map(p=>p.id===prospId?{...p,reminders:(p.reminders||[]).map((r,i)=>i===idx?{...r,done:!r.done}:r)}:p));
  };

  const logVisit = async (prospId, note) => {
    const prosp = prospects.find(p=>p.id===prospId);
    if (!prosp) return;
    // Generate a stable client_id ONCE (Phase B — shop_visits.client_id is
    // the exact-key dedupe target, migration 0014). Same id is used for the
    // SHARED_VISITS.add mirror so both sides point to the same entry.
    const clientId = Date.now() + "-" + Math.random().toString(36).slice(2, 6);
    // Rule Zero: Supabase first. Await the authoritative write, then mirror
    // to localStorage regardless of outcome so the rep never loses the note.
    let supabaseOk = true;
    if (repIdForSync) {
      try {
        const row = await upsertShopVisit({
          clientId,
          repId: repIdForSync,
          repName: repName || "Sales Rep",
          company: prosp.company,
          note: note || "",
          date: today,
          type: "cold_call",
          duration: 30,
        });
        if (!row) supabaseOk = false;
      } catch(e) {
        console.warn("[RepPotentialCustomers logVisit] shop_visits write threw:", e?.message || e);
        supabaseOk = false;
      }
    }
    // Per-prospect visitHistory (inline on the prospect record). Phase A's
    // rep_route_customers diff-effect syncs this when `prospects` changes.
    setProspects(ps=>ps.map(p=>p.id===prospId?{...p,lastVisit:today,visitHistory:[...(p.visitHistory||[]),{date:today,note}]}:p));
    // localStorage mirror — always runs, pre-set id so it matches the
    // Supabase row (or pending-retry row).
    SHARED_VISITS.add({
      id: clientId,
      rep: repName || "Sales Rep",
      company: prosp.company,
      date: today,
      note: note || "",
      type: "cold_call",
    });
    if (!supabaseOk && typeof onVisitSyncPending === "function") {
      onVisitSyncPending(clientId);
    }
  };

  const exportToCSV = () => {
    const headers = ["Company","Contact","Email","Phone","Address","Tier","Priority","Last Visit","Notes","Website","EIN"];
    const rows = prospects.map(c=>[c.company,c.contact||"",c.email||"",c.phone||"",c.address||"",c.tier||"",c.priority||"",c.lastVisit||"",c.notes||"",c.website||"",c.ein||""]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download="mpc_potential_customers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const importFromCSV = (e) => {
    const file = e.target.files[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const lines = ev.target.result.split("\n").filter(l=>l.trim());
        const headers = lines[0].split(",").map(h=>h.replace(/"/g,"").trim().toLowerCase());
        const imported = lines.slice(1).map((line,idx)=>{
          const vals = line.match(/(".*?"|[^,]+)(?=,|$)/g)||[];
          const clean = vals.map(v=>v.replace(/^"|"$/g,"").replace(/""/g,'"').trim());
          const get = (key) => clean[headers.indexOf(key)]||"";
          return {id:Date.now()+idx,company:get("company"),contact:get("contact"),email:get("email"),phone:get("phone"),address:get("address"),tier:get("tier")||"dealer",priority:get("priority")||"medium",lastVisit:get("last visit"),notes:get("notes"),website:get("website"),ein:get("ein"),lat:32.7+Math.random()*4,lng:-118+Math.random()*6,visitHistory:[],comments:[],reminders:[],visitCycle:14};
        }).filter(c=>c.company);
        if(imported.length>0) {setProspects(ps=>[...ps,...imported]);setImportError(`Imported ${imported.length} prospects`);}
        else setImportError("No valid rows found.");
      } catch(err) {setImportError("Import failed - check CSV format.");}
    };
    reader.readAsText(file); e.target.value="";
  };

  return (
    <div style={{display:"flex",height:"calc(100vh - 54px)",overflow:"hidden"}}>
      {toast && <div style={{position:"fixed",top:20,right:20,background:C.teal,color:C.white,padding:"10px 20px",borderRadius:8,fontSize:13,fontWeight:500,zIndex:9999,boxShadow:"0 4px 12px rgba(0,0,0,0.15)"}}>{toast}</div>}

      {/* Add prospect modal */}
      {addingProspect && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
          <div style={{background:C.white,borderRadius:16,padding:"28px 32px",width:580,maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{fontSize:16,fontWeight:600,color:C.ink,marginBottom:4}}>Add potential customer</div>
            <div style={{fontSize:12,color:C.inkLight,marginBottom:16}}>Add a business to your prospect list for tracking and follow-up.</div>
            {dupWarning && (
              <div style={{background:"#FEF3C7",border:"1px solid #FCD34D",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#92400E"}}>
                <strong>{dupWarning.company}</strong> is already on your {dupWarning.where} list — no need to add it again.
              </div>
            )}
            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`}}>Business information</div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Company name *</label><input style={S.input} value={newProsp.company} onChange={e=>{setNewProsp(v=>({...v,company:e.target.value}));setDupWarning(null);}}/></div>
              <div style={S.fg}><label style={S.label}>Account tier</label><select style={S.select} value={newProsp.tier} onChange={e=>setNewProsp(v=>({...v,tier:e.target.value}))}><option value="dealer">Dealer</option><option value="distributor">Distributor</option><option value="enterprise">Enterprise</option></select></div>
            </div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Contact name</label><input style={S.input} value={newProsp.contact} onChange={e=>setNewProsp(v=>({...v,contact:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>Email</label><input style={S.input} value={newProsp.email} onChange={e=>setNewProsp(v=>({...v,email:e.target.value}))}/></div>
            </div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Phone</label><input style={S.input} value={newProsp.phone} onChange={e=>setNewProsp(v=>({...v,phone:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>Priority</label><select style={S.select} value={newProsp.priority} onChange={e=>setNewProsp(v=>({...v,priority:e.target.value}))}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
            </div>
            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`,marginTop:4}}>Address</div>
            <div style={S.fg}><label style={S.label}>Street address *</label><input style={S.input} value={newProsp.address} onChange={e=>setNewProsp(v=>({...v,address:e.target.value}))}/></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 80px 100px",gap:10}}>
              <div style={S.fg}><label style={S.label}>City</label><input style={S.input} value={newProsp.city} onChange={e=>setNewProsp(v=>({...v,city:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>State</label><input style={S.input} value={newProsp.state} onChange={e=>setNewProsp(v=>({...v,state:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>ZIP</label><input style={S.input} value={newProsp.zip} onChange={e=>setNewProsp(v=>({...v,zip:e.target.value}))}/></div>
            </div>
            <div style={S.fg}><label style={S.label}>Notes</label><input style={S.input} value={newProsp.notes} onChange={e=>setNewProsp(v=>({...v,notes:e.target.value}))} placeholder="What products might interest them..."/></div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
              <button style={S.btnSecondary} onClick={()=>{setAddingProspect(false);setDupWarning(null);}}>Cancel</button>
              <button style={S.btnPrimary} disabled={!newProsp.company||!newProsp.address} onClick={()=>{
                // Duplicate guard — same check as RepRoutePlanner's "Add to
                // route": normalized company name against both lists before
                // creating a new row.
                const norm = s => String(s||"").trim().toLowerCase();
                const target = norm(newProsp.company);
                const existingProsp = (prospects||[]).find(p=>norm(p.company)===target);
                if (existingProsp) { setDupWarning({company:existingProsp.company, where:"potential customers"}); return; }
                const existingCust = (customers||[]).find(c=>norm(c.company)===target);
                if (existingCust) { setDupWarning({company:existingCust.company, where:"route customers"}); return; }
                const fullAddr = [newProsp.address,newProsp.city,newProsp.state,newProsp.zip].filter(Boolean).join(", ");
                setProspects(ps=>[...ps,{id:Date.now(),...newProsp,address:fullAddr||newProsp.address,lat:32.7+Math.random()*2,lng:-117.5+Math.random()*3,lastVisit:"",visitHistory:[],comments:[],reminders:[],visitCycle:14}]);
                setAddingProspect(false);
                setDupWarning(null);
                setNewProsp({company:"",contact:"",phone:"",email:"",address:"",city:"",state:"",zip:"",ein:"",tier:"dealer",notes:"",priority:"medium",website:""});
              }}>Add prospect</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit prospect modal */}
      {editingProspect && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
          <div style={{background:C.white,borderRadius:16,padding:"28px 32px",width:580,maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{fontSize:16,fontWeight:600,color:C.ink,marginBottom:16}}>Edit prospect</div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Company *</label><input style={S.input} value={editForm.company||""} onChange={e=>setEditForm(f=>({...f,company:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>Tier</label><select style={S.select} value={editForm.tier||"dealer"} onChange={e=>setEditForm(f=>({...f,tier:e.target.value}))}><option value="dealer">Dealer</option><option value="distributor">Distributor</option><option value="enterprise">Enterprise</option></select></div>
            </div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Contact</label><input style={S.input} value={editForm.contact||""} onChange={e=>setEditForm(f=>({...f,contact:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>Email</label><input style={S.input} value={editForm.email||""} onChange={e=>setEditForm(f=>({...f,email:e.target.value}))}/></div>
            </div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Phone</label><input style={S.input} value={editForm.phone||""} onChange={e=>setEditForm(f=>({...f,phone:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>Priority</label><select style={S.select} value={editForm.priority||"medium"} onChange={e=>setEditForm(f=>({...f,priority:e.target.value}))}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
            </div>
            <div style={S.fg}><label style={S.label}>Address</label><input style={S.input} value={editForm.address||""} onChange={e=>setEditForm(f=>({...f,address:e.target.value}))}/></div>
            <div style={S.fg}><label style={S.label}>Notes</label><textarea style={{...S.input,height:64,resize:"vertical"}} value={editForm.notes||""} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))}/></div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
              <button style={S.btnSecondary} onClick={()=>setEditingProspect(null)}>Cancel</button>
              <button style={S.btnPrimary} onClick={()=>{setProspects(ps=>ps.map(p=>p.id===editingProspect.id?{...p,...editForm}:p));setEditingProspect(null);}}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* LEFT PANEL */}
      <div style={{width:380,background:C.white,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column",flexShrink:0}}>
        <div style={{padding:"16px 18px",borderBottom:`1px solid ${C.border}`,background:"#1E293B"}}>
          <div style={{fontSize:15,fontWeight:600,color:C.white,marginBottom:6}}>Potential customers</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
            {[{l:"Total",v:prospects.length,c:"rgba(255,255,255,0.9)"},{l:"High priority",v:prospects.filter(p=>p.priority==="high").length,c:"#FCA5A5"},{l:"Reminders due",v:dueReminders,c:dueReminders>0?"#FCD34D":"#6EE7B7"}].map(s=>(
              <div key={s.l} style={{textAlign:"center",background:"rgba(255,255,255,0.08)",borderRadius:8,padding:"8px 4px"}}>
                <div style={{fontSize:18,fontWeight:700,color:s.c}}>{s.v}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.5)",marginTop:1}}>{s.l}</div>
              </div>
            ))}
          </div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search prospects..." style={{width:"100%",padding:"8px 12px",borderRadius:8,border:"1px solid rgba(255,255,255,0.15)",background:"rgba(255,255,255,0.1)",color:C.white,fontSize:12,outline:"none",boxSizing:"border-box"}}/>
        </div>

        <div style={{padding:"10px 12px",borderBottom:`1px solid ${C.border}`,background:C.surface}}>
          <div style={{display:"flex",gap:4,marginBottom:8}}>
            {[["distance","📍 Distance"],["priority","Priority"],["recent","Recent"],["alpha","A-Z"]].map(([k,l])=>(
              <button key={k} onClick={()=>setSortMode(k)} style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${sortMode===k?C.blue:C.border}`,background:sortMode===k?C.blueLight:C.white,color:sortMode===k?C.blue:C.inkMid,fontSize:10,cursor:"pointer"}}>{l}</button>
            ))}
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <button style={S.btnSmall} onClick={()=>setAddingProspect(true)}>+ Add</button>
            <button onClick={exportToCSV} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px"}}>Export</button>
            <label style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",cursor:"pointer",margin:0,border:`1px solid ${C.border}`,borderRadius:8,color:C.inkMid}}>Import<input type="file" accept=".csv" style={{display:"none"}} onChange={importFromCSV}/></label>
          </div>
          {importError && <div style={{marginTop:6,fontSize:11,padding:"5px 10px",borderRadius:6,background:importError.startsWith("Imported")?C.greenLight:C.redLight,color:importError.startsWith("Imported")?C.green:C.red}}>{importError}</div>}
        </div>

        <div style={{flex:1,overflowY:"auto"}}>
          {sorted.length===0 && <div style={{padding:"32px 16px",textAlign:"center",color:C.inkLight}}><div style={{fontSize:24,marginBottom:8}}>📋</div><div style={{fontSize:13,fontWeight:500,color:C.inkMid}}>No prospects found</div></div>}
          {sorted.map((p,i)=>{
            const isSelected = selectedId===p.id;
            const dueCount = (p.reminders||[]).filter(r=>!r.done&&r.date<=today).length;
            return (
              <div key={p.id} onClick={()=>setSelectedId(isSelected?null:p.id)} style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,cursor:"pointer",background:isSelected?"#EFF6FF":C.white,transition:"background 0.15s"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                  <div style={{minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      {sortMode==="distance"&&<div style={{width:20,height:20,borderRadius:"50%",background:"#1E293B",color:"#fff",fontSize:10,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{i+1}</div>}
                      <div style={{fontSize:13,fontWeight:600,color:C.ink,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.company}</div>
                    </div>
                    <div style={{fontSize:11,color:C.inkLight}}>{p.contact}{p.email?" - "+p.email:""}</div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3,flexShrink:0,marginLeft:8}}>
                    <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:8,background:priorityColor[p.priority]?.bg,color:priorityColor[p.priority]?.color}}>{p.priority}</span>
                    {dueCount>0&&<span style={{fontSize:9,fontWeight:600,padding:"1px 6px",borderRadius:8,background:"#FEF3C7",color:"#B45309"}}>{dueCount} reminder{dueCount>1?"s":""}</span>}
                  </div>
                </div>
                <div style={{fontSize:11,color:C.inkLight}}>{p.address}</div>
                {sortMode==="distance"&&p.distFromStart>0&&<div style={{fontSize:10,color:C.inkLight,marginTop:2}}>{p.distFromStart} mi away</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div style={{flex:1,overflowY:"auto",background:C.surface}}>
        {!selected ? (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:40,textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:16}}>📋</div>
            <div style={{fontSize:18,fontWeight:600,color:C.ink,marginBottom:8}}>Select a prospect to view details</div>
            <div style={{fontSize:13,color:C.inkLight,maxWidth:360}}>Track potential customers, add comments from visits, set follow-up reminders, and convert them to active route customers when they start ordering.</div>
            {dueReminders>0&&<div style={{marginTop:16,background:"#FEF3C7",border:"1px solid #FCD34D",borderRadius:10,padding:"12px 20px",fontSize:13,color:"#B45309",fontWeight:500}}>{dueReminders} reminder{dueReminders>1?"s":""} due today or overdue</div>}
          </div>
        ) : (
          <div style={{padding:24}}>
            {/* Header */}
            <div style={{background:C.white,borderRadius:12,padding:"20px 24px",marginBottom:16,border:`1px solid ${C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div>
                  <div style={{fontSize:20,fontWeight:600,color:C.ink,marginBottom:6}}>{selected.company}</div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <span style={{fontSize:11,padding:"3px 8px",borderRadius:6,background:priorityColor[selected.priority]?.bg,color:priorityColor[selected.priority]?.color,fontWeight:500}}>{selected.priority} priority</span>
                    <span style={{fontSize:11,padding:"3px 8px",borderRadius:6,background:C.blueLight,color:C.blue,fontWeight:500,textTransform:"capitalize"}}>{selected.tier||"dealer"}</span>
                    {selected.lastVisit&&<span style={{fontSize:11,color:C.inkLight}}>Last visit: {selected.lastVisit}</span>}
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button style={{...S.btnSecondary,fontSize:12}} onClick={()=>{setEditForm({...selected});setEditingProspect(selected);}}>Edit</button>
                  <button style={{...S.btnPrimary,fontSize:12,background:C.teal}} onClick={()=>markAsCustomer(selected)}>Mark as customer</button>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,padding:"12px 0",borderTop:`1px solid ${C.border}`}}>
                <div><div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>Contact</div><div style={{fontSize:13,fontWeight:500,color:C.ink}}>{selected.contact||"--"}</div></div>
                <div><div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>Phone</div><a href={`tel:${selected.phone}`} style={{fontSize:13,fontWeight:500,color:C.blue,textDecoration:"none"}}>{selected.phone||"--"}</a></div>
                <div><div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>Email</div><a href={`mailto:${selected.email}`} style={{fontSize:13,fontWeight:500,color:C.blue,textDecoration:"none"}}>{selected.email||"--"}</a></div>
              </div>
            </div>

            {/* Notes */}
            {selected.notes&&<div style={{background:C.white,borderRadius:12,padding:"14px 18px",marginBottom:16,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:12,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Notes</div>
              <div style={{fontSize:13,color:C.inkMid,lineHeight:1.6}}>{selected.notes}</div>
            </div>}

            {/* Reminders */}
            <div style={{background:C.white,borderRadius:12,padding:"14px 18px",marginBottom:16,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:12,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Reminders</div>
              {(selected.reminders||[]).length===0&&<div style={{fontSize:12,color:C.inkLight,marginBottom:8}}>No reminders set.</div>}
              {(selected.reminders||[]).map((r,i)=>(
                <div key={i} style={{display:"flex",gap:8,alignItems:"center",padding:"6px 0",borderBottom:`1px solid ${C.border}`}}>
                  <input type="checkbox" checked={r.done} onChange={()=>toggleReminder(selected.id,i)}/>
                  <span style={{fontSize:12,color:r.done?C.inkLight:(!r.done&&r.date<=today?"#B91C1C":C.ink),textDecoration:r.done?"line-through":"none",flex:1}}>{r.text}</span>
                  <span style={{fontSize:10,color:!r.done&&r.date<=today?"#B91C1C":C.inkLight,fontWeight:!r.done&&r.date<=today?600:400}}>{r.date}</span>
                </div>
              ))}
              <div style={{display:"flex",gap:6,marginTop:8}}>
                <input type="date" style={{...S.input,width:140,fontSize:11}} value={reminderForm.date} onChange={e=>setReminderForm(f=>({...f,date:e.target.value}))}/>
                <input style={{...S.input,flex:1,fontSize:12}} placeholder="Reminder text..." value={reminderForm.text} onChange={e=>setReminderForm(f=>({...f,text:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter")addReminder(selected.id);}}/>
                <button style={S.btnSmall} onClick={()=>addReminder(selected.id)}>Set</button>
              </div>
            </div>

            {/* Comments / visit notes */}
            <div style={{background:C.white,borderRadius:12,padding:"14px 18px",marginBottom:16,border:`1px solid ${C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:12,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em"}}>Comments & visit notes</span>
                <button style={S.btnSmall} onClick={()=>{const note=prompt("Quick visit note (optional):");if(note!==null){logVisit(selected.id,note);showToast("Visit logged");}}}>Log visit</button>
              </div>
              {(selected.comments||[]).length===0&&<div style={{fontSize:12,color:C.inkLight,marginBottom:8}}>No comments yet.</div>}
              {(selected.comments||[]).map((c,i)=>(
                <div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:i<(selected.comments||[]).length-1?`1px solid ${C.border}`:"none"}}>
                  <div style={{fontSize:10,color:C.inkLight,minWidth:80,flexShrink:0}}>{c.date}</div>
                  <div style={{fontSize:12,color:C.ink,lineHeight:1.5}}>{c.text}</div>
                </div>
              ))}
              <div style={{display:"flex",gap:6,marginTop:8}}>
                <input style={{...S.input,flex:1,fontSize:12}} placeholder="Add a comment..." value={newComment} onChange={e=>setNewComment(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addComment(selected.id);}}/>
                <button style={S.btnSmall} onClick={()=>addComment(selected.id)}>Add</button>
              </div>
            </div>

            {/* Visit history */}
            {(selected.visitHistory||[]).length>0&&(
              <div style={{background:C.white,borderRadius:12,overflow:"hidden",marginBottom:16,border:`1px solid ${C.border}`}}>
                <div style={{padding:"12px 18px",borderBottom:`1px solid ${C.border}`}}>
                  <span style={{fontSize:12,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em"}}>Visit history</span>
                </div>
                {[...(selected.visitHistory||[])].reverse().map((v,i)=>(
                  <div key={i} style={{padding:"8px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",gap:8,alignItems:"center",fontSize:12}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:C.teal,display:"inline-block"}}/>
                    <span style={{fontWeight:500,color:C.ink}}>{v.date}</span>
                    {v.note&&<span style={{color:C.inkMid}}>- {v.note}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div style={{display:"flex",gap:10}}>
              <button style={{...S.btnPrimary,background:C.teal,flex:1}} onClick={()=>markAsCustomer(selected)}>Mark as customer (move to route)</button>
              <button style={{...S.btnDanger,padding:"10px 16px"}} onClick={()=>deleteProspect(selected)}>Delete (business closed)</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RepRoutePlanner({customers, setCustomers, prospects, setProspects, onCreateInvoice, onViewInvoice, repName, repIdForSync, onVisitSyncPending}) {
  const [sortMode, setSortMode] = useState("route");
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [logVisitId, setLogVisitId] = useState(null);
  const [visitNote, setVisitNote] = useState("");
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [importError, setImportError] = useState("");
  const [newCust, setNewCust] = useState({company:"",contact:"",phone:"",email:"",address:"",city:"",state:"",zip:"",ein:"",tier:"dealer",notes:"",priority:"medium",visitCycle:30,website:""});
  // Duplicate-add guard: "Add to route" had no check at all against the
  // rep's existing route customers OR prospects, so adding a shop already
  // on the list silently created a second row for the same business
  // (distinct id, no relation to the original) instead of surfacing it.
  // Checked by normalized company name on submit — see findDuplicate below.
  const [dupWarning, setDupWarning] = useState(null);
  const [filter, setFilter] = useState("all");
  const today = new Date().toISOString().slice(0,10);

  const sorted = (() => {
    let list = [...customers];
    // Apply search filter
    if(search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c=>
        c.company?.toLowerCase().includes(q) ||
        c.contact?.toLowerCase().includes(q) ||
        c.address?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.includes(q)
      );
    }
    if(filter==="due")  list=list.filter(c=>visitStatus(c.lastVisit,c.visitCycle).status==="soon"||visitStatus(c.lastVisit,c.visitCycle).status==="late"||visitStatus(c.lastVisit,c.visitCycle).status==="overdue");
    if(filter==="late") list=list.filter(c=>["late","overdue"].includes(visitStatus(c.lastVisit,c.visitCycle).status));
    if(filter==="ok")   list=list.filter(c=>visitStatus(c.lastVisit,c.visitCycle).status==="ok");
    if(sortMode==="route")    return sortByRoute(list);
    if(sortMode==="status")   return [...list].sort((a,b)=>visitStatus(b.lastVisit,b.visitCycle).days-visitStatus(a.lastVisit,a.visitCycle).days);
    if(sortMode==="priority") return [...list].sort((a,b)=>["high","medium","low"].indexOf(a.priority)-["high","medium","low"].indexOf(b.priority));
    return [...list].sort((a,b)=>a.company.localeCompare(b.company));
  })();

  const [visitBusy, setVisitBusy] = useState(false);
  const logVisit = async () => {
    if (visitBusy) return;
    const cust = customers.find(c=>c.id===logVisitId);
    if (!cust) { setLogVisitId(null); setVisitNote(""); return; }
    setVisitBusy(true);
    // Generate a stable client_id ONCE. Used for both the Supabase
    // shop_visits.client_id (exact-key dedupe target, migration 0014) and
    // the SHARED_VISITS.add id so both sides reference the same entry.
    const clientId = Date.now() + "-" + Math.random().toString(36).slice(2, 6);
    const visitEntry = {
      id: clientId,
      rep: repName || "Sales Rep",
      company: cust.company,
      date: today,
      note: visitNote,
      type: "shop_visit",
      duration: 30,
    };
    // Rule Zero: Supabase first. Await the authoritative write, then mirror
    // to localStorage regardless of outcome so the rep never loses the note.
    let supabaseOk = true;
    if (repIdForSync) {
      try {
        const row = await upsertShopVisit({
          clientId,
          repId: repIdForSync,
          repName: visitEntry.rep,
          company: visitEntry.company,
          note: visitEntry.note,
          date: visitEntry.date,
          type: visitEntry.type,
          duration: visitEntry.duration,
        });
        if (!row) supabaseOk = false;
      } catch(e) {
        console.warn("[RepRoutePlanner logVisit] shop_visits write threw:", e?.message || e);
        supabaseOk = false;
      }
    }
    // Per-customer visitHistory (inline on the customer record — still the
    // display surface in the Route Planner UI). This mutation is synced to
    // Supabase via Phase A's rep_route_customers diff-effect because it
    // changes `customers`, which triggers an upsertRepRouteCustomer.
    setCustomers(cs=>cs.map(c=>c.id===logVisitId?{...c,lastVisit:today,visitHistory:[...(c.visitHistory||[]),{date:today,note:visitNote}]}:c));
    // localStorage mirror — always runs, so rep keeps the visit even if
    // Supabase rejected. The pre-generated id ensures the mirror is keyed
    // to the same value as the (pending) Supabase row.
    SHARED_VISITS.add(visitEntry);
    if (!supabaseOk && typeof onVisitSyncPending === "function") {
      onVisitSyncPending(clientId);
    }
    setVisitBusy(false);
    setLogVisitId(null);
    setVisitNote("");
  };

  const stats = {
    total: customers.length,
    ok:      customers.filter(c=>visitStatus(c.lastVisit,c.visitCycle).status==="ok").length,
    due:     customers.filter(c=>visitStatus(c.lastVisit,c.visitCycle).status==="soon").length,
    late:    customers.filter(c=>["late","overdue"].includes(visitStatus(c.lastVisit,c.visitCycle).status)).length,
    totalRoute: sorted.length>0 ? (sorted[sorted.length-1].distFromStart||0) : 0,
  };

  const selected = customers.find(c=>c.id===selectedId);
  const vs = selected ? visitStatus(selected.lastVisit, selected.visitCycle) : null;

  const openMaps = (address) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`,"_blank");
  };

  const openMapsRoute = () => {
    const waypoints = sorted.map(c=>encodeURIComponent(c.address)).join("|");
    const first = sorted[0];
    const last = sorted[sorted.length-1];
    window.open(`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(first?.address||"")}&destination=${encodeURIComponent(last?.address||"")}&waypoints=${waypoints}`,"_blank");
  };

  const priorityColor = {high:{color:"#B91C1C",bg:"#FEE2E2"}, medium:{color:"#B45309",bg:"#FEF3C7"}, low:{color:C.teal,bg:C.tealLight}};

  // Download blank CSV template with example row
  const downloadTemplate = () => {
    const headers = ["Company","Contact","Email","Phone","Address","Tier","Priority","Visit Cycle (days)","Last Visit","YTD Sales","Notes","Website","EIN"];
    const example = ["Apex Car Care LLC","John Smith","john@apexcarcare.com","619-555-0100","1234 Industrial Blvd, San Diego, CA 92101","dealer","medium","30","","0","New customer — needs product demo","www.example.com","82-1234567"];
    const csv = [headers,example].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href=url; a.download="mpc_import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // Export customers to CSV (downloadable as Excel-compatible)
  const exportToCSV = () => {
    const headers = ["Company","Contact","Email","Phone","Address","Tier","Priority","Visit Cycle (days)","Last Visit","YTD Sales","Notes","Website","EIN"];
    const rows = customers.map(c=>[
      c.company,c.contact||"",c.email||"",c.phone||"",c.address||"",
      c.tier||"",c.priority||"",c.visitCycle||30,c.lastVisit||"",
      c.ytd||0,c.notes||"",c.website||"",c.ein||""
    ]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href=url; a.download="mpc_route_customers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // Import customers from CSV
  const importFromCSV = (e) => {
    const file = e.target.files[0];
    if(!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const lines = ev.target.result.split("\n").filter(l=>l.trim());
        const headers = lines[0].split(",").map(h=>h.replace(/"/g,"").trim().toLowerCase());
        const imported = lines.slice(1).map((line,idx)=>{
          const vals = line.match(/(".*?"|[^,]+)(?=,|$)/g)||[];
          const clean = vals.map(v=>v.replace(/^"|"$/g,"").replace(/""/g,'"').trim());
          const get = (key) => clean[headers.indexOf(key)]||"";
          return {
            id: Date.now()+idx,
            company:get("company"),contact:get("contact"),email:get("email"),
            phone:get("phone"),address:get("address"),tier:get("tier")||"dealer",
            priority:get("priority")||"medium",visitCycle:parseInt(get("visit cycle (days)"))||30,
            lastVisit:get("last visit"),ytd:parseFloat(get("ytd sales"))||0,
            notes:get("notes"),website:get("website"),ein:get("ein"),
            lat:32.7+Math.random()*4,lng:-118+Math.random()*6,visitHistory:[]
          };
        }).filter(c=>c.company);
        if(imported.length>0) {
          setCustomers(cs=>[...cs,...imported]);
          setImportError(`✓ Imported ${imported.length} customers successfully`);
        } else {
          setImportError("No valid rows found. Check the CSV format.");
        }
      } catch(err) {
        setImportError("Import failed — check CSV format matches the export template.");
      }
    };
    reader.readAsText(file);
    e.target.value="";
  };

  return (
    <div style={{display:"flex",height:"calc(100vh - 54px)",overflow:"hidden"}}>

      {/* Log visit modal */}
      {logVisitId && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:C.white,borderRadius:16,padding:"28px 32px",width:440}}>
            <div style={{fontSize:18,fontWeight:600,color:C.ink,marginBottom:4}}>Log visit</div>
            <div style={{fontSize:13,color:C.inkLight,marginBottom:16}}>{customers.find(c=>c.id===logVisitId)?.company} · {today}</div>
            <div style={S.fg}><label style={S.label}>Visit notes (optional)</label>
              <textarea style={{...S.input,height:80,resize:"vertical"}} value={visitNote} onChange={e=>setVisitNote(e.target.value)} placeholder="Products discussed, orders placed, follow-ups needed..."/>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button style={S.btnSecondary} onClick={()=>{setLogVisitId(null);setVisitNote("");}} disabled={visitBusy}>Cancel</button>
              <button style={{...S.btnPrimary, opacity:visitBusy?0.6:1, cursor:visitBusy?"not-allowed":"pointer"}} onClick={logVisit} disabled={visitBusy}>{visitBusy ? "Saving…" : "✓ Log visit for today"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit customer modal */}
      {editingCustomer && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20,overflowY:"auto"}}>
          <div style={{background:C.white,borderRadius:16,padding:"28px 32px",width:580,maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{fontSize:16,fontWeight:600,color:C.ink,marginBottom:4}}>Edit customer</div>
            <div style={{fontSize:12,color:C.inkLight,marginBottom:16}}>{editingCustomer.company}</div>

            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`}}>Business information</div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Company name *</label><input style={S.input} value={editForm.company||""} onChange={e=>setEditForm(f=>({...f,company:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>Account tier</label>
                <select style={S.select} value={editForm.tier||"dealer"} onChange={e=>setEditForm(f=>({...f,tier:e.target.value}))}>
                  <option value="dealer">Dealer</option><option value="distributor">Distributor</option><option value="enterprise">Enterprise</option>
                </select>
              </div>
            </div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>EIN / Tax ID</label><input style={S.input} value={editForm.ein||""} onChange={e=>setEditForm(f=>({...f,ein:e.target.value}))} placeholder="XX-XXXXXXX"/></div>
              <div style={S.fg}><label style={S.label}>Website</label><input style={S.input} value={editForm.website||""} onChange={e=>setEditForm(f=>({...f,website:e.target.value}))} placeholder="www.shop.com"/></div>
            </div>

            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`,marginTop:4}}>Contact information</div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Contact name</label><input style={S.input} value={editForm.contact||""} onChange={e=>setEditForm(f=>({...f,contact:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>Email</label><input style={S.input} type="email" value={editForm.email||""} onChange={e=>setEditForm(f=>({...f,email:e.target.value}))}/></div>
            </div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Phone</label><input style={S.input} value={editForm.phone||""} onChange={e=>setEditForm(f=>({...f,phone:e.target.value}))}/></div>
              <div style={S.fg}><label style={S.label}>Priority</label>
                <select style={S.select} value={editForm.priority||"medium"} onChange={e=>setEditForm(f=>({...f,priority:e.target.value}))}>
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </div>
            </div>

            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`,marginTop:4}}>Address</div>
            <div style={S.fg}><label style={S.label}>Full address</label><input style={S.input} value={editForm.address||""} onChange={e=>setEditForm(f=>({...f,address:e.target.value}))}/></div>

            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`,marginTop:4}}>Visit settings & notes</div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Visit cycle (days)</label><input style={S.input} type="number" value={editForm.visitCycle||30} onChange={e=>setEditForm(f=>({...f,visitCycle:parseInt(e.target.value)||30}))} min={7} max={90}/></div>
              <div style={S.fg}><label style={S.label}>YTD sales ($)</label><input style={S.input} type="number" value={editForm.ytd||0} onChange={e=>setEditForm(f=>({...f,ytd:parseFloat(e.target.value)||0}))}/></div>
            </div>
            <div style={S.fg}><label style={S.label}>Notes</label>
              <textarea style={{...S.input,height:72,resize:"vertical"}} value={editForm.notes||""} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))} placeholder="Visit preferences, key products, opportunities..."/>
            </div>

            <div style={{display:"flex",gap:10,justifyContent:"space-between",marginTop:8}}>
              <button style={{...S.btnDanger,padding:"8px 14px"}} onClick={()=>{
                if(window.confirm(`Remove ${editingCustomer.company} from your route? They will be moved to the potential customers list.`)){
                  const removed = customers.find(c=>c.id===editingCustomer.id);
                  setCustomers(cs=>cs.filter(c=>c.id!==editingCustomer.id));
                  if(removed && setProspects) setProspects(ps=>[...ps,{...removed, comments:[...(removed.comments||[]),{date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),text:"Moved from route customers back to potential list."}], reminders:[]}]);
                  setSelectedId(null);
                  setEditingCustomer(null);
                }
              }}>Remove from route</button>
              <div style={{display:"flex",gap:10}}>
                <button style={S.btnSecondary} onClick={()=>setEditingCustomer(null)}>Cancel</button>
                <button style={S.btnPrimary} onClick={()=>{
                  setCustomers(cs=>cs.map(c=>c.id===editingCustomer.id?{...c,...editForm}:c));
                  setEditingCustomer(null);
                }}>Save changes</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {addingCustomer && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20,overflowY:"auto"}}>
          <div style={{background:C.white,borderRadius:16,padding:"28px 32px",width:580,maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{fontSize:16,fontWeight:600,color:C.ink,marginBottom:4}}>Add customer to route</div>
            <div style={{fontSize:12,color:C.inkLight,marginBottom:16}}>All information is saved to your customer database and synced with the wholesale portal.</div>
            {dupWarning && (
              <div style={{background:"#FEF3C7",border:"1px solid #FCD34D",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#92400E"}}>
                <strong>{dupWarning.company}</strong> is already on your {dupWarning.where} list — no need to add it again.{dupWarning.where==="potential customers"?" Convert it from the Potential customers page instead of re-adding it here.":""}
              </div>
            )}
            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`}}>Business information</div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Company name *</label><input style={S.input} value={newCust.company} onChange={e=>{setNewCust(v=>({...v,company:e.target.value}));setDupWarning(null);}} placeholder="Apex Auto Service"/></div>
              <div style={S.fg}><label style={S.label}>Account tier</label>
                <select style={S.select} value={newCust.tier} onChange={e=>setNewCust(v=>({...v,tier:e.target.value}))}>
                  <option value="dealer">Dealer</option><option value="distributor">Distributor</option><option value="enterprise">Enterprise</option>
                </select>
              </div>
            </div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>EIN / Tax ID</label><input style={S.input} value={newCust.ein} onChange={e=>setNewCust(v=>({...v,ein:e.target.value}))} placeholder="XX-XXXXXXX"/></div>
              <div style={S.fg}><label style={S.label}>Website</label><input style={S.input} value={newCust.website} onChange={e=>setNewCust(v=>({...v,website:e.target.value}))} placeholder="www.apexauto.com"/></div>
            </div>

            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`,marginTop:4}}>Contact information</div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Contact name *</label><input style={S.input} value={newCust.contact} onChange={e=>setNewCust(v=>({...v,contact:e.target.value}))} placeholder="John Smith"/></div>
              <div style={S.fg}><label style={S.label}>Email *</label><input style={S.input} type="email" value={newCust.email} onChange={e=>setNewCust(v=>({...v,email:e.target.value}))} placeholder="john@apexauto.com"/></div>
            </div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Phone *</label><input style={S.input} value={newCust.phone} onChange={e=>setNewCust(v=>({...v,phone:e.target.value}))} placeholder="619-555-0100"/></div>
              <div style={S.fg}><label style={S.label}>Priority</label>
                <select style={S.select} value={newCust.priority} onChange={e=>setNewCust(v=>({...v,priority:e.target.value}))}>
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </div>
            </div>

            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`,marginTop:4}}>Address</div>
            <div style={S.fg}><label style={S.label}>Street address *</label><input style={S.input} value={newCust.address} onChange={e=>setNewCust(v=>({...v,address:e.target.value}))} placeholder="1234 Main St"/></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 80px 100px",gap:10}}>
              <div style={S.fg}><label style={S.label}>City</label><input style={S.input} value={newCust.city} onChange={e=>setNewCust(v=>({...v,city:e.target.value}))} placeholder="San Diego"/></div>
              <div style={S.fg}><label style={S.label}>State</label><input style={S.input} value={newCust.state} onChange={e=>setNewCust(v=>({...v,state:e.target.value}))} placeholder="CA"/></div>
              <div style={S.fg}><label style={S.label}>ZIP</label><input style={S.input} value={newCust.zip} onChange={e=>setNewCust(v=>({...v,zip:e.target.value}))} placeholder="92101"/></div>
            </div>

            <div style={{fontSize:11,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`,marginTop:4}}>Visit settings</div>
            <div style={S.twoCol}>
              <div style={S.fg}><label style={S.label}>Visit cycle (days)</label><input style={S.input} type="number" value={newCust.visitCycle} onChange={e=>setNewCust(v=>({...v,visitCycle:parseInt(e.target.value)||30}))} min={7} max={90}/></div>
              <div style={S.fg}><label style={S.label}>Notes</label><input style={S.input} value={newCust.notes} onChange={e=>setNewCust(v=>({...v,notes:e.target.value}))} placeholder="Prefers mornings, DPF focus..."/></div>
            </div>

            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:8}}>
              <button style={S.btnSecondary} onClick={()=>{setAddingCustomer(false);setDupWarning(null);}}>Cancel</button>
              <button style={S.btnPrimary} disabled={!newCust.company||!newCust.address} onClick={()=>{
                // Duplicate guard — check the normalized company name against
                // both this rep's existing route customers AND their
                // prospects before creating a new row. Previously this
                // click always created a brand-new row with a fresh id, no
                // matter how many times the same shop name was submitted.
                const norm = s => String(s||"").trim().toLowerCase();
                const target = norm(newCust.company);
                const existingCust = (customers||[]).find(c=>norm(c.company)===target);
                if (existingCust) { setDupWarning({company:existingCust.company, where:"route customers"}); return; }
                const existingProsp = (prospects||[]).find(p=>norm(p.company)===target);
                if (existingProsp) { setDupWarning({company:existingProsp.company, where:"potential customers"}); return; }
                const fullAddress = [newCust.address, newCust.city, newCust.state, newCust.zip].filter(Boolean).join(", ");
                setCustomers(cs=>[...cs,{id:Date.now(),...newCust,address:fullAddress||newCust.address,lat:32.7+Math.random()*2,lng:-117.5+Math.random()*3,lastVisit:"",visitHistory:[]}]);
                setAddingCustomer(false);
                setDupWarning(null);
                setNewCust({company:"",contact:"",phone:"",email:"",address:"",city:"",state:"",zip:"",ein:"",tier:"dealer",notes:"",priority:"medium",visitCycle:30,website:""});
              }}>Add to route</button>
            </div>
          </div>
        </div>
      )}

      {/* LEFT PANEL — route list */}
      <div style={{width:380,background:C.white,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column",flexShrink:0}}>

        {/* Header */}
        <div style={{padding:"16px 18px",borderBottom:`1px solid ${C.border}`,background:C.navy}}>
          <div style={{fontSize:15,fontWeight:600,color:C.white,marginBottom:10}}>📍 Route planner</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:10}}>
            {[{l:"Total",v:stats.total,c:"rgba(255,255,255,0.9)"},{l:"On track",v:stats.ok,c:"#6EE7B7"},{l:"Due soon",v:stats.due,c:"#FCD34D"},{l:"Overdue",v:stats.late,c:"#FCA5A5"}].map(s=>(
              <div key={s.l} style={{textAlign:"center",background:"rgba(255,255,255,0.08)",borderRadius:8,padding:"8px 4px"}}>
                <div style={{fontSize:18,fontWeight:700,color:s.c}}>{s.v}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.5)",marginTop:1}}>{s.l}</div>
              </div>
            ))}
          </div>
          {/* Search bar */}
          <input
            value={search}
            onChange={e=>setSearch(e.target.value)}
            placeholder="Search company, contact, address..."
            style={{width:"100%",padding:"8px 12px",borderRadius:8,border:"1px solid rgba(255,255,255,0.15)",background:"rgba(255,255,255,0.1)",color:C.white,fontSize:12,outline:"none",boxSizing:"border-box"}}
          />
        </div>

        {/* Controls */}
        <div style={{padding:"10px 12px",borderBottom:`1px solid ${C.border}`,background:C.surface}}>
          <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
            {[["all","All"],["due","Due soon"],["late","Overdue"],["ok","On track"]].map(([k,l])=>(
              <button key={k} onClick={()=>setFilter(k)} style={{padding:"4px 10px",borderRadius:12,border:`1px solid ${filter===k?C.blue:C.border}`,background:filter===k?C.blueLight:C.white,color:filter===k?C.blue:C.inkMid,fontSize:11,cursor:"pointer"}}>
                {l}
              </button>
            ))}
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",gap:4}}>
              {[["route","🗺 Route"],["status","⏱ Status"],["priority","⭐ Priority"],["alpha","A–Z"]].map(([k,l])=>(
                <button key={k} onClick={()=>setSortMode(k)} style={{padding:"4px 8px",borderRadius:6,border:`1px solid ${sortMode===k?C.blue:C.border}`,background:sortMode===k?C.blueLight:C.white,color:sortMode===k?C.blue:C.inkMid,fontSize:10,cursor:"pointer"}}>
                  {l}
                </button>
              ))}
            </div>
            <button style={S.btnSmall} onClick={()=>setAddingCustomer(true)}>+ Add</button>
          </div>
          {/* Import/Export row */}
          <div style={{display:"flex",gap:6,marginTop:8,alignItems:"center"}}>
            <button onClick={downloadTemplate} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",flex:1}}>📋 Template</button>
            <button onClick={exportToCSV} style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",flex:1}}>⬇ Export</button>
            <label style={{...S.btnSecondary,fontSize:11,padding:"4px 10px",flex:1,textAlign:"center",cursor:"pointer",margin:0,border:`1px solid ${C.border}`,borderRadius:8,color:C.inkMid}}>
              ⬆ Import
              <input type="file" accept=".csv,.xlsx" style={{display:"none"}} onChange={importFromCSV}/>
            </label>
          </div>
          {importError && (
            <div style={{marginTop:6,fontSize:11,padding:"5px 10px",borderRadius:6,background:importError.startsWith("✓")?C.greenLight:C.redLight,color:importError.startsWith("✓")?C.green:C.red}}>
              {importError}
            </div>
          )}
          {sortMode==="route" && (
            <div style={{marginTop:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:11,color:C.inkLight}}>Sorted by distance · furthest: ~{stats.totalRoute} mi</span>
              <button style={{...S.btnPrimary,fontSize:11,padding:"5px 10px"}} onClick={openMapsRoute}>Open in Maps →</button>
            </div>
          )}
        </div>

        {/* Customer list */}
        <div style={{flex:1,overflowY:"auto"}}>
          {sorted.length===0 && (
            <div style={{padding:"32px 16px",textAlign:"center",color:C.inkLight}}>
              <div style={{fontSize:24,marginBottom:8}}>🔍</div>
              <div style={{fontSize:13,fontWeight:500,color:C.inkMid,marginBottom:4}}>No customers found</div>
              <div style={{fontSize:11}}>Try a different search or filter</div>
              {search&&<button style={{...S.btnSmall,marginTop:10}} onClick={()=>setSearch("")}>Clear search</button>}
            </div>
          )}
          {sorted.map((cust,i)=>{
            const vs = visitStatus(cust.lastVisit, cust.visitCycle);
            const isSelected = selectedId===cust.id;
            return (
              <div key={cust.id}
                onClick={()=>setSelectedId(isSelected?null:cust.id)}
                style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,cursor:"pointer",background:isSelected?"#EFF6FF":vs.status==="overdue"?"#FDF4FF":vs.status==="late"?"#FFF5F5":vs.status==="soon"?"#FFFBEB":C.white,transition:"background 0.15s"}}
              >
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0}}>
                    {sortMode==="route" && (
                      <div style={{width:22,height:22,borderRadius:"50%",background:C.navy,color:"#fff",fontSize:11,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{i+1}</div>
                    )}
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.ink,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cust.company}</div>
                      <div style={{fontSize:11,color:C.inkLight}}>{cust.contact}{cust.email?` · ${cust.email}`:""}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0,marginLeft:8}}>
                    <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:8,background:vs.bg,color:vs.color}}>{vs.label}</span>
                    <span style={{fontSize:10,padding:"1px 6px",borderRadius:6,background:priorityColor[cust.priority]?.bg,color:priorityColor[cust.priority]?.color}}>{cust.priority}</span>
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:2}}>
                  <div style={{fontSize:11,color:C.blue,cursor:"pointer",textDecoration:"underline",textUnderlineOffset:2,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                    onClick={e=>{e.stopPropagation();openMaps(cust.address);}}>
                    📍 {cust.address}
                  </div>
                  {sortMode==="route" && cust.distFromStart>0 && (
                    <span style={{fontSize:10,color:C.inkLight,marginLeft:8,flexShrink:0}}>{cust.distFromStart}mi</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT PANEL — customer detail */}
      <div style={{flex:1,overflowY:"auto",background:C.surface}}>
        {!selected ? (
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:40,textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:16}}>🗺</div>
            <div style={{fontSize:18,fontWeight:600,color:C.ink,marginBottom:8}}>Select a customer to view details</div>
            <div style={{fontSize:13,color:C.inkLight,marginBottom:24,maxWidth:320}}>Click any customer on the left to see their visit history, notes, and quick actions. The route is sorted nearest-to-nearest — follow it top to bottom for the most efficient drive.</div>
            {stats.late>0 && (
              <div style={{background:"#FEE2E2",border:"1px solid #FCA5A5",borderRadius:10,padding:"12px 20px",fontSize:13,color:"#B91C1C",fontWeight:500}}>
                {stats.late} customer{stats.late>1?"s":""} overdue for a visit
              </div>
            )}
          </div>
        ) : (
          <div style={{padding:24}}>
            {/* Header */}
            <div style={{background:C.white,borderRadius:12,padding:"20px 24px",marginBottom:16,border:`1px solid ${C.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div>
                  <div style={{fontSize:20,fontWeight:600,color:C.ink,marginBottom:6}}>{selected.company}</div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <span style={{fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:8,background:vs.bg,color:vs.color}}>{vs.label}</span>
                    <span style={{fontSize:11,padding:"3px 8px",borderRadius:6,background:priorityColor[selected.priority]?.bg,color:priorityColor[selected.priority]?.color,fontWeight:500}}>{selected.priority} priority</span>
                    <span style={{fontSize:11,padding:"3px 8px",borderRadius:6,background:C.blueLight,color:C.blue,fontWeight:500,textTransform:"capitalize"}}>{selected.tier||"dealer"}</span>
                    {selected.ein&&<span style={{fontSize:11,color:C.inkLight}}>EIN: {selected.ein}</span>}
                  </div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button style={{...S.btnSecondary,fontSize:12}} onClick={()=>{setEditForm({...selected});setEditingCustomer(selected);}}>✏ Edit</button>
                  <button style={S.btnPrimary} onClick={()=>setLogVisitId(selected.id)}>✓ Log visit</button>
                  <button style={{...S.btnSecondary,fontSize:12,background:C.blueLight,color:C.blue,borderColor:C.blue}} onClick={()=>onCreateInvoice&&onCreateInvoice(selected.company)}>📄 Create invoice</button>
                  <button style={{...S.btnSecondary,fontSize:12}} onClick={()=>openMaps(selected.address)}>📍 Directions</button>
                </div>
              </div>
              {/* Contact details row */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,padding:"12px 0",borderTop:`1px solid ${C.border}`}}>
                <div>
                  <div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>Contact</div>
                  <div style={{fontSize:13,fontWeight:500,color:C.ink}}>{selected.contact||"—"}</div>
                </div>
                <div>
                  <div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>Phone</div>
                  <a href={`tel:${selected.phone}`} style={{fontSize:13,fontWeight:500,color:C.blue,textDecoration:"none"}}>{selected.phone||"—"}</a>
                </div>
                <div>
                  <div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>Email</div>
                  <a href={`mailto:${selected.email}`} style={{fontSize:13,fontWeight:500,color:C.blue,textDecoration:"none"}}>{selected.email||"—"}</a>
                </div>
                {selected.website&&<div>
                  <div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>Website</div>
                  <a href={selected.website.startsWith("http")?selected.website:`https://${selected.website}`} target="_blank" rel="noreferrer" style={{fontSize:12,color:C.blue}}>{selected.website}</a>
                </div>}
              </div>
            </div>

            {/* Stats — computed from invoices */}
            {(()=>{
              const custInvs = SHARED_INVOICES.getForCustomer(selected.company).filter(inv=>inv.status!=="cancelled");   // cancelled invoices never count in these stats
              const now = new Date();
              const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate()-7);
              const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
              const yearStart = new Date(now.getFullYear(), 0, 1);
              const invTotal = (invs) => invs.reduce((s,inv)=>s+(inv.total||0),0);
              const parseD = (d) => { try { return new Date(d); } catch(e) { return new Date(0); } };
              const weekSales = invTotal(custInvs.filter(inv=>parseD(inv.date||inv.sharedAt)>=weekAgo));
              const monthSales = invTotal(custInvs.filter(inv=>parseD(inv.date||inv.sharedAt)>=monthStart));
              const yearSales = invTotal(custInvs.filter(inv=>parseD(inv.date||inv.sharedAt)>=yearStart));
              const allTimeSales = selected.ytd||0;
              const paidCount = custInvs.filter(inv=>inv.status==="paid").length;
              const pendingCount = custInvs.filter(inv=>inv.status!=="paid").length;
              return (
                <div style={{marginBottom:16}}>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:10}}>
                    {[
                      {l:"This week",v:`$${weekSales.toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}`,col:C.blue},
                      {l:"This month",v:`$${monthSales.toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}`,col:C.blue},
                      {l:"Year to date",v:`$${(yearSales||allTimeSales).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}`,col:C.teal},
                      {l:"All time",v:`$${allTimeSales.toLocaleString()}`,col:C.ink},
                    ].map(m=>(
                      <div key={m.l} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 12px"}}>
                        <div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>{m.l}</div>
                        <div style={{fontSize:15,fontWeight:700,color:m.col}}>{m.v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                    {[
                      {l:"Invoices",v:String(custInvs.length),col:C.ink},
                      {l:"Paid",v:String(paidCount),col:C.teal},
                      {l:"Pending",v:String(pendingCount),col:pendingCount>0?C.amber:C.ink},
                      {l:"Days since visit",v:vs.days>500?"Never":`${vs.days}d`,col:vs.color},
                    ].map(m=>(
                      <div key={m.l} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 12px"}}>
                        <div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>{m.l}</div>
                        <div style={{fontSize:15,fontWeight:700,color:m.col}}>{m.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Visit status bar */}
            <div style={{background:C.white,borderRadius:12,padding:"14px 18px",marginBottom:16,border:`1px solid ${vs.status==="ok"?C.border:"#FCA5A5"}`}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                <span style={{fontSize:12,fontWeight:600,color:C.ink}}>Visit cycle progress</span>
                <span style={{fontSize:12,color:vs.color,fontWeight:600}}>{vs.label}</span>
              </div>
              <div style={{height:10,background:C.border,borderRadius:5,overflow:"hidden"}}>
                <div style={{height:"100%",borderRadius:5,background:vs.status==="ok"?C.teal:vs.status==="soon"?"#F59E0B":vs.status==="late"?"#EF4444":"#7C3AED",width:`${Math.min(100,(vs.days/selected.visitCycle)*100)}%`,transition:"width 0.5s"}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:4,fontSize:10,color:C.inkLight}}>
                <span>Last visit</span><span>Day {selected.visitCycle} (due)</span>
              </div>
            </div>

            {/* Address & map link */}
            <div style={{background:C.white,borderRadius:12,padding:"14px 18px",marginBottom:16,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:12,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Address</div>
              <div style={{fontSize:13,color:C.ink,marginBottom:10}}>{selected.address}</div>
              <div style={{display:"flex",gap:8}}>
                <button style={{...S.btnPrimary,fontSize:12}} onClick={()=>openMaps(selected.address)}>📍 Open Google Maps</button>
                <button style={{...S.btnSecondary,fontSize:12}} onClick={()=>{
                  const idx = sorted.findIndex(c=>c.id===selected.id);
                  if(idx<sorted.length-1) openMaps(sorted[idx+1].address);
                }}>Next stop →</button>
              </div>
            </div>

            {/* Notes */}
            <div style={{background:C.white,borderRadius:12,padding:"14px 18px",marginBottom:16,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:12,fontWeight:600,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Account notes</div>
              <div style={{fontSize:13,color:C.inkMid,lineHeight:1.6}}>{selected.notes||"No notes yet."}</div>
            </div>

            {/* Visit history + invoices timeline */}
            <div style={{background:C.white,borderRadius:12,overflow:"hidden",border:`1px solid ${C.border}`}}>
              <div style={{padding:"12px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:13,fontWeight:600,color:C.ink}}>Activity history</span>
                <div style={{display:"flex",gap:6}}>
                  <button style={S.btnSmall} onClick={()=>setLogVisitId(selected.id)}>+ Log visit</button>
                  <button style={{...S.btnSmall,color:C.blue}} onClick={()=>onCreateInvoice&&onCreateInvoice(selected.company)}>📄 Invoice</button>
                </div>
              </div>
              {(()=>{
                // Merge visits + invoices into a single timeline
                const visits = (selected.visitHistory||[]).map(v=>({type:"visit",date:v.date,note:v.note,sortDate:new Date(v.date)}));
                const custInvs = SHARED_INVOICES.getForCustomer(selected.company).map(inv=>({
                  type:"invoice",date:inv.date,id:inv.id,total:inv.total,status:inv.status,inv,
                  sortDate:new Date(inv.date||inv.sharedAt)
                }));
                const timeline = [...visits,...custInvs].sort((a,b)=>b.sortDate-a.sortDate);
                if(timeline.length===0) return <div style={{padding:"20px 18px",textAlign:"center",color:C.inkLight,fontSize:13}}>No activity logged yet</div>;
                return timeline.map((entry,i)=>(
                  entry.type==="visit" ? (
                    <div key={"v"+i} style={{padding:"10px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12}}>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <span style={{width:8,height:8,borderRadius:"50%",background:C.teal,display:"inline-block"}}/>
                        <span style={{fontWeight:500,color:C.ink}}>{entry.date}</span>
                        <span style={{fontSize:10,background:C.tealLight,color:C.teal,padding:"1px 6px",borderRadius:4,fontWeight:500}}>Visit</span>
                        {entry.note&&<span style={{color:C.inkMid}}>— {entry.note}</span>}
                      </div>
                    </div>
                  ) : (
                    <div key={"i"+i} style={{padding:"10px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,background:"#FEFCE8"}}>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        <span style={{width:8,height:8,borderRadius:"50%",background:C.blue,display:"inline-block"}}/>
                        <span style={{fontWeight:500,color:C.ink}}>{entry.date}</span>
                        <span style={{fontSize:10,background:"#EFF6FF",color:C.blue,padding:"1px 6px",borderRadius:4,fontWeight:600}}>Invoice {entry.id}</span>
                        <span style={{fontWeight:600,color:C.ink}}>${(entry.total||0).toFixed(2)}</span>
                        <InvoicePill inv={entry.inv||entry}/>
                      </div>
                      <button style={{background:C.blue,color:"#fff",border:"none",padding:"4px 10px",borderRadius:6,fontSize:10,fontWeight:600,cursor:"pointer"}}
                        onClick={()=>onViewInvoice&&onViewInvoice(entry.inv)}>
                        View invoice
                      </button>
                    </div>
                  )
                ));
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export { TrainingHub, MarketingMaterials, ToolLending, RepCommission, RepRoutePlanner, RepPotentialCustomers, INIT_TRAINING, INIT_MATERIALS, INIT_TOOLS, INIT_AGREEMENTS, INIT_REP_PROFILES, INIT_COMMISSION_PAYMENTS };
